// Warbirds.io client entry point: wires net, prediction, rendering, and HUD.

import * as THREE from "three";
import { TerrainField } from "./terrain_core.js";
import * as FDM from "./fdm.js";
import { gunLeadPoint, gunBoresightReticle } from "./gunnery.js";
import { Input, CURSOR_REST_Y, MOUSE_SENS_MIN, MOUSE_SENS_MAX } from "./input.js";
import { AimDirector } from "./aimdirector.js";
import { keymap, ACTIONS, GROUPS, codeLabel, codeHint, repaintHints, setCapturing } from "./keybindings.js";
import { TouchControls, isTouchDevice } from "./touch.js";
import { Net } from "./net.js";
import { Hud } from "./hud.js";
import { GameAudio } from "./audio.js";
import { GameMusic } from "./music.js";
import { Radio } from "./radio.js";
import { makeSpacecraftStep, orbitReadout, craftDeltaV, spaceLeadPoint } from "./spacecraft.js";
import { stepOrbit, orbitAccel, circularVX } from "./orbit_core.js";
import { pursuitAdvice, closestApproach, relativeState, MERGE_RANGE_M } from "./spaceintercept.js";
import { setCurveRadius, setCurveCamera, curveScene, keepFlat, bendPoint, horizonDistance } from "./curveworld.js";
import { SpaceChart } from "./spacechart.js";
import { setSpaceTheater } from "./binsnap.js";
import { SpaceProps } from "./spaceprops.js";
import { SPACE_MODELS } from "./spacehangar.js";
import { PlaneVisual, TEAM_INFO, updateDebris, CLASS_TRAILS, CLASS_BANNERS, CLASS_JET, setSchemes, applyEraModels } from "./planes.js";
import {
  TerrainRenderer,
  buildSky,
  buildClouds,
  ZoneMarker,
  buildBases,
  buildLandTowns,
  AAGuns,
  Trucks,
  SupplyShips,
  GroundVehicles,
  Searchlights,
} from "./world.js";
import { Ships } from "./ships.js";
import { AmbientLife } from "./ambient.js";
import { StormFront } from "./storm.js";
import { Tracers, Bombs, Particles, Smoke, ShellTrails, PlaneContrails, Streamers } from "./effects.js";
import { Chutes, RescueMarkers } from "./chute.js";
import { Scars } from "./scars.js";
import { RoundIntel } from "./intel.js";
import { NearMiss } from "./nearmiss.js";
import { KillConfirm } from "./killconfirm.js";
import { coachLineFor } from "./coach.js";
import { nextTip } from "./tips.js";
import { analytics } from "./analytics.js";
import { serverURL } from "./server.js";

const INTERP_DELAY = 0.12; // render remotes this far in the past, seconds

function randomId(prefix) {
  return crypto.randomUUID?.() ?? `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// Analytics identity (see internal/game/analytics.go). player_id is the
// persistent localStorage UUID — the same value the join handshake carries as
// its token — and is what makes next-day return computable. session_id is fresh
// per page load. isNew is true only on the very first load for this browser.
const analyticsSession = (() => {
  let playerId = null;
  let isNew = false;
  try {
    playerId = localStorage.getItem("va-token");
    if (!playerId) {
      playerId = randomId("tok");
      localStorage.setItem("va-token", playerId);
      isNew = true;
    }
  } catch {
    playerId = randomId("tok"); // private-mode / storage-blocked: still tag this load
  }
  return { playerId, sessionId: randomId("sid"), isNew };
})();

function identityToken() {
  return analyticsSession.playerId;
}

// fireSessionStart posts the one event the server can't observe for itself —
// where the browser came from — to the server-side ingest (/e). Fire-and-forget
// via sendBeacon so it survives the navigation into the game; wrapped so a
// blocked beacon (adblock, no-storage, offline) can never break the page.
function fireSessionStart() {
  try {
    const q = new URLSearchParams(location.search);
    const body = JSON.stringify({
      event: "session_start",
      player_id: analyticsSession.playerId,
      session_id: analyticsSession.sessionId,
      ts: Date.now(),
      is_new_player: analyticsSession.isNew,
      referrer: document.referrer || null,
      utm_source: q.get("utm_source"),
      utm_medium: q.get("utm_medium"),
      utm_campaign: q.get("utm_campaign"),
      platform: matchMedia("(pointer:coarse)").matches ? "mobile" : "desktop",
    });
    const url = serverURL("/e");
    if (navigator.sendBeacon) navigator.sendBeacon(url, body);
    else fetch(url, { method: "POST", body, keepalive: true }).catch(() => {});
  } catch {
    /* analytics must never break the game */
  }
}
fireSessionStart();

// Show the "Add to Home Screen" nudge only on iOS Safari (which has no
// beforeinstallprompt), only when running in a browser tab rather than an
// already-installed standalone app, and only until the user dismisses it.
function maybeShowInstallHint() {
  const ua = navigator.userAgent || "";
  const iOS =
    /iphone|ipad|ipod/i.test(ua) ||
    // iPadOS 13+ reports a desktop UA; sniff it via touch points instead.
    (navigator.platform === "MacIntel" && (navigator.maxTouchPoints ?? 0) > 1);
  const standalone =
    navigator.standalone === true || matchMedia("(display-mode: standalone)").matches;
  if (!iOS || standalone || localStorage.getItem("va-ios-hint") === "1") return;

  const hint = document.getElementById("ios-install-hint");
  if (!hint) return;
  hint.classList.remove("hidden");
  document.getElementById("ios-install-dismiss")?.addEventListener("click", () => {
    hint.classList.add("hidden");
    localStorage.setItem("va-ios-hint", "1");
  });
}

// Exponential camera chase with velocity feed-forward: a plain lerp toward a
// moving anchor lags by an amount that varies with frame time, so dt jitter
// reads as the subject trembling on screen. Solving the follow ODE for a
// linearly moving target keeps the lag constant at v/k at any frame rate.
class Follower {
  constructor() {
    this.prev = new THREE.Vector3(); // anchor from last frame
    this.lag = new THREE.Vector3(); // anchor velocity / k = steady-state lag
    this.valid = false;
  }
  reset() {
    this.valid = false;
  }
  /** Move pos toward desired with stiffness k; snaps on teleports. */
  update(pos, desired, k, dt, snapDist = 60) {
    if (this.valid && dt > 0 && this.prev.distanceTo(desired) < snapDist) {
      this.lag.copy(desired).sub(this.prev).divideScalar(k * dt);
      pos.sub(this.prev).add(this.lag).multiplyScalar(Math.exp(-k * dt)).add(desired).sub(this.lag);
    } else {
      pos.copy(desired); // first frame or teleport: snap
    }
    this.prev.copy(desired);
    this.valid = true;
  }
}

async function boot() {
  analytics.init({ context: "game", props: { touch: isTouchDevice() } });
  const config = await (await fetch(serverURL("config.json"))).json();
  setSchemes(config.schemes ?? []); // earnable paint schemes (roadmap #25)
  const field = new TerrainField(config.world);
  // Fixed physics step mirrors the server tick; inputs are sampled and sent
  // every TICKS_PER_INPUT physics ticks so prediction, the reconciliation
  // replay, and the server's paced input queue all see each control sample
  // steer for exactly the same number of ticks.
  const PHYS_DT = 1 / (config.net.tickRateHz || 60);
  const TICKS_PER_INPUT = Math.max(
    1,
    Math.round((config.net.tickRateHz || 60) / (config.net.inputRateHz || 30)),
  );
  const planeTypes = config.aircraftTypes;
  // Era rosters (docs/early-jets-era.md) name their silhouettes via each
  // type's `model` key; rewire the per-index visual tables before anything
  // builds a plane. A WW2 roster carries no keys and nothing changes. The
  // orbital hangar (spacehangar.js) rides in on the same mechanism — the
  // space roster names hulls no era roster has ever heard of.
  applyEraModels(planeTypes, SPACE_MODELS);
  const calibers = config.calibers ?? [];
  const trainingScenarios = config.training?.scenarios ?? [];
  // Server mode flags injected into the page (see cmd/server): a standalone
  // training server flags itself so the join screen becomes the flight-school
  // picker; a game server may carry a link out to a training server.
  const trainingServer = typeof window !== "undefined" && window.__WARBIRDS_TRAINING__ === true;
  const trainURL = (typeof window !== "undefined" && window.__WARBIRDS_TRAIN_URL__) || "";
  // Where "back to the main game" leads from a training server. Prefer the
  // ?from= origin the game hands us when its TRAINING button links across (works
  // with zero config, the common path); fall back to an operator-set GAME_URL,
  // then the referring origin. Empty means we don't know — hide the button.
  const gameURL = (() => {
    try {
      const from = new URLSearchParams(location.search).get("from");
      if (from) return from;
    } catch {}
    if (typeof window !== "undefined" && window.__WARBIRDS_GAME_URL__) return window.__WARBIRDS_GAME_URL__;
    try {
      if (document.referrer) {
        const r = new URL(document.referrer);
        if (r.origin !== location.origin) return r.origin;
      }
    } catch {}
    return "";
  })();
  const calByName = new Map(calibers.map((c) => [c.name, c]));
  const caliberOf = (plane) =>
    calByName.get(planeTypes[plane]?.gun?.caliber) ?? calibers[0] ?? {};

  // --- three.js scaffolding ---
  const onTouch = isTouchDevice();
  const canvas = document.getElementById("game");
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  // Phone GPUs pay dearly for high-DPI fill; cap lower there to hold 60 fps.
  renderer.setPixelRatio(Math.min(devicePixelRatio, onTouch ? 1.5 : 2));
  // The naval theater fights across the whole ocean, so the far plane has to
  // reach a ship on the far side of the map (the air game keeps the tighter 12 km
  // plane for depth precision). Scales with the map so it follows a bigger sea.
  const farPlane =
    config.world.theater === "naval" || config.world.theater === "armada"
      ? Math.max(12000, config.world.radiusM * 2.6)
      : config.world.theater === "land"
        ? Math.max(12000, config.world.radiusM * 2.2)
        : config.world.theater === "space"
          ? Math.max(12000, config.world.radiusM * 2.4)
          : 12000;
  const camera = new THREE.PerspectiveCamera(72, 1, 0.5, farPlane);
  // Change the camera FOV only when it actually differs (the naval gunsight
  // punches it in; everything else uses the base 72°).
  const setCameraFov = (f) => {
    if (camera.fov !== f) {
      camera.fov = f;
      camera.updateProjectionMatrix();
    }
  };
  const scene = new THREE.Scene();

  const resize = () => {
    renderer.setSize(innerWidth, innerHeight);
    camera.aspect = innerWidth / innerHeight;
    camera.updateProjectionMatrix();
  };
  addEventListener("resize", resize);
  resize();

  const navalTheater = config.world.theater === "naval";
  // The land theater: players crew armored vehicles (Gunner-HEAT style).
  // Most of the naval conning plumbing (drive controls, consumable wire
  // fields, the auto-ranged sight) is shared; hullTheater gates the parts
  // common to both, landTheater the armor-war specifics.
  const landTheater = config.world.theater === "land";
  // The combined-arms theater ("armada", the Brick Gulf): planes are the
  // default spawn like the air game, but BOTH hull classes are on the roster,
  // so everything hull-flavored dispatches per driven hull type (see
  // drivenType) rather than per theater.
  const armadaTheater = config.world.theater === "armada";
  // The space theater (the Brick Moon, docs/space-game-mode-spec.md):
  // players fly spacecraft on the orbital kernel. Everything it gates is
  // additive — flag off, every other theater runs exactly as before.
  const spaceTheater = config.world.theater === "space";
  setSpaceTheater(spaceTheater); // the zone section carries an extra field
  // The Brick Moon's kernel constants, mirrored from the server's
  // zero-means-default reads (spacecombat.go / orbit.go).
  const spaceWrapM = config.world.wrapM > 0 ? config.world.wrapM : 100800;
  const spaceConsts = {
    wrapM: spaceWrapM,
    R: spaceWrapM / (2 * Math.PI),
    mu: 0, // filled below from R
    ceiling: config.space?.ceilingM > 0 ? config.space.ceilingM : 3200,
  };
  spaceConsts.mu =
    (config.space?.surfaceGravity > 0 ? config.space.surfaceGravity : 0.83) *
    spaceConsts.R * spaceConsts.R;
  // THE ARENA's anchor: the station group's live longitude (one f32 a
  // snapshot — the formation is rigid, so one is enough for all three prizes)
  // and the shell they all ride, learned from the welcome's zone plan.
  let spaceStationX = null;
  let spaceStationShellY = 450;
  // The nose-swing law, the shape of the server's spaceSteerRates: the
  // rotation axis is nose × target (its length is the sine of the error — a
  // natural proportional gain) taken into the body frame; a target behind
  // the tail gets a full-rate swing so the hold never stalls on the
  // antipode. Roll stays damped — the gyros keep the wings where you left
  // them. The gain is SOFTER than the server gyro's 5: the RCS needs ~36°
  // to spool a full-rate swing back down (maxRate²/2·accel), so a gain
  // whose proportional band is narrower than that overshoots the target
  // and hunts — the mind-reader watched the keeper's nose saw across its
  // burn vector, dot 0.99 → 0.36 → 0.93, bore gate slamming with it.
  const spaceSteerRatesClient = (att, dir, maxRate) => {
    const fwd = FDM.qRotate(att, FDM.v3(1, 0, 0));
    let ax = fwd.y * dir.z - fwd.z * dir.y;
    let ay = fwd.z * dir.x - fwd.x * dir.z;
    let az = fwd.x * dir.y - fwd.y * dir.x;
    if (fwd.x * dir.x + fwd.y * dir.y + fwd.z * dir.z < 0) {
      const n = Math.hypot(ax, ay, az);
      if (n < 0.05) {
        const p = FDM.qRotate(att, FDM.v3(0, 0, 1)); // dead astern: pick pitch
        ax = p.x; ay = p.y; az = p.z;
      } else {
        ax /= n; ay /= n; az /= n;
      }
    }
    const body = FDM.qRotate(FDM.qConj(att), FDM.v3(ax * 2.2, ay * 2.2, az * 2.2));
    const clampR = (v) => Math.max(-maxRate, Math.min(maxRate, v));
    return { yaw: clampR(body.y), pitch: clampR(body.z) };
  };
  // Gyro autopilot state ("sh" on the input wire: 0 free, 1 prograde,
  // 2 retrograde). The stick taking over clears it — mirrored server-side.
  let spaceHold = 0;
  // Sim time until which the DETECTED banner stands (the server nags while
  // an enemy holds you; see computeSpaceSpotting).
  let spaceDetectedUntil = 0;
  // Consumable pulses riding the next flight input (the naval pending idiom):
  // C pops a chaff cluster on the smoke field, T runs cold on the repair
  // field. Ready-clock estimates keep the HUD honest without new wire.
  let spaceDecoyPending = false;
  let spaceColdPending = false;
  let spaceDecoyReadyAtClient = 0;
  let spaceColdReadyAtClient = 0;
  // The touch ASAT chip pulses the launch hold (a tap can't hold R); the
  // pro/retro chips repaint through this hook when their hold engages.
  let spaceMslPulseUntil = 0;
  let spaceChipPaint = null;
  // The chip/key verb table, built in the space block above window.__va's
  // birth and exported onto it afterwards (setting it early killed the boot:
  // "Cannot set properties of undefined" — caught by the phone repro).
  let spaceActionsRef = null;
  // THE FLIGHT DIRECTOR — the camera IS the intent. Out here the stick and
  // the mouse orient your GAZE freely around the craft; the lever means
  // "travel the way I'm looking while it's in, hold when it's out"; the
  // trigger means "lay the nose on the firing solution and let the guns talk
  // when they're lined". Underneath, a velocity-error law with the kernel's
  // own feedforward translates the intent into burns — a held target is held
  // exactly, and a closed lever trims to circular and coasts for free — so
  // "up" finally means up, and nobody has to know that the burn that buys it
  // points somewhere else entirely ("is it possible to make the director
  // translate this for us?", the design verbatim). The roof and terrain
  // guards outrank everything: the director will not fly you into the
  // regolith to please your eyeline. P — or the GUID chip — drops to raw RCS.
  let keeperOn = true; // the master switch (P / GUID chip)
  let keeperMode = ""; // what the director is doing, for the chips line
  const spaceCam = { yaw: 0, pitch: 0 }; // the gaze (house yaw: atan2(-z, x))
  let spaceCamDir = { x: 1, y: 0, z: 0 }; // the gaze as a unit vector, refreshed each frame
  let spaceCamSync = true; // snap the gaze onto the nose (spawn, P toggle) — no camera cuts
  let spaceEngineCmd = -1; // the director's engine order this frame; -1 = the player's lever, raw
  let spaceFireOK = true; // GUNS: the trigger reaches the wire only when the nose is laid
  let spaceLandingIntent = false; // gaze steeply down + lever = a landing order (hysteresis)
  let spaceWasLanded = false; // touchdown edge: park the lever so it can't instantly relaunch
  let spaceLiftoffRawUntil = 0; // liftoff grace: the wire carries the raw lever until the server is airborne
  let spaceGunSolution = null; // the FCS mark, stashed by the pip solve each frame
  let spaceGunSolutionAt = 0;
  // THE GUN RING — the second half of the decoupling, and the one a gunner
  // feels. The guns are no longer bolted to the nose: they ride a mount that
  // walks the barrels wherever you are LOOKING while the hull goes on flying
  // the travel intent. Before this, aiming and going were the same order and
  // took turns badly — the nose cycled between "lay on the mark" and "point
  // at where we're going", so a burst cost you the burn and a burn cost you
  // the shot ("they are in competition", the design verdict). Now the camera
  // designates, the fire-control computer leads, the ring slews, and the
  // trigger waits for the barrels — all of it while the bell is lit.
  let spaceGunYaw = 0; // where our mount points (world frame, house yaw), predicted
  let spaceGunPitch = 0;
  let spaceGunAimYaw = 0; // the standing order the ring is walking toward
  let spaceGunAimPitch = 0;
  let spaceGunsLaid = true; // the barrels agree with the order — rounds may go
  let spaceGunsMode = ""; // for the chips line: LAID / SLEWING / SETTLING / free
  // RECOIL, mirrored from spacecombat.go's constants. Every round shoves the
  // barrels off the lay the mount is holding; the deflection bleeds off as it
  // comes back into battery, and the gun refuses to fire until it has. That is
  // the whole burst mechanic — fire three or four, walk off the mark, wait a
  // beat, fire again — and nothing anywhere counts rounds to get it.
  const SPACE_GUN_KICK_RAD = 0.007;
  const SPACE_GUN_KICK_YAW_FRAC = 0.25;
  const SPACE_GUN_SETTLE_TAU = 0.7;
  const SPACE_GUN_BREAK_RAD = 0.012;
  const SPACE_GUN_RESUME_RAD = 0.003;
  let spaceGunKickYaw = 0;
  let spaceGunKickPitch = 0;
  let spaceGunOutOfBattery = false;
  let spaceGunNextAt = 0; // local mirror of the server's cadence gate
  const SPACE_GUN_ELEV_MAX = 1.35; // the mount's stops (mirrors spaceGunElevMax)
  // THE CHASE PANEL — how a human closes on somebody out here.
  //
  // Orbital pursuit has exactly one counter-intuitive fact in it, and it is
  // the fact that decides fights: to catch a craft AHEAD of you, go DOWN. A
  // lower orbit is a faster one. Point at him and burn — which is what the
  // hands do on instinct, and what the director happily flies — and you climb,
  // slow down, and watch him pull away.
  //
  // We tried having the computer just fly the correct thing, and the probe
  // (tools/check_space_intercept.mjs) priced it: flown fast it is a hover and
  // costs 164-750 m/s of a 420 m/s tank, flown cheap it costs 40 and takes
  // three minutes. Neither is a button. So instead the cockpit DESIGNATES and
  // NARRATES: look at a contact and the panel names him, gives the range, the
  // signed closing rate, where this pass actually ends up (closest approach,
  // flown on the real kernel), and the one instruction that matters —
  // "▼ DROP 240 m — 118 s". The pilot flies it with the lever and the gaze
  // they already have, and the range starts falling, and after two or three
  // chases they know the trick for life.
  let spaceDesigId = 0; // the designated contact (0 = none)
  let spaceDesigUntil = 0; // stickiness: a glance at the instruments must not drop him
  let spaceChase = null; // {name, range, closing, missM, missT, advice} — the panel's data
  let spaceChaseTaught = 0; // when we last said the quiet part out loud
  const SPACE_DESIG_RANGE_M = 5200; // designation reach: past this the fog owns him anyway
  const SPACE_DESIG_CONE = 0.32; // ~18°: looking AT him, not merely past him
  // Stickiness has to outlast the MANEUVER, not just a glance — a lesson from
  // the first playtest of this panel. The advice is "look down and dive", and
  // at 1.2 s of stick the mark died the moment the pilot did what the panel
  // told them to, taking the numbers with it. Twelve seconds covers a dive,
  // and a held mark is only stolen by a contact clearly closer to the eyeline
  // (below), so diving through traffic doesn't re-target you either.
  const SPACE_DESIG_STICK_S = 12;
  // Posing a gun ring: the wire (and our own prediction) carry the mount's
  // aim in the WORLD frame, because that's the frame it's ordered in and the
  // frame the rounds fly in; the model's pivots want it in the hull's frame.
  // One conversion, shared by our craft and every craft we can see — which
  // is why an enemy's barrels track you now even as their hull slews off to
  // fly its own burn.
  const ringDirV = new THREE.Vector3();
  const ringQ = new THREE.Quaternion();
  function poseSpaceRings(visual, quat, gunYaw, gunPitch, firing) {
    if (!visual?.turrets?.length) return;
    const cp = Math.cos(gunPitch);
    ringDirV.set(cp * Math.cos(gunYaw), Math.sin(gunPitch), -cp * Math.sin(gunYaw));
    ringDirV.applyQuaternion(ringQ.copy(quat).invert());
    const by = Math.atan2(-ringDirV.z, ringDirV.x);
    const bp = Math.asin(Math.max(-1, Math.min(1, ringDirV.y)));
    // A degenerate attitude (a zero quaternion out of an interpolation gap)
    // inverts into NaN, and a NaN pivot rotation poisons every matrix under
    // it. Leave the mount where it was instead: a frame of stale aim is
    // invisible, a frame of NaN is a hull that vanishes.
    if (!Number.isFinite(by) || !Number.isFinite(bp)) return;
    for (let i = 0; i < visual.turrets.length; i++) visual.setTurretAim(i, by, bp, firing);
  }
  // Shortest-path ease for an angle — remote rings arrive at 20 Hz and would
  // step visibly without it (our own ring is exact: it's simulated locally).
  function easeAngle(cur, want, k) {
    return cur + Math.atan2(Math.sin(want - cur), Math.cos(want - cur)) * k;
  }
  /**
   * Who is the pilot chasing? Whoever they are LOOKING at — the same gesture
   * that already designates for the guns, so the theater keeps exactly one
   * verb: point the camera at the thing you mean. The mark is sticky for a
   * beat, because reading your own instruments must not drop the contact, and
   * it dies with him. Returns { name, pos, vel } or null.
   */
  function spaceDesignation() {
    const held = spaceDesigId ? entities.get(spaceDesigId) : null;
    const heldOK = held && held.status !== 0 && held.buffer.length;
    // Look for a better mark under the gaze: nearest to the eyeline wins, and
    // an enemy we can see at all has already cleared the fog (binsnap only
    // ships spotted hulls out here), so anything on the board is fair game.
    let best = null, bestAngle = SPACE_DESIG_CONE;
    let heldAngle = Infinity;
    for (const ent of entities.values()) {
      if (ent.status === 0 || ent.status === 3 || ent.status === 4 || ent.status === 5) continue;
      const info = roster.get(ent.id);
      if (!info || info.team === selfTeam || info.ad) continue;
      const last = ent.buffer.at(-1);
      if (!last) continue;
      const dx = last.pos.x - pred.pos.x, dy = last.pos.y - pred.pos.y, dz = last.pos.z - pred.pos.z;
      const dist = Math.hypot(dx, dy, dz);
      if (!Number.isFinite(dist) || dist > SPACE_DESIG_RANGE_M || dist < 20) continue;
      const angle = Math.acos(
        Math.min(1, (dx * spaceCamDir.x + dy * spaceCamDir.y + dz * spaceCamDir.z) / dist),
      );
      if (ent.id === spaceDesigId) heldAngle = angle;
      if (angle < bestAngle) {
        bestAngle = angle;
        best = { ent, info, last };
      }
    }
    // A held mark keeps the panel unless somebody else is CLEARLY the one being
    // looked at: half the angle. Without that, flying through a scrum shuffles
    // the intercept every second and the ETA never settles.
    if (best && heldOK && best.ent.id !== spaceDesigId && bestAngle > heldAngle * 0.5) {
      best = null;
    }
    if (best) {
      if (best.ent.id !== spaceDesigId) {
        spaceDesigId = best.ent.id;
        // Say the quiet part out loud, once in a while: this is the lesson the
        // whole panel exists to teach, and a pilot who reads it once tends to
        // remember it forever.
        if (perfNow() - spaceChaseTaught > 90) {
          spaceChaseTaught = perfNow();
          hud.message(`TRACKING ${best.info.name} — the panel has the intercept`, 2200, "#9fd8f0");
        }
      }
      spaceDesigUntil = perfNow() + SPACE_DESIG_STICK_S;
      return { name: best.info.name, pos: best.last.pos, vel: best.last.vel };
    }
    if (heldOK && perfNow() < spaceDesigUntil) {
      const last = held.buffer.at(-1);
      return { name: roster.get(spaceDesigId)?.name ?? "CONTACT", pos: last.pos, vel: last.vel };
    }
    spaceDesigId = 0;
    return null;
  }
  // THE TELESCOPE — the 1960s optic for a war fought at ranges where a hull
  // is a two-pixel glint ("they were so far away I couldn't see them", the
  // playtest verdict that built the tally board below). Hold Z to look, let
  // go to fly; the touch deck latches it with the ZOOM chip instead, because
  // a thumb can't hold a key and a stick at once.
  let spaceZoomKey = false; // desktop: held while Z is down
  let spaceZoomLatch = false; // touch: ZOOM chip toggle
  let spaceChipsRow = null; // the touch verb deck (hidden off-duty by the render loop)
  const SPACE_SCOPE_FOV = 14; // 72° wide → 14°: a ×5 spotting telescope
  const hullTheater = navalTheater || landTheater;
  // The TIME PERIOD, orthogonal to the theater (docs/early-jets-era.md):
  // "jets" swaps the roster for the early-jet hangar and lights up the
  // heat-seeker HUD (lock ring, rails/flare chips, R/L keys). Everything it
  // gates is additive — era off, the WW2 client runs exactly as before.
  const jetsEra = config.world.era === "jets";
  if (jetsEra) document.body.classList.add("jets");
  const navalSmokeOn = !!config.naval?.smokeEnabled;
  // Field-manual context (tips.js): which chapters this client should draw
  // from. Theater picks the book, the era adds the jets pages, and the smoke
  // flag keeps the manual from bragging about disabled kit.
  const tipCtx = { theater: config.world.theater || "air", jets: jetsEra, navalSmoke: navalSmokeOn };
  const vehTypesCfg = config.groundVehicles?.types ?? [];
  // Per-hull consumable tuning (cooldown estimates for the HUD): a tank
  // reads the land block, a warship the naval block, whatever the theater.
  const drivenIsLand = () => !!vehTypesCfg[vehType()]?.land;
  const drivenIsShip = () => !!vehTypesCfg[vehType()]?.naval;
  // First land-class index in the roster (combined arms lists warships first),
  // so the per-class optics/reticle tables index within the land class.
  let vehLandBase = 0;
  for (let i = 0; i < vehTypesCfg.length; i++) {
    if (vehTypesCfg[i]?.land) {
      vehLandBase = i;
      break;
    }
  }
  const landVehIdx = () => vehType() - vehLandBase;
  const crewCfg = () =>
    ((armadaTheater ? drivenIsLand() : landTheater) ? config.land : config.naval) ?? {};
  // Body hook so the naval theater can re-tune the touch HUD layout (the ship
  // overlays crowd the small screen differently than the air game does).
  if (navalTheater) document.body.classList.add("naval");
  // The land theater reuses the naval body layout hooks (same conning HUD
  // lanes) plus its own class for land-specific styling.
  if (landTheater) document.body.classList.add("naval", "land");
  // Combined arms drives both hull HUDs from the same lanes.
  if (armadaTheater) document.body.classList.add("naval", "armada");
  if (spaceTheater) {
    document.body.classList.add("space");
    const hb = document.getElementById("hint-bar");
    if (hb) {
      hb.textContent = onTouch
        ? "you arrive on orbit — guidance flies where you look · left thumb = your eyes · " +
          "HOLD a lever area to go that way, let go to hold station · look straight down to land · " +
          "the guns turn with your eyes — FIRE any time · ZOOM telescope · the chips up top are your kit"
        : "you arrive on orbit — guidance flies where you look (click for the glass) · mouse = your eyes · " +
          "HOLD the throttle to go that way, release to hold station · look straight down to land · " +
          "the guns turn with your eyes — FIRE any time · " +
          "P raw RCS · Z telescope · K/J gyros · M chart · B heavy gun/rods · R ASAT · C chaff · T cold run";
    }
    // The gyro autopilots — the two buttons that make orbits flyable. The
    // stick clears them (sendFlightInput mirrors the server's rule). K and J
    // are the map's last unclaimed letters: H is the comms wheel (a HOLD key
    // — the first playtest watched it eat a Shift release and pin the
    // throttle open all the way to escape velocity), N cycles spectate.
    // One set of verbs for two kinds of hands: the desktop keys and the
    // touch chips below both dispatch here, so the phone is never a lesser
    // cockpit (the first mobile sortie had NO path to the gyros, the chaff,
    // the cold run, or the chart — every one was a keyboard letter).
    const spaceActions = {
      pro() {
        spaceHold = spaceHold === 1 ? 0 : 1;
        // A manual hold is a standing order: the keeper defers to it (see the
        // steering block), and the assist channel is zeroed at the latch so a
        // keeper deflection sampled this same frame can't trip the
        // stick-clears-holds rule and eat the latch it just made.
        if (spaceHold) input.setAssist(0, 0, 0);
        hud.message(spaceHold === 1 ? "GYROS: HOLDING PROGRADE" : "GYROS: FREE", 1400);
      },
      retro() {
        spaceHold = spaceHold === 2 ? 0 : 2;
        if (spaceHold) input.setAssist(0, 0, 0);
        hud.message(spaceHold === 2 ? "GYROS: HOLDING RETROGRADE" : "GYROS: FREE", 1400);
      },
      chaff() {
        // Chaff on the smoke wire. The server owns the magazine and the
        // cooldown; the client clock just keeps the message honest.
        if (perfNow() >= spaceDecoyReadyAtClient) {
          spaceDecoyPending = true;
          spaceDecoyReadyAtClient = perfNow() + 9;
          hud.message("CHAFF AWAY — cluster blooming on your orbit", 1600, "#c2ccd6");
        } else {
          hud.message("chaff launcher cycling…", 1000, "#9fb2c8");
        }
      },
      cold() {
        // Cold run on the repair wire: the submarine dive, in vacuum.
        if (perfNow() >= spaceColdReadyAtClient) {
          spaceColdPending = true;
          spaceColdReadyAtClient = perfNow() + 26;
          hud.message("COLD RUN — radiators in, bell and guns offline", 2200, "#7fd1ff");
        } else {
          hud.message("radiators still soaking heat…", 1000, "#9fb2c8");
        }
      },
      plot() {
        const on = spaceChart?.toggle();
        hud.message(on ? "THE PLOT — the sky unrolled" : "plot stowed", 1200, "#8fa3c0");
      },
      asat() {
        // A tap is a press: pulse the launch hold long enough for the next
        // few input frames to carry the rising edge the server wants.
        spaceMslPulseUntil = perfNow() + 0.6;
      },
      guid() {
        keeperOn = !keeperOn;
        spaceCamSync = true; // hand the gaze over nose-to-nose, no camera cut
        hud.message(
          keeperOn
            ? "GUIDANCE: ON — look, and the ship follows"
            : "GUIDANCE: OFF — raw RCS, she's all yours, captain",
          2200, "#5fe07a",
        );
      },
      zoom() {
        // The touch latch — desktop holds Z instead and never sees this verb.
        spaceZoomLatch = !spaceZoomLatch;
        hud.message(spaceZoomLatch ? "TELESCOPE ×5 — steady on the RCS" : "telescope stowed", 1200, "#8fa3c0");
      },
    };
    spaceActionsRef = spaceActions; // exported onto __va after it's built below
    addEventListener("keydown", (e) => {
      if (e.repeat || e.target instanceof HTMLInputElement) return;
      const act = { KeyK: "pro", KeyJ: "retro", KeyC: "chaff", KeyT: "cold", KeyM: "plot", KeyP: "guid" }[e.code];
      if (act) spaceActions[act]();
    });
    // The telescope key. Z ships as a throttle-up default in the air game,
    // so in vacuum the CAPTURE phase claims it before the input layer can
    // ram the lever open — the first scoped playtest lit the main engine
    // and stood the keeper down; the "telescope" was a burn. Repeats are
    // swallowed too: one leaked repeat puts Z in the held-keys set and the
    // lever creeps for as long as the eye is at the glass.
    addEventListener("keydown", (e) => {
      if (e.code === "KeyZ" && !(e.target instanceof HTMLInputElement)) {
        spaceZoomKey = true; // a HOLD, not a toggle: let go and you're flying again
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    }, { capture: true });
    addEventListener("keyup", (e) => {
      if (e.code === "KeyZ") {
        spaceZoomKey = false;
        e.stopImmediatePropagation();
      }
    }, { capture: true });
    // The gaze on desktop: the pointer-lock mouse, consumed here rather than
    // by the cursor machinery (the theater quarantines mouseAim, and the lock
    // opens through input.js's spaceRcs gate on canvas click). House yaw is
    // atan2(-z, x), so panning RIGHT is yaw DOWN — copy a bearing, never
    // derive one. Raw mode (P) gives the mouse back: the gaze rides the nose.
    addEventListener("mousemove", (e) => {
      if (!input.pointerLocked || onTouch || !keeperOn) return;
      const s = 0.0026 * input.mouseSens;
      spaceCam.yaw -= (e.movementX ?? 0) * s;
      spaceCam.pitch = Math.max(-1.35, Math.min(1.35, spaceCam.pitch - (e.movementY ?? 0) * s));
    });
    // The touch deck's chip row: the same verbs as buttons, top-center where
    // neither thumb lives. PRO/RETRO light up while their hold is engaged
    // (the chips update block repaints them); ASAT shows the same refusal
    // narration the R key gets, because a silent no is how the first mobile
    // sortie read "there's nothing for the missiles".
    if (onTouch) {
      const row = document.createElement("div");
      spaceChipsRow = row; // the render loop stows the deck off-duty (join screen, death)
      row.id = "space-chips";
      row.style.cssText =
        "position:absolute;left:50%;transform:translateX(-50%);top:52px;z-index:8;display:flex;" +
        "gap:6px;pointer-events:none;";
      const mk = (label, act) => {
        const b = document.createElement("button");
        b.dataset.act = act;
        b.textContent = label;
        b.style.cssText =
          "pointer-events:auto;font:700 11px 'Courier New',monospace;color:#cfe3ff;" +
          "background:rgba(10,16,28,0.72);border:1px solid #3a4a63;border-radius:5px;" +
          "padding:7px 10px;touch-action:manipulation;";
        b.addEventListener("pointerdown", (e) => {
          e.preventDefault();
          spaceActions[act]();
        });
        row.appendChild(b);
        return b;
      };
      const chipGuid = mk("GUID", "guid");
      const chipPro = mk("PRO", "pro");
      const chipRetro = mk("RET", "retro");
      const chipZoom = mk("ZOOM", "zoom");
      mk("PLOT", "plot");
      mk("CHAFF", "chaff");
      mk("COLD", "cold");
      mk("ASAT", "asat");
      spaceChipPaint = () => {
        const lit = "rgba(95,224,122,0.35)", off = "rgba(10,16,28,0.72)";
        chipGuid.style.background = keeperOn && steerMode === "keeper" ? lit : off;
        chipGuid.style.opacity = keeperOn ? "1" : "0.45";
        chipPro.style.background = spaceHold === 1 ? lit : off;
        chipRetro.style.background = spaceHold === 2 ? lit : off;
        chipZoom.style.background = spaceZoomLatch ? lit : off;
      };
      document.body.appendChild(row);
    }
  }
  // The orbit chips: APO/PERI/Δv, the instruments that make the well legible
  // (the spec's M2 stub — the full orbital chart is a later milestone).
  let orbitChips = null;
  let orbitChipsTick = 0;
  if (spaceTheater) {
    orbitChips = document.createElement("div");
    orbitChips.id = "orbit-chips";
    // Desktop: the left rail under the score. Touch: that spot is the
    // flight-data panel's, so the instruments drop BELOW it (the phone
    // screenshot had APO/PERI printing straight through SPD/ALT/THR).
    // Touch: RIGHT of the throttle rail, not above it. The rail owns the left
    // 92 px from top:190 down, and the chase panel below added two more lines
    // to this block — which walked it straight into the lever and tripped the
    // e2e's chrome-overlap guard, the same guard that caught the gauge stack
    // eating full throttle. 104 px clears the rail with daylight.
    orbitChips.style.cssText = onTouch
      ? "position:absolute;left:104px;top:172px;z-index:6;font:600 12px/1.5 'Courier New',monospace;" +
        "color:#cfe3ff;text-shadow:0 1px 3px #000;white-space:pre;pointer-events:none;"
      : "position:absolute;left:14px;top:118px;z-index:6;font:600 13px/1.5 'Courier New',monospace;" +
        "color:#cfe3ff;text-shadow:0 1px 3px #000;white-space:pre;pointer-events:none;";
    document.body.appendChild(orbitChips);
  }
  // The barge's live plot (spec §6: the defender sees a visible arc; the
  // GUNNER sees where a rod released NOW would strike). A red ring laid on
  // the regolith, re-solved on the chips' slow clock by flying the release
  // ballistically on the same kernel the server will fly it on — the aim is
  // the release time, and this ring is what makes that a decision instead
  // of a guess.
  let rodPlot = null;
  if (spaceTheater) {
    rodPlot = new THREE.Group();
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(50, 66, 28),
      new THREE.MeshBasicMaterial({ color: 0xff6b6b, transparent: true, opacity: 0.8, side: THREE.DoubleSide, depthWrite: false, fog: false }),
    );
    ring.rotation.x = -Math.PI / 2;
    const dot = new THREE.Mesh(
      new THREE.CircleGeometry(12, 16),
      new THREE.MeshBasicMaterial({ color: 0xff6b6b, transparent: true, opacity: 0.55, side: THREE.DoubleSide, depthWrite: false, fog: false }),
    );
    dot.rotation.x = -Math.PI / 2;
    rodPlot.add(ring, dot);
    rodPlot.visible = false;
    scene.add(rodPlot);
  }
  // THE ORBITAL CHART (spec §9): the plotting table reborn as the x/h
  // side-view of the whole sky, toggled with M. Everything it draws is
  // either on the wire or flown by the mirrored kernel — see spacechart.js.
  const spaceChart = spaceTheater ? new SpaceChart(spaceConsts, field) : null;
  let rodImpactX = null; // the barge's last solved strike longitude (chart + ring share it)
  // THE TRAJECTORY RIBBON: your next three minutes drawn IN THE SKY — the
  // ellipse itself, hanging where you will be. It runs soft cyan while the
  // path clears the peaks and goes RED the moment any of it dips below
  // them, with a marker at the impact point: the periapsis alarm, made into
  // a thing you can see out the window instead of a number to interpret
  // (the playtest verdict, verbatim: "judging it by number on the screen is
  // way too hard").
  let trajRibbon = null;
  let trajMark = null;
  if (spaceTheater) {
    const N = 121;
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(N * 3), 3));
    geo.setAttribute("color", new THREE.BufferAttribute(new Float32Array(N * 3), 3));
    trajRibbon = new THREE.Line(
      geo,
      new THREE.LineBasicMaterial({ vertexColors: true, transparent: true, opacity: 0.75, fog: false, depthWrite: false }),
    );
    trajRibbon.frustumCulled = false;
    trajRibbon.visible = false;
    scene.add(trajRibbon);
    trajMark = new THREE.Mesh(
      new THREE.RingGeometry(26, 38, 20),
      new THREE.MeshBasicMaterial({ color: 0xff5a4a, transparent: true, opacity: 0.85, side: THREE.DoubleSide, depthWrite: false, fog: false }),
    );
    trajMark.rotation.x = -Math.PI / 2;
    trajMark.visible = false;
    scene.add(trajMark);
  }
  if (navalTheater) {
    const hb = document.getElementById("hint-bar");
    if (hb) {
      hb.textContent =
        "W/S engine telegraph · A/D rudder · mouse trains the guns (auto-ranged) · click to fire · B torpedoes · " +
        (navalSmokeOn ? "C smoke · " : "") +
        "Z gunsight · X swap HE/AP/SS · R damage control · T repair · G engine boost · F leave ship";
    }
    // Touch has no Z key — the gunsight is exited with the ZOOM button.
    if (onTouch) {
      const sl = document.getElementById("scope-label");
      if (sl) sl.textContent = "GUNSIGHT · auto-ranged · ZOOM exits";
    }
  }
  if (landTheater) {
    const hb = document.getElementById("hint-bar");
    if (hb) {
      hb.textContent =
        "W/S drive (bottom of the throttle = reverse) · A/D steer · mouse lays the gun (auto-ranged) · click to fire · " +
        "Z gunsight · X swap AP/HE · B missile · C smoke · R repair · T artillery · G overdrive · F bail out";
    }
    if (onTouch) {
      const sl = document.getElementById("scope-label");
      if (sl) sl.textContent = "GUNSIGHT · auto-ranged · ZOOM exits";
    }
  }
  if (armadaTheater) {
    const hb = document.getElementById("hint-bar");
    if (hb) {
      hb.textContent =
        "COMBINED ARMS — fly, sail, or roll out (pick a unit on respawn) · planes spot for the fleet · " +
        "ships shell the shore · tanks hold the towns · the Sky Ring multiplies your zone points";
    }
    if (onTouch) {
      const sl = document.getElementById("scope-label");
      if (sl) sl.textContent = "GUNSIGHT · auto-ranged · ZOOM exits";
    }
  }
  // The armada fights the naval theater's gunnery ranges over the same open
  // sea, so it takes the naval sky: the far fog plane floored past the guns
  // (a spotted hull at 7 km must be a silhouette, not a wall of haze).
  const sky = buildSky(scene, config.world.radiusM, navalTheater || armadaTheater, landTheater, spaceTheater);
  // THE MOON IS ROUND (curveworld.js). Arm the render curvature for the space
  // theater and exempt the sky's own furniture first — the star dome, the sun
  // block, its glow, the far moon — because those are at infinity and bending
  // them would set the sun permanently. Everything else in the scene gets
  // bent by curveScene() on the slow tick below, materials included as they
  // are created, so a mesh added by any module lands on the same sphere as the
  // ground under it. Purely pixels: the kernel, the wire and the fire control
  // all still run on the unrolled cylinder.
  let curveTick = 0; // the slow sweep that bends newly-built meshes
  if (spaceTheater) {
    setCurveRadius(config.world.wrapM > 0 ? config.world.wrapM : spaceWrapM);
    for (const part of sky.skyParts ?? []) keepFlat(part);
  }
  // The naval theater is open ocean, the land theater open steppe: no team
  // bases and no Sky Ring objective in either. Combined arms brings the air
  // game's bases back (on the authored HQ plateaus — buildBases reads the
  // theater off the field). The Brick Moon has neither — its furniture is M3.
  if (!hullTheater && !spaceTheater) buildBases(scene, field);
  // An airless moon has no weather: no cloud decks (storm.js null-checks).
  const cloudDecks = spaceTheater ? null : buildClouds(scene, field);
  // Storm fronts: marching thunderheads on "storm" rounds, every theater.
  // Deterministic from (seed, server time) — all clients share the same sky.
  const stormR = new StormFront(scene, field, config);
  stormR.cloudDecks = cloudDecks; // fair-weather puffs dim while it storms
  const terrainR = new TerrainRenderer(scene, field);
  // Land/armada theaters: the towns (deterministic street grids shared with
  // the server's collision) are their own meshes on top of the chunked
  // terrain.
  if (landTheater || armadaTheater) buildLandTowns(scene, field);
  const zone = new ZoneMarker(scene, config.match, {
    naval: hullTheater || armadaTheater,
    radiusM:
      ((landTheater || armadaTheater) ? config.land?.zoneRadiusM : config.naval?.zoneRadiusM) ?? 650,
    waterLevelM: config.world.waterLevelM,
    // Zone rings hug the ground on land; the armada's sea zones sit at the
    // waterline because surfaceAt floors to the water level out there.
    groundAt: (landTheater || armadaTheater) ? (x, z) => field.surfaceAt(x, z) : null,
  });
  // Space furniture (spaceprops.js), built once the welcome's zone plan lands.
  let spaceProps = null;
  const ringMarker = armadaTheater
    ? new ZoneMarker(scene, config.match, { centerX: 1200, centerZ: 0 })
    : null;
  const tracers = new Tracers(scene, calibers);
  const bombsR = new Bombs(scene, 128, config.naval?.starLightRadiusM ?? 1700);
  const chutesR = new Chutes(scene);
  const rescuesR = new RescueMarkers(scene, config.world.waterLevelM, config.rescue?.radiusM ?? 90);
  const rescueMarkers = new Map(); // roadmap #2: id -> {id, x, z, team}
  // Combined arms: only land-style smoke exists on the Gulf map (ships'
  // smoke generators are off in the shared config), so it renders land-size.
  // The Brick Moon reuses the whole renderer for chaff: a small glinting
  // ball at the cluster's wire altitude instead of a bank on the water.
  const smokeR = new Smoke(
    scene,
    spaceTheater
      ? 170 // spaceDecoyRadiusM — the server's cluster radius
      : ((landTheater || armadaTheater) ? config.land?.smokeRadiusM : config.naval?.smokeRadiusM) ?? 700,
    spaceTheater,
  );
  const particles = new Particles(scene);
  const scarsR = new Scars(scene, field, config.world.waterLevelM, particles); // roadmap #5 (needs particles)
  const shellTrails = new ShellTrails(scene);
  // High-altitude contrails + G-pull wingtip streamers (roadmap #45).
  const contrails = new PlaneContrails(scene);
  const streamers = new Streamers(scene);
  // The comeback ace's glittering gold ribbon (roadmap #14): a small tinted
  // pool of the same instanced-streak recipe.
  const goldTrail = new PlaneContrails(scene, 300);
  goldTrail.cWhite.set(0xffd700);
  goldTrail.cFade.set(0x8a6a1f);
  goldTrail.mesh.material.opacity = 0.6;
  const aaGuns = new AAGuns(scene);
  const trucksR = new Trucks(scene);
  const supplyShipsR = new SupplyShips(scene, particles);
  const vehiclesR = new GroundVehicles(scene, config.world.theater, config.groundVehicles?.types ?? [], particles);
  const lightsR = new Searchlights(scene);
  const ships = new Ships(scene, particles);

  // WoWS-style projected gunsight (naval): the dispersion ellipse painted on
  // the sea at the exact spot the auto-ranged battery will land, so long shots
  // are laid on the water — visible even when the target sits against the
  // horizon or beyond it. Built once; positioned every frame while driving.
  // depthTest off + fog off so the ring reads at 13 km through the haze.
  let aimMarker = null;
  if (hullTheater || armadaTheater) {
    const N = 64;
    const circle = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) {
      const a = (i / N) * Math.PI * 2;
      circle[i * 3] = Math.cos(a);
      circle[i * 3 + 2] = Math.sin(a);
    }
    const circGeo = new THREE.BufferGeometry();
    circGeo.setAttribute("position", new THREE.BufferAttribute(circle, 3));
    const markerMats = [];
    const mkMat = (op) => {
      const m = new THREE.LineBasicMaterial({
        color: 0xffd76b, transparent: true, opacity: op, depthTest: false, fog: false,
      });
      markerMats.push(m);
      return m;
    };
    const ellipse = new THREE.LineLoop(circGeo, mkMat(0.85));
    const ellipseInner = new THREE.LineLoop(circGeo, mkMat(0.3));
    const crossGeo = new THREE.BufferGeometry();
    crossGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array([-1, 0, 0, 1, 0, 0, 0, 0, -1, 0, 0, 1]), 3),
    );
    const cross = new THREE.LineSegments(crossGeo, mkMat(0.95));
    const grp = new THREE.Group();
    grp.add(ellipse, ellipseInner, cross);
    grp.visible = false;
    grp.renderOrder = 999;
    ellipse.renderOrder = ellipseInner.renderOrder = cross.renderOrder = 999;
    scene.add(grp);
    aimMarker = { grp, ellipse, ellipseInner, cross, mats: markerMats };
  }
  // Console-debugging handle (also used by the headless test harness).
  // Setting __va.freeCam = {pos:[x,y,z], look:[x,y,z]} pins the camera there.
  // Near-miss cues (roadmap #6): bullet rows from the latest snapshot are
  // swept once per snapshot in frame(), where the predicted pose lives.
  const nearMiss = new NearMiss(config.combat?.nearMiss);
  // Cinematic kill-confirm (roadmap #4): effects slow-mo + camera pull + banner.
  const killConfirm = new KillConfirm();
  window.__va = { tracers, bombs: bombsR, chutes: chutesR, rescues: rescuesR, scars: scarsR, smoke: smokeR, particles, aaGuns, ships, sky, storm: stormR, trucks: trucksR, supplyShips: supplyShipsR, vehicles: vehiclesR, lights: lightsR, freeCam: null, scene, camera, terrain: terrainR, field, config, zone, nearMiss, killConfirm, contrails, streamers, goldTrail };
  window.__va.state = () => ({
    selfId, selfStatus, selfHp, selfBombs, selfPlane, pendingPlane, pendingGun, selfGunId, spectating,
    selfTurretHost, selfTurretSeat,
    spectateTarget,
    // Jets era (null otherwise): the server-owned seeker section, for the
    // headless playtest to watch a lock wind up.
    seeker: seekerSrv,
    maxHp: selfMaxHp(), entities: entities.size,
    pos: [pred.pos.x + visualOffset.x, pred.pos.y + visualOffset.y, pred.pos.z + visualOffset.z],
    speed: Math.hypot(pred.vel.x, pred.vel.y, pred.vel.z),
    // Predicted attitude + rates (headless harness: the touch-feel probes
    // read bank angle and roll rate off the live client to grade handling).
    att: [pred.att.x, pred.att.y, pred.att.z, pred.att.w],
    omega: [pred.omega.x, pred.omega.y, pred.omega.z],
    ctl: { pitch: input.pitch, roll: input.roll, yaw: input.yaw },
    offset: visualOffset.length(),
    // Camera-to-subject range while spectating (headless harness: verifies
    // the follow/action framing keeps the subject close, wide shots far).
    specDist: (() => {
      const p = spectateTarget ? targetPos(spectateTarget) : null;
      return p ? Math.round(camera.position.distanceTo(p)) : null;
    })(),
  });

  const hud = new Hud();
  const intel = new RoundIntel(document.getElementById("intel-card"));
  hud.buildMinimapBase(field);
  // Altitude and G-load are meaningless on the water (and in a tank) — drop
  // both gauges when players crew hulls instead of planes. The pure hull
  // theaters hide them once at boot; the armada can't (its players also fly),
  // so setHullGauges toggles them per unit as the player swaps between a
  // cockpit and a conning tower.
  const setHullGauges = (hull) => {
    document.getElementById("g-alt")?.closest(".gauge")?.classList.toggle("hidden", hull);
    document.getElementById("g-gauge")?.closest(".gauge")?.classList.toggle("hidden", hull);
  };
  if (hullTheater) setHullGauges(true);
  if (spaceTheater) {
    // Orbit keeps the altimeter (altitude IS the game) and retires the
    // aeroplane's instruments: the G-meter reads 0.0 forever in free fall,
    // and a spacecraft has no wing to stall or elevator to trim.
    document.getElementById("g-gauge")?.classList.add("hidden");
    document.getElementById("stall-gauge")?.classList.add("hidden");
    document.getElementById("trim-gauge")?.classList.add("hidden");
  }
  let lastDrivingGauges = null; // armada gauge toggle edge (setHullGauges)
  let lastHullPos = null; // for deriving a ship's true speed from its track
  let navalSpdSmooth = 0; // smoothed, clamped hull speed for the SPD readout
  let lastHullSpeed = 0; // this frame's hull speed (the unstabilized land sight shakes with it)
  window.__va.hud = hud; // for the screenshot harness (plotting-chart scene)
  window.__va.intel = intel; // round-end summary card, for the screenshot harness
  const audio = new GameAudio();
  // The vacuum doctrine, set once: on the Brick Moon the audio layer mutes
  // every sound that would need air to reach you (audio.js decides which).
  audio.vacuum = spaceTheater;
  window.__va.audio = audio; // headless playtests read the live engine-voice gains
  // Thunder rides the strike callback: delay-by-distance lives in the synth,
  // so a far bolt's rumble arrives seconds after everyone saw the flash.
  stormR.onStrike = (pos) => audio.thunder(camera.position.distanceTo(pos));
  // Cloud-to-cloud strokes rumble too, softened as if the sound never quite
  // comes down from the cloud deck.
  stormR.onArc = (pos) => audio.thunder(camera.position.distanceTo(pos) + 1200);
  // Civilian life + living-airbase dressing (roadmap #42/#43): client-only
  // ambient scenery, air theater only (the island, its bases, and its shore
  // are what it decorates). Driven off the shared server clock each frame.
  const ambient = hullTheater ? null : new AmbientLife(scene, field, config, particles, audio);
  if (ambient) {
    ambient.onWhaleSeen = () => analytics.event("whale_seen");
    window.__va.ambient = ambient;
  }
  const input = new Input(planeTypes[0]);
  // A plane's lever wakes at cruise; a spacecraft's wakes CLOSED. The spawn
  // is already on the boost arc and the circularization burn is the takeoff
  // the pilot performs — a lever born at 0.7 rewrites the orbit during the
  // first seconds nobody is touching anything (the playtest watched a fresh
  // Dart triple its apoapsis and die on the ceiling, hands off).
  //
  // And it is SPRING-LOADED out here: the bell burns while the key is held
  // and shuts the instant it is let go. A latching lever is cruise on a wing
  // and a slow catastrophe on an orbit — every second it stays open is Δv
  // spent and a path rewritten, and the lever a pilot forgets is the one
  // that flies them into the ceiling. Hold to go; let go to hold station.
  if (spaceTheater) {
    input.throttle = 0;
    input.springThrottle = true;
  }
  // The mouse-aim director: world-anchored desired direction of flight (see
  // aimdirector.js). Owned here so the spawn handler can park it and the
  // steering block can drive it.
  const aimDirector = new AimDirector();
  let touch = null;
  if (onTouch) {
    document.body.classList.add("touch");
    // The desktop placeholder ("your callsign") truncates in the narrow touch
    // input — use a shorter one.
    document.getElementById("name-input")?.setAttribute("placeholder", "callsign");
    touch = new TouchControls(input);
    // The Brick Moon's lever is a different instrument: four areas a thumb
    // can find without looking, live only while it is held (touch.js). The
    // phone gets the same dead-man engine the desktop keys do.
    if (spaceTheater) touch.setThrottleMode({ spring: true, zoned: true });
    // The virtual stick steers through the mouse-aim assist; force it on so
    // a stale desktop preference can't strand the plane uncontrollable.
    input.mouseAim = true;
  }
  // The space theater flies direct RCS: the aim director and its trim/path
  // stack are plane-brained — they chase a cursor with aero gains, fight the
  // gyro holds through the stick deadband, and the playtest caught the trim
  // fold leaving 1% throttle idling (1% of a torch is a decaying orbit).
  // The gyros ARE the space assist; a space-native aim assist is M3's FCS.
  //
  // But the quarantine collided with the line just above it: touch FORCES
  // mouseAim on because the virtual stick steers through the assist channel
  // — and killing mouseAim here stranded exactly what that force-on exists
  // to prevent. A phone pilot's stick fed an assist nobody composed: "the
  // touch stick doesn't do anything," verbatim. spaceRcs reopens the assist
  // path in composeAxes without waking any aero machinery; the steering
  // block feeds it the thumb as plain RCS rate commands.
  //
  // And it must be open on EVERY device, not just touch: the Orbit Keeper
  // steers through the same assist channel, and the touch-only flag left it
  // commanding a nose that never answered on desktop — the keeper "worked"
  // whenever the spawn attitude or a gyro hold happened to pre-aim the
  // hull, and chased its apoapsis into the ground the moment a save needed
  // a real swing ("it chases it right into the ground", verbatim). Held
  // keys still outrank the assist axis-by-axis in composeAxes, and any key
  // on the stick stands the keeper down entirely.
  if (spaceTheater) {
    input.mouseAim = false;
    input.spaceRcs = true;
  }
  // --- stick telemetry overlay (?stickdebug=1, or tap the build stamp 3x) ---
  // When a phone says "the stick does nothing" and every emulation says
  // "works on my machine", instruments on the device settle it: who receives
  // the pointer events, what the thumb axes read, and which arbiter —
  // gamepad, mouse-aim, autopilot — actually owns the plane. Costs nothing
  // unless summoned; player-invisible otherwise.
  let stickDebugEl = null;
  const stickDebugEvt = { down: 0, cancel: 0, target: "-" };
  const stickDebugMount = () => {
    if (stickDebugEl) return;
    stickDebugEl = document.createElement("pre");
    stickDebugEl.id = "stick-debug";
    document.getElementById("ui").appendChild(stickDebugEl);
    document.addEventListener(
      "pointerdown",
      (e) => {
        stickDebugEvt.down++;
        stickDebugEvt.target = e.target?.id || e.target?.tagName || "?";
      },
      { capture: true, passive: true },
    );
    document.addEventListener("pointercancel", () => stickDebugEvt.cancel++, {
      capture: true,
      passive: true,
    });
  };
  if (
    new URLSearchParams(location.search).has("stickdebug") ||
    localStorage.getItem("va-stick-debug") === "1"
  ) {
    stickDebugMount();
  }
  // Three quick taps on the join screen's build stamp toggle it — an installed
  // PWA has no address bar to type a query string into.
  {
    let taps = 0,
      lastTap = 0;
    document.getElementById("build-stamp")?.addEventListener("pointerdown", () => {
      const t = performance.now();
      taps = t - lastTap < 800 ? taps + 1 : 1;
      lastTap = t;
      if (taps >= 3) {
        taps = 0;
        const on = localStorage.getItem("va-stick-debug") === "1";
        localStorage.setItem("va-stick-debug", on ? "0" : "1");
        if (!on) stickDebugMount();
        stickDebugEl?.classList.toggle("hidden", on);
      }
    });
  }

  // iOS suspends WebAudio when the PWA/tab backgrounds; revive it on return so
  // the engine and gunfire don't come back silent.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") audio.resume();
  });
  // iOS Safari offers no install prompt, so nudge users to Add to Home Screen
  // for a real full-screen app. Only on iOS, only when not already installed,
  // and only until they dismiss it once.
  maybeShowInstallHint();

  window.__va.input = input;
  if (spaceActionsRef) window.__va.spaceActions = spaceActionsRef; // touch/e2e drive the chips
  if (spaceTheater) {
    // The harness's hands on the gaze: e2e and the headless playtests steer
    // intent the way a mouse would, without synthesizing pointer lock.
    window.__va.spaceLook = (yaw, pitch) => {
      spaceCam.yaw = yaw;
      spaceCam.pitch = Math.max(-1.35, Math.min(1.35, pitch));
    };
  }
  window.__va.aimDirector = aimDirector; // playtest/e2e instrumentation
  input.muteToggled = () => {
    const m = audio.toggleMute();
    hud.message(m ? "muted" : "sound on", 1200);
  };
  input.onToggleMessage = (text) => hud.message(text, 1800);
  // Autopilot (P on desktop; touch flies it hands-off, see below): a simple
  // wing-leveller that holds the heading captured at engagement, flying straight
  // and level until toggled off. A plain on/off — it does NOT disengage on
  // manual input, so holding a mouse-aim offset can't fight it off. main.js owns
  // the steering (it has the predicted attitude); throttle stays under the pilot.
  const apHeadingDir = FDM.v3(0, 0, -1); // captured heading, flattened to the horizon
  const flying = () =>
    net.joined && !spectating && haveSpawned &&
    selfStatus !== 0 && selfStatus !== 3 && selfStatus !== 4 && selfStatus !== 6;
  // Snapshot the current nose heading, levelled onto the horizon, for the loop
  // to hold. If the nose is near-vertical, fall back to the flight path; if
  // that's degenerate too, keep whatever heading we last held.
  const captureApHeading = () => {
    const fwd = FDM.qRotate(pred.att, FDM.v3(1, 0, 0));
    let dir = FDM.v3(fwd.x, 0, fwd.z);
    if (FDM.vLen(dir) < 0.05) dir = FDM.v3(pred.vel.x, 0, pred.vel.z);
    if (FDM.vLen(dir) >= 0.05) {
      const n = FDM.vNorm(dir);
      apHeadingDir.x = n.x;
      apHeadingDir.y = n.y;
      apHeadingDir.z = n.z;
    }
  };
  // `quiet` engages/disengages with just the ✈ flag, no toast — the hands-off
  // touch gesture flips it constantly as the thumb comes and goes, so it can't
  // spam the toast lane; the deliberate P-key press still announces itself.
  input.onAutopilot = (on, quiet = false) => {
    if (on) {
      if (!flying()) {
        input.autopilot = false; // only the cockpit has an autopilot
        hud.setAutopilot(false);
        return;
      }
      captureApHeading();
      if (!quiet) hud.message("autopilot ON — straight & level (throttle still yours)", 2200, "#7fd1ff");
    } else if (!quiet) {
      hud.message("autopilot OFF — you have the controls", 1600);
    }
    hud.setAutopilot(input.autopilot);
  };
  // Hands-off autopilot (touch): let go of the stick and, once the wings settle,
  // the plane latches onto its heading and holds straight & level; grab the
  // stick back and you have it instantly. Timestamp of the last frame a thumb
  // was on the stick — the latch waits handsOffSec past it (long enough that a
  // quick thumb reposition mid-manoeuvre doesn't trip it).
  const handsOffSec = 1.2;
  let stickHeldAt = 0;
  // Auto-trim (T): a pilot-relief term folded into the commanded pitch so level
  // and cruising flight stop needing constant back-pressure. It only relieves
  // the steady stick force — the pilot still flies the plane — and the trimmed
  // pitch is what both the local prediction and the server integrate, so the
  // FDM itself is untouched. main.js owns the held value; here we just reset it
  // and flag the HUD when the assist is toggled.
  input.onAutoTrim = (on) => {
    pitchTrim = 0;
    hud.setTrim(0, on);
    hud.message(
      on ? "auto-trim ON — holds your pitch, no more back-pressure" : "auto-trim OFF — manual elevator",
      1800,
      on ? "#7fd1ff" : undefined,
    );
  };
  hud.setTrim(0, input.autoTrim);
  input.onDeviceChange = (device) => {
    hud.setDeviceHint(device);
    if (device) {
      hud.message(
        `${device.kind === "hotas" ? "HOTAS" : "gamepad"} connected — sticks are live`,
        2600,
      );
    }
  };

  // --- game state ---
  const net = new Net();
  window.__va.net = net; // headless test harness: drive joins/vehicle swaps
  const roster = new Map(); // id -> {id, name, team, bot, plane}
  const board = new Map(); // id -> {k, d, w}
  const entities = new Map(); // remote id -> {visual, buffer[], hp, status, plane}
  window.__va.entities = entities; // for the screenshot/verification harness
  window.__va.roster = roster;
  let dailyChallenges = []; // today's missions + this pilot's progress (from welcome)
  let selfId = 0;
  let selfTeam = 0;
  let prevZone = null; // last capture-zone holder seen (naval callouts)
  let navalZoneCenters = []; // capture-zone centres [{x,z}] (N7/N8 objective hints)
  let curZoneIdx = -1; // capture zone the local hull is currently inside (N7)
  let torpThreatActive = false; // an enemy torpedo is closing (N3, for the audio edge)
  let selfVisual = null;
  let spectating = false;
  let spectateTarget = null; // {kind: "plane"|"gun"|"ship", id} — null orbits the island
  const specAim = { yaw: 0, pitch: 0, has: false }; // smoothed spectator gun-cam aim
  const specFollow = new Follower(); // spectator plane-follow camera
  const specPos = new THREE.Vector3(); // interpolated subject pos (own scratch)
  // Spectator orbit: drag (mouse) or swipe (touch) swings the chase camera
  // around the followed plane; RECENTER / C zeroes it to snap back behind.
  const specOrbit = { yaw: 0, pitch: 0 };
  const specCamOff = new THREE.Vector3(); // scratch: chase offset incl. orbit
  const specOrbitAxis = new THREE.Vector3(); // scratch: orbit pitch axis
  // Action framing (director): when the followed plane is in a fight, the
  // camera swings off the subject→foe axis so both planes share the frame.
  const specAxis = new THREE.Vector3(); // scratch: engagement axis
  const specSide = new THREE.Vector3(); // scratch: lateral framing offset
  const specFwd = new THREE.Vector3(); // scratch: subject forward (own copy)
  const specFoe = new THREE.Vector3(); // scratch: interpolated foe position
  const specLook = new THREE.Vector3(); // scratch: desired camera look point
  const specLookFollow = new Follower(); // smoothed look point (foe steps at 20 Hz)
  const specLookPos = new THREE.Vector3(); // smoothed look point state
  const WORLD_UP = new THREE.Vector3(0, 1, 0);
  const killerFollow = new Follower(); // death-view killer chase camera
  const flybyRight = new THREE.Vector3(); // scratch: camera right axis for flyby audio
  const flybyPlanes = []; // reused per-frame list fed to audio.updateFlybys

  // Auto-director: spectate with ?director=1 to let the camera run itself —
  // it scores every live vantage for action, holds a subject long enough to
  // read, and cuts to a hotter fight (or straight to a fresh kill) on its own,
  // so a 24/7 stream can watch the live game with nobody at the keyboard.
  const autoDirector = new URLSearchParams(location.search).has("director");
  // Deep-link into spectating: hit /spectate (or ?spectate) and the join screen
  // clicks SPECTATE for you the moment the socket is up — a shareable "just let
  // me watch" link that skips the button. Director implies this too, but keeps
  // its own hands on the camera; a plain spectate link leaves you driving N/P.
  const autoSpectate =
    autoDirector ||
    location.pathname.replace(/\/+$/, "") === "/spectate" ||
    new URLSearchParams(location.search).has("spectate");
  // Cut rhythm (seconds): never cut before MIN_HOLD, aim to hold BASE_DWELL,
  // never sit on one subject past MAX_HOLD, and leave a fight that's gone cold
  // early. A rival must beat the current subject's score by MARGIN to cut in.
  const DIRECTOR_MIN_HOLD = 3;
  const DIRECTOR_BASE_DWELL = 7;
  const DIRECTOR_MAX_HOLD = 22;
  const DIRECTOR_MARGIN = 1.5;
  const DIRECTOR_COLD = 0.6; // a subject scoring under this has no action left
  const DIRECTOR_WIDE_EVERY = 40; // min seconds between establishing shots
  const DIRECTOR_WIDE_LEN = 4.5; // how long an establishing orbit holds
  // How long the founder cam holds a sinking hull. Matches the reel's sinkPost
  // (assemble.cjs) so the clip's tail and the camera's hold cover the same
  // seconds — the ship goes under on screen inside the window it's labeled for.
  const DIRECTOR_SINK_LEN = 9;
  // Duel coverage: when two planes lock into a close tail-chase the director
  // covers it like an editor with two cameras — a single on the attacker (over
  // the shoulder, working the gun) and a reverse single on the defender (the
  // hunted bird with its pursuer looming behind) — and cuts between them on a
  // rhythm, ending on the victim's wreck. Not a wide two-shot: the camera is ON
  // one bird at a time and switches.
  // The stream renders this far behind live so the camera can see kills coming
  // (they're already in dirSnapRing/dirEventQ) and frame them from known facts
  // instead of guessing. ~5 s is invisible on a 24/7 channel nobody interacts
  // with. LOOKAHEAD is how early, before a known kill, its coverage opens.
  const DIRECTOR_LAG = 5.0; // s
  const DIRECTOR_LOOKAHEAD = 3.0; // s
  const DIRECTOR_DUEL_EXIT = 1000; // m — beyond this a covered pair has broken; leave coverage
  const DIRECTOR_SHOT_MIN = 1.5; // s — hold one angle at least this long before the reverse
  const DIRECTOR_SHOT_MAX = 2.4; // s — and force the reverse by here, so it reads as edited
  const DIRECTOR_DUEL_MAX = 10; // s — never camp one duel longer than this without a kill
  const DIRECTOR_WRECK_LEN = 2.8; // s — ride the victim's wreck after the kill (the reel's ~3 s tail)
  const director = {
    heat: new Map(), // "kind:id" -> decaying action heat from recent events
    since: 0, // perfNow() the current subject was cut to
    lastTick: 0, // perfNow() of the previous scoring pass
    nextTick: 0, // perfNow() the next scoring pass is due (scoring runs at 4 Hz)
    cutNow: false, // a kill wants an immediate cut to the shooter
    lastWide: -1e9, // perfNow() of the last establishing shot
    wideUntil: 0, // perfNow() the current establishing orbit ends
    wideCenter: new THREE.Vector3(),
    wideAngle: 0,
    // Founder cam: an external orbit pinned to a hull going down. A sinking
    // ship stops being a spectate target the instant it dies (it drops out of
    // spectateTargets and scores -1), so the old "cut to the hull" never
    // actually landed — the camera cut to the hottest *live* fight while the
    // clip wore a "sank the battleship" caption. This owns the frame instead.
    sinkUntil: 0, // perfNow() the founder cam ends
    sinkPos: new THREE.Vector3(), // where the hull went down (world)
    sinkAngle: 0, // current orbit angle
    // Duel coverage: shot/reverse-shot on a tail-chase (see the constants above).
    cover: null, // active engagement: { aId, bId, shot, shotUntil, killDueAt, hardUntil, killed, killedAt, wreck, orbitA }
    coverCoolUntil: 0, // don't jump straight from one covered kill into the next
    intensity: 0, // last best-vantage score, drives the soundtrack arrangement
  };
  window.__va.director = director; // debug/verification handle, like __va.hud/__va.net
  // Procedural arcade soundtrack. Always constructed (start() is what builds
  // the WebAudio graph): the director/stream starts it unconditionally, and
  // players opt in via the join-screen SOUNDTRACK toggle (va-music). It
  // shares the audio context, so mute (M) covers it too. For players the
  // arrangement is driven by local combat state — see the dynamic-music block
  // in frame() (roadmap #46).
  const music = new GameMusic(audio, (name) => hud.nowPlaying(name));
  window.__va.music = music; // debug/test handle, like __va.hud
  // Squadron radio chatter (roadmap #22): turns situational bot/combat events
  // into team-chat lines through the existing chat pipeline.
  const radio = new Radio(hud);
  window.__va.radio = radio;
  const musicOn = () => localStorage.getItem("va-music") === "1";
  let musicDrive = 0; // asymmetric envelope over the raw combat intensity
  let musicFloorUntil = 0; // after a real fight, hold a simmering floor
  let musicCalmSince = null; // how long the sky has been quiet
  let lastDamagedAt = -1e9;
  let lastOwnHitAt = -1e9;

  // --- highlight log (director/stream only) ----------------------------------
  // A rolling record of watchable moments with wall-clock times. The stream
  // container reads window.__va.highlights over CDP and persists them (the page
  // reloads on disconnect, so it must harvest as it goes), then ranks and cuts
  // the twice-daily reel around the timestamps. `weight` ≈ watchability; the
  // assembler clusters nearby moments (a furball) and picks the best to fill
  // the reel. `round` markers (weight 0) let it avoid clipping across a reset.
  const highlights = [];
  window.__va.highlights = highlights;
  function recordHighlight(kind, weight, label) {
    if (!autoDirector) return;
    // Only moments during live play belong in the reel. A kill that trickles in
    // over the round-end results card would caption a clip of the scoreboard,
    // not a fight (the stream cuts to the intel card at the bell). Round markers
    // are exempt — they ARE the boundary the planner cuts around.
    if (kind !== "round" && score.ph !== "play") return;
    highlights.push({ t: Date.now(), kind, weight, label });
    if (highlights.length > 1000) highlights.shift();
  }
  // The reel is cut from what the stream camera actually filmed, so a moment
  // only earns a log entry if it happened on camera: inside the view frustum
  // at a distance where it reads, or close enough that its fireworks fill the
  // frame from any angle. Without this the log records every kill on the map
  // and the reel captions describe fights the footage never shows. Round
  // markers bypass it (they cut the reel, not fill it). Sinks don't use this
  // check at all — they're gated on the founder cam actually cutting to the
  // hull (see the "sink" event and directorCutToSink), which is a stricter,
  // truer test than the frustum: logged iff we really filmed her go under.
  const HIGHLIGHT_VIEW_RANGE = 1400; // m — beyond this the action is fog-sized
  const HIGHLIGHT_NEAR = 220; // m — on top of the camera counts even off-axis
  const hlProj = new THREE.Vector3();
  const hlDir = new THREE.Vector3();
  function onCamera(pos) {
    if (!autoDirector || !pos) return false;
    const d = camera.position.distanceTo(pos);
    if (d < HIGHLIGHT_NEAR) return true;
    if (d > HIGHLIGHT_VIEW_RANGE) return false;
    // In front of the camera (projection alone mirrors points behind it)…
    hlProj.copy(pos).sub(camera.position);
    if (hlProj.dot(camera.getWorldDirection(hlDir)) <= 0) return false;
    // …and inside the frustum, with a small margin.
    hlProj.copy(pos).project(camera);
    return Math.abs(hlProj.x) < 1.1 && Math.abs(hlProj.y) < 1.1;
  }

  const pred = FDM.newState(); // predicted local plane
  // Auto-trim: a gentle elevator-trim offset (-1..1, + = nose-up) folded into
  // the commanded pitch so level and cruising flight stop needing constant
  // back-pressure. It eases toward the elevator that holds the horizon (the
  // autopilot's tuned, non-porpoising pitch loop), but its authority fades out
  // as the pilot commands pitch: hands-off it holds altitude, and a deliberate
  // climb, dive, or hard turn overrides it completely — so it never fights the
  // pilot, never winds up in a maneuver, and never robs nose-down authority.
  let pitchTrim = 0;
  // Missile-refusal narration (jets era): rising-edge + cooldown state so a
  // held or mashed launch button teaches once, not every frame.
  let mslPressEdge = false;
  let mslNagAt = 0;
  const TRIM_RESPONSE = 2.5; // how fast trim eases toward the level-hold command
  const TRIM_FADE = 0.5; // pilot pitch (abs) at which trim authority hits zero
  const TRIM_LIMIT = 0.6; // clamp, so trim alone can't haul the nose to an extreme
  const inputHistory = []; // {seq, ctl} ring for replay reconciliation
  const visualOffset = new THREE.Vector3(); // decaying error hider
  // Rotational twin of visualOffset: reconciliation attitude corrections fold
  // in here and bleed away, instead of popping the camera basis.
  const attOffset = new THREE.Quaternion();
  const IDENTITY_Q = new THREE.Quaternion();
  // Controls the prediction integrates: the last *sent* sample, held for
  // TICKS_PER_INPUT ticks — the same timeline the replay and server apply.
  const predCtl = { pitch: 0, roll: 0, yaw: 0, throttle: 0.7 };
  // Space theater: the local craft predicts on the orbital kernel instead of
  // the aero model — same call shape (the damage arg is unused: a craft's
  // modules gate systems, not physics), so the predict and replay sites stay
  // theater-blind.
  // The arena's live centre: the station's longitude off the snapshot, on the
  // group's own shell. The prediction needs it because the boundary autopilot
  // is part of the physics now (spacecraft.js) — without it the hull would
  // rubber-band home instead of flying home.
  const spaceArenaCentre = () =>
    spaceStationX == null ? null : { x: spaceStationX, y: spaceStationShellY };
  const spacecraftStep = spaceTheater
    ? makeSpacecraftStep(spaceConsts, (x, z) => field.surfaceAt(x, z), spaceArenaCentre)
    : null;
  const physStep = spaceTheater ? (cfg, s, ctl, dt) => spacecraftStep(cfg, s, ctl, dt) : FDM.step;
  let predTicks = 0; // physics ticks since the prediction (re)started
  let lastInputSentAt = 0; // perfNow() of the last flight input
  // Fixed-step physics + variable-rate rendering: keep the previous tick's
  // state and draw the plane interpolated between ticks, otherwise motion
  // quantizes to 60 Hz and judders on every other refresh rate.
  const prevPos = new THREE.Vector3();
  const prevAtt = new THREE.Quaternion();
  const renderPos = new THREE.Vector3(); // interpolated pos incl. visualOffset
  const renderAtt = new THREE.Quaternion();
  // Off-screen enemy edge markers (touch HUD). Reused every frame — no
  // per-frame allocation in the projection loop.
  const EDGE_RANGE_M = 1500;
  const edgeNdc = new THREE.Vector3();
  // The same point, bent onto the moon's curvature (curveworld.js) — kept
  // beside the projection scratch because the tally board runs it per contact
  // per frame and a fresh vector each time is a garbage-collector drum solo.
  const edgeBent = new THREE.Vector3();
  const edgeMarkers = [];
  const EDGE_EMPTY = [];
  const camRight = new THREE.Vector3();
  let nearMissRows = null;
  let checkSixUntil = 0;
  // Contrail/streamer scratch (roadmap #45).
  const trailOff = new THREE.Vector3();
  const selfTrailQ = new THREE.Quaternion();
  const selfTrailStore = { c: 0, s: 0 };
  // Centripetal G of a remote plane from its last two snapshot velocities,
  // gravity removed; ~1 g when unknown or too slow to trust.
  const remoteG = (ent) => {
    const b = ent.buffer;
    if (b.length < 2) return 1;
    const a = b.at(-2), c = b.at(-1);
    const dtc = c.t - a.t;
    if (!(dtc > 0) || dtc > 0.4 || !a.vel || !c.vel) return 1;
    const sp = Math.hypot(c.vel.x, c.vel.y, c.vel.z);
    if (sp < 40) return 1;
    const ax = (c.vel.x - a.vel.x) / dtc;
    const ay = (c.vel.y - a.vel.y) / dtc + 9.81;
    const az = (c.vel.z - a.vel.z) / dtc;
    return Math.hypot(ax, ay, az) / 9.81;
  };
  let serverSelf = null;
  let selfStatus = 1;
  let selfHp = config.combat.maxHp;
  let selfBombs = 0;
  // Last seen 85 mm magazine count (space): a drop on the wire is the
  // server's word the round actually left, and the cue for the hull thud.
  let prevHeavyMag = -1;
  // Localized-damage subsystem health for the local plane (1 = pristine);
  // fed into the prediction FDM so a shot-up plane mispredicts no worse than
  // the server flies it.
  let selfDmg = { engine: 1, wingL: 1, wingR: 1, fuel: 1, pilot: 1, tail: 1 };
  const selfDamageMods = () => FDM.deriveDamageMods(selfDmg, config.combat);

  // True when a plane should be trailing any battle-damage effect.
  const isFxDamaged = (d, hpFrac) =>
    (d?.engine ?? 1) < 0.7 || (d?.fuel ?? 1) < 0.85 || hpFrac < 0.35;
  // Trail engine smoke / fire / a fuel-vapor streak off a damaged plane.
  // d = {engine,fuel,...} (missing field = healthy); hpFrac = HP fraction.
  // The caller gates the emission cadence.
  function damageFx(pos, d, hpFrac) {
    const engine = d?.engine ?? 1;
    if (engine < 0.7 || hpFrac < 0.35) particles.smoke(pos);
    if (engine < 0.2 || hpFrac < 0.15) particles.fire(pos);
    if ((d?.fuel ?? 1) < 0.85) particles.vapor(pos); // venting fuel
  }
  let selfPlane = 0;
  let pendingPlane = 0;
  let pendingGun = 0; // AA slot reserved for the next spawn (0 = fly)
  // Friendly crewable emplacements {id, kind}, in build order — lets the unit
  // picker reserve the first free mount of a chosen kind while respawning
  // (mirrors the server's first-free seating at join). Filled from the welcome.
  let respawnGunSlots = [];
  let pendingDeck = localStorage.getItem("va-deck") === "1"; // carrier catapult spawns
  let activeScenario = null; // training scenario in progress (null = live game)
  let selfGunId = 0; // gun currently manned (from the mangun event)
  let navalAmmo = 0; // naval: loaded main-battery shell — 0 HE, 1 AP, 2 SS (cycled with X)
  let dcPending = false; // naval: a damage-control request to send on the next input
  let navalScoped = false; // naval: looking through the zoomed gunsight (Z)
  let torpReadyAtClient = 0; // naval: client estimate of when the tubes reload
  let dcReadyAtClient = 0; // naval: client-side estimate of when DC is off cooldown
  let dcWasActive = false; // naval: previous damage-control-active flag (rising edge)
  let smokePending = false; // naval: a smoke-screen request to send on the next input (C)
  let smokeReadyAtClient = 0; // naval: client-side estimate of when smoke reloads
  let repairPending = false; // naval: a repair-party request to send on the next input (T)
  let repairReadyAtClient = 0; // naval: client-side estimate of when repair is off cooldown
  let boostPending = false; // naval: an engine-boost request to send on the next input (G)
  let boostReadyAtClient = 0; // naval: client-side estimate of when boost is off cooldown
  let boostActiveUntilClient = 0; // naval: client estimate of the boost window (HUD)
  let artyReadyAtClient = 0; // land: client-side estimate of the artillery battery's cooldown
  let navalRangeLive = 0; // naval: range to the spot under the gunsight crosshair this frame (m); the gun auto-elevates to it
  let navalRangeMax = false; // naval: the sight is clamped at the battery's maximum reach this frame
  let navalScopeRange = 0; // naval: the scoped range dial (m) — vertical mouse walks it, the sight pitch derives from it (0 = re-seed on scope entry)
  let scopeAimPend = 0; // naval scope windage: pending eased yaw travel (rad)
  let navalBorePitch = 0; // naval: predicted main-battery elevation (held when there's no aim point under the sight)
  const NAVAL_DIRECTOR_Y = 34; // rangefinder eye height — matches the gunsight camera (pivot +8, then +26) so the range is the exact spot under the crosshair; the height also spreads the far ranges across more sight pitch, so laying 8–13 km is controllable
  // Land theater: the sight head rides just over the turret roof — matches
  // the scoped camera so the ranged point is the spot under the crosshair.
  // Land sight-head height above the hull anchor. Mirrors the server's
  // landMountY (6.2 m muzzle, eye a hair above): the terrain steps in
  // quantized 3 m blocks and a rolling slope climbs a step or two per cell,
  // so a low eye stares straight into the first uphill lip in front of the
  // hull — and the shells fired from a low muzzle burst on it. Riding above
  // a couple of steps keeps sight and shot clear of the vehicle's own shelf.
  const LAND_SIGHT_Y = 6.4;
  // Land gun depression stop. Mirrors the server's landGunPitchMin: the tall
  // mount above needs real depression or everything inside a town block sits
  // below the barrel — the naval/flak stop (-0.1) left a ~56 m point-blank
  // dead zone where neither a player nor a bot could bring the gun to bear.
  const LAND_GUN_PITCH_MIN = -0.35;
  // Each land vehicle's optic: the Paladin's high-magnification gunner sight,
  // the Warden's missile sight, the Whirlwind's wide reflector, the Jackal's
  // iron ring. Indexed by vehicle type.
  const LAND_SCOPE_FOV = [6, 9, 18, 14];
  // Locally predicted turret aim: integrates the same slew the server applies
  // so the view answers instantly; snapshots only nudge it back in line.
  const gunAim = { yaw: 0, pitch: 0, has: false };
  const gunCtl = { pitch: 0, yaw: 0 }; // rate inputs sent while manning
  let pendingVeh = -1; // ground vehicle type reserved for next spawn (-1 = none)
  let selfVehicleId = 0; // ground vehicle currently driven (from the manveh event)
  // Locally predicted mount aim while driving (same idea as gunAim).
  const vehAim = { yaw: 0, pitch: 0, has: false };
  const vehCtl = { pitch: 0, yaw: 0, roll: 0, throttle: 0 }; // inputs sent while driving
  let pendingTur = -1; // bomber turret seat reserved for next spawn (-1 = none)
  let selfTurretHost = 0; // pilot whose bomber we're riding (from the mantur event)
  let selfTurretSeat = 0;
  // Parked on the spawn screen because we picked a bomber crew seat but no
  // friendly bomber was airborne (server's turwait / holdForBomber). The server
  // auto-seats us the instant one launches; until then the spawn screen explains
  // the wait instead of dropping us into a control-less camera.
  let waitingBomber = false;
  // Locally predicted turret aim, in the host airframe's body frame (yaw 0 =
  // over the nose) — mirrors the server's stepTurretGunners integration.
  const turAim = { yaw: 0, pitch: 0, has: false };
  const turCtl = { pitch: 0, yaw: 0 }; // slew-rate inputs sent while seated
  const turSrv = { yaw: 0, pitch: 0, has: false }; // authoritative aim off snapshots
  // Manned bomber turrets by host player id, refreshed every snapshot:
  // hostID -> [{gunner, seat, yaw, pitch, firing}].
  const turretsByHost = new Map();
  let inputSeq = 0;
  let haveSpawned = false;
  let deathPos = new THREE.Vector3();
  let deathKiller = 0; // killer-cam subject while waiting to respawn
  let deathAt = 0; // perfNow() when we went down, for the respawn cooldown
  let deathCoach = null; // {line, cause, index} for the death-recap coach
  let deathTip = null; // {id, text} field-manual tip for this death's screens
  let nemesisId = 0; // pilot currently marked as YOUR nemesis (roadmap #21)
  let aceId = 0; // the comeback ace on the field (roadmap #14)
  let equippedScheme = Number(localStorage.getItem("va-scheme")) || 0; // paint (roadmap #25)
  let lastOvertime = false; // OVERTIME chip/sky state edge detector (#16)
  const heatEvents = []; // recent kill/boom positions for the spawn map (#55)
  // First-session death counter: after a touch player's second death, nudge them
  // toward Flight School once (session = page load, no persistence needed).
  let sessionDeaths = 0;
  let flightSchoolNudged = false;
  function noteLocalDeath() {
    sessionDeaths++;
    if (onTouch && !flightSchoolNudged && sessionDeaths >= 2) {
      flightSchoolNudged = true;
      hud.message(
        "rough one — FLIGHT SCHOOL (TRAINING on this screen) teaches the turn",
        5000,
        "#f4c542",
      );
    }
  }
  // perfNow() deadline until which we hold the respawn screen back because a
  // killcam is inbound: the server ships it killcamDelaySec after death (so the
  // ring can record the wreckage), and the screen must not flash on in the gap.
  let awaitingKillcam = 0;
  // Player tapped/clicked to skip this death's replay: ignore the clip if it
  // lands afterwards (it must not restart over the respawn screen).
  let killcamSkipped = false;
  let wasNearRescue = false; // roadmap #2: edge-trigger the RESCUING prompt
  let respawnSent = false; // pressed TAKE OFF while down, awaiting the spawn
  // Respawn cooldown (the anti-spam floor before TAKE OFF unlocks). Plain
  // deaths use the normal delay; killcam deaths hold longer so the button
  // unlocks as the replay ends (startKillcam bumps this; server mirrors it).
  let deathRespawnSec = config.combat.respawnDelaySec;
  let replay = null; // active killcam playback (see startKillcam)
  // Jets era: the recipient's seeker state from the snapshot's trailing jets
  // section (lock target, wind-up progress, rails and flares remaining).
  let seekerSrv = null;
  let score = {
    c: 0, b: 0, t: config.match.roundSeconds, z: -1, ph: "play", w: -1, wx: "clear",
    md: config.match.modes?.[0] ?? "skyring",
  };
  // Fleet action: which hull class is the flagship (mirrors fleet config).
  const flagshipKind = () => (config.fleet?.flagship === "battleship" ? 2 : 0);
  const flagshipName = () => (config.fleet?.flagship === "battleship" ? "BATTLESHIP" : "CARRIER");
  // Flagship health for the fleet score bar: hull HP already rides the ship
  // snapshot rows, so this is pure client-side bookkeeping.
  function fleetHud() {
    if (score.md !== "fleet") return null;
    const kind = flagshipKind();
    const maxHp =
      (kind === 2 ? config.ships.battleship.hp : config.ships.carrier.hp) *
      (config.fleet?.flagshipHpMult || 1);
    const frac = (team) => {
      for (const sh of ships.ships.values()) {
        if (sh.site.team === team && sh.site.kind === kind) {
          return sh.alive ? Math.min(1, (sh.hp ?? maxHp) / maxHp) : 0;
        }
      }
      return 0;
    };
    return { c: frac(0), b: frac(1) };
  }

  const selfMaxHp = () => config.combat.maxHp * (planeTypes[selfPlane]?.hpMult ?? 1);
  const selfCaliber = () => caliberOf(selfPlane);
  const maxHpOf = (plane) => config.combat.maxHp * (planeTypes[plane]?.hpMult ?? 1);
  // Fire-control profile of the manned gun (land flak / naval twin mount /
  // battleship main-battery turret).
  const gunKind = () => aaGuns.guns.get(selfGunId)?.kind ?? 0;
  const gunProf = () =>
    gunKind() === 2 ? config.battleship : gunKind() === 1 ? config.navy : config.aaa;
  // The driven vehicle's type and its mount's fire-control profile.
  const vehType = () => vehiclesR.typeOf(selfVehicleId);
  const vehProf = () => config.groundVehicles?.types?.[vehType()]?.aa ?? null;
  // The crewed bomber turret's station config (arcs, slew rate, gun) — read
  // off the host's aircraft type.
  const turretCfg = () => {
    const hostPlane = entities.get(selfTurretHost)?.plane ?? roster.get(selfTurretHost)?.plane ?? 0;
    return planeTypes[hostPlane]?.turrets?.[selfTurretSeat] ?? null;
  };
  // A tank's direct-fire main gun (null for the flak vehicles): one heavy shell
  // on a reload cycle rather than a flak stream.
  const vehMainGun = () => {
    const mg = config.groundVehicles?.types?.[vehType()]?.mainGun;
    // A tank's gun has direct-fire `damage`; a warship's main battery instead
    // carries naval AP/HE shells — either marks a real main gun.
    return mg && (mg.damage > 0 || mg.apDamage > 0 || mg.heDamage > 0) ? mg : null;
  };
  // A warship's torpedo tubes (null when the hull carries none, e.g. the cruiser).
  const vehMainTorp = () => {
    const tp = config.groundVehicles?.types?.[vehType()]?.torpedoes;
    return tp && tp.count > 0 ? tp : null;
  };
  const wrapAngle = (a) => Math.atan2(Math.sin(a), Math.cos(a));
  const clamp1 = (v) => Math.min(1, Math.max(-1, v));
  const clamp01 = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
  const clamp1Range = (v, m) => (v < -m ? -m : v > m ? m : v);
  // Turret-aim expo: softens response near centre (fine tracking) while
  // keeping full slew authority at full deflection. k is the centre gain.
  const aimExpo = (v, k = 0.4) => (k + (1 - k) * v * v) * v;

  // --- battleship fire control (client mirror of internal/game/battleship.go) ---
  const bbCfg = config.battleship ?? {};
  // Local plotting state: the server is authoritative, this just drives the
  // HUD (solve/reload countdowns restart from our own send/event times).
  const fc = { target: null, plottedAt: 0, reloadUntil: 0, impacts: [], shells: [] };
  // Plot-table aim state: a draggable, uncommitted reticle the gunner places
  // before confirming it as the bombardment coordinate.
  const plotAim = { pending: null, dragging: false };
  // Firing cutaway: a cinematic camera that frames the salvo, rides the shells
  // along their arc, then lingers on the impact before returning to the chart.
  const cine = { mode: "off", t: 0, from: null, to: null, sol: null, shake: 0 };
  // Spotter-called fire missions: friendly pilots' pings, drawn on the
  // plotting chart until they age out (battleship.spotterMarkSec).
  const spotMarks = [];
  const commsPings = []; // quick-chat map pings (roadmap #17), like spotMarks
  const spotMarkSec = bbCfg.spotterMarkSec ?? 40;
  const resetFireControl = () => {
    fc.target = null;
    fc.impacts.length = 0;
    fc.shells.length = 0;
  };
  // Full launch-velocity vector + time of flight, for the shell-following
  // cutaway camera. Two elevations hit the same point: the low (flat) arc and
  // the high (lobbed) arc. Mirrors solveBallistic() in battleship.go.
  const solveBallisticVec = (from, to, v, highArc = false) => {
    const dx = to.x - from.x;
    const dz = to.z - from.z;
    const r = Math.hypot(dx, dz);
    const dy = to.y - from.y;
    const g = 9.81;
    const disc = v ** 4 - g * (g * r * r + 2 * dy * v * v);
    if (disc < 0 || r < 1) return null;
    const root = Math.sqrt(disc);
    const theta = Math.atan(((highArc ? v * v + root : v * v - root)) / (g * r));
    const vh = v * Math.cos(theta);
    return { vx: (dx / r) * vh, vy: v * Math.sin(theta), vz: (dz / r) * vh, tof: r / vh };
  };
  // Does this arc reach the target without plowing into a hill first? Mirrors
  // World.arcClearsTerrain — the terminal descent onto the target legitimately
  // meets the surface, so a contact only counts when it's well short, and
  // elsewhere the round must clear the crest with a little headroom.
  const arcClearsTerrain = (from, to, sol) => {
    const g = 9.81;
    const steps = 40;
    const crest = 12;
    const margin2 = (2 * (bbCfg.shellBlastRadiusM ?? 55)) ** 2;
    for (let i = 1; i <= steps; i++) {
      const t = (sol.tof * i) / steps;
      const x = from.x + sol.vx * t;
      const z = from.z + sol.vz * t;
      const y = from.y + sol.vy * t - 0.5 * g * t * t;
      const dx = x - to.x;
      const dz = z - to.z;
      if (dx * dx + dz * dz <= margin2) return true; // the fall of shot
      if (y <= field.surfaceAt(x, z) + crest) return false; // clips a mask short
    }
    return true;
  };
  // Variable-charge fire solution: the flattest, fastest charge that clears the
  // terrain, dropping powder to a plunging arc when a hill is in the way.
  // Mirrors World.solveFireMission. Returns {vx,vy,vz,tof,chargeMS,highArc}.
  const fullChargeMS = bbCfg.muzzleSpeedMS ?? 320;
  const minChargeMS = bbCfg.minChargeMS ?? fullChargeMS * 0.34;
  const chargeStep = 4;
  // Direct fire reaches maxRangeM; past that, out to maxRangeSpottedM, the
  // battery only looses with a friendly plane spotting over the target.
  const directRange = bbCfg.maxRangeM ?? 3600;
  const spottedRange = Math.max(directRange, bbCfg.maxRangeSpottedM ?? directRange);
  const solveFireMission = (from, to) => {
    for (let v = fullChargeMS; v >= minChargeMS; v -= chargeStep) {
      const sol = solveBallisticVec(from, to, v, false);
      if (sol && arcClearsTerrain(from, to, sol)) return { ...sol, chargeMS: v, highArc: false };
    }
    for (let v = minChargeMS; v <= fullChargeMS; v += chargeStep) {
      const sol = solveBallisticVec(from, to, v, true);
      if (sol && arcClearsTerrain(from, to, sol)) return { ...sol, chargeMS: v, highArc: true };
    }
    const sol = solveBallisticVec(from, to, fullChargeMS, false);
    return sol && { ...sol, chargeMS: fullChargeMS, highArc: false };
  };
  // A friendly plane near the plotted point tightens the salvo: estimate
  // from the (far-echo) entity positions so the HUD can say so.
  const spotterOnStation = () => {
    if (!fc.target) return false;
    const r2max = (bbCfg.spotRadiusM ?? 600) ** 2;
    for (const ent of entities.values()) {
      if (ent.status === 0 || roster.get(ent.id)?.team !== selfTeam) continue;
      const last = ent.buffer.at(-1);
      if (!last) continue;
      const dx = last.pos.x - fc.target.x;
      const dz = last.pos.z - fc.target.z;
      if (dx * dx + dz * dz <= r2max) return true;
    }
    return false;
  };

  // server-time estimation for interpolation
  let timeOffset = null;
  const perfNow = () => performance.now() / 1000;

  // --- join flow ---
  const joinStatus = document.getElementById("join-status");
  const playBtn = document.getElementById("play-btn");
  const spectateBtn = document.getElementById("spectate-btn");
  const nameInput = document.getElementById("name-input");
  nameInput.value = localStorage.getItem("va-name") ?? "";
  // MY PROFILE deep-link (roadmap #27): points at the saved callsign's page.
  const profileLink = document.getElementById("my-profile-link");
  const syncProfileLink = () => {
    const nm = (nameInput.value || "").trim();
    if (profileLink && nm) {
      profileLink.href = `/pilot/${encodeURIComponent(nm)}`;
      profileLink.classList.remove("hidden");
    } else if (profileLink) {
      profileLink.classList.add("hidden");
    }
  };
  syncProfileLink();
  nameInput.addEventListener("input", syncProfileLink);
  playBtn.disabled = true;
  spectateBtn.disabled = true;

  setupMissions();
  setupSettings(input);
  // Paint the join-card control help and HUD hint bar from the live keymap, then
  // keep them in step whenever a binding changes — the help always shows the
  // pilot's actual keys, never the shipped defaults.
  repaintHints();
  keymap.onChange(() => repaintHints());
  preloadJoinDaily();

  // Today's missions on the first-join screen too (the welcome later replaces
  // these with this pilot's live progress). The public board carries no
  // per-pilot progress, so the bars read 0 until you join — still a useful
  // at-a-glance brief, and it keeps the join and respawn screens identical.
  async function preloadJoinDaily() {
    try {
      const res = await fetch(serverURL("/daily.json"), { cache: "no-store" });
      if (!res.ok) return;
      const data = await res.json();
      const list = (data?.challenges ?? []).map((c) => ({
        id: c.id, desc: c.desc, goal: c.goal, have: 0,
      }));
      if (!net.joined && list.length) hud.setDaily(list);
    } catch {}
  }

  let selectedPlane = Number(localStorage.getItem("va-plane") ?? 0) || 0;
  if (selectedPlane < 0 || selectedPlane >= planeTypes.length) selectedPlane = 0;

  // Lifetime kills gate the career-locked aircraft in the picker. Seeded from
  // the last session so a returning ace sees their jet open before joining;
  // the welcome's career record is the truth and re-syncs it.
  let careerKills = Number(localStorage.getItem("va-kills") ?? 0) || 0;
  hud.setCareerKills(careerKills);

  // True while sitting on the respawn screen (down, in-game, not spectating).
  // Waiting for a bomber counts: the picker is live so the player can reserve a
  // different unit instead of holding out for one.
  const respawning = () =>
    net.joined && !spectating && (waitingBomber || (haveSpawned && selfStatus === 0));

  // Is a friendly emplacement standing and unoccupied right now?
  const gunFree = (id) => {
    const st = aaGuns.statusOf(id);
    return !!(st && st.alive && !st.occupied);
  };

  // Reserve the unit-picker choice for the next spawn (respawn screen). gk 0
  // flies the chosen plane; gk 21+ crews a bomber turret seat; gk 11+ drives
  // a ground vehicle; gk 1/2/3 reserves the first free friendly mount of
  // AA-site kind gk-1 (the server falls back to flying if it's gone or taken
  // by spawn time).
  function applyRespawnPick(sel) {
    // Picking anything but a bomber turret ends a "waiting for a bomber" hold —
    // the player chose to fly/drive/man now rather than wait one out.
    if (sel.gk < 21) waitingBomber = false;
    if (sel.gk === 0) {
      pendingPlane = sel.plane;
      net.setPlane(sel.plane);
      if (pendingGun !== 0) { pendingGun = 0; net.setGun(0); }
      if (pendingVeh >= 0) { pendingVeh = -1; net.setVehicle(0); }
      if (pendingTur >= 0) { pendingTur = -1; net.setTurret(0); }
    } else if (sel.gk >= 21) {
      pendingTur = sel.gk - 21;
      pendingGun = 0;
      pendingVeh = -1;
      net.setTurret(pendingTur + 1); // the server clears the other choices
    } else if (sel.gk >= 11) {
      pendingVeh = sel.gk - 11;
      pendingGun = 0;
      pendingTur = -1;
      net.setVehicle(pendingVeh + 1);
    } else {
      const kind = sel.gk - 1;
      const slot =
        respawnGunSlots.find((g) => g.kind === kind && gunFree(g.id)) ??
        respawnGunSlots.find((g) => g.kind === kind);
      if (slot) {
        pendingVeh = -1;
        pendingTur = -1;
        pendingGun = slot.id;
        net.setGun(slot.id);
      }
    }
  }

  // Unit picker: category tabs (AIRCRAFT default) over stat cards. Picking an
  // aircraft card sets the plane and flies (gk 0); picking a gun/vehicle card
  // sets the "spawn as" wire kind to man that mount or drive that vehicle. The
  // same picker serves the respawn screen, so while down a pick reserves the
  // unit for the next spawn live (applyRespawnPick).
  let joinGunGk = 0;
  hud.buildUnitSelect(config, planeTypes, calibers, selectedPlane, (sel) => {
    joinGunGk = sel.gk;
    if (sel.gk === 0) {
      selectedPlane = sel.plane;
      localStorage.setItem("va-plane", String(sel.plane));
      analytics.event("plane_select", {
        plane_type: planeTypes[sel.plane]?.name,
        plane_index: sel.plane,
      });
    } else {
      analytics.event("join_gun_select", { gun_kind: sel.gk });
    }
    // The deck (BASE / CARRIER) choice only applies to an aircraft spawn.
    hud.setDeckVisible(sel.gk === 0);
    hud.setPaintVisible(sel.gk === 0 && (config.schemes?.length ?? 0) > 1); // paint is for planes
    if (respawning()) applyRespawnPick(sel);
  });

  // PAINT SHOP (roadmap #25): equip a scheme live — repaint our plane, remember
  // it, and tell the server (which backstops the unlock). Rebuilt with the real
  // career in onWelcome; before that the locks read off the cached kills only.
  const equipScheme = (i) => {
    equippedScheme = i;
    localStorage.setItem("va-scheme", String(i));
    net.setScheme(i);
    selfVisual?.setScheme(i);
    analytics.event("scheme_equip", { scheme: (config.schemes?.[i] ?? {}).id });
  };
  hud.buildPaintRow(config.schemes ?? [], { kills: careerKills }, equippedScheme, equipScheme);
  hud.setPaintVisible(joinGunGk === 0 && (config.schemes?.length ?? 0) > 1);

  // LAUNCH FROM base / carrier deck — built once and shown on both the join
  // and respawn screens (the choice is a sticky preference, replayed into the
  // first spawn from localStorage; carrier-alive state is painted while down).
  hud.buildDeckSelect((deck) => {
    pendingDeck = deck;
    localStorage.setItem("va-deck", deck ? "1" : "0");
    net.setDeck(deck);
  });
  hud.updateDeckSelect(pendingDeck, true);
  hud.setDeckVisible(joinGunGk === 0);

  // Naval theater: the spawn screen is a warship lobby, not an airfield. Rewrite
  // the air-game copy/controls so the first thing a captain reads matches the
  // ship they're about to con. (Reuses the air lobby's layout, per design.)
  if (landTheater) {
    const set = (sel, html) => {
      const el = document.querySelector(`#join-screen ${sel}`);
      if (el) el.innerHTML = html;
    };
    set(".tagline", "blocky armored warfare on the Brick Steppe");
    if (playBtn) playBtn.textContent = "ROLL OUT";
    set(
      ".controls-help",
      `<div class="desktop-only"><b>W/S</b> drive (bottom of the throttle is reverse) &nbsp; <b>A/D</b> steer &nbsp; <b>MOUSE</b> lays the gun &nbsp; <b>CLICK</b>/<b>SPACE</b> fire</div>
       <div class="desktop-only"><b>Z</b> gunsight &nbsp; <b>X</b> swap AP / HE &nbsp; <b>B</b> wire-guided missile &nbsp; <b>C</b> smoke &nbsp; <b>R</b> field repair &nbsp; <b>T</b> artillery strike &nbsp; <b>G</b> overdrive &nbsp; <b>F</b> bail out</div>
       <div class="desktop-only"><b>TAB</b> scoreboard &nbsp; <b>ENTER</b> chat &nbsp; <b>M</b> mute &nbsp; &mdash; gamepads supported</div>
       <div class="desktop-only">Rounds strike the <b>plate they hit</b>: angle your armor &mdash; sharp hits <b>ricochet</b>, big overmatch punches <b>through and through</b>. Penetrations knock out <b>tracks, engines, guns</b> and can find the <b>ammo rack</b>. You're <b>hidden</b> until an enemy has line of sight inside your spotting range: treelines, ridges, and town walls are real cover. <b>R</b> repairs, <b>T</b> calls artillery on your crosshair, <b>C</b> pops smoke, <b>G</b> overdrives the engine &mdash; and mind the <b>strike planes</b>: that's what the Whirlwind is for.</div>
       <div class="touch-only"><b>LEFT THUMB</b> steers &nbsp; left <b>slider</b> drives (bottom = reverse) &nbsp; <b>FIRE</b> button &nbsp; <b>RIGHT DRAG</b> looks around</div>`,
    );
    set(
      ".blurb",
      `Two armored task forces fight for the <span class="gold">BRICK STEPPE</span> &mdash; kill armor and hold the <b>three towns</b> to score first, or lead at the bell. Pick your ride: the <b>Paladin</b> MBT shrugs anything off its front plate, the <b>Warden</b> IFV shreds light armor and wire-guides missiles into heavy, the <b>Whirlwind</b> sweeps the strike planes out of the sky, and the <b>Jackal</b> races the flanks with a recoilless rifle that has to stop to shoot.`,
    );
    document.getElementById("respawn-daily")?.classList.add("hidden");
    document.getElementById("missions-link")?.classList.add("hidden");
  }
  if (armadaTheater) {
    const set = (sel, html) => {
      const el = document.querySelector(`#join-screen ${sel}`);
      if (el) el.innerHTML = html;
    };
    set(".tagline", "combined arms over, on, and around the Brick Gulf");
    set(
      ".controls-help",
      `<div class="desktop-only"><b>FLY</b> a fighter or bomber, <b>SAIL</b> a warship, or <b>ROLL OUT</b> in armor &mdash; pick an arm above and walk the hangar with the arrows; each unit fights with its own controls (the brief plays when you spawn).</div>
       <div class="desktop-only">Planes <b>spot</b> for the fleet &nbsp; ships <b>shell the shore</b> &nbsp; tanks <b>hold the towns</b> &nbsp; the <b>Sky Ring</b> multiplies your side's zone points.</div>
       <div class="touch-only"><b>LEFT THUMB</b> steers &nbsp; left <b>slider</b> is throttle/telegraph &nbsp; <b>FIRE</b> button &nbsp; <b>RIGHT DRAG</b> looks around</div>`,
    );
    set(
      ".blurb",
      `The war for the <span class="gold">BRICK GULF</span> &mdash; one front from the steppe towns through <b>Brickhaven harbor</b> to <b>Fort Brick</b> at sea. Hold zones to score (tanks take the towns, ships the water, <b>either</b> takes the harbor), and hold the <b>Sky Ring</b> above the Gulf to multiply every tick. Planes are the glue: spot armor for the fleet's guns, put torpedoes into battleships, and keep the enemy's eyes off your own push.`,
    );
    document.getElementById("respawn-daily")?.classList.add("hidden");
    document.getElementById("missions-link")?.classList.add("hidden");
  }
  // ---- the calmer lobby (every theater) ----
  // Player feedback: a wall of stat cards greeting a brand-new pilot is
  // homework, not a welcome. Every lobby reorders itself: callsign + TAKE OFF
  // first, the picker as a one-card carousel (hud.buildUnitSelect), the side
  // doors (training / spectate / paint / manual) in a quiet row BELOW the
  // hangar, and the site chrome as icon buttons in the top-right corner.
  {
    const screenEl = document.getElementById("join-screen");
    const joinCard = screenEl?.querySelector(".join-card");
    const taglineEl = joinCard?.querySelector(".tagline");
    const joinRowEl = joinCard?.querySelector(".join-row");
    if (screenEl && joinCard && taglineEl && joinRowEl) {
      screenEl.classList.add("join-v2");
      // The first thing you see: your name, the big gold button, the tower.
      taglineEl.after(joinRowEl);
      joinRowEl.after(joinStatus);

      // The quiet row: doors you'll want eventually, not before first takeoff.
      const aux = document.createElement("div");
      aux.className = "join-aux";
      const paintRowEl = document.getElementById("paint-row");
      const paintBtn = document.createElement("button");
      paintBtn.type = "button";
      paintBtn.id = "paint-toggle";
      paintBtn.className = "ghost";
      paintBtn.textContent = "🎨 PAINT";
      paintBtn.onclick = () => {
        const open = paintRowEl?.classList.toggle("open");
        paintBtn.classList.toggle("active", !!open);
      };
      if (paintRowEl) {
        // The swatches only apply to aircraft spawns (hud.setPaintVisible flips
        // "hidden"); the toggle follows so it never offers paint a tank can't wear.
        const syncPaint = () =>
          paintBtn.classList.toggle("hidden", paintRowEl.classList.contains("hidden"));
        new MutationObserver(syncPaint).observe(paintRowEl, {
          attributes: true,
          attributeFilter: ["class"],
        });
        syncPaint();
      }
      const helpEl = joinCard.querySelector(".controls-help");
      const helpBtn = document.createElement("button");
      helpBtn.type = "button";
      helpBtn.id = "help-toggle";
      helpBtn.className = "ghost";
      helpBtn.textContent = "📖 HOW TO PLAY";
      helpBtn.onclick = () => {
        const open = helpEl?.classList.toggle("open");
        helpBtn.classList.toggle("active", !!open);
      };
      const trainingBtnEl = document.getElementById("training-btn");
      const auxKids = [trainingBtnEl, spectateBtn, paintRowEl ? paintBtn : null, helpEl ? helpBtn : null];
      for (const k of auxKids) if (k) aux.appendChild(k);
      (paintRowEl ?? helpEl ?? joinCard.querySelector(".blurb"))?.before(aux);

      // Site chrome as symbols, top right: soundtrack, settings, hall of fame,
      // about. Each proxies (or links to) the footer control it replaces, so
      // state and persistence stay where they always lived.
      const nav = document.createElement("div");
      nav.id = "corner-nav";
      const cornerBtn = (id, glyph, label, onClick, extraClass = "") => {
        const b = document.createElement("button");
        b.type = "button";
        b.id = id;
        b.className = ("corner-btn " + extraClass).trim();
        b.title = label;
        b.setAttribute("aria-label", label);
        b.textContent = glyph;
        b.onclick = onClick;
        return b;
      };
      const cornerLink = (href, glyph, label) => {
        const a = document.createElement("a");
        a.href = href;
        a.className = "corner-btn";
        a.title = label;
        a.setAttribute("aria-label", label);
        a.textContent = glyph;
        return a;
      };
      nav.append(
        cornerBtn("corner-music", "♪", "soundtrack", () =>
          document.getElementById("music-toggle")?.click(),
        ),
        cornerBtn(
          "corner-settings",
          "⚙",
          "settings",
          () => document.getElementById("settings-link")?.click(),
          "desktop-only",
        ),
        cornerLink("/leaderboard", "🏆", "leaderboard"),
        cornerLink("/about", "？", "about"),
      );
      screenEl.appendChild(nav);

      // Slim footer: what the corner icons replaced disappears; the rest of
      // the site nav (and the live MY PROFILE link + build stamp, moved so
      // their wiring survives) stays in one quiet line. The old footer stays
      // in the DOM, hidden, because the corner buttons proxy-click into it.
      const foot = joinCard.querySelector(".join-footer");
      if (foot) {
        const slim = document.createElement("p");
        slim.className = "join-footer v2-footer";
        // MISSIONS keeps its seat (air theater's daily board; the other
        // theaters hide it themselves), as does the live MY PROFILE link.
        const missions = document.getElementById("missions-link");
        if (missions) slim.appendChild(missions);
        const profile = document.getElementById("my-profile-link");
        if (profile) slim.appendChild(profile);
        for (const a of [...foot.querySelectorAll("a[href]")]) {
          const href = a.getAttribute("href");
          if (href === "/battleship" || href === "/blog" || href === "/live" || href === "/contact")
            slim.appendChild(a);
        }
        const stamp = document.getElementById("build-stamp");
        if (stamp) slim.appendChild(stamp);
        foot.classList.add("hidden");
        foot.after(slim);
      }
    }
  }
  if (navalTheater) {
    const set = (sel, html) => {
      const el = document.querySelector(`#join-screen ${sel}`);
      if (el) el.innerHTML = html;
    };
    set(".tagline", "blocky warship battles on the Brick Sea");
    if (playBtn) playBtn.textContent = "SET SAIL";
    set(
      ".controls-help",
      `<div class="desktop-only"><b>W/S</b> engine telegraph &nbsp; <b>A/D</b> rudder &nbsp; <b>MOUSE</b> trains the main battery &nbsp; <b>CLICK</b>/<b>SPACE</b> fire</div>
       <div class="desktop-only"><b>X</b> swap shell HE / AP / SS &nbsp; <b>B</b> torpedoes &nbsp; <b>C</b> smoke screen &nbsp; <b>Z</b> gunsight &nbsp; <b>R</b> damage control &nbsp; <b>T</b> repair party &nbsp; <b>G</b> engine boost &nbsp; <b>F</b> leave ship</div>
       <div class="desktop-only"><b>TAB</b> scoreboard &nbsp; <b>ENTER</b> chat &nbsp; <b>M</b> mute &nbsp; &mdash; gamepads &amp; HOTAS supported</div>
       <div class="desktop-only">AP must beat the <b>belt armor</b> to pierce &mdash; a <b>citadel</b> hit is devastating; HE starts <b>fires</b>. You're <b>hidden</b> beyond your spotting range until you fire or close in &mdash; lay <b>smoke</b> (C) to vanish, load <b>SS</b> star shells to strip the enemy's cover. Fight fires and flooding with the <b>damage-control party</b> (R), heal with the <b>repair party</b> (T), and kick in the <b>engine boost</b> (G) to close or run; mind the carrier <b>torpedo bombers</b> overhead.</div>
       <div class="touch-only"><b>LEFT THUMB</b> steers &nbsp; left <b>slider</b> works the engine telegraph &nbsp; <b>FIRE</b> button &nbsp; <b>RIGHT DRAG</b> looks around</div>`,
    );
    set(
      ".blurb",
      `Two fleets fight a <span class="gold">FLEET ACTION</span> on the open sea &mdash; sink enemy hulls and win the <b>domination</b> of <b>three capture zones</b> to score <b>800</b> first, or lead at the bell. Split the fleet to hold the map: destroyers race for the flanks while the heavies slug it out for the centre. Mind the islands: run aground and you'll have to back off the shoal.`,
    );
    document.getElementById("respawn-daily")?.classList.add("hidden");
    document.getElementById("missions-link")?.classList.add("hidden");
  }

  try {
    await net.connect();
    joinStatus.textContent = "tower online — ready for takeoff";
    playBtn.disabled = false;
    spectateBtn.disabled = false;
    analytics.event("server_connect", { result: "ok" });
  } catch {
    joinStatus.textContent = "could not reach the server";
    analytics.event("server_connect", { result: "fail" });
  }
  net.onClose = () => {
    document.getElementById("disconnect-screen").classList.remove("hidden");
    document.getElementById("hud").classList.add("hidden");
    analytics.event("disconnect", { joined: net.joined });
    analytics.sessionSummary("disconnect");
    // The unattended stream rides out server restarts on its own.
    if (autoDirector) setTimeout(() => location.reload(), 4000);
  };

  // Touch: go fullscreen and pin landscape on takeoff (best effort — iOS
  // Safari supports neither, the rotate overlay covers that case).
  const goImmersive = async () => {
    try {
      await document.documentElement.requestFullscreen?.({ navigationUI: "hide" });
    } catch {}
    try {
      await screen.orientation?.lock?.("landscape");
    } catch {}
  };

  const doJoin = () => {
    if (trainingServer || playBtn.disabled || net.joined) return; // training picks scenarios only
    audio.ensure();
    if (touch) goImmersive();
    localStorage.setItem("va-name", nameInput.value);
    // gk 21+ on the picker means "crew a bomber turret" (seat = gk - 21);
    // gk 11..20 means "drive a ground vehicle" (type = gk - 11).
    const joinTur = joinGunGk >= 21 ? joinGunGk - 20 : 0;
    const joinVeh = joinGunGk >= 11 && joinGunGk < 21 ? joinGunGk - 10 : 0;
    const joinGk = joinGunGk >= 11 ? 0 : joinGunGk;
    net.join(nameInput.value, selectedPlane, identityToken(), joinGk, joinVeh, "", joinTur, equippedScheme, analyticsSession.sessionId);
    playBtn.disabled = true;
    joinStatus.textContent = joinTur
      ? "crewing up…"
      : joinVeh
        ? "rolling out…"
        : joinGunGk
          ? "manning up…"
          : "taking off…";
    analytics.event("game_join", {
      plane_type: planeTypes[selectedPlane]?.name,
      plane_index: selectedPlane,
      deck: pendingDeck,
      gun_kind: joinGunGk,
      named: nameInput.value.trim().length > 0,
    });
  };

  // TAKE OFF while down: ask the server to respawn. The server holds the spawn
  // to the cooldown floor (RespawnAt), so this only does anything once the
  // button has unlocked — the respawn loop below gates it and counts it down.
  const requestRespawn = () => {
    if (!respawning() || respawnSent) return;
    if (perfNow() - deathAt < deathRespawnSec) return; // still on cooldown
    audio.ensure();
    net.respawn();
    respawnSent = true;
    playBtn.disabled = true;
    joinStatus.textContent = "taking off…";
    analytics.event("respawn", { plane_type: planeTypes[pendingPlane]?.name });
  };

  // One button serves both: join the first time, respawn every time after.
  const takeOff = () => {
    respawning() ? requestRespawn() : doJoin();
    deathCoach = null; // the lesson expires with the new life
    deathTip = null; // and so does the field-manual page
    document.getElementById("death-coach")?.classList.add("hidden");
    document.getElementById("respawn-tip")?.classList.add("hidden");
    // Both paths ran audio.ensure() inside this click, so the ctx is live.
    if (musicOn()) music.start();
  };
  playBtn.onclick = takeOff;
  // First-visit field manual: the join card opens on a "did you know?" too,
  // so the manual isn't gated behind dying first. Replaced per-death after.
  {
    const tipEl = document.getElementById("respawn-tip");
    const tip = tipEl && nextTip(tipCtx);
    if (tip) {
      tipEl.textContent = `★ DID YOU KNOW — ${tip.text}`;
      tipEl.classList.remove("hidden");
    }
  }
  spectateBtn.onclick = () => {
    // Allowed from the menu (not yet joined) AND from the respawn screen —
    // a downed pilot can step back up to the tower; the server releases the
    // player and hands this connection the spectator welcome.
    if (spectateBtn.disabled || (net.joined && !respawning())) return;
    audio.ensure();
    net.spectate();
    spectateBtn.disabled = true;
    analytics.event("spectate_start");
  };
  // Director mode (the 24/7 stream) and the /spectate deep link both drop
  // straight into spectating once the connection is up, so the container needs
  // nothing but a browser pointed at ?director=1 or /spectate. (The connect
  // above is awaited, so the button is ready here.)
  if (autoSpectate && !spectateBtn.disabled) {
    spectateBtn.click();
    music?.start(); // audio.ensure() ran inside the click, so the ctx is live
  }
  nameInput.onkeydown = (e) => {
    if (e.key === "Enter") takeOff();
  };
  // Join-screen soundtrack toggle (roadmap #46): players opt in to the
  // procedural score; the choice persists in localStorage ("va-music") and
  // flips live — the click satisfies the audio-context user gesture.
  const musicToggle = document.getElementById("music-toggle");
  const paintMusicToggle = () => {
    if (musicToggle) musicToggle.textContent = `♪ SOUNDTRACK: ${musicOn() ? "ON" : "OFF"}`;
    // The combined lobby's corner ♪ shows the same state as a lit icon.
    document.getElementById("corner-music")?.classList.toggle("on", musicOn());
  };
  paintMusicToggle();
  // --- comms wheel (roadmap #17): hold H for a radial of canned calls;
  // release over a slice to send its index. Desktop-only — the touch COMMS
  // button didn't earn its screen space and was removed. ---
  const commsWheel = { el: null, open: false, hover: -1, slices: [] };
  {
    const phrases = config.comms?.phrases ?? [];
    if (phrases.length) {
      const el = document.createElement("div");
      el.id = "comms-wheel";
      el.className = "hidden";
      const R = 110;
      phrases.forEach((ph, i) => {
        const a = (i / phrases.length) * Math.PI * 2 - Math.PI / 2;
        const d = document.createElement("div");
        d.className = "cw-slice";
        d.textContent = ph.label;
        d.style.left = `calc(50% + ${Math.round(Math.cos(a) * R)}px)`;
        d.style.top = `calc(50% + ${Math.round(Math.sin(a) * R)}px)`;
        el.appendChild(d);
        commsWheel.slices.push(d);
      });
      const hub = document.createElement("div");
      hub.className = "cw-hub";
      hub.textContent = "COMMS";
      el.appendChild(hub);
      document.body.appendChild(el);
      commsWheel.el = el;
    }
  }
  const paintCommsHover = () =>
    commsWheel.slices.forEach((d, i) => d.classList.toggle("hover", i === commsWheel.hover));
  const commsSetOpen = (open) => {
    if (!commsWheel.el) return;
    const can = open && net.joined && !spectating && selfStatus !== 0 && !replay;
    commsWheel.open = can;
    commsWheel.el.classList.toggle("hidden", !can);
    if (!can) {
      commsWheel.hover = -1;
      paintCommsHover();
    }
  };
  const commsTrack = (px, py) => {
    if (!commsWheel.open) return;
    const dx = px - innerWidth / 2, dy = py - innerHeight / 2;
    if (dx * dx + dy * dy < 30 * 30) {
      commsWheel.hover = -1;
      paintCommsHover();
      return;
    }
    const n = commsWheel.slices.length;
    let a = Math.atan2(dy, dx) + Math.PI / 2; // slice 0 sits at 12 o'clock
    a = ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    commsWheel.hover = Math.round((a / (2 * Math.PI)) * n) % n;
    paintCommsHover();
  };
  addEventListener("mousemove", (e) => commsTrack(e.clientX, e.clientY));
  addEventListener("pointermove", (e) => commsTrack(e.clientX, e.clientY));
  const commsPick = () => {
    if (commsWheel.open && commsWheel.hover >= 0) {
      net.comms(commsWheel.hover);
      analytics.event("comms_ping", { phrase: config.comms?.phrases?.[commsWheel.hover]?.id });
    }
    commsSetOpen(false);
  };
  input.commsOpen = () => commsSetOpen(true);
  input.commsPick = commsPick;

  // Spawn-heading rose (roadmap #55): 8 detents + AUTO, sticky like the
  // deck preference. Detent 0 is North (screen up = world -Z), clockwise.
  const roseHdg = (d) => {
    const theta = (d * Math.PI) / 4; // screen angle from north, clockwise
    const dx = Math.sin(theta), dz = -Math.cos(theta);
    return Math.atan2(-dz, dx); // world yaw: fwd = (cos, 0, -sin)
  };
  const roseButtons = [...document.querySelectorAll("#spawn-rose button")];
  const paintRose = (sel) =>
    roseButtons.forEach((b) => b.classList.toggle("sel", b.dataset.d === String(sel)));
  let spawnHdgDetent = Number(localStorage.getItem("va-spawn-hdg") ?? -1);
  if (!Number.isFinite(spawnHdgDetent)) spawnHdgDetent = -1;
  paintRose(spawnHdgDetent);
  roseButtons.forEach((b) =>
    b.addEventListener("click", () => {
      spawnHdgDetent = Number(b.dataset.d);
      localStorage.setItem("va-spawn-hdg", String(spawnHdgDetent));
      paintRose(spawnHdgDetent);
      net.setHeading(spawnHdgDetent < 0 ? 999 : roseHdg(spawnHdgDetent));
      analytics.event("spawn_heading_set", { auto: spawnHdgDetent < 0 });
    }),
  );

  if (musicToggle)
    musicToggle.onclick = () => {
      localStorage.setItem("va-music", musicOn() ? "0" : "1");
      paintMusicToggle();
      if (musicOn()) {
        audio.ensure();
        music.start();
        analytics.event("music_enabled");
      } else {
        music.stop();
      }
    };

  // --- training (flight school) ---
  // Training is hosted by a separate standalone server (run `server -training`
  // or set $TRAINING). When the page is served by one, the join screen *is* the
  // scenario picker; on the live game server the TRAINING button just links out
  // to a training server if one is configured (else it's hidden).
  setupTraining();
  setupModeRibbon();

  // The theater switcher on the join screen. Each theater (air / naval / land /
  // combined) is its own server on its own domain; the server injects the doors
  // between them as window.__WARBIRDS_MODES__ (see cmd/server). We mark the room
  // the player is standing in from config.world.theater and link out to the
  // rest — but only when there's somewhere else to go, so a solo deploy stays
  // the clean, no-chrome join screen it always was.
  function setupModeRibbon() {
    const host = document.getElementById("mode-ribbon");
    const modes =
      typeof window !== "undefined" && Array.isArray(window.__WARBIRDS_MODES__)
        ? window.__WARBIRDS_MODES__
        : null;
    if (!host || !modes) return;
    // Icon + label + canonical order for every theater we know how to badge.
    const META = {
      air: { icon: "✈", label: "AIR" },
      naval: { icon: "⚓", label: "NAVAL" },
      land: { icon: "⚔", label: "LAND" },
      armada: { icon: "🎯", label: "COMBINED ARMS" },
    };
    const ORDER = ["air", "naval", "land", "armada"];
    // Air is the baseline theater and ships with no world.theater set, so an
    // empty/undefined theater IS air — without this the flagship instance never
    // matches its own chip and links "AIR" back to itself.
    const here = config.world.theater || "air";
    const byTheater = new Map(
      modes.filter((m) => m && META[m.theater]).map((m) => [m.theater, m]),
    );
    // Always show where you are, even if the operator forgot to list this very
    // instance's own URL — you should never look at a ribbon that omits home.
    if (!byTheater.has(here) && META[here]) byTheater.set(here, { theater: here, url: "" });
    const ordered = ORDER.filter((t) => byTheater.has(t)).map((t) => byTheater.get(t));
    // Nowhere else to sail? Then it's not a switcher — leave the screen clean.
    if (!ordered.some((m) => m.theater !== here && m.url)) return;

    for (const m of ordered) {
      const meta = META[m.theater];
      const isHere = m.theater === here;
      let el;
      if (isHere) {
        el = document.createElement("span");
        el.className = "mode-chip current";
        el.setAttribute("aria-current", "page");
      } else if (m.url) {
        el = document.createElement("a");
        el.className = "mode-chip";
        el.href = m.url;
      } else {
        continue; // a theater we can name but can't reach — don't tease it
      }
      el.innerHTML =
        `<span class="mode-ic" aria-hidden="true">${meta.icon}</span>` +
        `<span class="mode-lbl">${meta.label}</span>`;
      host.appendChild(el);
    }
    host.classList.remove("hidden");
  }

  function setupTraining() {
    const btn = document.getElementById("training-btn");
    const screen = document.getElementById("training-screen");
    const list = document.getElementById("training-list");
    const close = document.getElementById("training-close");

    // EXIT TRAINING / "← FLIGHT SCHOOL" reload back to the picker; the private
    // world is torn down server-side on disconnect.
    const leave = () => location.reload();
    document.getElementById("training-exit")?.addEventListener("click", leave);
    document.getElementById("brief-exit")?.addEventListener("click", leave);
    document.getElementById("brief-begin")?.addEventListener("click", () => {
      document.getElementById("training-brief")?.classList.add("hidden");
    });

    if (!trainingServer) {
      // Live game server: no in-place training. Link out if a training server
      // URL was provided, otherwise drop the button entirely. Hand the training
      // server our origin as ?from= so its "BACK TO MAIN GAME" button can bring
      // the player straight home with no extra configuration.
      if (btn) {
        if (trainURL) {
          btn.addEventListener("click", () => {
            let dest = trainURL;
            try {
              const u = new URL(trainURL, location.href);
              u.searchParams.set("from", location.origin);
              dest = u.toString();
            } catch {}
            location.assign(dest);
          });
        } else btn.classList.add("hidden");
      }
      return;
    }

    // Training server: give the picker a door back to the main game, whenever we
    // know where it lives. Without it a player who lands here is trapped — the
    // whole app is the flight-school picker and reloading only returns here.
    const home = document.getElementById("training-home");
    if (home && gameURL) {
      home.addEventListener("click", () => location.assign(gameURL));
      home.classList.remove("hidden");
    }

    // Training server: the picker is the whole join experience. Hide the
    // normal join card entirely (title, callsign row, controls help) so it
    // doesn't show through behind the overlay, and show the scenario list.
    if (!screen || !list || !trainingScenarios.length) {
      btn?.classList.add("hidden");
      return;
    }
    document.getElementById("join-screen")?.classList.add("hidden");
    close?.classList.add("hidden"); // the picker is the whole app here
    // The syllabus runs easiest first — the feedback that built this was a
    // fresh pilot staring at nine unfamiliar drill names with no idea where
    // to start. Stable sort, so drills of a grade keep their config order.
    const syllabus = [...trainingScenarios].sort(
      (a, b) => (a.difficulty || 2) - (b.difficulty || 2),
    );
    for (const sc of syllabus) {
      const item = document.createElement("button");
      item.type = "button";
      item.className = "train-item";
      item.innerHTML =
        `<span class="ti-head"><span class="ti-name">${escapeName(sc.name ?? sc.id)}</span>` +
        `${scenarioDiffChip(sc)}</span>` +
        `${scenarioTagRow(sc)}` +
        `<span class="ti-blurb">${escapeName(sc.blurb ?? "")}</span>`;
      item.addEventListener("click", () => startTraining(sc));
      list.appendChild(item);
    }
    screen.classList.remove("hidden");
  }

  // The syllabus markers (see shared/config.json "training.scenarios"):
  // difficulty pips grade the drill ★ ROOKIE → ★★★ VETERAN, and the combat
  // tags say what kind of fight it is — OFFENSIVE (you attack) or DEFENSIVE
  // (they come to you), plus the discipline (air-to-air, gun crew...). A
  // scenario without markers just renders plain, the way old configs did.
  function scenarioDiffChip(sc) {
    const d = sc.difficulty;
    const label = { 1: "ROOKIE", 2: "REGULAR", 3: "VETERAN" }[d];
    if (!label) return "";
    return `<span class="ti-diff d${d}">${"★".repeat(d)}${"☆".repeat(3 - d)} ${label}</span>`;
  }

  function scenarioTagRow(sc) {
    const chips = [];
    if (sc.combat === "offense") chips.push(`<span class="ti-tag offense">OFFENSIVE</span>`);
    else if (sc.combat === "defense") chips.push(`<span class="ti-tag defense">DEFENSIVE</span>`);
    if (sc.focus) chips.push(`<span class="ti-tag">${escapeName(String(sc.focus).toUpperCase())}</span>`);
    return chips.length ? `<span class="ti-tags">${chips.join("")}</span>` : "";
  }

  function startTraining(sc) {
    if (playBtn.disabled || net.joined) return; // playBtn.disabled gates "connected"
    document.getElementById("training-screen")?.classList.add("hidden");
    audio.ensure();
    if (touch) goImmersive();
    localStorage.setItem("va-name", nameInput.value);
    activeScenario = sc;
    // Pre-select the scenario's recommended aircraft so HUD rates and the
    // pilot model match; the server still forces a ride where the drill needs
    // a specific one (the bomber, the torpedo plane).
    if (Number.isInteger(sc.plane) && sc.plane >= 0 && sc.plane < planeTypes.length) {
      selectedPlane = sc.plane;
    }
    net.join(nameInput.value, selectedPlane, identityToken(), 0, 0, sc.id);
    playBtn.disabled = true;
    spectateBtn.disabled = true;
    joinStatus.textContent = `flight school: ${sc.name ?? sc.id}…`;
    analytics.event("training_start", { scenario: sc.id });
  }

  function showBriefing(sc) {
    const card = document.getElementById("training-brief");
    if (!card) return;
    document.getElementById("brief-title").textContent = sc.name ?? "TRAINING";
    // The briefing wears the same markers as the picker card, so the drill's
    // grade and combat type follow the student into the cockpit.
    const btags = document.getElementById("brief-tags");
    if (btags) {
      btags.innerHTML = scenarioDiffChip(sc) + scenarioTagRow(sc);
      btags.classList.toggle("hidden", !btags.innerHTML);
    }
    document.getElementById("brief-text").textContent = sc.brief ?? "";
    const tips = document.getElementById("brief-tips");
    tips.innerHTML = "";
    for (const tip of sc.tips ?? []) {
      const li = document.createElement("li");
      li.textContent = tip;
      tips.appendChild(li);
    }
    tips.classList.toggle("hidden", !(sc.tips ?? []).length);
    card.classList.remove("hidden");
    document.getElementById("training-exit")?.classList.remove("hidden");
  }

  // Pointer lock for mouse aim: grab on canvas click while flying.
  // While spectating, a tap advances to the next vantage point — but a drag
  // orbits the camera around the followed subject instead (see below), so a
  // dragged pointer suppresses the cycle.
  let specDragged = false;
  canvas.addEventListener("click", () => {
    if (spectating) {
      if (specDragged) {
        specDragged = false;
        return;
      }
      cycleSpectate(1);
      return;
    }
    // F5: a click/tap while down skips straight to the respawn screen — whether
    // the death replay is playing or the clip is still inbound (awaitingKillcam)
    // — so re-entry is fast (naval deaths are decisive; high lethality is only
    // fun when you get back in quickly). Skipping is a full opt-out: the TAKE
    // OFF cooldown falls back to the plain respawn floor here and on the server
    // (skipcam → SkipKillcam), and a clip that lands after the skip is ignored.
    if (selfStatus === 0 && (replay || perfNow() < awaitingKillcam)) {
      analytics.event("killcam_skip", { crash: replay?.killer === selfId });
      killcamSkipped = true; // a late-arriving clip must not restart the show
      endKillcam(); // drop any active replay
      awaitingKillcam = 0; // stop holding the respawn screen back
      deathRespawnSec = config.combat.respawnDelaySec; // undo the killcam hold
      net.skipcam(); // and have the server drop its matching hold
      return;
    }
    if (!touch && net.joined && selfStatus !== 0) input.requestPointerLock(canvas);
  });

  // Spectator orbit: drag/swipe the field to swing the chase camera around the
  // followed plane. Works for mouse and touch via pointer events; the
  // touch-control layer is hidden while spectating so the canvas owns these.
  const ORBIT_SENS = 0.005; // rad per px
  let specDrag = null; // {id, x, y, moved}
  canvas.addEventListener("pointerdown", (e) => {
    if (!spectating || specDrag) return;
    specDragged = false; // clear any stale flag from a cancelled gesture
    specDrag = { id: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
  });
  canvas.addEventListener("pointermove", (e) => {
    if (!specDrag || e.pointerId !== specDrag.id) return;
    const dx = e.clientX - specDrag.x;
    const dy = e.clientY - specDrag.y;
    if (!specDrag.moved && Math.hypot(dx, dy) < 6) return; // ignore tap jitter
    specDrag.moved = true;
    specDrag.x = e.clientX;
    specDrag.y = e.clientY;
    specOrbit.yaw -= dx * ORBIT_SENS;
    specOrbit.pitch = Math.max(-1.0, Math.min(1.2, specOrbit.pitch + dy * ORBIT_SENS));
  });
  const specDragEnd = (e) => {
    if (!specDrag || e.pointerId !== specDrag.id) return;
    specDragged = specDrag.moved; // tell the click handler to skip the cycle
    specDrag = null;
  };
  canvas.addEventListener("pointerup", specDragEnd);
  canvas.addEventListener("pointercancel", specDragEnd);

  // While dead: 1–6 queues a different plane for the next spawn (and cancels
  // any reserved AA slot / vehicle). Mirror it on the unit picker so the
  // respawn screen's highlight tracks the keyboard.
  input.planeRequested = (idx) => {
    if (!net.joined || spectating || selfStatus !== 0) return;
    if (idx < 0 || idx >= planeTypes.length) return;
    if (hud.isAircraftLocked?.(idx)) return; // career-gated type not yet earned
    pendingPlane = idx;
    selectedPlane = idx;
    localStorage.setItem("va-plane", String(idx));
    net.setPlane(idx);
    analytics.event("plane_switch", { plane_type: planeTypes[idx]?.name, plane_index: idx });
    if (pendingGun !== 0) {
      pendingGun = 0;
      net.setGun(0);
    }
    if (pendingVeh >= 0) {
      pendingVeh = -1;
      net.setVehicle(0);
    }
    if (pendingTur >= 0) {
      pendingTur = -1;
      net.setTurret(0);
    }
    hud.selectUnit?.(0, idx);
    hud.setDeckVisible(true);
  };

  // Touch PREV/NEXT buttons: cycle the spectate vantage (keyboard uses N / P).
  input.spectateCycle = (dir) => {
    if (spectating) cycleSpectate(dir);
  };

  // Touch RECENTER button: snap the orbit back behind the plane (keyboard C).
  input.spectateRecenter = () => {
    if (spectating) recenterSpectate();
  };

  // F while manning a gun, driving a vehicle, or riding a bomber turret:
  // bail out into the respawn flow.
  input.bailRequested = () => {
    if (!net.joined || spectating) return;
    if (selfStatus === 3) net.setGun(0);
    else if (selfStatus === 4) net.setVehicle(0);
    else if (selfStatus === 5) net.setTurret(0);
  };

  // G while flying: call a fire mission — mark the ground below the plane
  // on every friendly battleship gunner's plotting chart.
  input.spotRequested = () => {
    if (!net.joined || spectating || selfStatus === 0 || selfStatus === 3 || selfStatus === 4 || selfStatus === 5) return;
    net.spot();
  };

  // Battleship plot table: tap-and-drag to place the aim reticle, then confirm
  // it with SET TARGET. The server re-validates the envelope on plot.
  const plotting = () => net.joined && !spectating && selfStatus === 3 && gunKind() === 2;
  hud.el.plotCanvas.addEventListener("pointerdown", (e) => {
    if (!plotting()) return;
    e.preventDefault();
    hud.el.plotCanvas.setPointerCapture(e.pointerId);
    const w = hud.plotPointToWorld(e);
    if (!w) return;
    plotAim.pending = { x: w.x, z: w.z };
    plotAim.dragging = true;
    hud.showLoupe(e.clientX, e.clientY, w.px, w.py);
  });
  hud.el.plotCanvas.addEventListener("pointermove", (e) => {
    if (!plotAim.dragging) return;
    e.preventDefault();
    const w = hud.plotPointToWorld(e);
    if (!w) return;
    plotAim.pending = { x: w.x, z: w.z };
    hud.showLoupe(e.clientX, e.clientY, w.px, w.py);
  });
  const endPlotDrag = () => {
    if (!plotAim.dragging) return;
    plotAim.dragging = false;
    hud.hideLoupe();
  };
  hud.el.plotCanvas.addEventListener("pointerup", endPlotDrag);
  hud.el.plotCanvas.addEventListener("pointercancel", endPlotDrag);

  // Primary action: SET TARGET commits the reticle as the plot; FIRE pulses
  // the trigger to loose a salvo once the solution is ready.
  hud.el.plotPrimary.addEventListener("click", () => {
    const kind = hud.el.plotPrimary.dataset.kind;
    if (kind === "set" && plotAim.pending) {
      const pivot = aaGuns.pivotOf(selfGunId);
      if (!pivot) return;
      const { x, z } = plotAim.pending;
      const d = Math.hypot(x - pivot.x, z - pivot.z);
      if (d < bbCfg.minRangeM || d > spottedRange || Math.hypot(x, z) > field.radius) {
        hud.message("TARGET OUT OF RANGE", 1400, "#e8a23a");
        return;
      }
      fc.target = { x, z };
      fc.plottedAt = perfNow();
      net.plot(x, z);
      plotAim.pending = null;
    } else if (kind === "fire") {
      input.plotFire = true;
      setTimeout(() => { input.plotFire = false; }, 350);
    }
  });
  // RE-AIM drops a fresh draggable reticle on the current target to nudge it.
  hud.el.plotReaim.addEventListener("click", () => {
    if (fc.target) plotAim.pending = { ...fc.target };
  });
  hud.el.plotExit.addEventListener("click", () => input.bailRequested());

  net.onWelcome = (msg) => {
    for (const r of msg.roster ?? []) roster.set(r.id, r);
    aaGuns.build(msg.aa ?? [], field);
    lightsR.build(msg.lights ?? []);
    ships.build(msg.ships ?? []);
    hud.setShips(msg.ships ?? []);
    // Naval domination capture zones sit at open-water centres the server picked;
    // draw a ring at each (the air Sky Ring stays at the origin).
    zone.setZones?.(msg.zns ?? []);
    // Space: the prizes are real objects — a brick wheel amidships with a
    // relay outpost either side of it, all three on one shell — so they are
    // built from the same zone plan rather than drawn as rings on an empty sky.
    if (spaceTheater && (msg.zns ?? []).length) {
      if (!spaceProps) spaceProps = new SpaceProps(scene, field, msg.zns);
      // The shell the group rides is also the arena's centre height, and the
      // prediction's boundary autopilot needs it.
      if (msg.zns[0]?.y > 0) spaceStationShellY = msg.zns[0].y;
    }
    hud.setNavalZones?.(
      msg.zns ?? [],
      ((landTheater || armadaTheater) ? config.land?.zoneRadiusM : config.naval?.zoneRadiusM) ?? 650,
    );
    navalZoneCenters = msg.zns ?? [];
    // Survivors already afloat (roadmap #2), so late joiners see the markers.
    rescueMarkers.clear();
    for (const m of msg.rescues ?? []) rescueMarkers.set(m.id, m);
    scarsR.setFromWelcome(msg.scars); // the round's craters/wrecks/slicks (roadmap #5)
    score = msg.score;
    aceId = msg.ace ?? 0;
    sky.set(score.wx);
    stormR.set(score.wx);
    hud.show();
    if (score.md === "fleet") {
      hud.feed(`⚓ fleet action: sink the enemy ${flagshipName().toLowerCase()}`);
    }
    if (msg.spec) {
      spectating = true;
      analytics.setProps({ role: "spectator", mode: score.md });
      analytics.event("match_enter", { role: "spectator", mode: score.md });
      hud.message(
        onTouch
          ? "SPECTATING — tap to cycle vantage, swipe to look around, RECENTER to reset"
          : "SPECTATING — N / P or click cycles, drag to look around, C recenters",
        4500,
      );
      return;
    }
    selfId = msg.id;
    selfTeam = msg.team;
    selfPlane = msg.plane ?? 0;
    pendingPlane = selfPlane;
    analytics.setProps({
      role: "pilot",
      team: TEAM_INFO[selfTeam].name,
      plane_type: planeTypes[selfPlane]?.name,
      mode: score.md,
    });
    analytics.event("match_enter", {
      role: "pilot",
      team: TEAM_INFO[selfTeam].name,
      plane_type: planeTypes[selfPlane]?.name,
      mode: score.md,
    });
    // Crewable friendly emplacements in build order (land flak, naval twin
    // mounts, battleship main battery), for the unit picker's first-free-of-kind
    // reservation while respawning. Installations/balloons (kind ≥ 3) aren't
    // crew stations.
    respawnGunSlots = (msg.aa ?? [])
      .filter((s) => s.team === selfTeam && s.kind < 3)
      .map((s) => ({ id: s.id, kind: s.kind }));
    if (pendingDeck) net.setDeck(true); // replay the stored preference
    if (spawnHdgDetent >= 0) net.setHeading(roseHdg(spawnHdgDetent)); // sticky rose (#55)
    input.setRates(planeTypes[selfPlane]);
    // The server resolves our persisted paint (roadmap #25); trust it over the
    // local hint so a scheme that got re-locked shows factory.
    equippedScheme = (msg.roster ?? []).find((r) => r.id === msg.id)?.skn ?? 0;
    localStorage.setItem("va-scheme", String(equippedScheme));
    selfVisual = new PlaneVisual(scene, selfTeam, msg.name, true, selfPlane, equippedScheme);
    selfVisual.entityId = msg.id; // seed our own bullet holes (roadmap #8)
    // The seat the join spawned us into, straight off the welcome. The
    // manveh/mangun/mantur broadcast can lose a race with the join handshake
    // (the tick that drains events may run before the hub marks this socket
    // joined), and a captain who misses his own manveh is stranded at a dead
    // helm for the whole session: no drive wire, no conning panel, X/B dead.
    // The welcome is composed under the same world lock as the spawn, so it
    // can't race — replay the seat event through the normal pipeline and let
    // the broadcast (when it does arrive) dedup on the already-current seat.
    if (msg.veh) net.onEvents?.([{ e: "manveh", id: selfId, v: msg.veh, plane: msg.vehType ?? 0 }]);
    else if (msg.gun) net.onEvents?.([{ e: "mangun", id: selfId, v: msg.gun }]);
    else if (msg.tur) net.onEvents?.([{ e: "mantur", id: selfId, v: msg.tur, plane: msg.turSeat ?? 0 }]);
    else if (msg.turWait) net.onEvents?.([{ e: "turwait", id: selfId, plane: msg.turWait - 1 }]);
    if (activeScenario) {
      showBriefing(activeScenario);
    } else {
      hud.message(`you fly for ${TEAM_INFO[selfTeam].name}`, 3500, TEAM_INFO[selfTeam].cssColor);
    }
    const c = msg.career;
    // Sync the career kill gate: the picker unlocks (or re-locks) the
    // kill-gated aircraft from the authoritative lifetime record.
    careerKills = c?.kills ?? 0;
    localStorage.setItem("va-kills", String(careerKills));
    hud.setCareerKills(careerKills);
    // Naval progression: the picker locks hulls until they're earned by sinking.
    hud.setCareerShips(c?.ships ?? 0);
    // Repaint the PAINT SHOP with the authoritative career so scheme locks and
    // the equipped swatch are correct (roadmap #25).
    hud.buildPaintRow(config.schemes ?? [], c, equippedScheme, equipScheme);
    if (c && c.kills + c.deaths + c.wins + (c.challenges ?? 0) > 0) {
      const myTitle = (msg.roster ?? []).find((r) => r.id === msg.id)?.title;
      hud.feed(
        `career: ${c.kills} kills · ${c.deaths} deaths · ${c.wins} wins` +
          (c.challenges ? ` · ${c.challenges} missions` : "") +
          (c.bestStreak ? ` · 🔥 best streak ${c.bestStreak}` : "") +
          (myTitle ? ` · 🎖 ${escapeName(myTitle)}` : ""),
      );
    }
    // Nudge a live streak: it's still alive if the last clear was today (already
    // continued) or yesterday (clear one today to keep it going).
    if (c?.streak > 1) {
      const utcDay = (off) => {
        const d = new Date();
        d.setUTCDate(d.getUTCDate() + off);
        return d.toISOString().slice(0, 10);
      };
      if (c.streakDay === utcDay(0)) {
        hud.feed(`🔥 ${c.streak}-day mission streak going strong`);
      } else if (c.streakDay === utcDay(-1)) {
        hud.feed(`🔥 ${c.streak}-day mission streak — clear a mission today to keep it!`);
      }
    }
    // Daily missions: the server seeds today's set and this pilot's progress;
    // the spawn screen shows it and self-events fill the bars live below.
    dailyChallenges = (msg.daily ?? []).map((d) => ({ ...d }));
    hud.setDaily(dailyChallenges);
    const open = dailyChallenges.filter((d) => (d.have ?? 0) < d.goal).length;
    if (open > 0) hud.feed(`📋 ${open} daily mission${open > 1 ? "s" : ""} open — see the spawn screen`);
  };

  net.onBoard = (msg) => {
    for (const row of msg.rows ?? []) board.set(row.id, { k: row.k, d: row.d, w: row.w });
  };

  net.onKillcam = (kc) => {
    if (spectating || kc.victim !== selfId || !kc.frames.length) return;
    if (killcamSkipped) return; // skipped this death — the hangar has the screen
    startKillcam(kc);
  };

  let lastSnapNow = 0; // sim time of the latest APPLIED snapshot (lags for the director)
  let liveSnapNow = 0; // sim time of the latest RECEIVED snapshot (always real-time)
  // The 24/7 director renders on a fixed lag (DIRECTOR_LAG) so its camera can see
  // a few seconds into the future — a kill it's about to show is already in these
  // buffers — and frame known events deterministically instead of guessing live.
  // Raw snapshots and events wait here until the per-frame pump (frame()) reaches
  // their sim time. Players (not the director) apply everything live, untouched.
  const dirSnapRing = []; // {t, snap} awaiting render-time
  const dirEventQ = []; // {t, ev} awaiting render-time

  net.onEvents = (evs) => {
    if (autoDirector) {
      for (const ev of evs) dirEventQ.push({ t: liveSnapNow, ev });
      return;
    }
    for (const ev of evs) handleEvent(ev);
  };
  // Debug/verification: the director's two clocks and how far it's rendering
  // behind live (should hold near DIRECTOR_LAG once warmed up).
  window.__va.clocks = () => ({
    live: liveSnapNow,
    applied: lastSnapNow,
    lag: liveSnapNow - lastSnapNow,
    queued: dirSnapRing.length,
    events: dirEventQ.length,
  });

  net.onSnapshot = (snap) => {
    // Derive sim time from the tick counter: the wire's f32 `now` loses
    // millisecond precision after hours of server uptime, which would wobble
    // the remote-interpolation clock.
    const snapNow = snap.tick * PHYS_DT;
    const localT = perfNow();
    const off = snapNow - localT;
    timeOffset = timeOffset === null ? off : timeOffset + (off - timeOffset) * 0.1;
    liveSnapNow = snapNow;
    if (autoDirector) {
      dirSnapRing.push({ t: snapNow, snap });
      return; // the pump applies it once the lagged render clock reaches snapNow
    }
    applySnapshot(snap, snapNow);
  };

  // Apply one snapshot to the world. Called live for players; replayed on a lag
  // by the director's pump. Everything downstream (entities, tracers, ships…) is
  // agnostic to which — it just sees a snapshot stream, real-time or delayed.
  function applySnapshot(snap, snapNow) {
    lastSnapNow = snapNow;

    // Space: fold every player row into the wrap image nearest our own craft
    // (row[1] is x) so the remote interp buffers never lerp a neighbor across
    // the seam — the enemy just over the wrap is close, not a map away.
    if (spaceTheater) {
      const ref = pred?.pos?.x ?? snap.pl[0]?.[1] ?? 0;
      for (const row of snap.pl) {
        const d = row[1] - ref;
        if (d > spaceWrapM * 0.5) row[1] -= spaceWrapM;
        else if (d <= -spaceWrapM * 0.5) row[1] += spaceWrapM;
      }
    }

    for (const row of snap.pl) {
      const [id, x, y, z, qx, qy, qz, qw, vx, vy, vz, hp, status, thr, plane, bombs, ox, oy, oz,
        engine, wingL, wingR, fuel, pilot, tail, gunYaw, gunPitch] = row;
      if (id === selfId && !spectating) {
        handleSelfRow(
          { x, y, z, qx, qy, qz, qw, vx, vy, vz, hp, status, thr, plane, bombs, ox, oy, oz,
            engine, wingL, wingR, fuel, pilot, tail },
          snap.ack,
          snap.phase ?? TICKS_PER_INPUT,
        );
        continue;
      }
      let ent = entities.get(id);
      if (!ent) {
        const info = roster.get(id) ?? { name: "???", team: 0, plane: 0 };
        const visual = new PlaneVisual(scene, info.team, info.name, false, plane | 0, info.skn ?? 0, info.ad ?? "");
        visual.entityId = id; // seed deterministic bullet holes (roadmap #8)
        visual.setTitle(info.title ?? ""); // career title line (roadmap #26)
        visual.setAce(!!info.ace); // gold spinner + pennant (roadmap #26)
        ent = {
          id,
          visual,
          buffer: [],
          hp,
          smokeAt: 0,
          plane: plane | 0,
        };
        entities.set(id, ent);
      }
      ent.hp = hp;
      ent.status = status;
      // Space: where that hull's gun ring is pointing (world frame). It is
      // its own wire field because the quaternion no longer answers the
      // question — a craft can be flying due prograde with its barrels on
      // you, which is exactly the fight this theater wanted.
      ent.gunYaw = gunYaw ?? 0;
      ent.gunPitch = gunPitch ?? 0;
      ent.engine = engine;
      ent.wingL = wingL;
      ent.wingR = wingR;
      ent.fuel = fuel;
      ent.pilot = pilot;
      ent.tail = tail;
      if ((plane | 0) !== ent.plane) {
        ent.plane = plane | 0;
        ent.visual.setType(ent.plane);
      }
      // Keep the ad banner in step with the roster: covers an entity built
      // before its join event landed, and any copy change we missed.
      if (CLASS_BANNERS[ent.plane]) ent.visual.setBanner(roster.get(id)?.ad ?? "");
      // Visible battle damage: scorched cowl, shredded wing, flak holes,
      // fuel weep, shattered canopy, chewed-up empennage.
      ent.visual.applyDamage({ engine, wingL, wingR, fuel, pilot, tail, hp: hp / maxHpOf(ent.plane) });
      ent.lastSeen = snapNow;
      ent.buffer.push({
        t: snapNow,
        pos: new THREE.Vector3(x, y, z),
        quat: new THREE.Quaternion(qx, qy, qz, qw),
        vel: new THREE.Vector3(vx, vy, vz),
        thr,
        status,
      });
      if (ent.buffer.length > 30) ent.buffer.shift();
    }
    // Interest management: near snapshots omit far players, so only cull on
    // full-roster ("far") frames or after a long silence.
    const seen = new Set(snap.pl.map((r) => r[0]));
    for (const [id, ent] of entities) {
      const stale = snapNow - (ent.lastSeen ?? 0) > 6;
      if ((snap.far && !seen.has(id)) || stale) {
        ent.visual.dispose(scene);
        entities.delete(id);
      }
    }

    // The killcam restages tracers/ordnance from its recorded frames; live
    // rounds would fly through the replay.
    if (!replay) {
      tracers.setFromSnapshot(snap.bl ?? []);
      bombsR.setFromSnapshot(snap.bm ?? []);
      chutesR.setFromSnapshot(snap.bm ?? []); // parachutes share the bomb section
      smokeR.setFromSnapshot(snap.sm ?? [], config.world.waterLevelM);
      nearMissRows = snap.bl ?? null; // swept once in frame()
    } else {
      chutesR.setFromSnapshot([]); // the killcam replay doesn't restage chutes
    }
    fc.shells = (snap.bm ?? []).filter((r) => r[7]); // main-battery rounds, for the plot map
    // Manned bomber turrets: index by host so each Leviathan's mounts can be
    // posed, and pull our own seat's authoritative aim for reconciliation.
    turretsByHost.clear();
    turSrv.has = false;
    for (const [gunner, host, seat, yaw, pitch, firing] of snap.tu ?? []) {
      let rows = turretsByHost.get(host);
      if (!rows) turretsByHost.set(host, (rows = []));
      rows.push({ gunner, seat, yaw, pitch, firing });
      if (gunner === selfId) {
        turSrv.has = true;
        turSrv.yaw = yaw;
        turSrv.pitch = pitch;
      }
    }
    // Era seeker / space ASAT rail: server truth either way (the space wire
    // reuses the section — the flare byte is the chaff magazine out there).
    if ((jetsEra || spaceTheater) && !replay && snap.jt) seekerSrv = snap.jt;
    aaGuns.applyTeams?.(snap.aa ?? [], snap.gt); // combined arms: Fort Brick re-flags
    aaGuns.applySnapshot(snap.aa ?? []);
    trucksR.applySnapshot(snap.tk ?? [], snapNow);
    supplyShipsR.applySnapshot(snap.ss ?? [], snapNow);
    vehiclesR.applySnapshot(snap.gv ?? [], snapNow);
    ships.applySnapshot(snap.sh ?? []);
    lightsR.applySnapshot(snap.sl ?? []);
    const prevWx = score?.wx;
    score = snap.sc;
    // __va.wxOverride is a debug/screenshot hook (sky.timeShift's sibling):
    // it pins the weather client-side so the director and the Playwright
    // harness can shoot storm scenes without waiting for the dice.
    score.wx = window.__va.wxOverride ?? config.match.weathers?.[snap.sc.wxi] ?? "clear";
    score.md = navalTheater
      ? "naval"
      : landTheater
        ? "land"
        : armadaTheater
          ? "armada"
          : spaceTheater
            ? "space"
            : (config.match.modes?.[snap.sc.mdi] ?? "skyring");
    sky.set(score.wx);
    stormR.set(score.wx);
    // A storm round opening is worth a line on the wire feed — the front is
    // about to march through everyone's fight.
    if (prevWx && prevWx !== "storm" && score.wx === "storm") {
      hud.feed("⛈ storm front on the march — thunderheads inbound");
    }
    hud.flagshipKind = score.md === "fleet" ? flagshipKind() : null;
    hud.setScore(score, fleetHud());
    // Domination callouts (naval): announce when any capture zone changes hands,
    // from the captain's own point of view.
    if ((hullTheater || armadaTheater) && !spectating && score.ph === "play" && prevZone) {
      const zs = score.zs ?? [];
      const enemy = selfTeam === 0 ? 1 : 0;
      const zi = navalTheater ? "⚓" : "⚑";
      let flipped = false;
      for (let i = 0; i < zs.length; i++) {
        if (prevZone[i] === undefined || zs[i] === prevZone[i]) continue;
        flipped = true;
        if (zs[i] === selfTeam) hud.message(`${zi} ZONE ${zoneLetter(i)} CAPTURED`, 2000, "#7CFC7C");
        else if (zs[i] === enemy) hud.message(`${zi} ZONE ${zoneLetter(i)} LOST`, 2000, "#ff6b6b");
      }
      // N8: refresh the fleet order when the objective picture changes and the
      // captain has a hull to steer (but not while sitting inside a contested cap,
      // where the N7 status banner already speaks). NB: this runs in the score
      // handler, outside frame() — derive the driving state here (referencing
      // frame()'s `driving` threw and silently killed these callouts).
      const drivingNow =
        net.joined && !spectating && selfStatus === 4 && vehiclesR.has(selfVehicleId);
      if (flipped && drivingNow && curZoneIdx < 0) {
        const order = fleetOrderHint();
        if (order) hud.message(order, 3000, "#8fd0ff");
      }
    }
    prevZone = hullTheater || armadaTheater ? score.zs ?? [] : null;
    if (score.ph === "play") intel.sample(perfNow(), score.c, score.b, score.md);
  }

  const oldAttQ = new THREE.Quaternion(); // reconciliation scratch
  const errAttQ = new THREE.Quaternion();
  const foldAttQ = new THREE.Quaternion();

  function resetPrediction(row) {
    snapPredTo(row);
    inputHistory.length = 0;
    visualOffset.set(0, 0, 0);
    attOffset.identity();
    predTicks = 0;
  }

  // Arm the killcam hold the instant we go down. The kill event that normally
  // arms it (see the "kill" case) rides a later message than the status→0
  // snapshot that ends the life, so without this the spawn card paints for the
  // frame or two between the two — on a slow link, several. The guards keep it
  // from re-arming a wreck's delayed impact (armed back at the mortal blow, so
  // the hold is still pending) or a replay that's already playing or declined;
  // the kill event refines the same value, so there's no visible change.
  function armKillcamHold() {
    if (config.net.killcamSec > 0 && !killcamSkipped && !replay && perfNow() >= awaitingKillcam) {
      awaitingKillcam =
        perfNow() + (config.net.killcamDelaySec || 0) + (config.net.killcamSec || 0) + 0.8;
    }
  }

  function handleSelfRow(row, ack, phase) {
    const prevStatus = selfStatus;
    selfStatus = row.status;
    if (prevStatus === 0 && row.status !== 0) {
      endKillcam(); // respawned: cut the cam
      // A fresh space life relights on the pad's terms: lever closed, coast
      // the arc, burn at apoapsis. The lever a pilot died holding must not
      // light the next hull's bell (the server zeroes its copy the same way).
      if (spaceTheater) input.throttle = 0;
    }
    selfHp = row.hp;
    selfBombs = row.bombs | 0;
    selfDmg = {
      engine: row.engine ?? 1, wingL: row.wingL ?? 1, wingR: row.wingR ?? 1,
      fuel: row.fuel ?? 1, pilot: row.pilot ?? 1, tail: row.tail ?? 1,
    };
    serverSelf = row;
    if ((row.plane | 0) !== selfPlane) {
      selfPlane = row.plane | 0;
      selfVisual?.setType(selfPlane);
      input.setRates(planeTypes[selfPlane]);
    }

    if (row.status === 3) {
      // Manning a gun: park flight prediction on the mount — the turret
      // camera and the local aim prediction own the view.
      pred.pos = FDM.v3(row.x, row.y, row.z);
      pred.vel = FDM.v3();
      // A fresh mount is in battery: recoil never carries across a life.
      spaceGunKickYaw = spaceGunKickPitch = 0;
      spaceGunOutOfBattery = false;
      inputHistory.length = 0;
      visualOffset.set(0, 0, 0);
      attOffset.identity();
      haveSpawned = true;
      return;
    }
    if (prevStatus === 3) {
      // Out of the gun: dead (bailed out / mount destroyed) or straight
      // into a plane (round reset). Restart flight prediction cleanly.
      selfGunId = 0;
      gunAim.has = false;
      aaGuns.clearLocalAim();
      resetFireControl();
      plotAim.pending = null;
      cine.mode = "off";
      hud.setPlotVisible(false);
      hud.setManningMain(false);
      if (row.status === 0) {
        deathAt = perfNow();
        armKillcamHold();
        deathRespawnSec = config.combat.respawnDelaySec;
        noteLocalDeath();
        deathPos.set(pred.pos.x, pred.pos.y, pred.pos.z);
        killerFollow.reset();
        if (document.pointerLockElement) document.exitPointerLock();
        return;
      }
      resetPrediction(row);
      return;
    }

    if (row.status === 5) {
      // Riding a bomber turret: the host's interpolated airframe owns the
      // camera and the local aim prediction owns the view; flight prediction
      // parks on the bomber (the server mirrors its position onto us).
      pred.pos = FDM.v3(row.x, row.y, row.z);
      pred.vel = FDM.v3(row.vx, row.vy, row.vz);
      inputHistory.length = 0;
      visualOffset.set(0, 0, 0);
      attOffset.identity();
      haveSpawned = true;
      return;
    }
    if (prevStatus === 5) {
      // Out of the turret: dead (bailed out / bomber shot down) or straight
      // into a plane (round reset). Restart flight prediction cleanly.
      selfTurretHost = 0;
      turAim.has = false;
      if (row.status === 0) {
        deathAt = perfNow();
        armKillcamHold();
        deathRespawnSec = config.combat.respawnDelaySec;
        noteLocalDeath();
        deathPos.set(pred.pos.x, pred.pos.y, pred.pos.z);
        killerFollow.reset();
        if (document.pointerLockElement) document.exitPointerLock();
        return;
      }
      resetPrediction(row);
      return;
    }

    if (row.status === 4) {
      // Driving a vehicle: like manning a gun, the hull camera and the local
      // aim prediction own the view, so flight prediction just parks here.
      pred.pos = FDM.v3(row.x, row.y, row.z);
      pred.vel = FDM.v3();
      // A fresh mount is in battery: recoil never carries across a life.
      spaceGunKickYaw = spaceGunKickPitch = 0;
      spaceGunOutOfBattery = false;
      inputHistory.length = 0;
      visualOffset.set(0, 0, 0);
      attOffset.identity();
      haveSpawned = true;
      return;
    }
    if (prevStatus === 4) {
      // Out of the vehicle: dead (bailed out / hull destroyed) or back into a
      // plane (round reset / FLY). Restart flight prediction cleanly.
      selfVehicleId = 0;
      vehAim.has = false;
      vehiclesR.clearLocalAim();
      navalBorePitch = 0; // reset the held main-battery elevation when we leave the ship
      if (row.status === 0) {
        deathAt = perfNow();
        armKillcamHold();
        deathRespawnSec = config.combat.respawnDelaySec;
        noteLocalDeath();
        deathPos.set(pred.pos.x, pred.pos.y, pred.pos.z);
        killerFollow.reset();
        if (document.pointerLockElement) document.exitPointerLock();
        return;
      }
      resetPrediction(row);
      return;
    }

    if (row.status === 6) {
      // Falling wreck (roadmap #3): the server integrates the tumble under the
      // ruined flight model. We follow it as an authoritative entity — NO input
      // prediction or reconciliation — so the chase camera rides it down. The
      // server flips us to status 0 at impact, and the 0-transition below fires
      // the death boom and hands the screen to the killcam / respawn card.
      snapPredTo(row);
      inputHistory.length = 0;
      visualOffset.set(0, 0, 0);
      attOffset.identity();
      if (prevStatus !== 6) {
        deathPos.set(pred.pos.x, pred.pos.y, pred.pos.z);
        if (document.pointerLockElement) document.exitPointerLock();
      }
      return;
    }
    if (!haveSpawned || (prevStatus === 0 && row.status !== 0)) {
      const firstSpawn = !haveSpawned;
      resetPrediction(row);
      haveSpawned = true;
      waitingBomber = false; // we're flying/crewing now, not waiting for a bomber
      // The click that launched us was on a BUTTON, not a target: park the
      // aim reticle so the stale menu-cursor position (low on the screen)
      // can't fly the fresh spawn into a diving turn. The next real mouse
      // move re-takes command unchanged. The director drops its world aim
      // too, so the first flying frame re-parks it level off the fresh pose.
      input.parkCursor();
      aimDirector.drop();
      // Space: the lever is INTENT now (speed along the gaze), and the
      // desktop lever's 0.7 default — or wherever the last sortie left it —
      // would read as "overtake, immediately" on a fresh spawn. You arrive
      // on orbit coasting, lever out, and the gaze snaps to the nose.
      if (spaceTheater) {
        input.throttle = 0;
        spaceCamSync = true;
      }
      // First-flight touch coach: ghosted control labels, once per device.
      if (row.status === 1 || row.status === 2) touch?.maybeShowCoach();
      // A plane spawn means any reserved slot was consumed or fell through
      // (the server clears it when the gun is gone or already taken).
      pendingGun = 0;
      pendingVeh = -1;
      pendingTur = -1;
      deathKiller = 0;
      awaitingKillcam = 0;
      killcamSkipped = false;
      // A deck spawn starts at ~20 m; base spawns start above 400 m.
      const catapulted = pendingDeck && row.y < 100;
      if (prevStatus === 0) {
        hud.message(
          catapulted ? "CATAPULT LAUNCH — clean shot off the bow" : "spawn shield active",
          2000,
        );
      }
      analytics.event("spawn", {
        plane_type: planeTypes[selfPlane]?.name,
        deck: catapulted,
        first: firstSpawn,
      });
      analytics.count("spawns");
      return;
    }
    if (prevStatus !== 0 && row.status === 0) {
      deathAt = perfNow();
      armKillcamHold();
      deathRespawnSec = config.combat.respawnDelaySec;
      noteLocalDeath();
      deathPos.set(pred.pos.x, pred.pos.y, pred.pos.z);
      killerFollow.reset();
      particles.explosion(deathPos);
      audio.explosion(0);
      if (document.pointerLockElement) document.exitPointerLock();
      return;
    }
    if (row.status === 0) return;

    // --- Replay reconciliation ---
    // Adopt the authoritative state (including the true angular rates), then
    // re-apply every input the server hasn't consumed yet. The server paces
    // inputs at exactly TICKS_PER_INPUT ticks each and tells us how many
    // ticks the acked input has already steered (phase), so the replay
    // reproduces the server's control timeline tick-for-tick — in the steady
    // state the residual is just wire quantization. What residual remains
    // becomes decaying visual offsets so corrections never pop on screen.
    while (inputHistory.length && inputHistory[0].seq < ack) inputHistory.shift();
    if (inputHistory.length > 45) {
      // Hopeless backlog (tab was asleep): hard snap.
      resetPrediction(row);
      return;
    }
    // Leave prevPos/prevAtt alone: resetting them collapsed the sub-tick
    // interpolation segment, freezing the plane until the next physics step
    // — a snapshot-rate stutter on every display faster than the tick rate.
    // Instead fold the correction, weighted by the current render fraction,
    // into the decaying visual offsets so the swap never pops on screen.
    const alpha = Math.min(physAcc / PHYS_DT, 1);
    const oldX = pred.pos.x, oldY = pred.pos.y, oldZ = pred.pos.z;
    oldAttQ.set(pred.att.x, pred.att.y, pred.att.z, pred.att.w);
    pred.pos = FDM.v3(row.x, row.y, row.z);
    pred.vel = FDM.v3(row.vx, row.vy, row.vz);
    pred.att = FDM.q4(row.qx, row.qy, row.qz, row.qw);
    pred.omega = FDM.v3(row.ox ?? 0, row.oy ?? 0, row.oz ?? 0);
    const planeCfg = planeTypes[selfPlane];
    const dmg = selfDamageMods();
    for (const h of inputHistory) {
      // Re-apply each input only for the ticks the local prediction actually
      // ran it (the newest may be mid-input), minus the ticks the server has
      // already applied of the acked one — rebuilding the locally-predicted
      // present on top of the authoritative state, tick-for-tick.
      const done = h.seq === ack ? phase : 0;
      for (let i = done; i < h.ticks; i++) physStep(planeCfg, pred, h.ctl, PHYS_DT, dmg);
    }
    visualOffset.x += (oldX - pred.pos.x) * alpha;
    visualOffset.y += (oldY - pred.pos.y) * alpha;
    visualOffset.z += (oldZ - pred.pos.z) * alpha;
    if (visualOffset.length() > 30) visualOffset.set(0, 0, 0);
    // Attitude residual: the rotation taking the new prediction to the old
    // one, alpha-weighted like the position fold, decays in attOffset.
    errAttQ.set(pred.att.x, pred.att.y, pred.att.z, pred.att.w).invert().premultiply(oldAttQ);
    attOffset.premultiply(foldAttQ.identity().slerp(errAttQ, alpha)).normalize();
    // A correction this large can't be eased credibly: let it pop once.
    if (Math.abs(attOffset.w) < 0.96) attOffset.identity();
  }

  function snapPredTo(row) {
    pred.pos = FDM.v3(row.x, row.y, row.z);
    pred.vel = FDM.v3(row.vx, row.vy, row.vz);
    pred.att = FDM.q4(row.qx, row.qy, row.qz, row.qw);
    pred.omega = FDM.v3(row.ox ?? 0, row.oy ?? 0, row.oz ?? 0);
    prevPos.set(row.x, row.y, row.z);
    prevAtt.set(row.qx, row.qy, row.qz, row.qw);
    if (spaceTheater) {
      // Refill the display tank only on a FRESH LIFE (spawn protection is
      // the tell) or first contact — a mid-flight desync snap must not top
      // the tank, and must never disengage the gyros: the server's hold
      // rides p.SpaceHold and survives the snap, so the client's must too
      // (the first headless playtest watched its own reset here fight every
      // K press under a slow GPU's constant snap traffic).
      if (pred.prop === undefined || row.status === 2) {
        pred.prop = craftDeltaV(planeTypes[selfPlane] ?? {});
      }
      pred.landed = false;
    }
  }

  const isAAId = (id) => id >= 60000;
  const SHIP_NAMES = ["CARRIER", "DESTROYER", "BATTLESHIP"];
  function nameOf(id) {
    if (isAAId(id)) {
      const g = aaGuns.guns.get(id);
      const team = g?.team ?? 0;
      const what =
        g?.kind === 4 ? "BARRAGE BALLOON"
        : g?.kind === 3 ? (g.sub === 1 ? "RADAR STATION" : "FUEL DEPOT")
        : g?.kind === 2 ? "BIG GUNS"
        : g?.kind === 1 ? "NAVAL GUNS"
        // N1: at sea the default AA mount is a warship's own gun crew, not a
        // shore flak battery — so a strike-plane shootdown reads "SHIP AA".
        : navalTheater ? "SHIP AA"
        : "FLAK";
      return `${TEAM_INFO[team].name} ${what}`;
    }
    return roster.get(id)?.name ?? "someone";
  }
  function teamOf(id) {
    if (isAAId(id)) return aaGuns.guns.get(id)?.team ?? 0;
    return roster.get(id)?.team ?? 0;
  }

  // nightNow mirrors the server's isNight (searchlight.go) exactly so the client
  // can credit a "kills after dark" mission off the shared day/night clock.
  const DAY_SECONDS = 1200;
  function nightNow() {
    let frac = ((lastSnapNow / DAY_SECONDS) % 1) + 1;
    frac %= 1;
    const ang = (frac - 0.25) * 2 * Math.PI;
    const x = Math.cos(ang) * 0.95;
    const y = Math.sin(ang) + 0.14;
    const e = y / Math.sqrt(x * x + y * y + 0.34 * 0.34);
    const v = Math.min(1, Math.max(0, (e + 0.3) / 0.32));
    return 1 - v * v * (3 - 2 * v) > 0.5;
  }

  // bumpDaily advances the local progress bar for every open mission tracking
  // `stat`. The server stays authoritative — it sends the completion event and
  // re-seeds progress on the next join — this is just live feedback between.
  function bumpDaily(stat, n = 1) {
    let changed = false;
    for (const c of dailyChallenges) {
      if (c.stat !== stat || (c.have ?? 0) >= c.goal) continue;
      c.have = Math.min(c.goal, (c.have ?? 0) + n);
      changed = true;
    }
    if (changed) hud.setDaily(dailyChallenges);
  }

  function handleEvent(ev) {
    // Squadron radio chatter (roadmap #22): turn the events it cares about
    // (bot "radio", kills, vengeance, comms, round end) into team-chat lines.
    // Harmless for the rest.
    radio.onEvent(ev, {
      selfId, selfTeam, spectating,
      nameOf, teamOf, roster,
      commsPhrase: (i) => config.comms?.phrases?.[i]?.label,
    });
    switch (ev.e) {
      case "join":
        roster.set(ev.id, {
          id: ev.id, name: ev.name, team: ev.team, bot: ev.bot, plane: ev.plane,
          title: ev.text || "", // career title (medals.go)
          skn: ev.skn ?? 0,     // equipped paint scheme (roadmap #25)
          ace: !!ev.ace,        // career ace marker (roadmap #26)
          ad: ev.ad || "",      // banner copy: marks the ad circus (adfleet.go)
        });
        if (ev.id !== selfId) {
          if (ev.ad) {
            hud.feed(`📢 ${escapeName(ev.name)} is flying a banner: “${escapeName(ev.ad)}”`);
            break;
          }
          const t = ev.text ? ` 〈${escapeName(ev.text)}〉` : "";
          hud.feed(`${escapeName(ev.name)}${t} joined the skies`);
        }
        break;
      case "ad": {
        // The round bell rolled the circus onto fresh copy — repaint the cloth.
        const r = roster.get(ev.id);
        if (r) r.ad = ev.ad || "";
        entities.get(ev.id)?.visual.setBanner(ev.ad || "");
        break;
      }
      case "adkill": {
        // Somebody popped a billboard: a token bounty and a marquee moment,
        // never a war kill (the server keeps those books apart — adfleet.go).
        const craft = CLASS_BANNERS[ev.plane]?.kind === "sides" ? "ad zeppelin" : "banner plane";
        hud.feed(`📢 ${escapeName(nameOf(ev.k))} downed the ${craft} — the sponsors will send another`);
        if (ev.k === selfId) {
          hud.message("BILLBOARD DOWN — THIS PLINK WAS SPONSORED", 2500, "#f4c542");
        }
        break;
      }
      case "leave":
        if (ev.id === nemesisId) nemesisId = 0;
        if (ev.id === aceId) aceId = 0;
        hud.feed(`${escapeName(roster.get(ev.id)?.name ?? "someone")} flew home`);
        roster.delete(ev.id);
        board.delete(ev.id);
        break;
      case "plane": {
        const r = roster.get(ev.id);
        if (r) r.plane = ev.plane;
        break;
      }
      case "scheme": {
        // A pilot repainted (roadmap #25) — update the roster and repaint live.
        const r = roster.get(ev.id);
        if (r) r.skn = ev.skn ?? 0;
        entities.get(ev.id)?.visual.setScheme(ev.skn ?? 0);
        if (ev.id === selfId) {
          equippedScheme = ev.skn ?? 0;
          localStorage.setItem("va-scheme", String(equippedScheme));
          selfVisual?.setScheme(equippedScheme);
        }
        break;
      }
      case "mangun":
        if (ev.id === selfId && ev.v !== selfGunId) {
          selfGunId = ev.v;
          gunAim.has = false;
          pendingGun = ev.v; // server keeps the reservation while seated
          resetFireControl();
          const mainBattery = aaGuns.guns.get(ev.v)?.kind === 2;
          if (mainBattery) {
            hud.message("MANNING MAIN BATTERY — tap the chart to aim · F bails out", 5000, "#f4c542");
          } else {
            hud.message("MANNING AA GUN — F bails out", 3500, "#f4c542");
          }
          analytics.event("man_gun", { gun: mainBattery ? "main_battery" : "aa" });
          analytics.count("guns_manned");
        }
        break;
      case "mantur":
        if (ev.id === selfId && !(ev.v === selfTurretHost && ev.plane === selfTurretSeat)) {
          selfTurretHost = ev.v;
          selfTurretSeat = ev.plane;
          turAim.has = false;
          gunCooldown = 0;
          pendingTur = ev.plane; // server keeps the reservation while seated
          waitingBomber = false; // a bomber came and we're aboard: the wait's over
          const hostPlane = roster.get(ev.v)?.plane ?? 0;
          const seatNm = planeTypes[hostPlane]?.turrets?.[ev.plane]?.name ?? "TURRET";
          const pilotNm = roster.get(ev.v)?.name ?? "a friendly pilot";
          hud.message(
            `${seatNm.toUpperCase()} — riding ${escapeName(pilotNm)}'s bomber · mouse aims · F bails out`,
            5000, "#f4c542",
          );
          analytics.event("man_turret", { seat: ev.plane });
          analytics.count("turrets_manned");
        }
        break;
      case "turwait":
        // No friendly bomber was airborne to crew: the server parked us waiting
        // for one (it auto-seats us the instant a bomber launches). Hold on the
        // spawn screen with a clear explanation instead of dropping into a
        // control-less camera. haveSpawned lets the respawn screen own the frame.
        if (ev.id === selfId) {
          waitingBomber = true;
          selfTurretSeat = ev.plane;
          pendingTur = ev.plane; // sticky: still the seat we want
          haveSpawned = true;
          if (document.pointerLockElement) document.exitPointerLock();
          analytics.event("turret_wait", { seat: ev.plane });
        }
        break;
      case "manveh":
        // ev.v !== selfVehicleId dedups the welcome's replayed seat event
        // (vehicle ids are never reused, so a same-id re-man can't exist).
        if (ev.id === selfId && ev.v !== selfVehicleId) {
          selfVehicleId = ev.v;
          vehAim.has = false;
          gunCooldown = 0; // fresh crew: main gun is loaded and ready
          // A land hull starts in neutral (the drive slider's reverse band
          // sits below 1/4), not creeping forward on the plane's default 0.7.
          const tcfg = config.groundVehicles?.types?.[ev.plane];
          if (tcfg?.land) input.throttle = 0.25;
          // A warship rings up half ahead: she's visibly under way from the
          // first second, and W/S move the telegraph from an honest midpoint.
          else if (tcfg?.naval) input.throttle = 0.5;
          pendingVeh = ev.plane; // server keeps the reservation while crewed
          const nm = tcfg?.name ?? "VEHICLE";
          hud.message(
            tcfg?.naval
              ? touch
                // Touch has no keyboard: the labeled buttons and chips carry the
                // control map, so the brief stays to the one thing that isn't
                // discoverable — the broadside tactic.
                ? `${nm.toUpperCase()} UNDER WAY — the ring on the water is where your salvo lands. Swing broadside to bring every gun to bear.`
                : `${nm.toUpperCase()} UNDER WAY — the ring on the water is where your salvo lands; swing broadside to bring every turret to bear. W/S engine telegraph · A/D rudder · mouse trains the guns · B torpedoes · ${navalSmokeOn ? "C smoke · " : ""}X swaps HE/AP/SS · Z gunsight · T repair · G boost · F leaves the ship`
              : tcfg?.land
                ? touch
                  ? `${nm.toUpperCase()} ROLLING — the ring is where your round lands. Angle your armor; halt to shoot straight.`
                  : `${nm.toUpperCase()} ROLLING — the ring is where your round lands; angle your armor and use the folds. W/S drive (low throttle = reverse) · A/D steer · mouse lays the gun · Z gunsight · X AP/HE · B missile · C smoke · R repair · T artillery · G overdrive · F bails out`
                : `DRIVING ${nm.toUpperCase()} — throttle drives · A/D steer · mouse aims · F bails out`,
            6000, "#f4c542",
          );
          analytics.event("drive_vehicle", { vehicle: nm });
          analytics.count("vehicles_driven");
          // N8: hand the freshly-spawned captain a fleet objective, after the
          // controls brief has had a moment on screen.
          if (tcfg?.naval) {
            setTimeout(() => {
              if (selfStatus === 4) {
                const order = fleetOrderHint();
                if (order) hud.message(order, 4000, "#8fd0ff");
              }
            }, 6200);
          }
        }
        break;
      case "hit":
        // Rounds on target: nudge both pilots up so the director finds the
        // dogfight before anyone goes down.
        directorBump(isAAId(ev.k) ? "gun" : "plane", ev.k, 0.6);
        directorBump("plane", ev.v, 0.4);
        if (ev.v === selfId) {
          // Directional damage flash (roadmap #56): light the screen edge the
          // fire came from when the hit carries the attacker's coarse position;
          // fall back to the whole-screen flash otherwise (older servers).
          if (ev.x != null || ev.z != null) {
            const fwd = FDM.qRotate(pred.att, FDM.v3(1, 0, 0));
            const rgt = FDM.qRotate(pred.att, FDM.v3(0, 0, 1));
            const dx = (ev.x ?? 0) - pred.pos.x, dz = (ev.z ?? 0) - pred.pos.z;
            hud.hitFrom(Math.atan2(dx * rgt.x + dz * rgt.z, dx * fwd.x + dz * fwd.z));
          } else {
            hud.damageFlash();
          }
          audio.damaged();
          lastDamagedAt = perfNow(); // drives the combat-music envelope
          // Sparks off our own skin where the round bit (roadmap #8): the
          // remote-victim case already does this for the shooter below.
          const selfHitPos = new THREE.Vector3(pred.pos.x, pred.pos.y, pred.pos.z);
          for (let i = 0; i < 3; i++) particles.hitSpark(selfHitPos);
        }
        if (ev.k === selfId) {
          hud.hitmarker();
          audio.hitmarker();
          lastOwnHitAt = perfNow();
          const ent = entities.get(ev.v);
          if (ent?.buffer.length) particles.hitSpark(ent.buffer.at(-1).pos);
        }
        break;
      case "kill": {
        // Cut the director straight to the pilot (or gun) that scored.
        if (ev.k) directorBump(isAAId(ev.k) ? "gun" : "plane", ev.k, 3, true);
        const victimEnt = entities.get(ev.v);
        const vPos = ev.v === selfId ? deathPos : victimEnt?.buffer.at(-1)?.pos;
        const killerPos = ev.k ? entities.get(ev.k)?.buffer.at(-1)?.pos : null;
        // Only kills the stream actually shows earn a reel moment — and an
        // air-to-air kill has to show BOTH birds: the caption names a hunter and
        // a prey, so both must be in frame or the clip is half a story. (Flak
        // stays victim-only — the story there is the plane going down, not the
        // fixed gun that did it.)
        // If the director was already covering this exact tail-chase, the kill is
        // filmed by definition — otherwise it must have shown BOTH birds (the
        // caption names a hunter and a prey, so a lone bird is half the story).
        const coveringThis =
          director.cover && !director.cover.killed &&
          director.cover.aId === ev.k && director.cover.bId === ev.v;
        const airFilmed =
          ev.k && !isAAId(ev.k) && (coveringThis || (onCamera(vPos) && onCamera(killerPos)));
        if (airFilmed || (ev.k && isAAId(ev.k) && onCamera(vPos))) {
          // Air-to-air is the show the reel is meant to be about, so a
          // plane-on-plane kill is weighted to matter: at 2 a lone kill out-ranks
          // a medal, and a three-kill furball (summed) clears a sinking — which,
          // with the sink share capped in the reel planner, is how the tape lands
          // ~4 dogfights to every sinking. Flak (an AA gun downing a plane) stays
          // a footnote at 0.5 — it's a kill, not a dogfight.
          recordHighlight("kill", isAAId(ev.k) ? 0.5 : 2, `${nameOf(ev.k)} downed ${nameOf(ev.v)}`);
          // Hand the coverage its ending — roll into the wreck beat so the reel's
          // tail is the victim going down (see directorCoverKill).
          if (airFilmed) directorCoverKill(ev.k, ev.v);
        }
        // Structural death (roadmap #3): a shot-down airframe tumbles as a
        // wreck instead of bursting on the spot — the big bang moves to the
        // `crashed` event at impact. Manned mounts (gun/vehicle/turret crew,
        // status 3/4/5) never fall, so they still burst here. The victim's
        // latest snapshot status tells them apart; a flying status (1/2) or an
        // already-falling one (6) means a wreck is on its way down.
        const vLast = victimEnt?.buffer.at(-1)?.status;
        const willFall = vLast === 1 || vLast === 2 || vLast === 6;
        if (vPos && ev.v !== selfId && !willFall) {
          if (vPos.y < config.world.waterLevelM + 8) particles.splash(vPos);
          else particles.explosion(vPos);
          audio.explosion(vPos.distanceTo(camera.position));
        }
        // A killer id above the AA base can be a vehicle-mounted gun the client
        // has no roster entry for (land theater flak): two teams, no friendly
        // fire, so the credit is simply the other side.
        const killerTeam = ev.k && isAAId(ev.k) && !aaGuns.guns.get(ev.k)
          ? 1 - teamOf(ev.v)
          : teamOf(ev.k);
        hud.feedKill(ev.k ? nameOf(ev.k) : null, killerTeam, nameOf(ev.v), teamOf(ev.v),
          ev.wg ? "WINGMAN ×1.2" : "");
        // Wingman bonus: the killer's own toast below carries the badge; the
        // nearby friend who earned it gets their own.
        if (ev.wg === selfId && ev.k !== selfId) {
          hud.message("WINGMAN ×1.2", 2000, "#f4c542");
          audio.blip({ freq: 1200, dur: 0.1 });
        }
        // Round intel: credit the shoot-down to the killer's team (pilots
        // also tally toward top guns; AA mounts and crashes don't).
        if (ev.k) intel.noteKill(killerTeam, isAAId(ev.k) ? 0 : ev.k);
        // Spawn-map heat (roadmap #55): remember where the killing is.
        if (ev.x != null) {
          heatEvents.push({ x: ev.x, z: ev.z ?? 0, w: 1, t: perfNow() });
          if (heatEvents.length > 64) heatEvents.shift();
        }
        if (ev.v === selfId) {
          deathKiller = ev.k ?? 0;
          // Death-recap coach (roadmap #54): the kill event carries the
          // authoritative cause (Text) and the killer's plane type (Plane);
          // pick the line once per death, shown on the killcam tag and the
          // respawn card.
          deathCoach = coachLineFor(
            ev.text, ev.k ? nameOf(ev.k) : "", ev.plane ?? -1, selfPlane, planeTypes,
          );
          if (deathCoach)
            analytics.event("coach_shown", { cause: deathCoach.cause, line_index: deathCoach.index });
          // Field manual (tips.js): one "did you know?" per death, picked here
          // so the killcam tag and the respawn card show the same fact.
          deathTip = nextTip(tipCtx);
          if (deathTip) analytics.event("tip_shown", { id: deathTip.id });
          // A killcam is coming for every death — from the killer's seat when
          // an attacker got us, chasing our own final seconds for a crash.
          // Hold the respawn screen back until the clip lands and its replay
          // takes over the screen. The clip is a fat binary (seconds of full
          // snapshots) that can lag its own tiny kill event by seconds on a
          // slow link, so the hold spans the WHOLE window the server keeps the
          // respawn locked for (send delay + playback + tail), not just the
          // send delay — a short window lapses before a late clip arrives and
          // flashes the spawn card on ahead of the replay. startKillcam clears
          // this the instant the clip lands (the common case); the full span
          // only bounds the fall-through if a clip never shows (it shouldn't),
          // and it lines up with the server's respawn unlock when it does.
          awaitingKillcam =
            config.net.killcamSec > 0
              ? perfNow() + (config.net.killcamDelaySec || 0) + (config.net.killcamSec || 0) + 0.8
              : 0;
          killcamSkipped = false; // each death gets its replay until declined
          analytics.event("death", {
            cause: ev.text || "unknown",
            killer_plane: planeTypes[ev.plane]?.name,
            plane_type: planeTypes[selfPlane]?.name,
          });
          analytics.count("deaths");
        }
        if (ev.k === selfId) {
          // Flying kills get the cinematic stamp (banner + slow-mo + camera
          // pull); manned mounts and turret seats keep the plain toast.
          const cinematic = !replay && ev.v !== selfId &&
            selfStatus !== 3 && selfStatus !== 4 && selfStatus !== 5;
          if (cinematic) {
            if (killConfirm.trigger(perfNow(), vPos ?? null)) audio.killConfirm();
            hud.killBanner(selfPlane, planeTypes[selfPlane]?.name ?? "", nameOf(ev.v));
            if (musicOn()) music.accent(); // stinger over the groove
            if (ev.wg) hud.message("WINGMAN ×1.2", 1600, "#f4c542");
          } else {
            hud.message(`✚ ${nameOf(ev.v)}${ev.wg ? " · WINGMAN ×1.2" : ""}`, 1600, "#f4c542");
          }
          if (ev.wg) audio.blip({ freq: 1200, dur: 0.1 });
          if (ev.v !== selfId) {
            analytics.event("kill", {
              victim: roster.get(ev.v)?.bot ? "bot" : "player",
              wingman: !!ev.wg,
            });
            analytics.count("kills");
            bumpDaily("kills");
            if (nightNow()) bumpDaily("nightKills");
            if (selfStatus === 3 || selfStatus === 5) bumpDaily("gunKills");
            // Keep the picker's kill-gate badge live ("18/25 KILLS").
            careerKills++;
            localStorage.setItem("va-kills", String(careerKills));
            hud.setCareerKills(careerKills);
          }
        }
        break;
      }
      case "crashed": {
        // A tumbling wreck (roadmap #3) reached the ground: the impact burst
        // the `kill` event held back. The self case is booms handled by the
        // status 6→0 transition in handleSelfRow, so only fire for others here.
        if (ev.v !== selfId) {
          const pos = new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
          if (pos.y < config.world.waterLevelM + 8) particles.splash(pos);
          else particles.explosion(pos);
          audio.explosion(pos.distanceTo(camera.position));
          heatEvents.push({ x: pos.x, z: pos.z, w: 0.5, t: perfNow() });
          if (heatEvents.length > 64) heatEvents.shift();
        }
        break;
      }
      case "bail": {
        // A pilot popped out of a downed plane (roadmap #1): a small white
        // canopy pop where he jumped; the Chutes renderer takes it from there,
        // fed off the snapshot's bomb section.
        const pos = new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        particles.spawn(pos, {
          count: 10, speed: 6, life: 0.5, size: 0.7,
          colors: [0xffffff, 0xe8e8e8, 0xd6d6d6], gravity: 2, drag: 0.7,
        });
        if (pos.distanceTo(camera.position) < 1200) audio.blip({ freq: 620, dur: 0.09 });
        if (ev.id === selfId) analytics.event("bail");
        break;
      }
      case "chutedown": {
        // A chute reached the ground: home (points), captured, or adrift at sea.
        const cls = ev.team === 0 ? "crimson" : "cobalt";
        const name = nameOf(ev.id);
        if (ev.text === "returned") {
          hud.feed(`<span class="${cls}">${name}</span> made it home &#10003;`);
          if (ev.team === selfTeam) {
            hud.message(`PILOT RETURNED +${Math.round(config.combat?.bailout?.returnPoints ?? 3)}`, 1600, "#7bd88a");
            analytics.count("chute_returns");
          }
        } else if (ev.text === "captured") {
          hud.feed(`<span class="${cls}">${name}</span> captured behind the line`);
        }
        break;
      }
      case "chutekill": {
        // Someone gunned a bailed pilot under his canopy — a dishonorable kill.
        hud.feedDishonor(nameOf(ev.k), teamOf(ev.k), nameOf(ev.v), ev.team);
        if (ev.k === selfId) analytics.event("chute_strafe");
        break;
      }
      case "rescuemark": {
        // A pilot splashed into the sea (roadmap #2): a survivor both teams can
        // reach. ev.id is the marker, ev.v the downed pilot, ev.team his side.
        rescueMarkers.set(ev.id, { id: ev.id, x: ev.x ?? 0, z: ev.z ?? 0, team: ev.team });
        break;
      }
      case "rescue": {
        // Fished out (rescued) or grabbed by the enemy (captured).
        const rescuer = nameOf(ev.id), pilot = nameOf(ev.v);
        if (ev.text === "captured") {
          hud.feed(`<span class="${ev.team === 0 ? "crimson" : "cobalt"}">${rescuer}</span> pulled ${pilot} from the drink &mdash; captured`);
        } else {
          hud.feed(`<span class="${ev.team === 0 ? "crimson" : "cobalt"}">${rescuer}</span> fished ${pilot} out of the drink &#9945;`);
        }
        if (ev.text !== "captured" && ev.x != null &&
            new THREE.Vector3(ev.x, config.world.waterLevelM, ev.z).distanceTo(camera.position) < 2500) {
          audio.blip({ freq: 720, dur: 0.08 }); // rising two-note "safe"
          setTimeout(() => audio.blip({ freq: 980, dur: 0.11 }), 90);
        }
        if (ev.id === selfId && ev.team === selfTeam) analytics.count("rescues");
        // The pilot leaves the water either way.
        rescueMarkers.forEach((m, id) => { if (m.x === ev.x && m.z === ev.z) rescueMarkers.delete(id); });
        break;
      }
      case "rescueexpired": {
        rescueMarkers.delete(ev.id);
        break;
      }
      case "scar": {
        // A new battlefield decal (roadmap #5): kind rides Plane, radius rides
        // Dmg (reused Event fields, no new wire).
        scarsR.add(ev.id, ev.plane, ev.x ?? 0, ev.y ?? 0, ev.z ?? 0, ev.d ?? 0);
        break;
      }
      case "boom": {
        const pos = new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        particles.bombBlast(pos);
        audio.explosion(pos.distanceTo(camera.position));
        heatEvents.push({ x: pos.x, z: pos.z, w: 0.5, t: perfNow() });
        if (heatEvents.length > 64) heatEvents.shift();
        break;
      }
      case "salvo": {
        // Battleship main battery firing: a long, deep report carries across
        // the map — the full-weight salvo boom, not a generic blast.
        const pos = new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        audio.salvo(1, Math.max(40, pos.distanceTo(camera.position) * 0.5));
        // Muzzle blast at the firing turret: flash + a rising gunsmoke plume
        // thrown along the bore from each barrel, so every salvo (ours or
        // theirs) announces itself at the guns.
        const sg = aaGuns.guns.get(ev.v);
        if (sg && sg.alive && sg.barrel) {
          const dir = new THREE.Vector3(1, 0, 0)
            .applyQuaternion(sg.barrel.getWorldQuaternion(new THREE.Quaternion()))
            .normalize();
          // Twin-rifle main battery: a plume off each tube (z = ±1.2, muzzles
          // ~16 m out in barrel space); single tube otherwise.
          const isMain = sg.kind === 2;
          const offs = isMain ? [1.2, -1.2] : [0];
          for (const z of offs) {
            const mp = sg.barrel.localToWorld(new THREE.Vector3(isMain ? 16 : 5, 0, z));
            particles.muzzleBlast(mp, dir);
          }
        }
        if (ev.v === selfGunId && ev.id === selfId) {
          fc.reloadUntil = perfNow() + (bbCfg.reloadSec ?? 24);
          hud.message(ev.sp ? "SALVO AWAY — SPOTTED, TIGHT PATTERN" : "SALVO AWAY", 2200, "#f4c542");
          analytics.event("salvo_fired", { spotted: !!ev.sp });
          analytics.count("salvos_fired");
          // Kick off the firing cutaway: frame the muzzle, then ride the shells
          // along their computed arc to the impact.
          const from = { x: ev.x ?? 0, y: ev.y ?? 0, z: ev.z ?? 0 };
          const tgt = fc.target;
          const to = tgt
            ? { x: tgt.x, y: field.surfaceAt(tgt.x, tgt.z), z: tgt.z }
            : null;
          // Ride the exact charge and arc the ship fired (carried on the event),
          // falling back to our own fire-mission solve if it's missing.
          const sol =
            to &&
            (ev.chg
              ? solveBallisticVec(from, to, ev.chg, !!ev.hi)
              : solveFireMission(from, to));
          if (sol) {
            cine.mode = "fire";
            cine.t = 0;
            cine.from = from;
            cine.to = to;
            cine.sol = sol;
            cine.shake = 1;
          }
        }
        break;
      }
      case "fortflip": {
        // Fort Brick's garrison joins whoever took zone D — the round's
        // biggest land-grab gets a banner and a name.
        const tn = TEAM_INFO[ev.team]?.name ?? "";
        hud.message(
          ev.team === selfTeam
            ? "⚑ FORT BRICK GARRISON JOINS US — the fort's guns are yours"
            : `⚑ FORT BRICK FALLS TO ${tn.toUpperCase()}`,
          3200,
          ev.team === selfTeam ? "#7CFC7C" : "#ff6b6b",
        );
        hud.feed(`⚑ ${tn} captures the Fort Brick garrison`);
        break;
      }
      case "sboom": {
        // Main-battery shell impact: column of water or a heavy ground burst.
        const pos = new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        if (pos.y <= config.world.waterLevelM + 2) particles.shellSplash(pos, { big: navalTheater || armadaTheater });
        else particles.shellBlast(pos);
        audio.explosion(pos.distanceTo(camera.position));
        if (ev.team === selfTeam) fc.impacts.push({ x: ev.x ?? 0, z: ev.z ?? 0, at: perfNow() });
        break;
      }
      case "truckkill": {
        if (ev.k) directorBump(isAAId(ev.k) ? "gun" : "plane", ev.k, 1.2);
        const pos = trucksR.positionOf(ev.v) ?? new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        particles.explosion(pos.clone ? pos.clone() : pos);
        audio.explosion(pos.distanceTo(camera.position));
        if (ev.k === selfId) {
          hud.message(`SUPPLY TRUCK DESTROYED +${config.convoys?.killPoints ?? 8}`, 2000, "#f4c542");
          analytics.event("destroy_truck", { points: config.convoys?.killPoints ?? 8 });
          analytics.count("trucks_destroyed");
          bumpDaily("trucks");
        }
        if (ev.k) {
          hud.feed(`${escapeName(nameOf(ev.k))} 💥 ${TEAM_INFO[ev.team].name} convoy`);
          intel.note(teamOf(ev.k), "trucks");
        }
        break;
      }
      case "deliver":
        intel.note(ev.team, "supplies");
        if (ev.team === selfTeam) {
          hud.feed(`📦 supply truck delivered — downed guns rebuild faster`);
        }
        break;
      case "supplyshipkill": {
        if (ev.k) directorBump(isAAId(ev.k) ? "gun" : "plane", ev.k, 1.2);
        const pos = supplyShipsR.positionOf(ev.v) ?? new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        particles.explosion(pos.clone ? pos.clone() : pos);
        if (pos.y !== undefined && pos.y <= config.world.waterLevelM + 3) particles.shellSplash(pos.clone ? pos.clone() : pos);
        audio.explosion(pos.distanceTo(camera.position));
        if (ev.k === selfId) {
          hud.message(`SUPPLY SHIP SUNK +${config.supplyShips?.killPoints ?? 20}`, 2000, "#f4c542");
          analytics.event("destroy_supplyship", { points: config.supplyShips?.killPoints ?? 20 });
        }
        if (ev.k) {
          hud.feed(`${escapeName(nameOf(ev.k))} 💥 ${TEAM_INFO[ev.team].name} sealift`);
          intel.note(teamOf(ev.k), "ships");
        }
        break;
      }
      case "supplydeliver":
        intel.note(ev.team, "supplies");
        if (ev.team === selfTeam) {
          hud.feed(`🚢 supply ship delivered — downed guns rebuild faster`);
        }
        break;
      case "vehkill": {
        // A crewed hull's death arrives twice by design: the kill event tells
        // the driver's story (banner, feed, score), and this one — marked
        // "crewed" — exists only to sink the ship on every screen. Skip the
        // killer-facing rewards for it or they'd all pay out double.
        const crewed = ev.text === "crewed";
        if (ev.k && !crewed) directorBump(isAAId(ev.k) ? "gun" : "plane", ev.k, 1.2);
        const pos = vehiclesR.positionOf(ev.v) ?? new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        particles.explosion(pos.clone ? pos.clone() : pos);
        audio.explosion(pos.distanceTo(camera.position));
        const name = config.groundVehicles?.types?.[ev.plane]?.name ?? "VEHICLE";
        const pts = config.groundVehicles?.types?.[ev.plane]?.killPoints ?? 0;
        const sunk = !!config.groundVehicles?.types?.[ev.plane]?.naval;
        const killWord = sunk ? "SUNK" : "DESTROYED";
        const killIcon = sunk ? "⚓" : "💥";
        // A killed warship doesn't blink out: the hull rides down through the
        // founder (GroundVehicles.beginSink) — the same settle-and-list the
        // anchored armada gets — while the cook-off above and the splash at the
        // waterline sell the moment. The death replay's wreck orbit films it.
        if (sunk && vehiclesR.beginSink(ev.v)) {
          particles.shellSplash(new THREE.Vector3(pos.x, pos.y, pos.z));
          // The stream's founder cam owns a player-hull sinking too, under the
          // armada's honesty contract: the highlight is logged iff the cut
          // actually took the frame, so the reel never captions a sinking the
          // tape doesn't show.
          if (directorCutToSink(pos)) {
            recordHighlight("sink", 5, `${nameOf(ev.k)} sank the ${TEAM_INFO[ev.team].name} ${name}`);
          }
        }
        if (crewed) break;
        if (ev.k === selfId) {
          hud.message(`${name.toUpperCase()} ${killWord} +${pts}`, 2000, "#f4c542");
          analytics.event("destroy_vehicle", { points: pts });
          analytics.count("vehicles_destroyed");
          bumpDaily("ground");
        }
        if (ev.k) {
          hud.feed(`${escapeName(nameOf(ev.k))} ${killIcon} ${TEAM_INFO[ev.team].name} ${name}`);
          intel.note(teamOf(ev.k), sunk ? "ships" : "ground");
        }
        break;
      }
      case "navalhit": {
        // Main-battery shell splat. The shooter gets the result of their round;
        // a hit on the local ship just shakes the deck.
        const pos = vehiclesR.positionOf(ev.v);
        if (pos) {
          const hp = new THREE.Vector3(pos.x, (ev.y ?? 0) + 6, pos.z);
          // G-polish: a hull hit is metal on metal — a spark burst and a small
          // flash, not the old water column at deck height. Only an over-pen (the
          // shell punches clean through and out into the sea) throws a low splash.
          if (ev.text === "overpen") {
            particles.shellSplash(new THREE.Vector3(pos.x, ev.y ?? 0, pos.z));
          } else {
            particles.hitSpark(hp);
            particles.explosion(hp);
          }
        }
        if (ev.k === selfId) {
          const splat = {
            citadel: ["CITADEL!", "#ff5a3c"],
            pen: ["PENETRATION", "#f4c542"],
            overpen: ["over-pen", "#c9d4e0"],
            shatter: ["shattered on the belt", "#9fb0c0"],
            he: ["HE hit", "#f4c542"],
          }[ev.text] ?? ["hit", "#f4c542"];
          // N9: name the shell type on the ribbon (current loadout ≈ the round in
          // flight — reloads are seconds long). AP/HE prefix, skipped for shatter.
          const ammo = navalAmmo === 1 ? "AP" : "HE";
          const label = ev.text === "shatter" ? splat[0] : `${ammo} ${splat[0]}`;
          hud.message(`${label} ${ev.d ? `−${Math.round(ev.d)}` : ""}`, 1100, splat[1]);
          // G-polish: every own hit gets a hitmarker tick, not just citadels.
          hud.hitmarker();
          audio.hitmarker();
          if (ev.text === "citadel") audio.explosion(80);
        }
        break;
      }
      case "landhit": {
        // Land round splat: ev.text is "kind:facet" from the facet armor
        // model — ricochet / shatter / pen / overpen / he, on front / side /
        // rear / turret / roof.
        const [kind, facet] = (ev.text ?? "").split(":");
        const pos = vehiclesR.positionOf(ev.v);
        if (pos) {
          const hp = new THREE.Vector3(ev.x ?? pos.x, ev.y ?? pos.y + 1.5, ev.z ?? pos.z);
          if (kind === "ricochet" || kind === "shatter") {
            particles.hitSpark(hp);
          } else {
            particles.hitSpark(hp);
            particles.explosion(hp);
          }
        }
        if (ev.k === selfId) {
          const splat = {
            pen: ["PENETRATION", "#f4c542"],
            overpen: ["THROUGH AND THROUGH", "#ffd76b"],
            ricochet: ["ricochet", "#9fb0c0"],
            shatter: ["no penetration", "#9fb0c0"],
            he: ["HE hit", "#f4c542"],
          }[kind] ?? ["hit", "#f4c542"];
          const where = facet && kind !== "ricochet" ? ` (${facet})` : "";
          hud.message(`${splat[0]}${where} ${ev.d ? `−${Math.round(ev.d)}` : ""}`, 1100, splat[1]);
          if (kind !== "ricochet" && kind !== "shatter") {
            hud.hitmarker();
            audio.hitmarker();
          }
        }
        if (ev.v === selfVehicleId && (kind === "ricochet" || kind === "shatter")) {
          hud.message(kind === "ricochet" ? "ricochet — armor held" : "shattered on the armor", 1100, "#8fd0ff");
        }
        break;
      }
      case "vhit": {
        // A gun round biting a hull — MG, autocannon, or aircraft cannon raking
        // a tank or a ship. The rifle shells throw landhit/navalhit; this is the
        // small-caliber plink, throttled server-side so a strafing run reads as a
        // bright stream of sparks off the steel rather than a wall of them. Gate
        // on the hull being rendered (spotted) so a broadcast spark can't paint
        // an unseen hull through the fog of war — same guard landhit uses.
        const pos = vehiclesR.positionOf(ev.v);
        if (pos) {
          const hp = new THREE.Vector3(ev.x ?? pos.x, ev.y ?? pos.y + 1.5, ev.z ?? pos.z);
          particles.hitSpark(hp);
        }
        break;
      }
      case "module": {
        // A knocked module: warn the crew that owns it; the shooter reads it
        // off the pen splat cadence instead (no spam).
        if (ev.v === selfVehicleId) {
          const label = {
            engine: "⚙ ENGINE HIT — crawling · R to repair",
            track: "🛞 TRACK BLOWN — immobilized · R to repair",
            gun: "✕ GUN DAMAGED — hold fire · R to repair",
            crew: "✚ CREW HIT",
            ammo: "☢ AMMO RACK HIT",
          }[ev.text] ?? "MODULE HIT";
          hud.message(label, 2400, "#ff7b2e");
        }
        break;
      }
      case "cookoff": {
        // The catastrophic kill: the ammo rack goes up.
        const pos = new THREE.Vector3(ev.x ?? 0, (ev.y ?? 0) + 1.5, ev.z ?? 0);
        particles.explosion(pos);
        particles.shellBlast(pos);
        audio.explosion(pos.distanceTo(camera.position) * 0.6);
        if (ev.k === selfId) hud.message("☢ AMMO RACK — CATASTROPHIC KILL", 2400, "#ff5a3c");
        if (ev.v === selfVehicleId) hud.message("☢ AMMO RACK DETONATION", 2400, "#ff5a3c");
        break;
      }
      case "artycall": {
        // A called fire mission: the caller's team sees the plot (and hears
        // the guns); the target team gets no warning until the whistle.
        // Our own call coming back is the battery's word that it FIRED — this
        // is where the real cooldown starts (the keypress only armed a short
        // guard, so a server-refused call frees the battery for a retry).
        if (ev.id === selfId) artyReadyAtClient = perfNow() + (crewCfg().artyCooldownSec ?? 110);
        if (ev.team === selfTeam) {
          hud.message(ev.id === selfId ? "📡 FIRE MISSION — rounds inbound" : "📡 artillery called in", 2000, "#f4c542");
        }
        break;
      }
      case "repaired": {
        if (ev.v === selfVehicleId) hud.message("🔧 REPAIRS COMPLETE", 1600, "#7CFC7C");
        break;
      }
      case "atgm": {
        if (ev.id === selfId) {
          hud.message("MISSILE AWAY — keep the sight on the target", 1800, "#ffd76b");
          const t = config.groundVehicles?.types?.[vehType()];
          torpReadyAtClient = perfNow() + (t?.atgm?.reloadSec ?? 16);
        }
        audio.cannon?.();
        break;
      }
      case "fox2": {
        // Jets era: a heat-seeker off the rail. The shooter gets the stamp;
        // the target gets the wingman call — the era-honest warning (an IR
        // launch rang no cockpit tone; somebody had to SEE the smoke).
        if (ev.id === selfId) hud.message("FOX TWO", 1400, "#ffd76b");
        else if (ev.v === selfId) {
          hud.message("⚠ MISSILE! MISSILE! — BREAK HARD, PUNCH FLARES ⚠", 2600, "#ff6b6b");
          audio.checkSix?.();
        }
        audio.missileLaunch?.(ev.id === selfId ? 0 : 900);
        break;
      }
      case "flare": {
        // Jets era: a countermeasure pop (the star-shell glow renders it).
        audio.flarePop?.();
        break;
      }
      case "samtrack": {
        // Jets era RWR: a guidance radar settled on you. A radar is the one
        // thing this era CAN hear coming — slow chirp, and the recovery in
        // the warning (get under the beam).
        if (ev.v === selfId) {
          hud.message("⚠ SAM RADAR — GET BELOW THE BEAM ⚠", 2200, "#ffd76b");
          audio.rwrChirp?.(false);
        }
        break;
      }
      case "samfire": {
        // The pole is up. Urgent tone for the target; everyone nearby hears
        // the booster.
        if (ev.v === selfId) {
          hud.message("⚠ SAM LAUNCH — DIVE! DIVE! ⚠", 2600, "#ff6b6b");
          audio.rwrChirp?.(true);
        }
        audio.missileLaunch?.(ev.v === selfId ? 400 : 1400);
        break;
      }
      case "ssmfire": {
        // A ship-killer in the water lane: the launching captain gets the
        // stamp, the targeted hull's crew gets the era's scariest sentence.
        if (ev.id === selfId) hud.message("SHIP-KILLER AWAY", 1600, "#ffd76b");
        else if (ev.v === selfVehicleId && selfVehicleId) {
          hud.message("⚠ MISSILE INBOUND — MAKE SMOKE (C) ⚠", 3000, "#ff6b6b");
          audio.checkSix?.();
        }
        audio.missileLaunch?.(ev.id === selfId ? 0 : 1200);
        break;
      }
      case "fire": {
        if (ev.v === selfVehicleId) {
          hud.message(landCrew() ? "🔥 FIRE! — press R for field repair" : "🔥 FIRE! — press R for damage control", 2200, "#ff7b2e");
        }
        break;
      }
      case "flood": {
        if (ev.v === selfVehicleId) hud.message("🌊 FLOODING! — press R for damage control", 2200, "#5aa0ff");
        break;
      }
      case "dc": {
        if (ev.v === selfVehicleId) hud.message("DAMAGE CONTROL — fires out, holding flooding", 1800, "#7CFC7C");
        break;
      }
      // Space module knocks (spacecombat.go spaceModuleRoll): the moments a
      // captain must FEEL — the tank is the health bar of positioning.
      case "spacespot": {
        // The server re-sends this every couple of seconds while an enemy
        // holds you, so a plain expiry clock is all the state the client
        // needs to keep the banner honest.
        if (ev.id === selfId) spaceDetectedUntil = perfNow() + 3.2;
        break;
      }
      case "spaceleak": {
        if (ev.id === selfId) hud.message("⚠ TANK HOLED — Δv BLEEDING ⚠", 2600, "#ff9f43");
        break;
      }
      case "spaceeng": {
        if (ev.id === selfId) hud.message("⚠ MAIN ENGINE KNOCKED OUT ⚠", 2600, "#ff6b6b");
        break;
      }
      case "spacercs": {
        if (ev.id === selfId) hud.message("⚠ RCS CLUSTERS HIT — ATTITUDE SLUGGISH ⚠", 2600, "#ff9f43");
        break;
      }
      case "asat": {
        // The launch is loud on both HUDs: the shooter's rail thuds through
        // their own frame, and the target's warning carries the recovery —
        // the two counters the round respects (foil and silence).
        if (ev.id === selfId) {
          hud.message("ASAT AWAY", 1500, "#7fd1ff");
          audio.gunshot();
        }
        if (ev.v === selfId) hud.message("⚠ ASAT INBOUND — CHAFF (C) OR RUN COLD (T) ⚠", 3200, "#ff6b6b");
        break;
      }
      case "asatboom": {
        // Silent, expanding, brief — the vacuum gate in audio.js keeps every
        // burst past your own hull as pure light.
        const pos = new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        particles.explosion(pos);
        audio.explosion(pos.distanceTo(camera.position));
        if (ev.id === selfId) hud.hitmarker?.();
        break;
      }
      case "spacedecoy": {
        // The bloom reads on the smoke wire already; the message is for the
        // hull that popped it (bots pop them too — no banner for those).
        if (ev.id === selfId) audio.gunshot();
        break;
      }
      case "rodaway": {
        if (ev.id === selfId) {
          hud.message("ROD AWAY — falling on the plot", 1800, "#7fd1ff");
          audio.gunshot(); // the release clamps let go through your own frame
        }
        break;
      }
      case "rodwarn": {
        // The whole point of a half-minute fall: the target hears about it
        // with time to DO something. The recovery is in the warning.
        if (ev.v === selfId) hud.message("⚠ INBOUND TRAJECTORY — RELAUNCH NOW ⚠", 4200, "#ff6b6b");
        break;
      }
      case "rodboom": {
        // A silent streak, then a crater: the vacuum gate keeps the sound
        // yours-only, and the ground burst reads like naval shellfire.
        const pos = new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        particles.shellBlast(pos);
        particles.explosion(pos);
        audio.explosion(pos.distanceTo(camera.position));
        break;
      }
      case "tend": {
        if (ev.id === selfId) hud.message("ALONGSIDE THE TENDER — taking on propellant and ordnance", 2600, "#f4c542");
        break;
      }
      case "sink": {
        // A hull going down is the biggest moment on the map: the founder cam
        // cuts to it and rides it under. Only if that cut actually lands do we
        // log the highlight — a sink filmed, not a sink merely tallied — so the
        // reel never captions a sinking its footage doesn't show.
        const pos = ships.positionOf(ev.v) ?? new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        if (directorCutToSink(pos)) {
          if (ev.k) directorBump(isAAId(ev.k) ? "gun" : "plane", ev.k, 2.5);
          recordHighlight("sink", 5, `${nameOf(ev.k)} sank the ${TEAM_INFO[ev.team].name} ${SHIP_NAMES[ev.plane ?? 0] ?? "ship"}`);
        }
        // A torpedoed hull jackknifes and breaks in half (roadmap #7); other
        // weapons keep the settle-and-list. The cause rides the sink event Text.
        ships.beginSink(ev.v, ev.text);
        particles.shellBlast(new THREE.Vector3(pos.x, (ev.y ?? 0) + 8, pos.z));
        particles.shellSplash(new THREE.Vector3(pos.x, ev.y ?? 0, pos.z));
        audio.explosion(camera.position.distanceTo(pos) * 0.5);
        const what = `${TEAM_INFO[ev.team].name} ${SHIP_NAMES[ev.plane ?? 0] ?? "SHIP"}`;
        hud.feed(`${escapeName(nameOf(ev.k))} ⚓ sank the ${what}`);
        if (ev.k) intel.note(teamOf(ev.k), "ships");
        if (ev.k === selfId) {
          const pts = [config.ships.carrier, config.ships.destroyer, config.ships.battleship][ev.plane ?? 0];
          hud.message(`${what} SUNK +${pts?.sinkPoints ?? ""}`, 3500, "#f4c542");
          analytics.event("ship_sunk", {
            ship: (SHIP_NAMES[ev.plane ?? 0] ?? "ship").toLowerCase(),
            points: pts?.sinkPoints ?? 0,
          });
          analytics.count("ships_sunk");
          bumpDaily("ships");
        } else if (ev.team === selfTeam) {
          hud.message(`our ${SHIP_NAMES[ev.plane ?? 0] ?? "ship"} is going down!`, 3000, "#e8a23a");
        }
        break;
      }
      case "torphit": {
        const pos = new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0);
        // A torpedo run: heat the struck hull and the pilot who put it there.
        directorBumpShipAt(pos, 2.5, true);
        directorBump("plane", ev.id, 1.5);
        if (ev.id && onCamera(pos)) recordHighlight("torphit", 2.5, `${nameOf(ev.id)} — torpedo hit`);
        particles.shellSplash(pos);
        particles.explosion(new THREE.Vector3(pos.x, pos.y + 4, pos.z));
        audio.explosion(pos.distanceTo(camera.position));
        if (ev.id === selfId) {
          hud.hitmarker();
          hud.message("TORPEDO HIT", 1600, "#f4c542");
          analytics.event("torpedo_hit");
          analytics.count("torpedo_hits");
        }
        if (ev.id) intel.note(teamOf(ev.id), "torps");
        break;
      }
      case "torpfire": {
        // A warship loosed a torpedo salvo. The launcher hears the tubes and
        // starts the reload clock on the HUD.
        if (ev.id === selfId) {
          hud.message("TORPEDOES AWAY", 1400, "#7CFC7C");
          const reload = vehMainTorp()?.dropIntervalSec ?? 18;
          torpReadyAtClient = perfNow() + reload;
          analytics.count("torpedoes_fired");
        }
        break;
      }
      case "torpdud":
        if (ev.id === selfId) {
          hud.message("TORPEDO BROKE UP — drop lower and slower", 2600, "#e8a23a");
        }
        particles.splash(new THREE.Vector3(ev.x ?? 0, ev.y ?? 0, ev.z ?? 0));
        break;
      case "comms": {
        if (spectating || ev.team !== selfTeam) break;
        const ph = config.comms?.phrases?.[ev.plane ?? -1];
        if (!ph) break;
        hud.feed(
          `📻 <span class="${["crimson", "cobalt"][ev.team] ?? ""}">${escapeName(nameOf(ev.id))}</span> ${ph.label}`,
        );
        audio.blip({ freq: 880, dur: 0.06, vol: 0.14 });
        if (ph.ping) commsPings.push({ x: ev.x ?? 0, z: ev.z ?? 0, at: perfNow() });
        break;
      }
      case "spotmark": {
        intel.note(ev.team, "fires");
        if (spectating || ev.team !== selfTeam) break;
        spotMarks.push({ x: ev.x ?? 0, z: ev.z ?? 0, at: perfNow() });
        if (ev.id === selfId) {
          hud.message("FIRE MISSION CALLED — marked on the gunners' charts", 2600, "#7bff8a");
          analytics.event("fire_mission");
          analytics.count("fire_missions");
        } else if (selfStatus === 3 && gunKind() === 2) {
          hud.message(`FIRE MISSION — ${nameOf(ev.id)} marked the chart`, 3000, "#7bff8a");
        }
        hud.feed(`${escapeName(nameOf(ev.id))} 📡 called a fire mission`);
        break;
      }
      case "spotassist":
        if (ev.id === selfId) {
          hud.message("SPOTTED KILL — your eyes guided the guns", 2600, "#7bff8a");
          analytics.event("spot_assist");
          analytics.count("spot_assists");
        }
        if (ev.id === selfId || ev.team === selfTeam) {
          hud.feed(`${escapeName(nameOf(ev.id))} 🔭 spotted ${TEAM_INFO[1 - ev.team].name} wreckage`);
        }
        break;
      case "aakill": {
        if (ev.k) directorBump(isAAId(ev.k) ? "gun" : "plane", ev.k, 1.5);
        // Only real emplacements (not routine balloon pops) filmed by the
        // current vantage are worth a clip.
        if (ev.k && aaGuns.guns.get(ev.v)?.kind !== 4 && onCamera(aaGuns.positionOf(ev.v))) {
          recordHighlight("ground", 0.5, `${nameOf(ev.k)} knocked out ${TEAM_INFO[ev.team].name} flak`);
        }
        const pos = aaGuns.positionOf(ev.v);
        if (pos) {
          particles.explosion(pos.clone().setY(pos.y + 4));
          audio.explosion(pos.distanceTo(camera.position));
        }
        // Balloon pops are routine; keep them out of the feed (and the
        // round tally) — only real emplacements count as ground razed.
        if (ev.k && aaGuns.guns.get(ev.v)?.kind !== 4) {
          intel.note(teamOf(ev.k), "ground");
          hud.feed(
            `<span class="${ev.k === selfId ? "" : ""}">${escapeName(nameOf(ev.k))}</span> 💥 ${TEAM_INFO[ev.team].name} FLAK`,
          );
        }
        if (ev.k === selfId) {
          const g = aaGuns.guns.get(ev.v);
          const kind = g?.kind ?? 0;
          const label =
            kind === 4 ? "BALLOON POPPED"
            : kind === 3 ? (g.sub === 1 ? "RADAR STATION DESTROYED" : "FUEL DEPOT DESTROYED")
            : kind === 2 ? "MAIN BATTERY DESTROYED"
            : kind === 1 ? "NAVAL GUN DESTROYED"
            : "FLAK DESTROYED";
          const pts =
            kind === 4 ? config.balloons?.destroyPoints ?? 3
            : kind === 3 ? config.structures.destroyPoints
            : kind === 2 ? config.battleship.destroyPoints
            : kind === 1 ? config.navy.destroyPoints
            : config.aaa.destroyPoints;
          hud.message(`${label} +${pts}`, 2000, "#f4c542");
          analytics.event("destroy_ground", {
            target: ["flak", "naval", "main_battery", "installation", "balloon"][kind] ?? "flak",
            points: pts,
          });
          analytics.count("ground_destroyed");
          if (kind !== 4) bumpDaily("ground"); // balloons aren't siege work
        }
        break;
      }
      case "challenge": {
        // A daily mission was completed (server-authoritative). Fill its bar and
        // celebrate; the feed names whoever cleared it.
        const mine = ev.id === selfId;
        const c = dailyChallenges.find((d) => d.desc === ev.text);
        if (c) {
          c.have = c.goal;
          hud.setDaily(dailyChallenges);
        }
        if (mine) {
          hud.message(`📋 MISSION COMPLETE — ${ev.text}`, 5000, "#7bff8a");
          analytics.event("challenge_complete", { challenge: ev.text });
          analytics.count("challenges_completed");
        }
        hud.feed(`📋 ${escapeName(nameOf(ev.id))} cleared a daily mission`);
        break;
      }
      case "overtime": {
        hud.message("⏱ OVERTIME — every plane painted, no hiding", 5000, "#f4c542");
        hud.feed("⏱ OVERTIME — a close round's final minute, fought in the open");
        audio.blip({ freq: 660, type: "square", dur: 0.12, vol: 0.2 });
        setTimeout(() => audio.blip({ freq: 880, type: "square", dur: 0.18, vol: 0.2 }), 140);
        if (musicOn()) music.accent();
        analytics.event("overtime", { gap: Math.abs((score.c ?? 0) - (score.b ?? 0)) });
        break;
      }
      case "ace": {
        aceId = ev.id;
        hud.feed(
          `⚜ <span class="${["crimson", "cobalt"][ev.team] ?? ""}">${escapeName(ev.name ?? "The Ace")}</span>` +
            ` ${escapeName(ev.text ?? "has taken the field")} — bounty on his head`,
        );
        hud.message(`⚜ ${(ev.name ?? "THE ACE").toUpperCase()} HAS TAKEN THE FIELD`, 4000, "#ffd700");
        audio.blip({ freq: 520, type: "square", dur: 0.14, vol: 0.2 });
        setTimeout(() => audio.blip({ freq: 392, type: "square", dur: 0.22, vol: 0.2 }), 160);
        if (musicOn()) music.accent();
        analytics.event("ace_spawn");
        break;
      }
      case "acedown": {
        hud.feed(
          `⚜ <span class="${["crimson", "cobalt"][ev.team] ?? ""}">${escapeName(nameOf(ev.k))}</span>` +
            ` downed the ace ${escapeName(nameOf(ev.v))}!`,
        );
        if (ev.k === selfId) {
          hud.message(`⚜ ACE DOWN — +${config.ace?.bountyPoints ?? 100} BOUNTY`, 3500, "#ffd700");
          audio.fanfare(true);
        }
        if (ev.v === aceId) aceId = 0;
        analytics.event("ace_down", { by_human: !roster.get(ev.k)?.bot });
        break;
      }
      case "nemesis": {
        if (ev.id !== selfId) break;
        nemesisId = ev.k ?? 0;
        hud.message(`☠ ${nameOf(ev.k).toUpperCase()} IS YOUR NEMESIS`, 3500, "#ff9a3a");
        audio.blip({ freq: 220, type: "sawtooth", dur: 0.3, vol: 0.22 });
        analytics.event("nemesis_declared", { nemesis_bot: !!roster.get(ev.k)?.bot });
        break;
      }
      case "vengeance": {
        hud.feed(
          `⚔ <span class="${["crimson", "cobalt"][ev.team] ?? ""}">${escapeName(nameOf(ev.id))}</span>` +
            ` settled the score with ${escapeName(nameOf(ev.v))}`,
        );
        if (ev.id === selfId) {
          nemesisId = 0;
          hud.message("⚔ VENGEANCE! ×2 BOUNTY", 3000, "#ff9a3a");
          audio.fanfare(true);
          analytics.event("vengeance");
        }
        break;
      }
      case "victoryroll": {
        // A pilot with 5+ kills on one life ripped a full roll over their own
        // base — the server-detected emote (roadmap #23). Feed for everyone,
        // fanfare for the roller.
        hud.feed(
          `★ <span class="${["crimson", "cobalt"][ev.team] ?? ""}">${escapeName(nameOf(ev.id))}</span>` +
            ` VICTORY ROLL — ${ev.st ?? 0} kills and showing off`,
        );
        if (ev.id === selfId) {
          hud.message(`★ VICTORY ROLL — ${ev.st ?? 0} KILLS`, 3000, "#f4c542");
          audio.fanfare(true);
          if (musicOn()) music.accent();
          analytics.event("victory_roll", { kills: ev.st ?? 0 });
        }
        break;
      }
      case "streak": {
        // Daily-mission streak advanced (the first clear of the day). Owner-only
        // toast to keep the feed quiet; the count rides ev.st.
        const n = ev.st ?? 0;
        if (ev.id === selfId && n > 1) {
          hud.message(`🔥 ${n}-DAY MISSION STREAK`, 4000, "#ff9a3a");
          analytics.event("streak_advanced", { days: n });
        }
        break;
      }
      case "unlock": {
        // A naval hull class just unlocked (sank enough ships): open it in the
        // picker live and toast the milestone.
        if (ev.id === selfId) {
          hud.markUnlocked(ev.text);
          hud.message(`⚓ NEW SHIP UNLOCKED — ${escapeName(ev.text ?? "")}`, 5000, "#7bd4ff");
          analytics.event("ship_unlocked", { ship: ev.text });
        }
        hud.feed(`⚓ ${escapeName(nameOf(ev.id))} unlocked the ${escapeName(ev.text ?? "")}`);
        break;
      }
      case "medal": {
        // A career ribbon crossed its threshold; mirror the server's
        // prestige order so a lesser ribbon never demotes the shown title.
        const RANK = ["VETERAN", "CAMPAIGNER", "DEDICATED", "CONVOY RAIDER", "SIEGE MASTER", "FIRE CONTROLMAN", "NIGHT ACE", "SHIP KILLER", "ACE"];
        const r = roster.get(ev.id);
        if (r && RANK.indexOf(ev.text) >= RANK.indexOf(r.title || "")) r.title = ev.text;
        // Title line + (for ACE) the gold marker light up live (roadmap #26).
        const rv = entities.get(ev.id)?.visual;
        if (r) rv?.setTitle(r.title || "");
        if (ev.text === "ACE") {
          if (r) r.ace = true;
          rv?.setAce(true);
        }
        if (ev.id === selfId) {
          hud.message(`🎖 MEDAL EARNED — ${ev.text}`, 5000, "#f4c542");
          analytics.event("medal_earned", { medal: ev.text });
          analytics.count("medals_earned");
        }
        hud.feed(`🎖 ${escapeName(nameOf(ev.id))} earned ${escapeName(ev.text ?? "")}`);
        // A medal has no visual of its own — it's only reel-worthy when the
        // pilot who earned it is the plane the stream is following, so the
        // clip shows the flying that clinched it.
        if (ev.id && spectateTarget?.kind === "plane" && spectateTarget.id === ev.id) {
          recordHighlight("medal", 2, `${nameOf(ev.id)} earned ${ev.text ?? ""}`);
        }
        break;
      }
      case "unlock": {
        // A career-gated aircraft just unlocked (enough lifetime kills):
        // open it in the picker live and toast the milestone.
        if (ev.id === selfId) {
          hud.markUnlocked(ev.text);
          hud.message(`🚀 NEW AIRCRAFT UNLOCKED — ${escapeName(ev.text ?? "")}`, 5000, "#7bd4ff");
          analytics.event("aircraft_unlocked", { plane: ev.text });
        }
        hud.feed(`🚀 ${escapeName(nameOf(ev.id))} unlocked the ${escapeName(ev.text ?? "")}`);
        break;
      }
      case "radardown":
        if (!spectating && ev.team === selfTeam) {
          hud.message("RADAR DESTROYED — long-range contacts lost", 4000, "#e8a23a");
        } else if (!spectating) {
          hud.message("ENEMY RADAR DOWN — their map just went dark", 3000, "#7bff8a");
        }
        hud.feed(`${TEAM_INFO[ev.team].name} radar coverage is dark`);
        break;
      case "radarup":
        if (!spectating && ev.team === selfTeam) {
          hud.message("RADAR BACK ONLINE", 2500, "#7bff8a");
        }
        hud.feed(`${TEAM_INFO[ev.team].name} radar back on the air`);
        break;
      case "roundEnd": {
        recordHighlight("round", 0, ev.w === -1 ? "round end — draw" : `round end — ${TEAM_INFO[ev.w].name} wins`);
        const txt =
          ev.w === -1
            ? "ROUND DRAW"
            : !spectating && ev.w === selfTeam
              ? `${TEAM_INFO[ev.w].name} WINS — VICTORY!`
              : `${TEAM_INFO[ev.w].name} WINS THE ROUND`;
        hud.message(txt, 6000, ev.w === -1 ? "" : ev.w === 0 ? "#e84a4a" : "#4a7de8");
        if (net.joined) audio.fanfare(!spectating && ev.w === selfTeam);
        intel.show({
          winner: ev.w,
          mode: score.md,
          scoreToWin: navalTheater
            ? (config.naval?.scoreToWin ?? 800)
            : landTheater
              ? (config.land?.scoreToWin ?? 700)
              : armadaTheater
                ? (config.armada?.scoreToWin ?? 1400)
                : spaceTheater
                  ? (config.space?.scoreToWin ?? 900)
                  : config.match.scoreToWin,
          fleet: fleetHud(),
          roster,
          selfTeam,
          spectating,
        });
        if (!spectating) {
          const result = ev.w === -1 ? "draw" : ev.w === selfTeam ? "win" : "loss";
          analytics.event("round_end", {
            result,
            winner: ev.w === -1 ? "draw" : TEAM_INFO[ev.w].name,
            mode: score.md,
          });
          analytics.count(
            result === "win" ? "rounds_won" : result === "loss" ? "rounds_lost" : "rounds_drawn",
          );
          if (result === "win") bumpDaily("wins");
        }
        break;
      }
      case "roundStart":
        nemesisId = 0; // grudges don't survive the bell
        aceId = 0;
        scarsR.clear(); // a fresh round starts on a clean battlefield (roadmap #5)
        recordHighlight("round", 0, ev.text === "fleet" ? "round start — fleet action" : "round start — sky ring");
        intel.hide();
        intel.reset();
        hud.message(
          ev.text === "fleet"
            ? `FLEET ACTION — SINK THE ENEMY ${flagshipName()}!`
            : "NEW ROUND — FIGHT!",
          4000,
          "#f4c542",
        );
        break;
      case "chat":
        hud.chat(nameOf(ev.id), teamOf(ev.id), ev.text ?? "");
        break;
    }
  }

  // --- chat composer: Enter opens, Enter sends, Esc cancels ---
  let chatOpen = false;
  const chatInput = document.getElementById("chat-input");
  const closeChat = () => {
    chatOpen = false;
    hud.setChatOpen(false);
  };
  chatInput.addEventListener("keydown", (e) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      const text = chatInput.value.trim();
      if (text) {
        net.chat(text);
        analytics.count("chat_messages");
      }
      closeChat();
    } else if (e.key === "Escape") {
      closeChat();
    }
  });
  // Closing via click-away: flying resumes the moment focus leaves the box.
  chatInput.addEventListener("blur", closeChat);
  // Hull crew actions (naval conning / land fighting compartment), shared by
  // the keyboard (X/R/C/Z/T/G) and the tappable panel chips (mobile has no
  // keys). The wire fields are identical in both theaters — only the meaning
  // shifts: at sea R is damage control and T the repair party; on land R is
  // the field repair and T calls an artillery fire mission on the crosshair.
  const navalConning = () =>
    (hullTheater || armadaTheater) && !chatOpen && net.joined && !spectating && selfStatus === 4;
  // Which crew kit the driven hull carries: combined arms decides per hull
  // type, the pure theaters per theater (their rosters are single-class).
  const landCrew = () => (armadaTheater ? drivenIsLand() : landTheater);
  const navalCrew = () => (armadaTheater ? drivenIsShip() : navalTheater);
  const navalActions = {
    ammo() {
      if (landCrew()) {
        // AP ↔ HE only, and only on a gun that actually carries both.
        if (!(vehMainGun()?.heDamage > 0) || vehMainGun()?.heat) {
          hud.message("single ammunition nature loaded", 1100, "#9fb2c8");
          return;
        }
        navalAmmo = navalAmmo === 1 ? 0 : 1;
        hud.message(navalAmmo === 1 ? "LOADED: AP — sabot, beats armor" : "LOADED: HE — blast, wrecks soft targets", 1400, "#f4c542");
        return;
      }
      navalAmmo = (navalAmmo + 1) % 3; // HE → AP → SS → HE
      const msg = ["SHELLS: HE — high-explosive", "SHELLS: AP — armor-piercing", "SHELLS: SS — star shell (illumination)"][navalAmmo];
      hud.message(msg, 1400, navalAmmo === 2 ? "#ffe39a" : "#f4c542");
    },
    dc() {
      // Naval: damage-control party. Land: the field-repair crew (same wire).
      if (landCrew()) {
        if (perfNow() >= repairReadyAtClient) {
          dcPending = true;
          repairReadyAtClient = perfNow() + (crewCfg().repairCooldownSec ?? 40);
          hud.message("🔧 FIELD REPAIR — crew working…", 1800, "#7CFC7C");
        } else {
          hud.message("repair on cooldown…", 1000, "#9fb2c8");
        }
        return;
      }
      dcPending = true;
    },
    smoke() {
      if (navalCrew() && !config.naval?.smokeEnabled) {
        // Naval smoke screens are shelved for now (bots spammed them); no-op.
      } else if (perfNow() >= smokeReadyAtClient) {
        smokePending = true;
        smokeReadyAtClient = perfNow() + (crewCfg().smokeCooldownSec ?? 50);
        hud.message(landCrew() ? "SMOKE LAUNCHERS — popping smoke" : "SMOKE SCREEN — making smoke", 1500, "#c2ccd6");
      } else {
        hud.message("smoke on cooldown…", 1000, "#9fb2c8");
      }
    },
    zoom() {
      setNavalScope(!navalScoped);
    },
    repair() {
      // Naval: repair party. Land: call artillery on the gunsight point.
      if (landCrew()) {
        if (!(navalRangeLive > 0)) {
          hud.message("no aim point for the fire mission — range something", 1400, "#9fb2c8");
          return;
        }
        if (perfNow() >= artyReadyAtClient) {
          repairPending = true;
          // Only a short in-flight guard here: the server is the referee (it
          // can still refuse — the aim point can vanish from the input packet
          // mid-traverse) and a refused call must NOT lock the battery for the
          // full cooldown on a mission that never fired. The real cooldown is
          // armed by our own artycall broadcast coming back (see the event).
          artyReadyAtClient = perfNow() + 2.5;
          hud.message("📡 CALLING FIRE MISSION…", 1400, "#f4c542");
        } else {
          hud.message("battery reloading…", 1000, "#9fb2c8");
        }
        return;
      }
      if (perfNow() >= repairReadyAtClient) {
        repairPending = true;
        repairReadyAtClient = perfNow() + (crewCfg().repairCooldownSec ?? 80);
        hud.message("REPAIR PARTY — patching the hull", 1600, "#7CFC7C");
      } else {
        hud.message("repair on cooldown…", 1000, "#9fb2c8");
      }
    },
    boost() {
      if (perfNow() >= boostReadyAtClient) {
        boostPending = true;
        boostReadyAtClient = perfNow() + (crewCfg().boostCooldownSec ?? 90);
        boostActiveUntilClient = perfNow() + (crewCfg().boostDurSec ?? 15);
        hud.message(landCrew() ? "OVERDRIVE — engine redlined" : "ENGINE BOOST — all ahead flank", 1600, "#5ad0ff");
      } else {
        hud.message("boost on cooldown…", 1000, "#9fb2c8");
      }
    },
  };
  addEventListener("keydown", (e) => {
    if (!navalConning()) return;
    if (e.target.tagName === "INPUT") return;
    const act = {
      KeyX: "ammo", KeyR: "dc", KeyC: "smoke", KeyZ: "zoom", KeyT: "repair", KeyG: "boost",
    }[e.code];
    if (act) navalActions[act]();
  });
  // The conning-panel chips double as buttons (the touch path to X/R/T/G, and
  // a mouse convenience). hud routes taps here.
  hud.onNavalAction = (kind) => {
    if (navalConning() && navalActions[kind]) navalActions[kind]();
  };
  // Touch ZOOM button (the Z key's on-screen equivalent).
  input.zoomToggle = () => {
    if (navalConning()) navalActions.zoom();
  };

  // The scope's on-screen range-dial buttons: hold ▲/▼ to walk the aim range
  // out/in — the touch path to "aim for distance" (mouse and wheel also work).
  let navalScopeHold = 0;
  for (const [id, dir] of [["scope-rng-up", 1], ["scope-rng-dn", -1]]) {
    const el = document.getElementById(id);
    if (!el) continue;
    el.addEventListener("pointerdown", (e) => {
      e.preventDefault();
      try {
        el.setPointerCapture(e.pointerId); // keeps the hold if the thumb drifts
      } catch {} // synthetic pointers (tests) have no capturable id
      navalScopeHold = dir;
      el.classList.add("held");
    });
    const off = () => {
      if (navalScopeHold === dir) navalScopeHold = 0;
      el.classList.remove("held");
    };
    el.addEventListener("pointerup", off);
    el.addEventListener("pointercancel", off);
  }

  // Toggle the gunsight: punch the camera in along the gun line (the camera
  // branch below reads navalScoped) and show the scope reticle. Leaving the
  // hull drops the scope. Each land vehicle looks through its own optic —
  // see LAND_SCOPE_FOV — and the overlay wears a per-vehicle class so the
  // reticle matches the weapon.
  function setNavalScope(on) {
    navalScoped = on && (hullTheater || armadaTheater) && selfStatus === 4;
    const scope = document.getElementById("naval-scope");
    scope?.classList.toggle("hidden", !navalScoped);
    if (scope && landCrew()) {
      scope.classList.remove("scope-mbt", "scope-ifv", "scope-aaa", "scope-rr");
      if (navalScoped) {
        scope.classList.add(["scope-mbt", "scope-ifv", "scope-aaa", "scope-rr"][landVehIdx()] ?? "scope-mbt");
      }
    }
    // Body hook: while looking through the binocular gunsight the touch layout
    // yields the top-right minimap corner to the range dial (see style.css).
    document.body.classList.toggle("naval-scoped", navalScoped);
    if (!navalScoped) setCameraFov(72); // restore the wide view on exit
  }

  // The scroll wheel walks the gunsight's range dial — fast, precise "aim for
  // distance" that never fights the mouse's bearing control. One notch ≈ 7%.
  addEventListener(
    "wheel",
    (e) => {
      if (!navalScoped || !navalCrew()) return;
      e.preventDefault();
      if (navalScopeRange > 0 && e.deltaY) {
        navalScopeRange *= Math.exp(-Math.sign(e.deltaY) * 0.07);
      }
    },
    { passive: false },
  );

  // A land hull's ranging reach: the main gun's cap, stretched to the missile
  // envelope on a hull that carries one, with the artillery plot range as the
  // floor so every vehicle can range a fire mission.
  function landAimRangeMax() {
    const t = config.groundVehicles?.types?.[vehType()];
    let r = navalGunRange(vehMainGun());
    if (t?.atgm?.damage > 0) r = Math.max(r, t.atgm.rangeM ?? 0);
    return Math.max(r, config.land?.artyRangeM ?? 3200);
  }

  // The main battery's maximum reach (m): the configured cap, else the farthest
  // a shell can fly before its life expires. Mirrors navalGunRangeM in
  // internal/game/navalcombat.go so the sight's clamp is exactly the server's.
  function navalGunRange(mg) {
    if (!mg) return 0;
    if (mg.rangeM > 0) return mg.rangeM;
    const v = mg.muzzleSpeedMS ?? 0, life = mg.shellLifeSec ?? 0;
    if (!(v > 0) || !(life > 0)) return 0;
    const s = (FDM.GRAVITY * life) / (2 * v);
    if (s >= Math.SQRT1_2) return (v * v) / FDM.GRAVITY;
    return (v * v * Math.sin(2 * Math.asin(s))) / FDM.GRAVITY;
  }

  // Range to the point under the gunsight crosshair, in metres — the aim point
  // the main battery auto-elevates onto. Like World of Warships it snaps to an
  // enemy hull the line of sight passes over (a clean, steady range on a ship),
  // and otherwise falls back to where the sight meets the sea (so you can range
  // open water and lead a target onto it). A sight at or above the sea horizon
  // lashes out to the battery's MAXIMUM reach instead of going dead — that's
  // the over-the-horizon answer to a ship you can't quite see: put the sight on
  // the horizon and the salvo flies as far as the guns can throw (the impact
  // reticle on the water shows where). Sets navalRangeMax while clamped.
  function measureNavalRange(sightYaw, sightPitch) {
    navalRangeMax = false;
    const me = vehiclesR.positionOf(selfVehicleId);
    if (!me) return 0;
    // Land theater: the eye is the vehicle's own sight head, and the reach is
    // the gun's — or, for a hull whose long arm is the missile (the Warden),
    // the missile's, so the sight ranges everything the weapons can.
    const maxR = landTheater ? landAimRangeMax() : navalGunRange(vehMainGun());
    const ch = Math.cos(sightPitch);
    const dx = Math.cos(sightYaw) * ch, dy = Math.sin(sightPitch), dz = -Math.sin(sightYaw) * ch;
    const wl = config.world?.waterLevelM ?? 0;
    const eyeY = me.y + (landTheater ? LAND_SIGHT_Y : NAVAL_DIRECTOR_Y);
    // Snap to the nearest enemy hull the sight ray passes near (so the crosshair
    // on a ship reads that ship's range, no jitter).
    let bestT = Infinity;
    for (const [id, v] of vehiclesR.vehicles) {
      if (id === selfVehicleId || v.team === selfTeam || (v.hp ?? 0) <= 0) continue;
      const px = v.root.position.x - me.x, py = wl - eyeY, pz = v.root.position.z - me.z;
      const t = px * dx + py * dy + pz * dz; // distance along the ray to the closest approach
      if (t <= 0 || t >= bestT) continue;
      const ex = px - dx * t, ey = py - dy * t, ez = pz - dz * t; // miss vector
      const reach = (config.groundVehicles?.types?.[v.type]?.hullHalfLenM ?? 40) + 15;
      if (ex * ex + ey * ey + ez * ez <= reach * reach) bestT = t;
    }
    // Else the spot where the sight meets the sea (Infinity when it never does).
    const tWater = dy < -1e-4 ? (eyeY - wl) / -dy : Infinity;
    let tHit = Math.min(bestT, tWater);
    // An island in the line of sight ranges CLOSER than the sea plane hidden
    // behind it: march the heightfield along the ray so aiming at visible
    // land lays the guns on the slope (the flat naval arcs can't clear a tall
    // island anyway — the server bursts shells on terrain).
    const tCap = maxR > 0 ? maxR / Math.max(ch, 0.5) : 14000;
    const tLim = Math.min(tHit, tCap);
    const peakY = (config.world?.maxTerrainHeightM ?? 240) + 10;
    // Tank sights range terrain from 2 m off the ground: march much finer
    // than the naval spotter (whose islands are chunky).
    const step0 = landTheater ? 24 : 250;
    const stepM = landTheater ? 24 : 90;
    for (let d = step0; d < tLim; d += stepM) {
      const rayY = eyeY + dy * d;
      if (dy >= 0 && rayY > peakY) break; // climbed above any terrain
      if (field.surfaceAt(me.x + dx * d, me.z + dz * d) > rayY) {
        tHit = d;
        break;
      }
    }
    let r = tHit * ch;
    if (maxR > 0 && r >= maxR - 2) { // small tolerance: the scope's range dial derives pitch from the cap itself
      navalRangeMax = true;
      r = Math.min(r, maxR);
    }
    return Number.isFinite(r) ? r : 0;
  }

  // Barrel elevation (rad) that lands a shell at horizontal range R for muzzle
  // speed v0 — the low root of R = (v0²/g)·sin(2θ). Mirrors elevationForRange in
  // internal/game/navalcombat.go so the predicted aim matches the server.
  function navalElevation(rangeM, muzzleMS) {
    if (!(muzzleMS > 0) || rangeM <= 0) return 0;
    const s = (FDM.GRAVITY * rangeM) / (muzzleMS * muzzleMS);
    return s >= 1 ? Math.PI / 4 : 0.5 * Math.asin(s);
  }
  function clampPitch(p) { return Math.min(1.45, Math.max(-0.1, p)); }

  // The 8-wind compass point of the world offset from the local hull to (tx,tz),
  // in the game's yaw convention (atan2(-(dz), dx)). Drives the threat-direction
  // hints (P3 "DETECTED", N3 torpedoes). "" when we have no hull to reference.
  const COMPASS_8 = ["E", "NE", "N", "NW", "W", "SW", "S", "SE"];
  function compassTo(tx, tz) {
    const me = vehiclesR.positionOf(selfVehicleId);
    if (!me) return "";
    const ang = Math.atan2(-(tz - me.z), tx - me.x);
    const i = ((Math.round(ang / (Math.PI / 4)) % 8) + 8) % 8;
    return COMPASS_8[i];
  }

  // The nearest rendered enemy warship to the local hull (concealment means any
  // rendered enemy is a live, spotted threat), or null. For the P3 threat bearing.
  function nearestRenderedEnemy() {
    const me = vehiclesR.positionOf(selfVehicleId);
    if (!me) return null;
    let best = null, bestD2 = Infinity;
    for (const [id, v] of vehiclesR.vehicles) {
      if (id === selfVehicleId || v.team === selfTeam || (v.hp ?? 0) <= 0) continue;
      const dx = v.root.position.x - me.x, dz = v.root.position.z - me.z;
      const d2 = dx * dx + dz * dz;
      if (d2 < bestD2) { bestD2 = d2; best = v; }
    }
    return best;
  }

  // P4a shoal warning: ray-march the water ahead of the driven hull (like the
  // server's clearRange feeler) and warn before she noses a shoal, so grounding
  // isn't a silent trap that only tells you once you're already stuck. Throttled;
  // recomputed off navalShoalAt. Returns true if land is inside the look-ahead.
  let navalShoalAt = 0;
  let navalShoalAhead = false;
  // Land twin of the shoal warning: a hull ordered forward that isn't moving
  // is grinding against a treeline, building, or wall-grade flank — which
  // otherwise reads as a silent 0 km/h. Timestamp of when the grind started.
  let landBlockedSince = 0;
  function shoalAheadCheck(hullYaw) {
    const me = vehiclesR.positionOf(selfVehicleId);
    if (!me) return false;
    const wl = config.world?.waterLevelM ?? 0;
    const fx = Math.cos(hullYaw), fz = -Math.sin(hullYaw);
    const spd = config.groundVehicles?.types?.[vehType()]?.speedMS ?? 16;
    const reach = Math.max(240, spd * 15); // ~15 s of way at flank
    for (let d = 80; d <= reach; d += 80) {
      if (field.surfaceAt(me.x + fx * d, me.z + fz * d) > wl) return true;
    }
    return false;
  }

  const zoneLetter = (i) => String.fromCharCode(65 + i); // 0 -> A, 1 -> B, ...

  // N8: the one-line fleet objective — the nearest capture zone your team doesn't
  // hold, with a bearing and range, surfaced on spawn and on every zone flip so a
  // human joining the AI divisions gets an order like the bots already follow.
  function fleetOrderHint() {
    const me = vehiclesR.positionOf(selfVehicleId);
    if (!me || !navalZoneCenters.length) return "";
    const holders = score?.zs ?? [];
    // A tank reads a platoon order, a warship a fleet order — and neither is
    // ever pointed at a zone its class can't take (the armada wire carries
    // l/n cap flags per zone; the single-class theaters omit them, so every
    // zone stays orderable there).
    const order = landCrew() ? "PLATOON ORDER" : "FLEET ORDER";
    let best = -1, bestD = Infinity;
    for (let i = 0; i < navalZoneCenters.length; i++) {
      if (holders[i] === selfTeam) continue; // already ours
      const c = navalZoneCenters[i];
      if ((c.l || c.n) && !(landCrew() ? c.l : c.n)) continue; // not our class's fight
      const d = Math.hypot(c.x - me.x, c.z - me.z);
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best < 0) return `${order} — HOLD THE LINE, all zones are ours`;
    const c = navalZoneCenters[best];
    return `${order} — TAKE ZONE ${zoneLetter(best)} (${compassTo(c.x, c.z)} ${(bestD / 1000).toFixed(1)} km)`;
  }

  // N7: which capture zone (if any) the local hull is inside, for the status
  // banner. Returns the index or -1.
  function hullZoneIndex() {
    const me = vehiclesR.positionOf(selfVehicleId);
    if (!me || !navalZoneCenters.length) return -1;
    const rDefault =
      ((landTheater || armadaTheater) ? config.land?.zoneRadiusM : config.naval?.zoneRadiusM) ?? 650;
    for (let i = 0; i < navalZoneCenters.length; i++) {
      const c = navalZoneCenters[i];
      // Armada zones are authored at their own radii (Kilnfeld's tight 420 m
      // town ring up to Fort Brick's 1200 m water); the wire carries each.
      const r = c.r || rDefault;
      if (Math.hypot(c.x - me.x, c.z - me.z) <= r) return i;
    }
    return -1;
  }

  // The scoped camera FOV (vertical, degrees) — the camera punch-in reads this
  // same constant. Drives the calibrated reticle notches. 7° over the old 11°:
  // WoWS-grade magnification, enough to lay on a hull at the guns' full reach.
  const NAVAL_SCOPE_FOV = 7;
  // Extra windage damping through the gunsight, on top of the FOV-scaled
  // sensitivity. FOV scaling alone keeps the sight at full screen-speed under
  // magnification, where laying on a hull is an arc-minute job — every mouse
  // tremor threw the crosshair off the target. Half speed makes the lay stick.
  const NAVAL_SCOPE_WINDAGE = 0.5;
  // The nominal target speed (m/s) one lead tick is calibrated to — a hull at
  // roughly two-thirds flank, so "lead N ticks" reads as a learnable skill.
  const NAVAL_LEAD_MS = 20;

  // The reticle's lead notches (WoWS-style ruler): built once, laid out every
  // scope frame. Minor notch every half lead unit, numbered major every unit,
  // out to ±5 units.
  let scopeNotches = null;
  function buildScopeNotches() {
    const ruler = document.getElementById("sr-ruler");
    if (!ruler) return null;
    const out = [];
    for (let side = -1; side <= 1; side += 2) {
      for (let k = 0.5; k <= 5; k += 0.5) {
        const n = document.createElement("span");
        const major = Number.isInteger(k);
        n.className = "sr-notch" + (major ? " major" : "");
        if (major) n.dataset.n = String(k);
        ruler.appendChild(n);
        out.push({ el: n, u: k * side });
      }
    }
    return out;
  }

  // Paint the gunsight rangefinder: the live range to the spot under the
  // crosshair (the guns auto-lay onto it), plus the shell flight time to it
  // (G4), and lay the reticle's lead scale so one numbered unit = the
  // on-screen distance a hull at NAVAL_LEAD_MS covers during one shell flight
  // (G5). "— —" when there's no aim point.
  function updateScopeRange() {
    const el = document.getElementById("scope-range");
    if (!el) return;
    scopeNotches ??= buildScopeNotches();
    if (!(navalRangeLive > 0)) {
      el.innerHTML = `RNG <span class="nolock">— —</span>`;
      if (scopeNotches) for (const n of scopeNotches) n.el.style.display = "none";
      return;
    }
    // The scoped hull's ballistics: the warship battery, the tank cannon, or
    // (a hull with no cannon, e.g. the Warden) its autocannon caliber.
    const mg = vehMainGun();
    const muzzle =
      mg?.muzzleSpeedMS ?? (landCrew() ? calByName.get(vehProf()?.caliber)?.muzzleSpeedMS ?? 0 : 0);
    let flightT = 0;
    if (muzzle > 0) {
      const th = navalElevation(navalRangeLive, muzzle);
      flightT = navalRangeLive / (muzzle * Math.max(0.2, Math.cos(th)));
    }
    // Land ranges read in whole metres (a tank duel cares about 1240 m, not
    // "1.2 km"); the sea reads in kilometres.
    const rngLabel = landTheater
      ? `RNG ${Math.round(navalRangeLive)} m`
      : `RNG ${(navalRangeLive / 1000).toFixed(1)} km`;
    el.innerHTML =
      rngLabel +
      (flightT > 0 ? ` &middot; ${flightT.toFixed(1)}s` : "") +
      (navalRangeMax
        ? ` <span class="max">MAX</span>`
        : ` <span class="set">AUTO</span>`);

    // Lay the ruler: pxPerM at range R under the scoped FOV is half the
    // viewport height over tan(FOV/2)·R; one lead unit is NAVAL_LEAD_MS ×
    // flight time of metres. Notches beyond the scale's edge are hidden.
    if (scopeNotches && flightT > 0) {
      const ruler = document.getElementById("sr-ruler");
      const half = (ruler?.parentElement?.clientWidth ?? 600) / 2;
      const vh = window.innerHeight || 720;
      const fovNow = landCrew() ? (LAND_SCOPE_FOV[landVehIdx()] ?? 8) : NAVAL_SCOPE_FOV;
      const pxPerM = (vh / 2) / (Math.tan((fovNow / 2) * Math.PI / 180) * navalRangeLive);
      const unitPx = NAVAL_LEAD_MS * flightT * pxPerM;
      for (const n of scopeNotches) {
        const off = n.u * unitPx;
        if (Math.abs(off) > half - 8) {
          n.el.style.display = "none";
          continue;
        }
        n.el.style.display = "";
        n.el.style.left = `calc(50% + ${off.toFixed(1)}px)`;
      }
    }
  }

  addEventListener("keydown", (e) => {
    if (e.key !== "Enter" || chatOpen || !net.joined || spectating) return;
    if (e.target.tagName === "INPUT") return;
    e.preventDefault();
    chatOpen = true;
    // Held flight keys would never see their keyup while the input has
    // focus, so release them rather than leave the stick pinned.
    input.keys.clear();
    hud.setChatOpen(true);
  });

  // Spectator: N / P (or a click) cycle the followed view across every
  // vantage point — planes (humans and bots alike), live gun mounts (land
  // flak, carrier deck guns, battleship turrets), and each ship's bridge.
  function spectateTargets() {
    const out = [];
    for (const [id, ent] of entities) {
      if (ent.status !== 0) out.push({ kind: "plane", id });
    }
    for (const [id, g] of aaGuns.guns) {
      if (g.alive && g.kind !== 3) out.push({ kind: "gun", id }); // installations are unarmed
    }
    for (const [id, sh] of ships.ships) {
      if (sh.alive) out.push({ kind: "ship", id });
    }
    // The driven hulls are vehicles, not the anchored fleet — add them so the
    // spectator/director has something to follow instead of orbiting empty
    // terrain. This is every hull theater: warships at sea (naval), tanks on the
    // steppe (land), and both in the combined-arms armada. Leaving LAND out here
    // was why the director looked broken ashore — the tanks that ARE the action
    // were never candidates, so the camera flickered on the lull fallback
    // instead of riding the brawl the way it does at sea.
    if (hullTheater || armadaTheater) {
      for (const [id, v] of vehiclesR.vehicles) {
        if ((v.hp ?? 0) > 0) out.push({ kind: "vehicle", id });
      }
    }
    return out;
  }

  function spectateLabel(t) {
    if (t.kind === "vehicle") {
      const v = vehiclesR.vehicles.get(t.id);
      const nm = config.groundVehicles?.types?.[v?.type ?? 0]?.name ?? "warship";
      return `${TEAM_INFO[v?.team ?? 0].name} ${nm}`;
    }
    if (t.kind === "ship") {
      const site = ships.ships.get(t.id)?.site;
      return `${TEAM_INFO[site?.team ?? 0].name} ${SHIP_NAMES[site?.kind ?? 0]} bridge`;
    }
    if (t.kind === "gun" && aaGuns.guns.get(t.id)?.occupied) {
      return `${nameOf(t.id)} (crewed)`;
    }
    return nameOf(t.id);
  }

  // Snap the orbit back to the default behind-the-plane chase pose.
  function recenterSpectate() {
    specOrbit.yaw = 0;
    specOrbit.pitch = 0;
  }

  // Snap the spectator onto a specific subject, resetting the follow camera so
  // the new framing starts clean. Shared by manual cycling and the director.
  function focusSpectate(t) {
    specAim.has = false;
    specFollow.reset();
    specLookFollow.reset();
    recenterSpectate(); // fresh framing for the new subject
    spectateTarget = t;
    if (t) hud.message(`following ${spectateLabel(t)}`, 1500);
  }

  function cycleSpectate(dir) {
    const targets = spectateTargets();
    if (!targets.length) {
      focusSpectate(null);
      return;
    }
    const cur = targets.findIndex(
      (t) => t.kind === spectateTarget?.kind && t.id === spectateTarget?.id,
    );
    focusSpectate(
      cur === -1
        ? targets[dir > 0 ? 0 : targets.length - 1]
        : targets[(cur + dir + targets.length) % targets.length],
    );
  }

  // --- auto-director ---------------------------------------------------------
  const heatKey = (t) => `${t.kind}:${t.id}`;

  // An event worth pointing the camera at: bump an actor's heat so it scores
  // higher for the next few seconds. `cut` asks for an immediate cut (a kill).
  function directorBump(kind, id, amount, cut = false) {
    if (!autoDirector || id == null) return;
    const k = `${kind}:${id}`;
    director.heat.set(k, (director.heat.get(k) || 0) + amount);
    if (cut) {
      director.cutNow = true;
      music?.accent(); // a stinger over the groove when a kill cuts the camera
    }
  }

  // Position-only events (a torpedo hit, a bomb on a hull) heat the nearest
  // ship so naval action pulls the camera to the right vantage.
  function directorBumpShipAt(pos, amount, cut = false) {
    if (!autoDirector || !pos) return;
    let nid = null;
    let nd = Infinity;
    for (const [id] of ships.ships) {
      const sp = ships.positionOf(id);
      if (!sp) continue;
      const d = (sp.x - pos.x) ** 2 + (sp.z - pos.z) ** 2;
      if (d < nd) {
        nd = d;
        nid = id;
      }
    }
    if (nid != null) directorBump("ship", nid, amount, cut);
  }

  // World position of a vantage, for centring the establishing orbit.
  function targetPos(t) {
    if (!t) return null;
    if (t.kind === "plane") return entities.get(t.id)?.buffer.at(-1)?.pos ?? null;
    if (t.kind === "ship") return ships.positionOf(t.id);
    if (t.kind === "vehicle") return vehiclesR.positionOf(t.id);
    return aaGuns.positionOf(t.id);
  }

  // Score a vantage by how much action is on it right now.
  function directorScore(t, heat) {
    if (t.kind === "plane") {
      const ent = entities.get(t.id);
      if (!ent || ent.status === 0 || !ent.buffer.length) return -1;
      const p = ent.buffer.at(-1).pos;
      const team = teamOf(t.id);
      // Threats bearing down: sum enemy closeness (0..1 inside 600 m) and note
      // the nearest, so a plane with foes in gun range reads as a live fight.
      let cluster = 0;
      let nearest = Infinity;
      for (const [oid, oe] of entities) {
        if (oid === t.id || oe.status === 0 || !oe.buffer.length) continue;
        if (teamOf(oid) === team) continue;
        const d = p.distanceTo(oe.buffer.at(-1).pos);
        if (d < nearest) nearest = d;
        if (d < 600) cluster += 1 - d / 600;
      }
      return 0.2 + cluster * 2 + (nearest < 250 ? 1.5 : 0) + heat;
    }
    if (t.kind === "ship") {
      return ships.ships.get(t.id)?.alive ? 0.1 + heat : -1;
    }
    if (t.kind === "vehicle") {
      const v = vehiclesR.vehicles.get(t.id);
      if (!v || (v.hp ?? 0) <= 0) return -1;
      // A warship reads as a live fight when enemy hulls are in gun range and it
      // (or a neighbour) is firing — so the director rides the actual brawl.
      const p = v.root.position;
      let cluster = 0;
      for (const [oid, ov] of vehiclesR.vehicles) {
        if (oid === t.id || ov.team === v.team || (ov.hp ?? 0) <= 0) continue;
        const d = Math.hypot(ov.root.position.x - p.x, ov.root.position.z - p.z);
        if (d < 6000) cluster += 1 - d / 6000;
      }
      return 0.3 + cluster * 1.5 + (v.firing ? 1 : 0) + heat;
    }
    return aaGuns.guns.get(t.id)?.alive ? 0.05 + heat : -1;
  }

  // Pull back to a high, slow orbit looking down on the hottest area — a
  // breather between close follows. Drives the camera for the shot's length.
  function startWideShot(now, center) {
    director.wideCenter.copy(center);
    director.wideUntil = now + DIRECTOR_WIDE_LEN;
    director.lastWide = now;
    director.wideAngle = Math.random() * Math.PI * 2;
    director.since = now; // the close follow restarts fresh afterwards
    spectateTarget = null; // dropped now; the next tick re-picks the subject
  }

  function directorWideShot(dt) {
    director.wideAngle += dt * 0.18;
    const c = director.wideCenter;
    camera.position.set(
      c.x + Math.cos(director.wideAngle) * 420,
      c.y + 300,
      c.z + Math.sin(director.wideAngle) * 420,
    );
    camera.up.set(0, 1, 0);
    camera.lookAt(c.x, c.y, c.z);
  }

  // Cut the founder cam to a hull going down at `pos` and hold it there. Returns
  // whether it took the frame — the sink is only worth logging as a highlight if
  // it did (a sink we actually filmed, not one merely tallied off-screen). While
  // one founder is playing we ignore a second: we can only point at one hull, so
  // the reel never captions a sinking the tape doesn't show.
  function directorCutToSink(pos) {
    if (!autoDirector || !spectating || !pos) return false;
    const now = perfNow();
    if (now < director.sinkUntil - 1) return false; // already riding a founder
    director.sinkPos.copy(pos);
    director.sinkUntil = now + DIRECTOR_SINK_LEN;
    director.sinkAngle = Math.random() * Math.PI * 2;
    director.wideUntil = 0; // an establishing orbit doesn't outrank a sinking
    director.since = now; // the next close follow restarts fresh afterwards
    spectateTarget = null; // hand the frame to the founder cam
    music?.accent(); // a stinger over the groove as she goes down
    return true;
  }

  // Drive the founder cam: a slow orbit that eases in and lifts as the hull
  // settles, looking a touch below the surface so the descending wreck stays
  // framed the whole way under. Drives the camera for the shot's length.
  function directorSinkShot(dt) {
    const c = director.sinkPos;
    director.sinkAngle += dt * 0.25;
    const t = Math.max(0, (director.sinkUntil - perfNow()) / DIRECTOR_SINK_LEN); // 1 -> 0
    const radius = 80 + t * 70; // pushes in as she settles
    const height = 34 + (1 - t) * 34; // and lifts for the final plunge
    camera.position.set(
      c.x + Math.cos(director.sinkAngle) * radius,
      c.y + height,
      c.z + Math.sin(director.sinkAngle) * radius,
    );
    camera.up.set(0, 1, 0);
    camera.lookAt(c.x, c.y - 8, c.z);
    return true;
  }

  // --- duel coverage (shot / reverse-shot on a tail-chase) -------------------
  // Its own smoothing (reset on every cut so a flip is a hard cut, not a pan)
  // and scratch — kept off the shared tmp/spec vectors so the two subjects don't
  // clobber each other between interpolateRemote calls.
  const coverFollow = new Follower();
  const duAPos = new THREE.Vector3(), duAQuat = new THREE.Quaternion();
  const duBPos = new THREE.Vector3(), duBQuat = new THREE.Quaternion();
  const duFwd = new THREE.Vector3(), duSide = new THREE.Vector3();
  const duCamPos = new THREE.Vector3(), duLook = new THREE.Vector3();
  const shotLen = () => DIRECTOR_SHOT_MIN + Math.random() * (DIRECTOR_SHOT_MAX - DIRECTOR_SHOT_MIN);

  // Copy a flying plane's interpolated pose into out-vectors (false if it isn't a
  // plane in the air right now). Copies out because interpolateRemote reuses
  // shared scratch — two live calls would otherwise alias.
  function planePoseInto(id, outPos, outQuat) {
    const e = entities.get(id);
    if (!e || e.status === 0 || e.status > 2 || !e.buffer.length) return false;
    const s = interpolateRemote(e, renderT);
    if (!s) return false;
    outPos.copy(s.pos);
    outQuat.copy(s.quat);
    return true;
  }

  // Because the director renders on a lag, the next kill is already sitting in
  // the (un-fired) event queue. A few seconds before it lands on screen, open
  // coverage on the exact pair that WILL score — no live "is this a real duel?"
  // guess, no persistence/closure heuristic to tune: we know who dies and when,
  // so we cover only fights that pay off, straight from the recorded facts.
  function directorConsiderDuel(now) {
    if (director.cover || now < director.coverCoolUntil || score.ph !== "play") return false;
    if (timeOffset === null) return false;
    const renderNow = perfNow() + timeOffset - DIRECTOR_LAG;
    for (const q of dirEventQ) {
      const dt = q.t - renderNow; // sim-seconds until this event renders
      if (dt < 0) continue;
      if (dt > DIRECTOR_LOOKAHEAD) break; // the queue is time-ordered — nothing sooner remains
      const ev = q.ev;
      if (ev.e !== "kill" || !ev.k || !ev.v || isAAId(ev.k)) continue;
      if (!entities.has(ev.k) || !entities.has(ev.v)) continue; // both already on screen
      enterCover(ev.k, ev.v, dt, now);
      return true;
    }
    return false;
  }

  function enterCover(killerId, victimId, killIn, now) {
    director.cover = {
      aId: killerId, bId: victimId, // the shooter and the bird it's about to down
      shot: "A", // open on the aggressor working the gun
      shotUntil: now + shotLen(),
      killDueAt: now + killIn, // when the kill will render — hold the pair until then
      hardUntil: now + killIn + 2, // safety net if the kill event never fires
      killed: false, killedAt: 0, wreck: new THREE.Vector3(), orbitA: 0,
    };
    director.wideUntil = 0; // a known kill outranks an establishing orbit
    director.since = now;
    coverFollow.reset(); // snap onto the opening angle
    spectateTarget = null; // the coverage owns the camera, not the follow cam
  }

  function exitCover(now) {
    director.cover = null;
    director.coverCoolUntil = now + 1.5; // a beat before the next duel can grab the camera
    director.since = now;
    director.nextTick = 0; // repick a vantage immediately, no one-frame orbit gap
    spectateTarget = null;
  }

  // A filmed air-to-air kill: hand the coverage its ending. If we were already
  // covering this exact pair, roll straight into the wreck beat; otherwise open a
  // fresh coverage on the victim so even an uncovered kill gets its fall shot.
  function directorCoverKill(killerId, victimId) {
    if (!autoDirector || !spectating || score.ph !== "play") return;
    const now = perfNow();
    const ve = entities.get(victimId);
    if (!ve || !ve.buffer.length) return;
    const c = director.cover;
    const onThisPair = c && !c.killed && c.aId === killerId && c.bId === victimId;
    if (!onThisPair) {
      director.cover = {
        aId: killerId, bId: victimId, shot: "B",
        shotUntil: now, hardUntil: now + DIRECTOR_DUEL_MAX,
        killed: false, killedAt: 0, wreck: new THREE.Vector3(), orbitA: 0,
      };
      director.wideUntil = 0;
    }
    const k = director.cover;
    k.killed = true;
    k.killedAt = now;
    k.wreck.copy(ve.buffer.at(-1).pos);
    k.orbitA = Math.atan2(camera.position.z - k.wreck.z, camera.position.x - k.wreck.x);
    director.cutNow = false; // the coverage owns the aftermath; ignore the kill's bump
  }

  // Drive duel coverage; returns false when the engagement's over (so the frame
  // loop falls through to the normal director that same frame).
  function directorCoverShot(dt) {
    const c = director.cover;
    const now = perfNow();
    if (c.killed) {
      if (now > c.killedAt + DIRECTOR_WRECK_LEN) { exitCover(now); return false; }
      // Ride the wreck: track the tumbling airframe while its entity lives, then
      // hold its last spot. A slow pull-out orbit — the money shot.
      const ve = entities.get(c.bId);
      if (ve && ve.status !== 0 && ve.buffer.length) c.wreck.copy(ve.buffer.at(-1).pos);
      c.orbitA += dt * 0.5;
      duCamPos.set(
        c.wreck.x + Math.cos(c.orbitA) * 50,
        c.wreck.y + 22,
        c.wreck.z + Math.sin(c.orbitA) * 50,
      );
      duLook.copy(c.wreck);
      driveCoverCam(dt);
      return true;
    }
    // Both must still be flying, or one bailed/collided before the kill lands.
    const aOK = planePoseInto(c.aId, duAPos, duAQuat);
    const bOK = planePoseInto(c.bId, duBPos, duBQuat);
    if (!aOK || !bOK || now > c.hardUntil) { exitCover(now); return false; }
    // Don't bail on separation before the kill is due — we KNOW they re-converge
    // for it (the kill's already in the buffer); only a genuine post-kill drift
    // (or a kill that never came) ends it early.
    if (now > c.killDueAt + 1 && duAPos.distanceTo(duBPos) > DIRECTOR_DUEL_EXIT) { exitCover(now); return false; }
    if (now > c.shotUntil) {
      c.shot = c.shot === "A" ? "B" : "A"; // the reverse
      c.shotUntil = now + shotLen();
      coverFollow.reset(); // a cut, not a pan
    }
    if (c.shot === "A") frameAttacker();
    else frameDefender();
    driveCoverCam(dt);
    return true;
  }

  // Attacker single: over the shoulder, the shooter prominent with the victim
  // downrange in the same look.
  function frameAttacker() {
    duFwd.set(1, 0, 0).applyQuaternion(duAQuat);
    duSide.set(-duFwd.z, 0, duFwd.x).normalize();
    duCamPos.copy(duAPos).addScaledVector(duFwd, -30).addScaledVector(duSide, 13);
    duCamPos.y += 12;
    duLook.copy(duAPos).lerp(duBPos, 0.28); // attacker held, prey ahead
  }

  // Defender reverse: ahead of the hunted plane looking back, so it faces camera
  // with its pursuer charging in behind — the opposite angle to the attacker cam.
  function frameDefender() {
    duFwd.set(1, 0, 0).applyQuaternion(duBQuat);
    duSide.set(-duFwd.z, 0, duFwd.x).normalize();
    duCamPos.copy(duBPos).addScaledVector(duFwd, 32).addScaledVector(duSide, 17);
    duCamPos.y += 9;
    duLook.copy(duBPos).lerp(duAPos, 0.18); // defender held, pursuer looming behind
  }

  // Common tail of the cover cams: keep the eye out of the terrain, smooth it
  // (reset on cuts), and look. Orbit/wreck sets duCamPos/duLook itself first.
  function driveCoverCam(dt) {
    const g = field.surfaceAt(duCamPos.x, duCamPos.z) + 5;
    if (duCamPos.y < g) duCamPos.y = g;
    coverFollow.update(camera.position, duCamPos, 8, dt, 160);
    camera.up.set(0, 1, 0);
    camera.lookAt(duLook);
  }

  // Pick the hottest vantage, holding the current one until the dwell elapses
  // (or a much hotter fight — or a fresh kill — demands an early cut). On a
  // long hold or a routine cut while the action is high, pull back for a wide
  // establishing orbit instead.
  function directorTick(now, force = false) {
    // Heat fades with a ~4 s half-life so old fights don't pin the camera.
    const dt = now - director.lastTick;
    director.lastTick = now;
    if (dt > 0) {
      const decay = Math.exp(-dt / 4);
      for (const [k, v] of director.heat) {
        const nv = v * decay;
        if (nv < 0.02) director.heat.delete(k);
        else director.heat.set(k, nv);
      }
    }

    const cands = spectateTargets();
    if (!cands.length) {
      focusSpectate(null);
      return;
    }
    // A tight tail-chase steals the camera into shot/reverse coverage (directorCoverShot
    // then owns the frame). Only from a normal tick — a forced repick is a subject
    // that just vanished, no time to set up a duel.
    if (!force && directorConsiderDuel(now)) return;
    const cur = spectateTarget;
    const sameAsCur = (t) => cur && t.kind === cur.kind && t.id === cur.id;
    // Best overall, and best that isn't the current subject (for forced variety
    // when a single subject has held too long).
    let best = null;
    let bestScore = -Infinity;
    let other = null;
    let otherScore = -Infinity;
    for (const t of cands) {
      const s = directorScore(t, director.heat.get(heatKey(t)) || 0);
      if (s > bestScore) {
        bestScore = s;
        best = t;
      }
      if (!sameAsCur(t) && s > otherScore) {
        otherScore = s;
        other = t;
      }
    }
    director.intensity = Math.max(0, bestScore); // feeds the soundtrack drive
    const isCur = best && sameAsCur(best);
    const curScore = cur ? directorScore(cur, director.heat.get(heatKey(cur)) || 0) : -Infinity;
    const held = now - director.since;
    const cutNow = director.cutNow;
    director.cutNow = false;

    // A long single-subject hold (or a routine cut while action is high) is a
    // good moment to pull back for a wide establishing orbit of the fight.
    if (
      best &&
      now - director.lastWide > DIRECTOR_WIDE_EVERY &&
      bestScore > 1 &&
      (held > DIRECTOR_MAX_HOLD || (!isCur && held > DIRECTOR_BASE_DWELL))
    ) {
      const c = targetPos(best);
      if (c) {
        startWideShot(now, c);
        return;
      }
    }

    // Held a subject past the cap with no scene change due: cut to the next
    // best so the stream doesn't lock onto one plane forever.
    if (!force && isCur && held > DIRECTOR_MAX_HOLD && other) {
      focusSpectate(other);
      director.since = now;
      return;
    }

    const dwell = curScore < DIRECTOR_COLD ? DIRECTOR_MIN_HOLD : DIRECTOR_BASE_DWELL;
    const cut =
      force ||
      curScore < 0 || // current subject is gone
      (held > DIRECTOR_MIN_HOLD && cutNow) || // a kill broke out elsewhere
      (!isCur && held > dwell) || // held the planned time
      (!isCur && held > DIRECTOR_MIN_HOLD && bestScore > curScore + DIRECTOR_MARGIN);
    if (best && cut && !isCur) {
      focusSpectate(best);
      director.since = now;
    }
  }

  addEventListener("keydown", (e) => {
    if (!spectating) return;
    if (e.code === "KeyN") cycleSpectate(1);
    else if (e.code === "KeyP") cycleSpectate(-1);
    else if (e.code === "KeyC") recenterSpectate();
  });

  // --- input sampling & sending ---
  // Flight inputs are sampled inside the physics loop, every TICKS_PER_INPUT
  // ticks, and the prediction integrates exactly what was sent. The wire
  // values are rounded the same way net.sendInput rounds them, so the replay
  // history matches the controls the server parses bit-for-bit.
  const round3 = (v) => Math.round(v * 1000) / 1000;
  function sendFlightInput() {
    const c = input.controls();
    predCtl.pitch = round3(c.pitch);
    predCtl.roll = round3(c.roll);
    predCtl.yaw = round3(c.yaw);
    predCtl.throttle = round3(c.throttle);
    // Jets era: the launch/flare holds ride the same input message (server
    // edge-detects). The space theater borrows the launch hold for its ASAT
    // rail. Inert fields everywhere else — never sent.
    predCtl.msl = (jetsEra || spaceTheater) && (input.msl || perfNow() < spaceMslPulseUntil);
    predCtl.flare = jetsEra && input.flare;
    // Space: hands on the stick take the gyros off the line (mirrored
    // server-side), and the hold selection rides the same message — along
    // with the consumable pulses (C chaff on the smoke field, T cold run on
    // the repair field: the naval wire, the naval idiom, the vacuum verbs).
    if (spaceTheater) {
      if (Math.abs(predCtl.pitch) > 0.15 || Math.abs(predCtl.roll) > 0.15 || Math.abs(predCtl.yaw) > 0.15) {
        spaceHold = 0;
      }
      predCtl.sh = spaceHold;
      predCtl.smoke = spaceDecoyPending;
      predCtl.repair = spaceColdPending;
      // The gun ring's order rides the flight message, because out here it
      // IS a flight input: the mount is slewing while the hull flies, and
      // the server walks its own copy of the ring toward the same bearing
      // (stepSpaceTurret). Rounds leave along the SERVER's mount, so this
      // field is what actually decides where your tracers go.
      predCtl.gunAim = !spectating && haveSpawned && (selfStatus === 1 || selfStatus === 2);
      predCtl.gunYaw = spaceGunAimYaw;
      predCtl.gunPitch = spaceGunAimPitch;
      // The director's engine order outranks the lever ON THE WIRE: the
      // lever is intent (speed along the gaze) and the director translates
      // it into burns — server and prediction fly the same translated
      // order. -1 means no director (raw mode, landed, gyro hold): the
      // lever goes out as the throttle it always was.
      if (spaceEngineCmd >= 0) predCtl.throttle = round3(spaceEngineCmd);
    }
    inputSeq++;
    // Space GUNS discipline: while the director is laying the nose on a
    // fire-control mark, the held trigger reaches the server only once the
    // bore agrees (spaceFireOK) — everywhere else it passes untouched.
    net.sendInput(predCtl, input.trigger && spaceFireOK, input.bomb, inputSeq);
    if (spaceTheater) {
      spaceDecoyPending = false; // a countermeasure request is a single pulse
      spaceColdPending = false;
      predCtl.smoke = predCtl.repair = false;
    }
    // ticks counts how many physics steps the prediction has actually run
    // with this input — the snapshot can land mid-input, and the replay must
    // rebuild exactly the locally-predicted present, no further.
    inputHistory.push({ seq: inputSeq, ctl: { ...predCtl }, ticks: 0 });
    if (inputHistory.length > 90) inputHistory.shift();
    lastInputSentAt = perfNow();
  }

  // Everything that isn't predicted flight still sends on a timer: turret
  // slew, the dead/respawn wait, and a throttled background tab — these feed
  // the server's read deadline and (for turrets) the slew-rate inputs.
  setInterval(() => {
    if (!net.joined || spectating) return;
    if (selfStatus === 3) {
      // Turret mode: pitch/yaw are slew-rate inputs (composed each frame),
      // no replay history — the gun has no client-side flight model.
      inputSeq++;
      net.sendInput(
        { pitch: gunCtl.pitch, roll: 0, yaw: gunCtl.yaw, throttle: 0 },
        input.trigger,
        false,
        inputSeq,
      );
      return;
    }
    if (selfStatus === 4) {
      // Driving: throttle drives, roll steers, pitch/yaw slew the mount. Hull
      // theaters also carry the loaded shell and the crew-action pulses (R/C/
      // T/G) — the same wire fields serve the ship's parties and the tank's
      // crew (see the navalActions comment).
      inputSeq++;
      net.sendInput(
        {
          pitch: vehCtl.pitch, roll: vehCtl.roll, yaw: vehCtl.yaw, throttle: vehCtl.throttle,
          ammo: hullTheater || armadaTheater ? navalAmmo : 0,
          dc: (hullTheater || armadaTheater) && dcPending,
          smoke: (hullTheater || armadaTheater) && smokePending,
          repair: (hullTheater || armadaTheater) && repairPending,
          boost: (hullTheater || armadaTheater) && boostPending,
          aimRange: hullTheater || armadaTheater ? navalRangeLive : 0,
        },
        input.trigger,
        (hullTheater || armadaTheater) && input.bomb, // B: torpedo salvo at sea, wire-guided missile ashore
        inputSeq,
      );
      dcPending = false; // a damage-control request is a single pulse
      smokePending = false; // ...so is a smoke request
      repairPending = false; // ...repair too
      boostPending = false; // ...and boost
      return;
    }
    if (selfStatus === 5) {
      // Bomber turret: pitch/yaw are slew-rate inputs, like a manned gun.
      inputSeq++;
      net.sendInput(
        { pitch: turCtl.pitch, roll: 0, yaw: turCtl.yaw, throttle: 0 },
        input.trigger,
        false,
        inputSeq,
      );
      return;
    }
    // While flying, the physics loop owns the cadence; only step in if it
    // has stalled (hidden tab) so the connection stays alive.
    if (perfNow() - lastInputSentAt < 0.25) return;
    inputSeq++;
    const c = input.controls();
    net.sendInput(
      { pitch: round3(c.pitch), roll: round3(c.roll), yaw: round3(c.yaw), throttle: round3(c.throttle) },
      false,
      false,
      inputSeq,
    );
    lastInputSentAt = perfNow();
  }, 1000 / config.net.inputRateHz);

  // --- main loop ---
  const clock = new THREE.Clock();
  let physAcc = 0;
  let gunCooldown = 0;
  let smokeCooldown = 0;
  let contrailAt = 0; // throttles shell vapor-trail puffs
  let buffetCooldown = 0; // throttles the pre-stall airframe rumble
  const camTarget = new THREE.Vector3();
  const chaseFollow = new Follower(); // own-plane chase camera
  const tmpV = new THREE.Vector3();
  const tmpV2 = new THREE.Vector3();
  // Turret-view scratch (interpolateRemote reuses tmpV/tmpQ, so the gunner
  // camera keeps its own copies of the host pose and aim direction).
  const turQuat = new THREE.Quaternion();
  const turPivot = new THREE.Vector3();
  const turDir = new THREE.Vector3();
  const turFwd = new THREE.Vector3(); // bomber nose heading, for gunner freelook
  const turFreeEye = new THREE.Vector3(); // freelook orbit eye scratch
  const bombImpactV = new THREE.Vector3(); // scratch for the bombsight projection
  const bombRingWorld = new THREE.Vector3(); // roadmap #56: confidence-ring scratch
  const bombRingEdge = new THREE.Vector3();
  const bombRingCamR = new THREE.Vector3();
  const leadAim = { x: 0, y: 0, z: 0 }; // scratch for the gun lead solution
  const tmpQ = new THREE.Quaternion();
  const predQ = new THREE.Quaternion();
  let menuAngle = 0;
  let renderT = 0; // smoothed remote-interpolation clock
  // This frame's steering-assist context (null when the keyboard owns the
  // plane): the physics loop re-runs the assist against it at every input
  // tick so a long frame never holds a stale chase command — see the comment
  // on the steering block in frame(). The aim itself lives in aimDirector
  // (world-anchored — see aimdirector.js), whose virtual-camera basis is the
  // same rigid predicted-pose camera that cured the low-fps aim oscillator.
  let steerMode = null; // null | "stick" (touch) | "cursor" (the director's aim)
  // The path hold: with a SETTLED hand, the plane holds a LEVEL FLIGHT PATH.
  //
  // Born on the jets (the Starfang's stable 12-degree dive into the sea:
  // "reticle at rest" used to mean "weld the nose to the chase camera's
  // depressed aim ray", and the assist pinned −0.3 of elevator against a trim
  // that faded exactly as it wound up). The world-anchored director killed
  // the depressed-ray disease for everyone — a settled aim now eases to the
  // HORIZON — but nose-on-the-horizon still sinks: the path rides alpha
  // below the nose. So the settled hand means "hold the flight path level":
  // the assist's pitch blends into the trim's proven pitch loop (attitude
  // hold toward zero vertical speed, rate damped), aiming the NOSE alpha
  // above the horizon so the PATH rides on it — for every airframe, prop and
  // jet alike, because the fight it ends (assist position loop vs trim level
  // loop, the two-controller tug-of-war) was never a jet exclusive. Work the
  // mouse, bank into a turn, or touch the pitch keys and the hold fades out
  // entirely — aiming is pure pursuit, bit-for-bit. pathHoldS is the live
  // blend weight; the trim folds read it so relief and hold never stack on
  // the same elevator.
  let pathHoldS = 0;
  function pathHold(assistPitch) {
    pathHoldS = 0;
    // "Settled" is measured where the hand lives: under pointer lock the
    // director's slow walked-away clock plus a near-horizon aim (the fast
    // idle clock engages INSIDE a gunnery pause and steals the solution —
    // see AimDirector.holdW); in the absolute pre-lock mode, the cursor
    // sitting at its parked rest point.
    const settled = input.pointerLocked
      ? aimDirector.holdW() * clamp01(1 - Math.abs(Math.asin(clamp1(aimDirector.aim.y))) / 0.1)
      : clamp01(1 - Math.abs(input.cursor.y - CURSOR_REST_Y) / 0.12);
    pathHoldS = clamp01(settled - Math.abs(input.cursor.x) / 0.35 - Math.abs(input.kb.pitch) / 0.3);
    if (pathHoldS <= 0) return assistPitch;
    const pitchAngle = Math.asin(clamp1(FDM.qRotate(pred.att, FDM.v3(1, 0, 0)).y));
    const vsTarget = clamp1Range(-pred.vel.y * 0.03, 0.1);
    // Same gains as the auto-trim's level-hold (no porpoising in a storm),
    // plus the nose-above-path offset: level PATH needs the nose alpha high.
    const noseTarget = vsTarget + Math.min(Math.max(pred.alpha ?? 0, 0), 0.25);
    const hold = clamp1Range((noseTarget - pitchAngle) * 1.6 - pred.omega.z * 0.9, 0.7);
    return assistPitch * (1 - pathHoldS) + hold * pathHoldS;
  }
  function refreshSteerAssist() {
    // The autopilot owns the axes outright (it writes them after compose, and
    // its gains are gentle enough to hold across a long frame) — never fight it.
    // Space: no aero assists at all — RCS is direct, the gyros do the holding.
    if (!steerMode || input.autopilot || spaceTheater) return;
    // jetLead: rate-anticipated G/AoA limiting for jets (see fdm.js) —
    // the prop-tuned limiter recoils at jet q and every pull-up humps.
    const opts = { alphaStallRad: planeTypes[selfPlane]?.alphaStallRad ?? 0.3, gMax: 6.0, jetLead: CLASS_JET.has(selfPlane) ? 1.4 : 0 };
    const a =
      steerMode === "stick"
        ? FDM.steerStickAssist(pred, touch.ax, touch.ay, opts)
        : aimDirector.steer(pred, opts);
    if (steerMode === "cursor") a.pitch = pathHold(a.pitch);
    input.setAssist(a.pitch, a.roll, a.yaw);
    input.composeAxes();
    // Re-fold the held auto-trim the way the frame loop does (same fade-out
    // as the pilot commands pitch), WITHOUT advancing the trim state — the
    // trim wheel integrates on frame dt, this only re-applies its setting.
    // While the path hold owns the elevator, trim relief steps aside
    // (pathHoldS) instead of stacking a second level-hold on top of it.
    if (input.autoTrim) {
      const manual = input.pitch;
      const w = clamp01(1 - Math.abs(manual) / TRIM_FADE) * (1 - pathHoldS);
      input.pitch = clamp1(manual + pitchTrim * w);
    }
  }

  // Firing cutaway camera. Returns true while it owns the camera; on the final
  // frame it flips cine.mode back to "off" so the plot table returns.
  const cineLook = new THREE.Vector3();
  function updateCineCam(dt) {
    if (cine.mode === "off") return false;
    cine.t += dt;
    cine.shake = Math.max(0, cine.shake - dt * 1.6);
    const g = 9.81;
    const { from, sol, to } = cine;
    const FIRE_DUR = 0.85;
    const hlen = Math.hypot(sol.vx, sol.vz) || 1;
    const dirx = sol.vx / hlen;
    const dirz = sol.vz / hlen;

    // Dwell on the firing turret long enough for the muzzle flash and smoke to
    // read before riding the shells downrange.
    if (cine.mode === "fire") {
      // Frame the turret from behind and to one side, looking down the bore as
      // the muzzle flashes.
      const back = 26, side = 17, up = 9;
      tmpV2.set(
        from.x - dirx * back - dirz * side,
        from.y + up,
        from.z - dirz * back + dirx * side,
      );
      camera.position.lerp(tmpV2, 1 - Math.exp(-18 * dt));
      cineLook.set(from.x + dirx * 70, from.y + 8, from.z + dirz * 70);
      if (cine.t >= FIRE_DUR) cine.mode = "follow";
    } else if (cine.mode === "follow") {
      // Ride behind and above the shell along its arc. Reduced-charge plunging
      // fire stays aloft longer than direct fire (~25s at most); cap the ride
      // as a safety net so an extreme lob can't lock the camera indefinitely —
      // past the cap it cuts to the impact area instead of riding the shell.
      const FOLLOW_MAX = 26;
      const rideEnd = Math.min(sol.tof, FOLLOW_MAX);
      const tau = Math.min(cine.t - FIRE_DUR, sol.tof);
      const px = from.x + sol.vx * tau;
      const py = from.y + sol.vy * tau - 0.5 * g * tau * tau;
      const pz = from.z + sol.vz * tau;
      const vx = sol.vx, vy = sol.vy - g * tau, vz = sol.vz;
      const vlen = Math.hypot(vx, vy, vz) || 1;
      const back = 34, up = 11;
      tmpV2.set(
        px - (vx / vlen) * back,
        py - (vy / vlen) * back + up,
        pz - (vz / vlen) * back,
      );
      camera.position.lerp(tmpV2, 1 - Math.exp(-10 * dt));
      cineLook.set(px + (vx / vlen) * 30, py + (vy / vlen) * 30, pz + (vz / vlen) * 30);
      if (cine.t - FIRE_DUR >= rideEnd) {
        cine.mode = "linger";
        cine.t = 0;
        // Only shake on a synced impact; a capped lob is still in the air.
        cine.shake = sol.tof <= FOLLOW_MAX ? 1.2 : 0;
      }
    } else {
      // linger: hold on the impact for a couple of seconds.
      tmpV2.set(to.x - 42, to.y + 28, to.z - 42);
      camera.position.lerp(tmpV2, 1 - Math.exp(-6 * dt));
      cineLook.set(to.x, to.y + 2, to.z);
      if (cine.t >= 2) cine.mode = "off";
    }

    if (cine.shake > 0) {
      const s = cine.shake * 0.7;
      camera.position.x += (Math.random() * 2 - 1) * s;
      camera.position.y += (Math.random() * 2 - 1) * s;
      camera.position.z += (Math.random() * 2 - 1) * s;
    }
    camera.up.set(0, 1, 0);
    camera.lookAt(cineLook);
    return true;
  }

  function interpolateRemote(ent, renderT) {
    const buf = ent.buffer;
    if (!buf.length) return null;
    if (buf.length === 1 || renderT <= buf[0].t) return buf[0];
    for (let i = buf.length - 1; i >= 0; i--) {
      if (buf[i].t <= renderT) {
        const a = buf[i];
        const b = buf[i + 1];
        if (!b) {
          const dt = Math.min(renderT - a.t, 0.25);
          return {
            pos: tmpV.copy(a.pos).addScaledVector(a.vel, dt),
            quat: a.quat,
            thr: a.thr,
            status: a.status,
          };
        }
        if (a.status === 0 && b.status !== 0) {
          // Respawn boundary: don't sweep the plane across the map.
          return b;
        }
        const span = b.t - a.t;
        const f = (renderT - a.t) / span;
        if (span > 0.001 && span < 0.25) {
          // Cubic Hermite through the snapshot velocities: C1-smooth motion
          // instead of a 20 Hz polyline (visible as faceted arcs in turns,
          // especially up close in spectator follow).
          const f2 = f * f, f3 = f2 * f;
          const h00 = 2 * f3 - 3 * f2 + 1, h10 = f3 - 2 * f2 + f;
          const h01 = 3 * f2 - 2 * f3, h11 = f3 - f2;
          tmpV.set(
            h00 * a.pos.x + h10 * span * a.vel.x + h01 * b.pos.x + h11 * span * b.vel.x,
            h00 * a.pos.y + h10 * span * a.vel.y + h01 * b.pos.y + h11 * span * b.vel.y,
            h00 * a.pos.z + h10 * span * a.vel.z + h01 * b.pos.z + h11 * span * b.vel.z,
          );
        } else {
          // Degenerate or gappy segment: Hermite tangents would overshoot.
          tmpV.copy(a.pos).lerp(b.pos, f);
        }
        return {
          pos: tmpV,
          quat: tmpQ.copy(a.quat).slerp(b.quat, f),
          thr: b.thr,
          status: b.status,
        };
      }
    }
    return buf[0];
  }

  // Turret view from a gun mount, looking along its (smoothed) server aim.
  // Shared by the "follow this gun" and "follow this gunner" spectate kinds.
  function spectateGunCam(id, dt) {
    const g = aaGuns.guns.get(id);
    if (!g || !g.alive || g.kind === 3) return false;
    const pivot = aaGuns.pivotOf(id);
    if (!specAim.has) {
      specAim.has = true;
      specAim.yaw = g.srvYaw;
      specAim.pitch = g.srvPitch;
    }
    // Ease toward the 20 Hz snapshot aim so the view pans instead of stepping.
    const blend = 1 - Math.exp(-8 * dt);
    specAim.yaw += wrapAngle(g.srvYaw - specAim.yaw) * blend;
    specAim.pitch += (g.srvPitch - specAim.pitch) * blend;
    const main = g.kind === 2;
    const back = main ? 30 : 13;
    const up = main ? 11 : 5.5;
    const ch = Math.cos(specAim.pitch);
    tmpV.set(
      Math.cos(specAim.yaw) * ch,
      Math.sin(specAim.pitch),
      -Math.sin(specAim.yaw) * ch,
    );
    camera.position.lerp(
      tmpV2.set(pivot.x - tmpV.x * back, pivot.y - tmpV.y * back + up, pivot.z - tmpV.z * back),
      1 - Math.exp(-6 * dt),
    );
    camera.up.set(0, 1, 0);
    camera.lookAt(pivot.x + tmpV.x * 400, pivot.y + tmpV.y * 400, pivot.z + tmpV.z * 400);
    return true;
  }

  // Drive the camera from the current spectate target. False means the
  // subject is gone (shot down, mount destroyed, hull sunk) — caller cycles.
  function spectateCam(dt) {
    const t = spectateTarget;
    if (!t) return false;
    if (t.kind === "gun") return spectateGunCam(t.id, dt);
    if (t.kind === "vehicle") {
      // N5: chase a driven warship from astern and above, so the LIVE stream and
      // free-cam frame the actual hull instead of empty sea.
      const v = vehiclesR.vehicles.get(t.id);
      if (!v || (v.hp ?? 0) <= 0) return false;
      const p = v.root.position;
      const yaw = v.target?.yaw ?? 0;
      const fx = Math.cos(yaw), fz = -Math.sin(yaw);
      specCamOff.set(-fx * 90, 42, -fz * 90);
      if (specOrbit.yaw || specOrbit.pitch) {
        specCamOff.applyAxisAngle(WORLD_UP, specOrbit.yaw);
        specOrbitAxis.set(specCamOff.z, 0, -specCamOff.x).normalize();
        specCamOff.applyAxisAngle(specOrbitAxis, specOrbit.pitch);
      }
      specFollow.update(camera.position, tmpV2.set(p.x + specCamOff.x, p.y + specCamOff.y, p.z + specCamOff.z), 6, dt);
      camera.up.set(0, 1, 0);
      camera.lookAt(p.x, p.y + 8, p.z);
      return true;
    }
    if (t.kind === "ship") {
      const sh = ships.ships.get(t.id);
      if (!sh?.alive) return false;
      const view = ships.bridgeOf(t.id);
      if (!view) return false;
      camera.position.lerp(view.eye, 1 - Math.exp(-6 * dt));
      camera.up.set(0, 1, 0);
      camera.lookAt(view.look);
      return true;
    }
    const ent = entities.get(t.id);
    if (!ent || ent.status === 0) return false;
    // Chase the same interpolated state the plane is drawn at — the raw
    // latest sample steps at snapshot rate, which made the followed plane
    // (and the whole world, via lookAt) judder while spectating.
    const s = interpolateRemote(ent, renderT);
    if (!s) return false;
    specPos.copy(s.pos); // own scratch: tmpV/tmpQ get reused below
    if (ent.status === 3) {
      // A gunner has no plane to chase — spawnInGun parks them exactly on
      // the mount's pivot, so look up that gun and show its turret view.
      const gid = aaGuns.nearestTo(specPos);
      return gid ? spectateGunCam(gid, dt) : false;
    }
    // Own copy of the forward axis: interpolateRemote below reuses tmpV/tmpQ.
    specFwd.set(1, 0, 0).applyQuaternion(s.quat);
    // Action framing (director only, and only while the viewer isn't hand-
    // orbiting): with a live foe inside fight range, sit on the flank of the
    // subject→foe axis and look between them, so hunter AND prey share the
    // frame — instead of the stock chase that hides the fight dead ahead
    // behind the subject's own tail. Falls back to the stock chase otherwise.
    let framed = false;
    if (autoDirector && !specOrbit.yaw && !specOrbit.pitch) {
      // Ambient two-shot for a plane the director is merely following (a tight
      // tail-chase gets promoted to shot/reverse coverage instead — see
      // directorCoverShot). Sit on the flank of the subject→foe line so hunter and
      // prey share the frame, rather than hiding the fight behind the subject's tail.
      const foe = nearestFoe(t.id, specPos, 700);
      if (foe) {
        specAxis.copy(foe).sub(specPos);
        const sep = specAxis.length();
        specAxis.y = 0;
        if (sep > 8 && specAxis.lengthSq() > 4) {
          specAxis.normalize();
          // Stay on whichever flank the camera already holds: no jump cuts.
          specSide.set(-specAxis.z, 0, specAxis.x);
          tmpV2.copy(camera.position).sub(specPos);
          if (specFollow.valid && specSide.dot(tmpV2) < 0) specSide.multiplyScalar(-1);
          // Pull back (and lift) with separation so both planes stay in frame
          // through the pass, without the wide shot's detachment.
          const back = 24 + Math.min(30, sep * 0.05);
          specCamOff
            .copy(specAxis)
            .multiplyScalar(-back)
            .addScaledVector(specSide, back * 0.6);
          specCamOff.y = 9 + sep * 0.025;
          specLook.copy(specPos).lerp(foe, 0.3);
          framed = true;
        }
      }
    }
    if (!framed) {
      // Base chase offset: 26 behind the nose, 10 above. The orbit angles then
      // swing that offset around the subject — yaw about world up, pitch about
      // the offset's horizontal-right axis — so zero orbit leaves the original
      // framing untouched.
      specCamOff.set(-specFwd.x * 26, -specFwd.y * 26 + 10, -specFwd.z * 26);
      if (specOrbit.yaw || specOrbit.pitch) {
        specCamOff.applyAxisAngle(WORLD_UP, specOrbit.yaw);
        specOrbitAxis.set(specCamOff.z, 0, -specCamOff.x).normalize();
        specCamOff.applyAxisAngle(specOrbitAxis, specOrbit.pitch);
      }
      specLook.copy(specPos);
    }
    specFollow.update(
      camera.position,
      tmpV2.set(
        specPos.x + specCamOff.x,
        specPos.y + specCamOff.y,
        specPos.z + specCamOff.z,
      ),
      6,
      dt,
    );
    // The look point tracks a 20 Hz-stepping foe; smooth it like the eye so
    // the whole frame pans instead of ticking.
    specLookFollow.update(specLookPos, specLook, 8, dt, 200);
    camera.up.set(0, 1, 0);
    camera.lookAt(specLookPos);
    return true;
  }

  // Nearest live enemy to the followed plane — the second actor the action
  // frame should hold. Returns an interpolated position (in specFoe) or null.
  // NOTE: goes through interpolateRemote, which reuses tmpV/tmpQ scratch.
  function nearestFoe(id, pos, within) {
    const team = teamOf(id);
    let best = null;
    let bd = within * within;
    for (const [oid, oe] of entities) {
      if (oid === id || oe.status === 0 || !oe.buffer.length) continue;
      if (teamOf(oid) === team) continue;
      const d = pos.distanceToSquared(oe.buffer.at(-1).pos);
      if (d < bd) {
        bd = d;
        best = oe;
      }
    }
    if (!best) return null;
    const s = interpolateRemote(best, renderT);
    return s ? specFoe.copy(s.pos) : null;
  }

  // --- killcam playback (server killcam.go ships the recorded frames) ---
  const kcFollow = new Follower(); // killcam chase camera
  const kcKiller = new THREE.Vector3();
  const kcDir = new THREE.Vector3();
  const kcSide = new THREE.Vector3(); // lateral offset off the gun-target line
  const kcLook = new THREE.Vector3();
  const kcCamPos = new THREE.Vector3();

  const frameT = (f) => f.tick * PHYS_DT; // killcam frame time (see onSnapshot)

  function startKillcam(kc) {
    endKillcam();
    awaitingKillcam = 0; // the clip is here; the replay now owns the screen
    kcFollow.reset();
    // Index each player's rows by id so playback can interpolate them with
    // the same machinery as live remotes.
    const buffers = new Map();
    for (const f of kc.frames) {
      for (const row of f.pl) {
        const [id, x, y, z, qx, qy, qz, qw, vx, vy, vz, , status, thr, plane] = row;
        let buf = buffers.get(id);
        if (!buf) buffers.set(id, (buf = []));
        buf.push({
          t: frameT(f),
          pos: new THREE.Vector3(x, y, z),
          quat: new THREE.Quaternion(qx, qy, qz, qw),
          vel: new THREE.Vector3(vx, vy, vz),
          thr,
          status,
          plane,
        });
      }
    }
    const t0 = frameT(kc.frames[0]);
    const t1 = frameT(kc.frames.at(-1));
    // Real time, not fast-forward: the server holds the respawn for the whole
    // clip (see kill()/killcamRespawnTailSec), so play it 1:1. The Math.max(1,…)
    // floor still guards against an over-long clip ever running slow-motion.
    const wait = config.net.killcamSec || Math.max(1.5, config.combat.respawnDelaySec - 0.8);
    // Hold the TAKE-OFF unlock until the replay ends (mirrors the server's
    // killcamRespawnTailSec), so the button frees up just as the cam finishes.
    // Measured from death (deathAt), so it spans the server's send delay plus
    // the 1:1 playback that follows.
    deathRespawnSec = Math.max(
      config.combat.respawnDelaySec,
      (config.net.killcamDelaySec || 0) + (config.net.killcamSec || 0) + 0.8,
    );
    // Our own death moment, for the boom at the end of the clip.
    const meBuf = buffers.get(selfId) ?? [];
    let deathT = t1;
    const lastAlive = meBuf.findLast((e) => e.status !== 0);
    if (lastAlive) deathT = lastAlive.t;
    // Wreck-orbit framing: a plane's 62 m ring is INSIDE a battleship's hull.
    // A driver death (status 4 — we went down with a vehicle) pulls the orbit
    // out and up so the whole foundering warship stays in frame as she goes.
    const droveHull = lastAlive?.status === 4;
    // Camera flank for the whole clip (picked once — no mid-replay jump
    // cuts): sit on the side of the gun-target line the victim is drifting
    // away from, so its motion plays across the frame instead of out of it.
    let side = 1;
    const k0 = buffers.get(kc.killer)?.[0];
    const v0 = meBuf[0];
    if (k0 && v0) {
      const ax = v0.pos.x - k0.pos.x;
      const az = v0.pos.z - k0.pos.z;
      side = -az * v0.vel.x + ax * v0.vel.z > 0 ? -1 : 1;
    }
    replay = {
      frames: kc.frames,
      buffers,
      killer: kc.killer,
      t: t0,
      t1,
      rate: Math.max(1, (t1 - t0) / wait),
      fi: 0,
      visuals: new Map(),
      deathT,
      deathPos: lastAlive ? lastAlive.pos : null,
      boomed: false,
      side,
      orbitA: null, // wreck-orbit angle once the fatal moment lands
      orbitR: droveHull ? 150 : 62, // ring radius: hull-sized for a warship
      orbitH: droveHull ? 55 : 24,
    };
    // Persistent badge for the whole replay so it plainly reads as a killcam
    // (the live killer-cam that follows looks similar) and keeps the killer's
    // name on screen the entire time, not for a fleeting beat. A crash replay
    // (killer == self, see killcam.go) has no killer to name — brand it as the
    // crash it was and let the coach line explain what went wrong.
    if (kc.killer === selfId) {
      hud.setKillcamTag("", deathCoach?.line ?? "", "↻ CRASH REPLAY", "", deathTip?.text ?? "");
    } else {
      const kcName = (kc.killer === nemesisId ? "NEMESIS " : "") + nameOf(kc.killer);
      hud.setKillcamTag(kcName, deathCoach?.line ?? "", undefined, undefined, deathTip?.text ?? "");
    }
  }

  function endKillcam() {
    if (!replay) return;
    for (const v of replay.visuals.values()) v.dispose(scene);
    replay = null;
    hud.setKillcamTag(null);
  }

  // Advance and render the killcam; drives the camera while it returns true.
  function stepKillcam(dt) {
    if (!replay) return false;
    replay.t += dt * replay.rate;
    if (replay.t > replay.t1 + 0.05) {
      endKillcam();
      return false;
    }
    // Re-stage tracers and ordnance from the recorded frames as we cross them.
    while (
      replay.fi + 1 < replay.frames.length &&
      frameT(replay.frames[replay.fi + 1]) <= replay.t
    ) {
      replay.fi++;
      const f = replay.frames[replay.fi];
      tracers.setFromSnapshot(f.bl ?? []);
      bombsR.setFromSnapshot(f.bm ?? []);
    }
    // Replayed planes — including our own, about to be shot down.
    for (const [id, buf] of replay.buffers) {
      const s = interpolateRemote({ buffer: buf }, replay.t);
      // Gunners (3) and drivers (4) sit inside their mount/hull: no plane.
      const show = s && s.status !== 0 && s.status !== 3 && s.status !== 4;
      let v = replay.visuals.get(id);
      if (!v) {
        if (!show) continue;
        const info = roster.get(id);
        v = new PlaneVisual(scene, info?.team ?? 0, info?.name ?? "", false, (buf.at(-1).plane ?? 0) | 0);
        v.entityId = id; // identical bullet holes in the replay (roadmap #8)
        replay.visuals.set(id, v);
      }
      v.group.visible = !!show;
      if (!show) continue;
      v.group.position.copy(s.pos);
      v.group.quaternion.copy(s.quat);
      v.throttle = s.thr;
      v.update(dt);
    }
    // The fatal moment, again.
    if (!replay.boomed && replay.t >= replay.deathT && replay.deathPos) {
      replay.boomed = true;
      particles.explosion(replay.deathPos);
      audio.explosion(camera.position.distanceTo(replay.deathPos));
    }
    // Camera, in two phases. Chase: the killer's point of view, framing the
    // victim (us) — but offset OFF the gun-target line, so the killer's own
    // fuselage sits at the frame's edge instead of dead-center hiding the
    // victim behind it. Wreck: from the fatal moment on, a slow pull-out
    // orbit of the falling wreckage — the money shot the chase used to fly
    // away from while it kept following the killer.
    const meBuf = replay.buffers.get(selfId);
    const meS = meBuf ? interpolateRemote({ buffer: meBuf }, replay.t) : null;
    let look = null;
    if (meS) look = kcLook.copy(meS.pos);
    if (replay.boomed && replay.deathPos) {
      if (replay.orbitA == null) {
        // Enter the orbit from wherever the chase left the camera.
        replay.orbitA = Math.atan2(
          camera.position.z - replay.deathPos.z,
          camera.position.x - replay.deathPos.x,
        );
      }
      replay.orbitA += dt * 0.35;
      kcCamPos.set(
        replay.deathPos.x + Math.cos(replay.orbitA) * replay.orbitR,
        replay.deathPos.y + replay.orbitH,
        replay.deathPos.z + Math.sin(replay.orbitA) * replay.orbitR,
      );
      look = kcLook.copy(replay.deathPos);
    } else if (isAAId(replay.killer)) {
      const pivot = aaGuns.pivotOf(replay.killer);
      if (pivot && look) {
        // Just behind and above the mount, sighting along the gun-target line.
        kcDir.copy(look).sub(pivot).normalize();
        kcCamPos.copy(pivot).addScaledVector(kcDir, -12);
        kcCamPos.y = pivot.y + 7;
      } else if (pivot) {
        kcCamPos.set(pivot.x + 40, pivot.y + 25, pivot.z + 40);
        look = kcLook.copy(pivot);
      } else if (look) {
        kcCamPos.set(look.x + 60, look.y + 24, look.z + 60);
      } else {
        endKillcam();
        return false;
      }
    } else {
      const kBuf = replay.buffers.get(replay.killer);
      const kS = kBuf ? interpolateRemote({ buffer: kBuf }, replay.t) : null;
      if (!kS && !look) {
        endKillcam();
        return false;
      }
      if (kS) {
        kcKiller.copy(kS.pos);
        if (look && kcKiller.distanceToSquared(look) > 25) {
          kcDir.copy(look).sub(kcKiller).normalize();
        } else {
          kcDir.set(1, 0, 0).applyQuaternion(kS.quat);
          if (!look) look = kcLook.copy(kcKiller).addScaledVector(kcDir, 300);
        }
        // High three-quarter over the shoulder: back along the gun-target
        // line, out to the picked flank, and up — killer at the frame edge,
        // victim (and the tracer stream between them) clear beyond it.
        kcSide.set(-kcDir.z, 0, kcDir.x);
        kcCamPos
          .copy(kcKiller)
          .addScaledVector(kcDir, -22)
          .addScaledVector(kcSide, replay.side * 11);
        kcCamPos.y += 9;
        look.lerp(kcKiller, 0.12); // bias the frame so the shooter stays in it
      } else {
        // The killer never appears in the clip: orbit our own last position.
        kcCamPos.set(look.x + 60, look.y + 24, look.z + 60);
      }
    }
    // Never let a low kill sink the camera into a hill or the sea.
    const kcGround = field.surfaceAt(kcCamPos.x, kcCamPos.z) + 4;
    if (kcCamPos.y < kcGround) kcCamPos.y = kcGround;
    kcFollow.update(camera.position, kcCamPos, 10, dt, 120);
    camera.up.set(0, 1, 0);
    camera.lookAt(look);
    return true;
  }

  // Pick the enemy most in front of us for the gunnery lead pip.
  function pickLeadTarget(fwd) {
    let best = null;
    let bestAngle = 0.5; // ~28 degrees
    for (const ent of entities.values()) {
      if (ent.status === 0) continue;
      const info = roster.get(ent.id);
      if (!info || info.team === selfTeam) continue;
      const last = ent.buffer.at(-1);
      if (!last) continue;
      tmpV2.set(last.pos.x - pred.pos.x, last.pos.y - pred.pos.y, last.pos.z - pred.pos.z);
      const dist = tmpV2.length();
      if (dist > 1700 || dist < 30) continue;
      tmpV2.divideScalar(dist);
      const angle = Math.acos(Math.min(1, tmpV2.x * fwd.x + tmpV2.y * fwd.y + tmpV2.z * fwd.z));
      if (angle < bestAngle) {
        bestAngle = angle;
        best = { last, dist };
      }
    }
    return best;
  }

  // Predict where a bomb pickled right now would land. A forward integration of
  // the exact physics the server flies a bomb under (stepBombs in
  // internal/game/world.go): gravity, linear drag toward the wind vector, and a
  // stop at the terrain surface — so the pipper drifts downwind just like the
  // ordnance. Returns the world-space impact point (written into `out`) or null
  // if the bomb wouldn't reach the ground within its 40 s fuze. Client-only:
  // recomputed every frame off the locally-predicted state, no extra wire data.
  const BOMB_DRAG_PER_SEC = 0.1; // internal/game/world.go bombDragPerSec
  function predictBombImpact(pos, vel, att, wind, out) {
    const dt = 1 / 60; // match the server tick so the solution lands true
    // Bombs leave the bay 2.5 m below the airframe along its up axis.
    const up = FDM.qRotate(att, FDM.v3(0, 1, 0));
    let x = pos.x - up.x * 2.5, y = pos.y - up.y * 2.5, z = pos.z - up.z * 2.5;
    let vx = vel.x, vy = vel.y, vz = vel.z;
    for (let t = 0; t < 40; t += dt) {
      vy -= FDM.GRAVITY * dt;
      const k = BOMB_DRAG_PER_SEC * dt;
      vx += (wind.x - vx) * k;
      vz += (wind.z - vz) * k;
      vy *= 1 - k;
      const nx = x + vx * dt, ny = y + vy * dt, nz = z + vz * dt;
      const ground = field.surfaceAt(nx, nz);
      if (ny <= ground) return out.set(nx, ground, nz);
      x = nx; y = ny; z = nz;
    }
    return null;
  }
  let minimapAt = 0;

  function frame() {
    requestAnimationFrame(frame);
    const dt = Math.min(clock.getDelta(), 0.1);
    // Kill-confirm slow-mo dilates ONLY the cosmetic clock (particles,
    // tracers, debris). The FDM prediction, input sampling, and net
    // interpolation must always run on the real dt or reconciliation pops.
    const fxDt = dt * killConfirm.scale(perfNow());

    // Director pump: replay the buffered snapshot/event streams up to the lagged
    // render clock, so the entire world advances together a few seconds behind
    // live. The near future still sits in dirSnapRing/dirEventQ for the camera
    // planner to read (a kill it's about to show is already known).
    if (autoDirector && timeOffset !== null) {
      const renderNow = perfNow() + timeOffset - DIRECTOR_LAG;
      while (dirSnapRing.length && dirSnapRing[0].t <= renderNow) {
        const s = dirSnapRing.shift();
        applySnapshot(s.snap, s.t);
      }
      while (dirEventQ.length && dirEventQ[0].t <= renderNow) {
        handleEvent(dirEventQ.shift().ev);
      }
    }

    const manning =
      net.joined && !spectating && selfStatus === 3 && aaGuns.guns.has(selfGunId);
    const manningMain = manning && gunKind() === 2; // battleship main battery
    const driving =
      net.joined && !spectating && selfStatus === 4 && vehiclesR.has(selfVehicleId);
    const turretting =
      net.joined && !spectating && selfStatus === 5 && entities.has(selfTurretHost);
    // Passenger chip: keep it clear the whole ride whose bomber you're on and
    // how to look around / leave, so a bot flying you into the fight never reads
    // as a broken control-less camera.
    if (turretting) {
      const host = roster.get(selfTurretHost);
      const bomberNm = planeTypes[host?.plane ?? 0]?.name ?? "bomber";
      const pilotNm = host?.name ?? "a friendly pilot";
      const look = touch ? "drag to look" : "hold RMB to look";
      hud.setTurretChip(`RIDING ${pilotNm}'s ${bomberNm} · ${look} · F to bail`);
    } else {
      hud.setTurretChip(null);
    }
    if (navalScoped && !driving) setNavalScope(false); // sank/bailed: drop the scope
    // Armada swaps cockpits for conning towers mid-round: gauge the unit.
    if (armadaTheater && driving !== lastDrivingGauges) {
      lastDrivingGauges = driving;
      setHullGauges(driving);
    }
    const alive =
      net.joined && !spectating && haveSpawned && !waitingBomber &&
      selfStatus !== 0 && selfStatus !== 3 && selfStatus !== 4 && selfStatus !== 5 &&
      selfStatus !== 6;
    // A falling wreck (roadmap #3): not a controllable pilot (input/HUD treat
    // it as dead), but the airframe is still rendered and chased to the ground.
    const falling = net.joined && !spectating && haveSpawned && selfStatus === 6;

    // Rescue prompt (roadmap #2): flying low and slow over a survivor, nudge
    // the pilot to hold the pass. Edge-triggered so it never fights the
    // combat warning line (CHECK SIX etc.).
    let nearRescue = false;
    if (alive && rescueMarkers.size &&
        pred.pos.y < config.world.waterLevelM + (config.rescue?.maxAltM ?? 40) &&
        Math.hypot(pred.vel.x, pred.vel.y, pred.vel.z) < (config.rescue?.maxSpeedMS ?? 55)) {
      const r = config.rescue?.radiusM ?? 90;
      for (const m of rescueMarkers.values()) {
        if ((pred.pos.x - m.x) ** 2 + (pred.pos.z - m.z) ** 2 < r * r) { nearRescue = true; break; }
      }
    }
    if (nearRescue && !wasNearRescue) hud.message("RESCUING — hold low & slow", 1500, "#7bd8c4");
    wasNearRescue = nearRescue;

    // Spectators fly nothing, so the flight HUD (gauges, throttle) reads zeros
    // and only clutters the frame. A body flag lets the stylesheet strip the
    // cockpit furniture and re-tuck the corners for the watch-only layout.
    document.body.classList.toggle("spectating", spectating);

    // Touch layer: stick deflection becomes the aim cursor before the
    // assist/controls are composed below.
    if (touch) {
      touch.setVisible(alive || manning || driving || turretting);
      touch.setMode(manning || turretting, driving);
      touch.setSpectate(spectating);
      touch.setZoomVisible((hullTheater || armadaTheater) && driving); // gunsight toggle while crewing a hull
      touch.update(dt);
    }

    // Stick telemetry (see stickDebugMount): one line per link in the chain,
    // thumb -> pointer events -> stick axes -> assist -> composed controls ->
    // attitude, plus every arbiter that can take the plane away.
    if (stickDebugEl && !stickDebugEl.classList.contains("hidden")) {
      const dUp = FDM.qRotate(pred.att, FDM.v3(0, 1, 0));
      const dRight = FDM.qRotate(pred.att, FDM.v3(0, 0, 1));
      const dBank = (Math.atan2(-dRight.y, dUp.y) * 180) / Math.PI;
      const st = touch
        ? `id:${touch.stick.id ?? "-"} d:(${touch.stick.dx.toFixed(2)},${touch.stick.dy.toFixed(2)}) a:(${touch.ax.toFixed(2)},${touch.ay.toFixed(2)})`
        : "no touch layer";
      stickDebugEl.textContent =
        `${document.getElementById("build-stamp")?.textContent ?? "?"} fps:${(1 / Math.max(dt, 1e-3)).toFixed(0)} down:${stickDebugEvt.down} cancel:${stickDebugEvt.cancel} tgt:${stickDebugEvt.target}\n` +
        `aim:${input.mouseAim ? 1 : 0} pad:${input.pad.active ? 1 : 0}${input.pad.index >= 0 ? ` [${(input.pad.name || "?").slice(0, 20)}]` : ""} ap:${input.autopilot ? 1 : 0} alive:${alive ? 1 : 0}\n` +
        `stick ${st}\n` +
        `assist p:${input.assist.pitch.toFixed(2)} r:${input.assist.roll.toFixed(2)} y:${input.assist.yaw.toFixed(2)}\n` +
        `out p:${input.pitch.toFixed(2)} r:${input.roll.toFixed(2)} y:${input.yaw.toFixed(2)} thr:${input.throttle.toFixed(2)}\n` +
        `bank:${dBank.toFixed(0)} upY:${dUp.y.toFixed(2)} spd:${Math.hypot(pred.vel.x, pred.vel.y, pred.vel.z).toFixed(0)}`;
    }

    // --- mouse-aim / touch-stick steering (before input.update composes controls) ---
    // The assist is recomputed HERE from the frame's opening attitude, and
    // AGAIN inside the physics loop at every input tick (refreshSteerAssist):
    // on a slow GPU one frame spans a dozen ticks, and holding the opening
    // command that long staircases the control timeline the server sees. The
    // director's aim is WORLD-anchored, so the per-tick refresh steers toward
    // a fixed direction with a live attitude — no stale chase command at any
    // frame rate (its screen projection uses the rigid virtual camera, not
    // the render camera: the render camera trails the physics by a frame,
    // and feeding that lag back into the steering is what made single-digit
    // fps oscillate hands-off). Freelook needs no special freeze anymore:
    // the orbit consumes the mouse, no deltas reach the director, and the
    // world-fixed aim simply stands its ground.
    steerMode = null;
    pathHoldS = 0; // re-earned below: only the cursor instructor engages it
    if (alive && spaceTheater) {
      // THE GAZE. One vector owns the theater: where you're looking. The
      // thumb steers it as a rate (turning your head), the pointer-lock
      // mouse writes it directly (the listener in the theater block), and
      // on spawn or a P toggle it snaps to the nose so the handover never
      // cuts the camera. In raw mode it rides the nose full-time — the old
      // chase view, and the re-sync point when guidance comes back.
      if (spaceCamSync || !keeperOn) {
        const nose0 = FDM.qRotate(pred.att, FDM.v3(1, 0, 0));
        spaceCam.yaw = Math.atan2(-nose0.z, nose0.x);
        spaceCam.pitch = Math.asin(Math.max(-1, Math.min(1, nose0.y)));
        spaceCamSync = false;
      } else if (touch) {
        spaceCam.yaw -= touch.ax * 2.0 * dt;
        spaceCam.pitch = Math.max(-1.35, Math.min(1.35, spaceCam.pitch + touch.ay * 1.5 * dt));
      }
      const camCp = Math.cos(spaceCam.pitch);
      spaceCamDir = {
        x: camCp * Math.cos(spaceCam.yaw),
        y: Math.sin(spaceCam.pitch),
        z: -camCp * Math.sin(spaceCam.yaw),
      };
      // Touchdown parks the lever. The landing was flown lever-IN (that's
      // the intent), but a landed hull relaunches at throttle > 0.6 — left
      // alone, the same lever that flew you down bounces you straight back
      // off the pad. One hop each: the relaunch asks for a fresh shove.
      // The liftoff-grace guard keeps a reconcile bounce (prediction up,
      // server still down for a beat) from reading as a fresh touchdown
      // and eating the relaunch shove mid-ramp.
      if (pred.landed && !spaceWasLanded && perfNow() >= spaceLiftoffRawUntil) {
        input.throttle = 0;
        if (touch) touch.setThrottleValue(0); // repaint the lever, don't just zero it
      }
      if (!pred.landed && spaceWasLanded) spaceLiftoffRawUntil = perfNow() + 1.2;
      spaceWasLanded = !!pred.landed;
      spaceEngineCmd = -1;
      spaceFireOK = true;
      // THE RING, every frame and in every mode — guidance on, raw RCS,
      // gyro hold, even parked on a relay pad. Aiming is not a manoeuvre
      // out here any more: the gaze designates, the fire-control computer
      // turns a designation into the lead mark the pip already draws, and
      // the mount walks the barrels onto it at the hull's traverse rate.
      // The trigger waits for the barrels (the bots' own 0.997 discipline),
      // so a held trigger through a slew is a burst that starts the instant
      // the guns arrive — and the nose never hears about any of it.
      {
        let ax = spaceCamDir.x, ay = spaceCamDir.y, az = spaceCamDir.z;
        let onMark = false;
        if (spaceGunSolution && perfNow() - spaceGunSolutionAt < 0.35) {
          const gx = spaceGunSolution.x - pred.pos.x;
          const gy = spaceGunSolution.y - pred.pos.y;
          const gz = spaceGunSolution.z - pred.pos.z;
          const gl = Math.hypot(gx, gy, gz) || 1;
          ax = gx / gl; ay = gy / gl; az = gz / gl;
          onMark = true; // a real firing solution: the gate below means it
        }
        spaceGunAimYaw = Math.atan2(-az, ax);
        spaceGunAimPitch = Math.max(
          -SPACE_GUN_ELEV_MAX,
          Math.min(SPACE_GUN_ELEV_MAX, Math.asin(Math.max(-1, Math.min(1, ay)))),
        );
        // Slew, mirroring stepSpaceTurret: shortest way round, the type's
        // rate, and the mount's elevation stops.
        const rate = planeTypes[selfPlane]?.gunTraverseRPS > 0
          ? planeTypes[selfPlane].gunTraverseRPS
          : 2.4;
        const step = rate * dt;
        let dy = spaceGunAimYaw - spaceGunYaw;
        while (dy > Math.PI) dy -= 2 * Math.PI;
        while (dy <= -Math.PI) dy += 2 * Math.PI;
        let dp = spaceGunAimPitch - spaceGunPitch;
        spaceGunYaw += Math.max(-step, Math.min(step, dy));
        spaceGunPitch = Math.max(
          -SPACE_GUN_ELEV_MAX,
          Math.min(SPACE_GUN_ELEV_MAX, spaceGunPitch + Math.max(-step, Math.min(step, dp))),
        );
        while (spaceGunYaw > Math.PI) spaceGunYaw -= 2 * Math.PI;
        while (spaceGunYaw <= -Math.PI) spaceGunYaw += 2 * Math.PI;
        // RECOIL, mirrored (spaceGunKick / spaceGunInBattery in
        // spacecombat.go). The server owns the real thing — it refuses to fire
        // an out-of-battery mount whoever pulls the trigger — but the cockpit
        // has to agree or the glass lies: the pipper would sit on a target
        // saying LAID while the gun quietly declined, which is the single most
        // maddening thing a gunsight can do. Predicting it here also keeps the
        // trigger off the wire during the settle, so the burst rhythm is the
        // player's own input rather than something happening to them.
        spaceGunKickYaw *= Math.exp(-dt / SPACE_GUN_SETTLE_TAU);
        spaceGunKickPitch *= Math.exp(-dt / SPACE_GUN_SETTLE_TAU);
        const deflect = Math.hypot(spaceGunKickYaw, spaceGunKickPitch);
        if (spaceGunOutOfBattery && deflect <= SPACE_GUN_RESUME_RAD) spaceGunOutOfBattery = false;
        // The bore is the lay plus the deflection — where the shells go.
        const boreYaw = spaceGunYaw + spaceGunKickYaw;
        const borePitch = spaceGunPitch + spaceGunKickPitch;
        const gcp = Math.cos(borePitch);
        const bore =
          gcp * Math.cos(boreYaw) * ax +
          Math.sin(borePitch) * ay +
          -gcp * Math.sin(boreYaw) * az;
        spaceGunsLaid = bore > 0.997;
        // No mark in the glass means no computer to wait for: the guns hose
        // wherever they happen to be looking, which is where the tracers go.
        // Out of battery outranks both — a settling mount shoots at nothing.
        spaceFireOK = !spaceGunOutOfBattery && (!onMark || spaceGunsLaid);
        spaceGunsMode = spaceGunOutOfBattery
          ? "SETTLING"
          : !onMark
            ? "FREE"
            : spaceGunsLaid
              ? "LAID"
              : "SLEWING";

        // Fire the local mirror of the cadence, and take the kick for it. The
        // server is authoritative about the rounds; this exists so the glass
        // knows when the mount is about to walk off the mark.
        if (alive && input.trigger && spaceFireOK) {
          const kit = planeTypes[selfPlane];
          const cal = calibers[kit?.gun?.caliberIdx] ?? calibers.find((c) => c.name === kit?.gun?.caliber);
          const rps = cal?.roundsPerSec > 0 ? cal.roundsPerSec : 3;
          const barrels = Math.max(1, kit?.gun?.barrels ?? 1);
          const now = perfNow();
          if (now >= spaceGunNextAt) {
            spaceGunNextAt = now + (barrels > 1 ? 2 : 1) / rps;
            // The same walk for every hull — see spaceGunKick's note on why
            // scaling this by mass exempted the heavy ships entirely.
            const kick = SPACE_GUN_KICK_RAD;
            for (let b = 0; b < barrels; b++) {
              spaceGunKickPitch += kick * (1 - SPACE_GUN_KICK_YAW_FRAC);
              spaceGunKickYaw += kick * SPACE_GUN_KICK_YAW_FRAC * (Math.random() * 2 - 1);
            }
            if (Math.hypot(spaceGunKickYaw, spaceGunKickPitch) > SPACE_GUN_BREAK_RAD) {
              spaceGunOutOfBattery = true;
            }
          }
        }
      }
      // A latched gyro hold (PRO/RET) still outranks the director — it's
      // the player saying "I have the nose", and the server's gyro flies
      // it. A landed hull flies nothing: the lever stays raw (that IS the
      // relaunch) while the gaze keeps looking around. And held RCS keys
      // need no suspension clock anymore: composeAxes fades the director's
      // assist out axis-by-axis as a key deflects, so a committed key owns
      // its surface and a light tap trims on top.
      const directorOn = keeperOn && !pred.landed && !spectating && spaceHold === 0;
      if (directorOn) {
        // THE DIRECTOR FLIES. Guards first, intent second — and that is the
        // whole list now. The guns used to sit in the middle of it, stealing
        // the nose for as long as the trigger was down; they have their own
        // mount above and the hull never hears the trigger again.
        const kt = planeTypes[selfPlane] || {};
        const accMax = kt.maxThrustN > 0 && kt.massKg > 0 ? kt.maxThrustN / kt.massKg : 8;
        const ro = orbitReadout(pred, spaceConsts);
        const speed = Math.hypot(pred.vel.x, pred.vel.y, pred.vel.z);
        const prograde =
          speed > 3
            ? { x: pred.vel.x / speed, y: pred.vel.y / speed, z: pred.vel.z / speed }
            : { x: 0, y: 1, z: 0 };
        let dir = spaceCamDir;
        let thr = 0;
        keeperMode = "HOLD";
        const lever = touch ? touch.throttleValue : input.throttle;
        // LANDING INTENT, with hysteresis so the frame never flickers at the
        // boundary: gaze steeply DOWN with the lever in is a landing order —
        // the one travel intent that must be flown against the GROUND, not
        // the orbit (holding "circular minus a bit" all the way down is an
        // arrival at a hundred metres a second). Enter steep, leave shallow.
        // (The trigger used to veto this and every other intent — it was the
        // order that took the nose. It isn't any more: you can shoot on the
        // way down.)
        if (spaceLandingIntent) {
          if (spaceCam.pitch > -0.65 || lever < 0.03) spaceLandingIntent = false;
        } else if (spaceCam.pitch <= -0.8 && lever >= 0.03) {
          spaceLandingIntent = true;
        }
        const powered = lever >= 0.03;
        // Suicide-burn arithmetic (the raid doctrine's): how much sky does
        // stopping this sink rate cost? Plus a short look down the track —
        // the hill ahead is the one with your name on it.
        const g0k = spaceConsts.mu / (spaceConsts.R * spaceConsts.R);
        // aglNow is the height under the hull RIGHT NOW — the landing
        // envelope plans on it. aglK is the guard's pessimist: the same
        // height min'd over a 4/8 s lookahead, which is right for "is the
        // hill ahead mine" and fatally wrong for planning a descent (any
        // sink collapses it toward zero, the envelope reads "nearly down",
        // and the whole final hundred metres becomes a tank-draining creep
        // — the telemetry watched Δv hit zero at sixty metres, twice).
        const aglNow = pred.pos.y - (field?.surfaceAt?.(pred.pos.x, pred.pos.z) ?? 0);
        let aglK = aglNow;
        if (field?.surfaceAt && pred.vel.y < 0) {
          for (const ts of [4, 8]) {
            const ah =
              pred.pos.y + pred.vel.y * ts -
              field.surfaceAt(pred.pos.x + pred.vel.x * ts, pred.pos.z + pred.vel.z * ts);
            if (ah < aglK) aglK = ah;
          }
        }
        const netUpK = Math.max(0.8, accMax * 0.85 - g0k);
        const stopDK = pred.vel.y < 0 ? (pred.vel.y * pred.vel.y) / (2 * netUpK) : 0;
        if ((ro.apo === -1 || ro.apo > spaceConsts.ceiling - 300) && pred.vel.y > 0 && !powered) {
          // The roof — judged by the OSCULATING orbit, which is only honest
          // while coasting. Under power the bell is holding the path and the
          // apoapsis "balloons" ~43 m per m/s of overtake without the craft
          // going anywhere near the roof (the first director playtest
          // watched a level overtake read ROOF GUARD and get retro-slammed
          // mid-intent). Powered flight gets an intent clamp in TRAVEL
          // instead; the server's bleed only ever bites real altitude.
          dir = { x: -prograde.x, y: -prograde.y, z: -prograde.z };
          thr = 0.5; // the heavy moon: a burn-second moves apsides ~1.5 km — small levers
          keeperMode = "ROOF GUARD";
        } else if (
          pred.vel.y < -2 && aglK < stopDK * 1.8 + 150 &&
          !(spaceLandingIntent && pred.vel.y > -55)
        ) {
          // TERRAIN — the bell goes under the ship, now. The first keeper
          // chased a falling prograde here: burning down the flight path
          // raises an apoapsis that rotates away ahead of you while the
          // ground does not move at all ("it chases it right into the
          // ground", verbatim). Mostly-up with a lean down-track keeps the
          // energy while it buys the altitude. A CONTROLLED landing descent
          // (the intent above, sink under 55 m/s) is exempt — the envelope
          // in the landing branch is this same suicide-burn arithmetic,
          // flown on purpose; without the exemption no hull could ever get
          // low enough to touch its own relay.
          const hh = Math.hypot(pred.vel.x, pred.vel.z) || 1;
          dir = { x: (pred.vel.x / hh) * 0.3, y: 1, z: (pred.vel.z / hh) * 0.3 };
          const dl = Math.hypot(dir.x, dir.y, dir.z);
          dir = { x: dir.x / dl, y: dir.y / dl, z: dir.z / dl };
          thr = 1.0;
          keeperMode = "TERRAIN — CLIMBING";
        } else {
          // TRAVEL / HOLD — the intent law, validated headless before it
          // ever flew: demand a = K·(vTarget − v) − kernelAccel, so a held
          // target is held exactly and "centered" IS altitude hold. The
          // lever times the gaze into a velocity target laid over the LOCAL
          // circular rate — the x component through the wire conversion
          // (vx is the datum rate; a physical along-track speed is ×R/r on
          // the wire — the ASAT kick's frame-mixing lesson). Lever out:
          // trim to circular at the current altitude and coast — pure
          // damping, no feedforward, because the terminal state has none —
          // with the nose idling on the gaze so the guns are always one
          // squeeze from ready. K = 1.4 and the 0.85·accMax cap are the
          // validated pair: firm without out-running what the RCS can keep
          // the nose pointed at.
          const r = spaceConsts.R + pred.pos.y;
          const vCircW = circularVX(spaceConsts.mu, spaceConsts.R, pred.pos.y);
          let dax, day, daz;
          if (lever >= 0.03) {
            const os = {
              x: pred.pos.x, h: pred.pos.y, z: pred.pos.z,
              vx: pred.vel.x, vh: pred.vel.y, vz: pred.vel.z,
            };
            const ka = orbitAccel(os, spaceConsts.mu, spaceConsts.R);
            const vmax = 60; // full lever, relative m/s: a brisk overtake, not an escape
            let vTx, vTy, vTz;
            if (spaceLandingIntent) {
              // LANDING — the ground frame, in the bots' own phases: KILL
              // THE ORBIT at held altitude first (braking and sinking share
              // one engine badly — the saturated deorbit burn can't also
              // track a sink profile, and the first lander arrived at −37),
              // then FALL: gravity does the descending and the bell only
              // argues with it, riding the suicide-burn envelope down to
              // the touchdown gate (vy > −4, drift < 8). The envelope
              // replaced a gentle constant-sink profile that measured
              // beautifully and killed absolutely: tracking a slow creep
              // means hovering, hovering costs ~8 m/s of Δv per second,
              // and the telemetry watched the tank hit zero at sixty
              // metres — a dead-stick crater with a full instrument log.
              // Lateral intent fades out on final so the drift gate is met
              // no matter how slanted the gaze that ordered the landing.
              const gndSpd = Math.hypot(pred.vel.x * (r / spaceConsts.R), pred.vel.z);
              const lat = Math.max(0, Math.min(1, (aglNow - 40) / 220));
              vTx = spaceCamDir.x * lever * vmax * (spaceConsts.R / r) * lat;
              vTz = spaceCamDir.z * lever * vmax * lat;
              if (gndSpd > 25) {
                vTy = 0;
                keeperMode = "LANDING — BRAKING";
              } else {
                const env = Math.min(45, 0.85 * Math.sqrt(2 * netUpK * Math.max(0, aglNow - 14)));
                vTy = -Math.max(2.2, env);
                keeperMode = "LANDING";
              }
            } else {
              // TRAVEL — the orbit frame: the gaze times the lever, laid
              // over the LOCAL circular rate.
              vTx = vCircW + spaceCamDir.x * lever * vmax * (spaceConsts.R / r);
              vTy = spaceCamDir.y * lever * vmax;
              vTz = spaceCamDir.z * lever * vmax;
              // The roof, translated for intent flight: near the ceiling
              // "up" stops meaning up. (The coasting guard above stands
              // down under power — osculating apsides lie mid-burn.)
              if (pred.pos.y > spaceConsts.ceiling - 150 && vTy > 0) vTy = 0;
              keeperMode = "TRAVEL";
            }
            dax = 1.4 * (vTx - pred.vel.x) - ka.ax;
            day = 1.4 * (vTy - pred.vel.y) - ka.ah;
            daz = 1.4 * (vTz - pred.vel.z) - ka.az;
            // A landing never burns DOWNWARD: sinking slower than the
            // envelope allows is free fall's job, not the bell's — the
            // feedforward would otherwise hover-track the target sink and
            // eat the tank doing gravity's work.
            if (spaceLandingIntent && day < 0) day = 0;
          } else {
            dax = 1.4 * (vCircW - pred.vel.x);
            day = 1.4 * (0 - pred.vel.y);
            daz = 1.4 * (0 - pred.vel.z);
            keeperMode = "TRIMMING";
          }
          let mag = Math.hypot(dax, day, daz);
          const cap = accMax * 0.85;
          if (spaceLandingIntent && lever >= 0.03) {
            // Powered-descent allocation: the VERTICAL axis eats first, the
            // deorbit gets the remainder. A plain magnitude cap let the
            // saturated retro burn starve the hover entirely — gravity ran
            // the sink to −33 m/s while every newton argued with the orbit,
            // and the quiet-server lander cratered with nobody shooting at
            // it. Braking a touch slower is the price of arriving.
            const dayC = Math.max(-cap, Math.min(cap, day));
            const hb = Math.sqrt(Math.max(0, cap * cap - dayC * dayC));
            const hm = Math.hypot(dax, daz);
            if (hm > hb) {
              dax *= hb / hm;
              daz *= hb / hm;
            }
            day = dayC;
            mag = Math.hypot(dax, day, daz);
          } else if (mag > cap) {
            dax *= cap / mag; day *= cap / mag; daz *= cap / mag;
            mag = cap;
          }
          // Converged means "below what the engine can even do": the craft
          // step zeroes any order under 3% (the idle deadband — a lever
          // resting at 1% must not rewrite the orbit), so a demand that maps
          // under it is a phantom forever — the first trim sat at
          // eng=0.014, narrating TRIMMING for a burn that never fired.
          if (mag > accMax * 0.035) {
            dir = { x: dax / mag, y: day / mag, z: daz / mag };
            thr = Math.min(1, mag / accMax);
          } else {
            // On target and the kernel agrees: engines cold — coasting is
            // the only thing up here that's free.
            dir = spaceCamDir;
            thr = 0;
            keeperMode = "HOLD";
          }
        }
        const fwd = FDM.qRotate(pred.att, FDM.v3(1, 0, 0));
        const bore = fwd.x * dir.x + fwd.y * dir.y + fwd.z * dir.z;
        if (thr > 0 && bore < 0.95) thr = 0; // swing first, burn second
        const maxRate = kt.rcsRatePS > 0 ? kt.rcsRatePS : 1.2;
        const rr = spaceSteerRatesClient(pred.att, dir, maxRate);
        input.setAssist(
          Math.max(-1, Math.min(1, rr.pitch / maxRate)), 0,
          Math.max(-1, Math.min(1, rr.yaw / maxRate)),
        );
        spaceEngineCmd = thr;
        // LIFTOFF GRACE: the server's landed latch releases at throttle
        // > 0.6, and it reads the WIRE. The prediction lifts a beat before
        // the server does, the director takes the wire over, and its first
        // hover order (< 0.6) parks the server on the pad forever — the
        // snapshot slams the hull back down and the touchdown park eats
        // the lever mid-shove. For the first second past the landed edge
        // the lever rides the wire raw: the shove that lifts the
        // prediction is the shove the server hears.
        if (perfNow() < spaceLiftoffRawUntil) spaceEngineCmd = -1;
        if (window.__va) {
          window.__va.keeperDbg = {
            dot: bore, dir: [dir.x, dir.y, dir.z], fwd: [fwd.x, fwd.y, fwd.z],
            pc: rr.pitch, yc: rr.yaw, thr, mode: keeperMode, fireOK: spaceFireOK,
            gunYaw: spaceGunYaw, gunPitch: spaceGunPitch, guns: spaceGunsMode,
          };
        }
        steerMode = "keeper";
      } else {
        keeperMode = keeperOn ? (spaceHold ? "GYRO HOLD" : pred.landed ? "STANDBY" : "") : "";
        if (touch && !keeperOn) {
          // Raw mode: the thumb IS the RCS. Deflection maps straight onto
          // body rate commands — up pitches the nose up, sideways swings it
          // (spacecraft argue with their noses, not their wings) — through
          // the same assist channel the aero stick rides, with zero aero
          // brains attached.
          // Yaw sign: +yaw is nose-LEFT in the flight model (the rudder
          // convention), and the thumb pushed right must swing the nose
          // right — so the stick's x feeds the assist NEGATED. Shipping it
          // raw reversed left and right on every phone ("left/right are
          // reversed on the touch stick", verbatim).
          input.setAssist(touch.ay, 0, -touch.ax);
          steerMode = "stick";
        } else {
          input.setAssist(0, 0, 0);
        }
      }
    } else if (alive && input.mouseAim && !input.pad.active) {
      if (touch) {
        // A thumb is a stick, not a mouse: sideways deflection commands a
        // held bank angle (full stick = the hardest sustained turn), not a
        // camera-locked aim point. Chasing an aim point pinned 30 degrees off
        // the nose wound the wings past vertical and buried the plane — the
        // "I only have rudder and elevator" report. Steering no longer reads
        // the camera at all, so freelook needs no aim-freeze here either.
        const assist = FDM.steerStickAssist(pred, touch.ax, touch.ay, {
          alphaStallRad: planeTypes[selfPlane]?.alphaStallRad ?? 0.3,
          gMax: 6.0,
          jetLead: CLASS_JET.has(selfPlane) ? 1.4 : 0, // jets: no limiter hump (fdm.js)
        });
        input.setAssist(assist.pitch, assist.roll, assist.yaw);
        steerMode = "stick";
      } else {
        // Feed the director this frame's hand: the pointer-lock pixels (with
        // the elevator convention applied — aimDelta is deliberately raw so
        // the naval position sights stay un-inverted), the live camera
        // pinhole for the projection, and the busy-hand flags that hold its
        // settle clock (a freelook orbit or keyboard flying is a hand on the
        // controls, not a settled one).
        const d = input.takeAimDelta();
        const kbBusy =
          Math.abs(input.kb.pitch) + Math.abs(input.kb.roll) + Math.abs(input.kb.yaw) > 0.05;
        aimDirector.frame(pred, {
          dxPx: d.x,
          dyPx: input.invertPitch ? -d.y : d.y,
          sens: input.mouseSens,
          dt,
          fovDeg: camera.fov,
          aspect: camera.aspect,
          locked: input.pointerLocked,
          absX: input.cursor.x,
          absY: input.cursor.y,
          handBusy: input.look.active || kbBusy,
        });
        // The reticle everyone reads — the HUD dot, the e2e aim probes —
        // IS the director's projection while it has command.
        if (input.pointerLocked) {
          input.cursor.x = aimDirector.cursor.x;
          input.cursor.y = aimDirector.cursor.y;
        }
        steerMode = "cursor"; // the refresh re-solves against live pred
        const assist = aimDirector.steer(pred, {
          alphaStallRad: planeTypes[selfPlane]?.alphaStallRad ?? 0.3,
          gMax: 6.0,
          jetLead: CLASS_JET.has(selfPlane) ? 1.4 : 0, // jets: no limiter hump (fdm.js)
        });
        assist.pitch = pathHold(assist.pitch);
        input.setAssist(assist.pitch, assist.roll, assist.yaw);
      }
    } else {
      input.setAssist(0, 0, 0);
      aimDirector.drop();
      pathHoldS = 0;
    }
    // While crewing a hull the flight throttle keys are parked: Z and X double
    // as the gunsight and shell selector in the hull theaters, so they must not
    // silently drag the telegraph. W/S run the drive slider instead (above).
    input.hullDrive = driving && hullTheater;
    input.update(dt);

    // --- hands-off autopilot (touch): the stick IS the switch ---
    // A thumb on the stick means you're flying it, so autopilot drops the
    // instant one lands. Lift off and, once handsOffSec passes (and you're not
    // mid-stunt), it latches the heading hold and levels the wings. Flag-only,
    // no toast — it flips as your thumb comes and goes. The P key still works on
    // desktop; this replaces the old, unreliable double-tap.
    if (touch) {
      const stickHeld = touch.stick.id !== null;
      if (stickHeld && input.autopilot) {
        input.autopilot = false;
        input.onAutopilot(false, true);
      }
      if (stickHeld || !alive) {
        // Hands on the stick, or not a live cockpit (dead, or crewing a gun /
        // vehicle / bomber turret — where the left zone slews, it doesn't fly):
        // hold the clock at now so the latch always needs a fresh handsOffSec of
        // genuine hands-off flight — no autopilot waiting on you the instant you
        // spawn, and never while you're a gunner.
        stickHeldAt = perfNow();
      } else if (!input.autopilot && perfNow() - stickHeldAt >= handsOffSec) {
        // Latch and recover to level. Hands-off flight slowly diverges (the
        // wings roll off on their own), so the point is to CATCH that — don't
        // demand the plane already be level. The one thing we won't hijack is a
        // deliberate stunt held hands-light: skip while inverted or pointed
        // near-vertical, where auto-levelling would fight the pilot.
        const fwd = FDM.qRotate(pred.att, FDM.v3(1, 0, 0));
        const up = FDM.qRotate(pred.att, FDM.v3(0, 1, 0));
        const notAerobatic = up.y > 0.2 && Math.abs(fwd.y) < 0.8; // upright-ish, not near-vertical
        if (notAerobatic) {
          input.autopilot = true;
          input.onAutopilot(true, true);
        }
      }
    }

    // --- autopilot: hold the captured heading straight and level ---
    // input.update() just composed the pilot's manual controls; override them
    // toward the locked horizon heading (throttle stays the pilot's). It's a
    // plain on/off toggle — manual input does NOT disengage it — but dying drops
    // it, since only a live cockpit has an autopilot.
    if (input.autopilot) {
      if (!alive || spaceTheater) {
        // Only a live AIRPLANE cockpit has this autopilot — "straight and
        // level" is not a thing an orbit does; space has the gyro holds.
        input.autopilot = false;
        hud.setAutopilot(false);
      } else {
        const ap = FDM.autopilotLevel(pred, apHeadingDir);
        input.pitch = ap.pitch;
        input.roll = ap.roll;
        input.yaw = ap.yaw;
      }
    }

    // --- auto-trim: relieve the steady back-pressure of level/cruise flight ---
    // Without trim the airframe only holds altitude hands-off near its (high)
    // trim speed, so at normal speeds the pilot fights a constant sink. This
    // eases the vertical speed toward zero and folds the result into the
    // commanded pitch — which is exactly what both the local prediction and the
    // server integrate, so the FDM itself is untouched. Its authority fades out
    // as the pilot commands pitch, so a deliberate climb, dive, or hard turn
    // overrides it; the autopilot owns pitch outright, so trim steps aside while
    // it's engaged.
    // Space flies without the aero comfort systems: auto-trim holds a
    // HORIZON, and a coasting orbit pitches through every horizon there is —
    // the wheel winds up a phantom elevator that fights the gyros (and trips
    // their hands-on-stick release). The gyro holds are the space trim.
    if (alive && input.autoTrim && !input.autopilot && !spaceTheater) {
      const manual = input.pitch; // composed pilot command, pre-trim
      // Elevator that would hold the horizon right now. This is the autopilot's
      // pitch loop (a proportional pitch-attitude hold with a small vertical-
      // speed bias and strong rate damping) — kept in step with the gentle,
      // delay-tolerant gains in FDM.autopilotLevel so trim settles on the
      // horizon without porpoising, even in a gusty storm round.
      const pitchAngle = Math.asin(clamp1(FDM.qRotate(pred.att, FDM.v3(1, 0, 0)).y));
      const vsTarget = clamp1Range(-pred.vel.y * 0.03, 0.1);
      const levelPitch = clamp1Range((vsTarget - pitchAngle) * 1.6 - pred.omega.z * 0.9, 0.7);
      // Ease the held trim toward that command (a smooth lag, so it reads like a
      // trim wheel settling and never winds up), then fold it in with an
      // authority that fades as the pilot pushes or pulls — so a deliberate
      // climb, dive, or hard turn always takes over.
      // While the path hold owns the elevator (pathHoldS), trim relief
      // steps aside the same way it does for the autopilot — the hold IS the
      // relief, and stacking both porpoises the nose.
      const w = clamp01(1 - Math.abs(manual) / TRIM_FADE) * (1 - pathHoldS);
      pitchTrim += (levelPitch - pitchTrim) * TRIM_RESPONSE * dt;
      pitchTrim = clamp1Range(pitchTrim, TRIM_LIMIT);
      input.pitch = clamp1(manual + pitchTrim * w);
      hud.setTrim(pitchTrim * w, true);
    } else if (!alive) {
      // Each life starts in trim; the gauge shows centred & live (or greyed).
      pitchTrim = 0;
      hud.setTrim(0, input.autoTrim);
    }

    // --- manned AA gun: local aim prediction ---
    if (manningMain) {
      // Main battery: the ballistic computer trains the turret — the player
      // plots targets instead of slewing. Ease the camera aim toward the
      // authoritative mount orientation; snapshots drive the mesh directly.
      const ent = aaGuns.guns.get(selfGunId);
      if (!gunAim.has) {
        gunAim.has = true;
        gunAim.yaw = ent.srvYaw;
        gunAim.pitch = ent.srvPitch;
      }
      gunCtl.yaw = 0;
      gunCtl.pitch = 0;
      const blend = 1 - Math.exp(-4 * dt);
      gunAim.yaw += wrapAngle(ent.srvYaw - gunAim.yaw) * blend;
      gunAim.pitch += (ent.srvPitch - gunAim.pitch) * blend;
      aaGuns.clearLocalAim();
    } else if (manning) {
      const ent = aaGuns.guns.get(selfGunId);
      if (!gunAim.has) {
        gunAim.has = true;
        gunAim.yaw = ent.srvYaw;
        gunAim.pitch = ent.srvPitch;
      }
      // Compose slew-rate inputs: mouse cursor deflection (or sticks), with
      // the keyboard pitch/roll axes stacked on top as a trim.
      let yawIn = input.kb.roll;
      let pitchIn = input.kb.pitch;
      if (input.pad.active) {
        yawIn += input.pad.roll;
        pitchIn += input.pad.pitch;
      } else if (input.mouseAim) {
        // Expo curve: gentle near centre for fine tracking, full slew at the
        // edge of the steering circle. Tames the "too twitchy" flak aim.
        yawIn += aimExpo(clamp1(input.cursor.x / 0.55));
        pitchIn += aimExpo(clamp1(input.cursor.y / 0.55));
      }
      gunCtl.yaw = clamp1(yawIn);
      gunCtl.pitch = clamp1(pitchIn);
      // Mirror the server's manned slew (trackRate × mannedSlewMult, see
      // stepMannedGun): +yaw input traverses right, i.e. decreasing world yaw.
      const prof = gunProf();
      const rate = prof.trackRateRadPerSec * 1.3;
      gunAim.yaw = wrapAngle(gunAim.yaw - gunCtl.yaw * rate * dt);
      gunAim.pitch = Math.min(1.45, Math.max(-0.1, gunAim.pitch + gunCtl.pitch * rate * dt));
      // Gentle reconciliation toward the authoritative aim.
      const blend = 1 - Math.exp(-1.2 * dt);
      gunAim.yaw += wrapAngle(ent.srvYaw - gunAim.yaw) * blend;
      gunAim.pitch += (ent.srvPitch - gunAim.pitch) * blend;
      aaGuns.setLocalAim(selfGunId, gunAim.yaw, gunAim.pitch);
      // Local muzzle feedback; the server's flak rounds arrive as tracers.
      ent.flash.visible = input.trigger && Math.random() > 0.45;
      gunCooldown -= dt;
      if (input.trigger && gunCooldown <= 0) {
        gunCooldown = 1 / (prof.burstRoundsPerSec ?? 6);
        audio.gunshot();
      }
    } else if (driving) {
      // Driving: the throttle drives, A/D steers the hull (server-side), and
      // the mouse/right-stick slews the mount — locally predicted like a gun.
      const aim = vehiclesR.srvAimOf(selfVehicleId);
      if (!vehAim.has) {
        vehAim.has = true;
        vehAim.yaw = aim.yaw;
        vehAim.pitch = aim.pitch;
      }
      // POSITION-control mouse aim (pointer locked): the sight moves exactly
      // as far as the mouse does and STOPS when the hand stops — no slew-rate
      // "virtual joystick", whose deflected cursor kept the sight sailing past
      // the target and forced endless overcorrection. The server's mount then
      // CHASES the sight at its own rate cap via the wire's slew input.
      // Sticks and the touch stick keep the classic rate control.
      const posAim = input.mouseAim && input.pointerLocked && !input.pad.active && !touch;
      const aimD = posAim ? input.takeAimDelta() : null;
      // Mouse sensitivity scales with the live FOV so a hand sweep covers the
      // same fraction of the screen through the zoomed gunsight as out of it.
      const fovNow = navalScoped
        ? landCrew() ? (LAND_SCOPE_FOV[landVehIdx()] ?? 8) : NAVAL_SCOPE_FOV
        : 72;
      const aimSens = 0.0022 * (fovNow / 72); // rad per mouse px
      let yawIn = 0, pitchIn = 0;
      if (input.pad.active) {
        yawIn = input.pad.roll;
        pitchIn = input.pad.pitch;
      } else if (input.mouseAim && !posAim) {
        // Same expo curve as the flak mount: gentle near centre for fine
        // tracking, full slew at the edge of the steering circle.
        yawIn = aimExpo(clamp1(input.cursor.x / 0.45));
        pitchIn = aimExpo(clamp1(input.cursor.y / 0.45));
      }
      vehCtl.yaw = clamp1(yawIn);
      vehCtl.pitch = clamp1(pitchIn);
      vehCtl.roll = clamp1(input.kb.roll); // A/D steers the hull
      if ((hullTheater || armadaTheater) && !touch) {
        // W/S work the drive slider directly: W accelerates, S brakes and —
        // below the bottom quarter — backs a land hull up (the server's
        // reverse band; a ship's telegraph clamps at stop). Gated on the hull
        // theaters so the air game's Shift/Ctrl throttle stays untouched —
        // and keyed on the THEATER, not the hull class, because every hull a
        // player can drive here works its drive order the same way. The
        // armada playtest found tanks parked forever: this gate read
        // `landTheater`, so the combined-arms theater never mapped W/S at
        // all and a desktop tank had no way to move. And it must read
        // driveAxis, NOT kb.pitch: kb.pitch carries the elevator's
        // invertPitch flip (the shipped default in the pull-back-to-climb
        // era), which put every fresh-settings hull in reverse gear on W —
        // the e2e helm probe caught tanks and warships driving backwards.
        input.throttle = clamp01(input.throttle + input.driveAxis * dt * 0.9);
      }
      vehCtl.throttle = input.throttle; // drives forward
      const prof = vehProf();
      const mainGun = vehMainGun();
      const trackRate = mainGun?.trackRateRadPerSec ?? prof?.trackRateRadPerSec ?? 1;
      const srvRate = trackRate * 1.3; // mirror mannedSlewMult
      let rate = srvRate;
      // Fine aim through the magnified gunsight. G-polish: floor the reduction at
      // close range so a destroyer knife-fight inside 1.5 km stays trackable
      // (0.3× of an already rate-capped turret was too slow to follow a fast pass).
      if (navalScoped && !posAim) {
        // Tighter slew under magnification, scaled to each optic's zoom: the
        // Paladin's 6° sight tracks finest, the Whirlwind's wide reflector
        // barely slows at all. (Position control needs none of this — the
        // FOV-scaled sensitivity is the fine aim.)
        rate *= landCrew()
          ? Math.min(0.65, (LAND_SCOPE_FOV[landVehIdx()] ?? 8) / 26)
          : navalRangeLive > 0 && navalRangeLive < 1500 ? 0.45 : 0.14; // tighter under the deeper 7° naval zoom
      }
      const blend = 1 - Math.exp(-1.2 * dt);
      if (posAim) {
        if (navalTheater && navalScoped) {
          // Scoped windage: damp the axis further (NAVAL_SCOPE_WINDAGE) and
          // ease the travel out over ~50 ms — a critically-damped filter that
          // swallows hand jitter but still stops the sight exactly where the
          // hand stops, so the crosshair can be laid ON a hull and stay there.
          scopeAimPend += aimD.x * aimSens * NAVAL_SCOPE_WINDAGE;
          const step = scopeAimPend * (1 - Math.exp(-22 * dt));
          vehAim.yaw = wrapAngle(vehAim.yaw - step);
          scopeAimPend -= step;
        } else {
          scopeAimPend = 0;
          vehAim.yaw = wrapAngle(vehAim.yaw - aimD.x * aimSens);
        }
        // Keep the sight within a turret's-catch-up of the server mount so a
        // wild sweep can't park the crosshair 180° from where the guns point.
        const lead = wrapAngle(vehAim.yaw - aim.yaw);
        const maxLead = 0.6;
        if (Math.abs(lead) > maxLead) vehAim.yaw = wrapAngle(aim.yaw + Math.sign(lead) * maxLead);
        // The wire's yaw input becomes a chase command: full slew until the
        // mount is a few degrees out, easing to a stop ON the sight. No
        // reconciliation drags the sight itself — that rubber-band toward the
        // latency-lagged server aim was the "momentum" that overshot targets.
        vehCtl.yaw = clamp1(wrapAngle(aim.yaw - vehAim.yaw) / (srvRate * 0.15));
        // Park the flight cursor so leaving the hull starts from neutral.
        input.cursor.x = 0;
        input.cursor.y = 0.04;
      } else {
        vehAim.yaw = wrapAngle(vehAim.yaw - vehCtl.yaw * rate * dt);
        vehAim.yaw += wrapAngle(aim.yaw - vehAim.yaw) * blend;
      }
      // vehAim is the LINE OF SIGHT (camera + crosshair), always mouse-driven.
      if (navalCrew() && mainGun && navalScoped) {
        // Through the gunsight the vertical axis is a RANGE DIAL, not raw
        // pitch: from a mast-height eye the whole 4–13 km band spans well
        // under a degree of depression, so integrating pitch made long-range
        // laying twitchy beyond use. Instead the aim range walks
        // proportionally (mouse up = farther, or the scroll wheel) and the
        // sight pitch is DERIVED to keep the crosshair on that water point —
        // buttery at every range, and the enemy-hull snap still takes over
        // when the sight crosses a ship. The vertical input is RAW (no expo
        // curve): the expo deadzone made small mouse offsets read as zero and
        // the dial felt stuck.
        if (!(navalScopeRange > 0)) navalScopeRange = Math.max(600, navalRangeLive || 3000);
        const maxR = navalGunRange(mainGun) || 13000;
        let rawPitch = input.pad.active
          ? input.pad.pitch
          : input.mouseAim
            ? clamp1(input.cursor.y / 0.45)
            : 0;
        // Deadband: the touch stick eases back to a slightly-above-centre
        // rest, and a mouse cursor parks a hair off zero — neither should
        // creep the dial.
        if (Math.abs(rawPitch) < 0.12) rawPitch = 0;
        const dial = clamp1(rawPitch + navalScopeHold); // ▲/▼ hold buttons stack in
        navalScopeRange = Math.min(maxR, Math.max(400, navalScopeRange * (1 + dial * 1.5 * dt)));
        if (posAim && aimD.y) {
          // Position control: mouse travel walks the dial directly — push up
          // to reach out, pull back to bring the salvo in, stop to hold.
          navalScopeRange = Math.min(maxR, Math.max(400, navalScopeRange * (1 + aimD.y * 0.0016)));
        }
        const me = vehiclesR.positionOf(selfVehicleId);
        const eyeUp = (me?.y ?? 0) + NAVAL_DIRECTOR_Y - (config.world?.waterLevelM ?? 0);
        vehAim.pitch = -Math.atan(eyeUp / navalScopeRange);
      } else {
        // Land mounts depress further than the naval director (tall sight
        // head, see LAND_GUN_PITCH_MIN); the server clamps to the same stop.
        const pitchMin = landCrew() ? LAND_GUN_PITCH_MIN : -0.1;
        vehAim.pitch = posAim
          ? Math.min(1.45, Math.max(pitchMin, vehAim.pitch + aimD.y * aimSens))
          : Math.min(1.45, Math.max(pitchMin, vehAim.pitch + vehCtl.pitch * rate * dt));
        navalScopeRange = 0; // re-seed the dial from the live range on scope entry
      }
      let borePitch;
      if (navalCrew() && mainGun) {
        // Naval fire control (World-of-Warships style): the guns auto-elevate to
        // the range of the point under the crosshair every frame, so the sight is
        // purely the camera — never reconciled to the (auto-elevated) bore. Hold
        // the last elevation when there's no aim point under the sight.
        navalRangeLive = measureNavalRange(vehAim.yaw, vehAim.pitch);
        if (navalRangeLive > 0) {
          navalBorePitch = clampPitch(navalElevation(navalRangeLive, mainGun.muzzleSpeedMS));
        }
        borePitch = navalBorePitch;
        if (navalScoped) updateScopeRange();
      } else if (landCrew()) {
        // Land fire control: the sight IS the bore line (direct fire) and the
        // server adds superelevation for the crosshair's range, so the round
        // drops onto the exact point under the sight. The range also feeds
        // the missile guidance and the artillery plot, so every hull ranges.
        // (landCrew, not landTheater: an armada tank ranges exactly like a
        // land-theater one.)
        navalRangeLive = measureNavalRange(vehAim.yaw, vehAim.pitch);
        borePitch = vehAim.pitch;
        if (navalScoped) updateScopeRange();
      } else {
        if (!posAim) vehAim.pitch += (aim.pitch - vehAim.pitch) * blend; // tanks: the bore follows the sight
        borePitch = vehAim.pitch;
      }
      if (posAim) {
        // Pitch chase for the mounts the server elevates by wire (land guns,
        // tank bores — naval main batteries auto-elevate from the aim range
        // and ignore it): slew the server pitch onto the sight's.
        vehCtl.pitch = clamp1((vehAim.pitch - aim.pitch) / (srvRate * 0.15));
      }
      vehiclesR.setLocalAim(selfVehicleId, vehAim.yaw, borePitch);
      gunCooldown -= dt;
      if (input.trigger && gunCooldown <= 0) {
        if (mainGun) {
          // Main gun: one heavy round, then the reload. The server gates the
          // shell; this is feedback. A warship battery gets the full salvo
          // boom, weighted by its class; a tank keeps the cannon's bark.
          gunCooldown = mainGun.reloadSec ?? 4;
          if (navalTheater) audio.salvo(vehiclesR.muzzlesOf(selfVehicleId)?.k ?? 0.6);
          else audio.cannon();
        } else if (prof) {
          gunCooldown = 1 / (prof.burstRoundsPerSec ?? 6);
          audio.gunshot();
        }
      }
    } else if (turretting) {
      // Bomber turret: aim lives in the host airframe's body frame, so the
      // mount holds its bearing as the bomber banks. Mirror the server's
      // stepTurretGunners integration (slew rate, elevation and arc clamps)
      // so the view answers instantly; snapshots only nudge it back in line.
      const tcfg = turretCfg();
      if (!turAim.has) {
        turAim.has = true;
        turAim.yaw = turSrv.has ? turSrv.yaw : (tcfg?.yawCenterRad ?? 0);
        turAim.pitch = turSrv.has ? turSrv.pitch : 0.15;
      }
      let yawIn = input.kb.roll;
      let pitchIn = input.kb.pitch;
      if (input.pad.active) {
        yawIn += input.pad.roll;
        pitchIn += input.pad.pitch;
      } else if (input.mouseAim && !input.look.active) {
        // While freelooking (view-only, see the turret camera) the mouse moves
        // the eye, not the gun: hold the mount steady instead of chasing the
        // cursor the player is using to glance around.
        yawIn += aimExpo(clamp1(input.cursor.x / 0.55));
        pitchIn += aimExpo(clamp1(input.cursor.y / 0.55));
      }
      turCtl.yaw = clamp1(yawIn);
      turCtl.pitch = clamp1(pitchIn);
      const rate = (tcfg?.trackRateRadPerSec ?? 2) * dt;
      turAim.yaw = wrapAngle(turAim.yaw - turCtl.yaw * rate);
      turAim.pitch = Math.min(
        tcfg?.pitchMaxRad ?? 1.4,
        Math.max(tcfg?.pitchMinRad ?? -0.5, turAim.pitch + turCtl.pitch * rate),
      );
      const arc = tcfg?.yawArcRad ?? 4;
      if (arc < Math.PI) {
        const center = tcfg?.yawCenterRad ?? 0;
        turAim.yaw = wrapAngle(center + clamp1Range(wrapAngle(turAim.yaw - center), arc));
      }
      if (turSrv.has) {
        const blend = 1 - Math.exp(-1.2 * dt);
        turAim.yaw += wrapAngle(turSrv.yaw - turAim.yaw) * blend;
        turAim.pitch += (turSrv.pitch - turAim.pitch) * blend;
      }
      gunCooldown -= dt;
      if (input.trigger && gunCooldown <= 0) {
        // Twin volley cadence, same maths as the wing guns' paired volleys.
        const cal = calByName.get(tcfg?.gun?.caliber) ?? selfCaliber();
        gunCooldown = 2 / (cal.roundsPerSec ?? 12);
        audio.gunshot();
      }
    }

    // Tank main-gun reload readout under the crosshair (hidden otherwise).
    hud.setReload(driving && vehMainGun() ? { left: Math.max(0, gunCooldown) } : null);

    // Naval projected gunsight: park the dispersion ellipse on the water at the
    // predicted point of impact (battery bearing + auto-elevated range), sized
    // to the salvo's real scatter — wide across the line of fire by the
    // dispersion half-angle, long down-range where the shallow arc stretches
    // it. Gold on a live aim, red when the sight is clamped at maximum reach.
    if (aimMarker) {
      const me = driving ? vehiclesR.positionOf(selfVehicleId) : null;
      // A land hull without a cannon (the Warden) still shows the ring: its
      // autocannon stream and missile fly to the same ranged point.
      const mg =
        (driving ? vehMainGun() : null) ??
        (driving && landCrew() ? { dispersionRad: 0.006, muzzleSpeedMS: 1100 } : null);
      if (me && mg && navalRangeLive > 0) {
        const dist = navalRangeLive;
        const wl = config.world.waterLevelM;
        const mx = me.x + Math.cos(vehAim.yaw) * dist;
        const mz = me.z - Math.sin(vehAim.yaw) * dist;
        aimMarker.grp.visible = true;
        // The ring rides the sea — or climbs an island slope when the ranged
        // point is on land (measureNavalRange marches the terrain).
        aimMarker.grp.position.set(mx, Math.max(wl, field.surfaceAt(mx, mz)) + 0.6, mz);
        aimMarker.grp.rotation.y = vehAim.yaw;
        const disp = mg.dispersionRad > 0 ? mg.dispersionRad : 0.008;
        const elev = navalElevation(dist, mg.muzzleSpeedMS ?? 800);
        let lateral, along;
        if (navalTheater) {
          // At sea the ring IS the salvo's scatter, and nothing more: the old
          // danger-space stretch (dispersion / tan(elevation)) blew the ellipse
          // out to 600+ m down-range even at 3 km, drowning a 70 m hull and
          // making fine ranging near max reach guesswork. Now it hugs the true
          // dispersion ellipse — a Tachi's ring at 3 km is ~40x20 m, and at
          // full reach it stays a mark you lay ON a ship, not around it.
          lateral = Math.max(8, disp * dist);
          along = Math.max(12, Math.min(disp * dist * 2.4, 170));
        } else {
          // Land keeps the honest flat-fire danger space: a tank round that
          // falls short still kills along the corridor the ring shows.
          lateral = Math.max(10, dist * 0.005, disp * dist);
          along = Math.min(
            dist * 0.3,
            Math.max(16, dist * 0.012, (disp * 0.6 * dist) / Math.max(Math.tan(elev), 0.03)),
          );
        }
        aimMarker.ellipse.scale.set(along, 1, lateral);
        aimMarker.ellipseInner.scale.set(along * 0.5, 1, lateral * 0.5);
        aimMarker.cross.scale.setScalar(
          navalTheater ? Math.min(30, Math.max(6, dist * 0.006)) : Math.max(6, dist * 0.012),
        );
        const col = navalRangeMax ? 0xff6b5a : 0xffd76b;
        for (const m of aimMarker.mats) m.color.setHex(col);
      } else {
        aimMarker.grp.visible = false;
      }
      // Turret readout: how much of the battery bears on this lay ("GUNS 4/6")
      // — swing broadside to bring the masked mounts into arc.
      hud.setBattery(
        navalCrew() && driving && vehMainGun()
          ? { ...(vehiclesR.batteryOf(selfVehicleId, vehAim.yaw) ?? { bearing: 1, total: 1 }), max: navalRangeMax }
          : null,
      );
    }

    // --- predict own plane ---
    if (alive) {
      physAcc += dt;
      const planeCfg = planeTypes[selfPlane];
      const dmg = selfDamageMods();
      while (physAcc >= PHYS_DT) {
        // Sample/send controls on the input cadence so the prediction
        // integrates the exact control timeline the server will apply. The
        // assist refresh keeps that timeline fresh across a long frame: at
        // 60 fps it recomputes from the same attitude the frame's steering
        // block just used (a no-op); on a slow GPU it stops the wire from
        // carrying one stale command for a dozen ticks. (The per-frame aim
        // direction is still the binding constraint at single-digit fps —
        // see the steering block.)
        if (predTicks % TICKS_PER_INPUT === 0) {
          refreshSteerAssist();
          sendFlightInput();
        }
        prevPos.set(pred.pos.x, pred.pos.y, pred.pos.z);
        prevAtt.set(pred.att.x, pred.att.y, pred.att.z, pred.att.w);
        physStep(planeCfg, pred, predCtl, PHYS_DT, dmg);
        // Wrap seam: when the kernel folds x back across the map edge, pull
        // prevPos into the same image so the interp lerp (and the camera
        // spring behind it) doesn't sweep the whole cylinder for one frame.
        if (spaceTheater && Math.abs(pred.pos.x - prevPos.x) > spaceWrapM * 0.5) {
          prevPos.x += pred.pos.x > prevPos.x ? spaceWrapM : -spaceWrapM;
        }
        const lastSent = inputHistory[inputHistory.length - 1];
        if (lastSent) lastSent.ticks++;
        physAcc -= PHYS_DT;
        predTicks++;
      }
      // Space: refresh the orbit chips (10 Hz is plenty for an instrument).
      if (orbitChips && ++orbitChipsTick % 6 === 0) {
        const ro = orbitReadout(pred, spaceConsts);
        // apo/peri of -1 mark an OPEN orbit (escape); a merely negative
        // periapsis is a closed ellipse that dips under the regolith — a
        // re-entry, not an escape, and the instrument must say which.
        const fmtApo = (v) => (v === -1 ? "ESC" : `${Math.round(v)} m`);
        const fmtPeri = (v) => (v === -1 ? "ESC" : v < 0 ? "IMPACT" : `${Math.round(v)} m`);
        let line =
          `APO ${fmtApo(ro.apo)}   PERI ${fmtPeri(ro.peri)}   Δv ${Math.round(pred.prop ?? 0)} m/s` +
          (spaceHold === 1 ? "   [PROGRADE]" : spaceHold === 2 ? "   [RETROGRADE]" : "") +
          (keeperMode === "STANDBY"
            ? "   [GUIDANCE: standing by]"
            : keeperMode
              ? `   [GUIDANCE: ${keeperMode}]`
              : "   [GUIDANCE OFF — P]") +
          // The gun ring has its own line on the instrument now, because it
          // has its own war: LAID means the barrels agree with the mark and
          // the trigger is live, SLEWING means they're still walking there.
          // (Nothing under the gaze, nothing to say.)
          (spaceGunsMode === "LAID"
            ? "   [GUNS: LAID]"
            : spaceGunsMode === "SLEWING"
              ? "   [GUNS: SLEWING]"
              : "");
        // Ordnance chips off the wire's own counts (the jets section): the
        // rack and the chaff drum, shown only on craft that carry them.
        if (seekerSrv) {
          const kit = planeTypes[selfPlane];
          const bits = [];
          if ((kit?.asats ?? 0) > 0) bits.push(`ASAT ×${seekerSrv.msl ?? 0}`);
          if ((kit?.decoys ?? 0) > 0) bits.push(`CHAFF ×${seekerSrv.fl ?? 0}`);
          if (bits.length) line += `   ${bits.join("   ")}`;
        }
        let warn = "";
        if (pred.returning) {
          // Being escorted home is not a failure state and must not read like
          // one: say who has the ship, say it hands back on its own, and say
          // the one thing that is still yours — the guns never left the ring.
          warn = "⟲ AUTOPILOT — RETURNING TO THE COMBAT ZONE · GUNS STILL YOURS";
        } else if (pred.landed) {
          warn = "ON THE REGOLITH — THROTTLE UP TO RELAUNCH";
        } else if (steerMode !== "keeper") {
          // Manual-burn coaching belongs to manual hands only: while the
          // director flies, IT is the coach — and under power the osculating
          // apsides these warnings judge by are liars anyway (a level
          // overtake reads as DEPARTING THE WAR, a landing order as a
          // periapsis emergency).
          if (ro.apo === -1 || ro.apo > spaceConsts.ceiling) {
            warn = onTouch ? "⚠ DEPARTING THE WAR — TAP RET AND THROTTLE UP ⚠" : "⚠ DEPARTING THE WAR — BURN RETROGRADE (J) ⚠";
          } else if (ro.peri < 300 && pred.pos.y > 400) {
            warn = onTouch ? "⚠ PERIAPSIS BELOW THE PEAKS — TAP PRO AND THROTTLE UP ⚠" : "⚠ PERIAPSIS BELOW THE PEAKS — BURN PROGRADE (K) ⚠";
          }
        }
        // THE CHASE PANEL. Refreshed on this same slow clock — a closest
        // approach flown 180 s forward on the kernel is real arithmetic, and
        // an instrument does not need it sixty times a second.
        spaceChase = null;
        if (!pred.landed) {
          const desig = spaceDesignation();
          if (desig) {
            const tgt = { pos: desig.pos, vel: desig.vel };
            const rel = relativeState(pred, tgt, spaceConsts.wrapM);
            const ca = closestApproach(pred, tgt, spaceConsts, 180, 1.0);
            const surf = field?.surfaceAt?.(pred.pos.x, pred.pos.z) ?? 0;
            spaceChase = {
              name: desig.name,
              rel,
              missM: ca.missM,
              missT: ca.tSec,
              advice: pursuitAdvice(pred, tgt, spaceConsts),
            };
          }
        }
        if (spaceChase) {
          const c = spaceChase;
          const km = (m) => (m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`);
          let chase =
            `TGT ${c.name}   ${km(c.rel.range)}   ` +
            (c.rel.closing > 0.5
              ? `closing ${c.rel.closing.toFixed(0)} m/s`
              : c.rel.closing < -0.5
                ? `OPENING ${(-c.rel.closing).toFixed(0)} m/s`
                : "range steady") +
            `   pass ${km(c.missM)} in ${Math.round(c.missT)} s`;
          // The instruction. On this moon it is finally the one a newcomer
          // already believes: look at him and open the throttle.
          if (c.advice && c.advice.etaSec > 0) {
            const a = c.advice;
            chase += a.holding
              ? `\n▶ HOLDING THE MERGE — he's yours in ${Math.round(a.etaSec)} s`
              : a.opening
                ? `\n▶ POINT AT HIM AND BURN — he's opening; ${Math.round(a.closeMS)} m/s closes it`
                : `\n▶ POINT AT HIM AND BURN — hold ${Math.round(a.closeMS)} m/s and he's yours in ${Math.round(a.etaSec)} s`;
          } else if (c.rel.range < MERGE_RANGE_M) {
            chase += "\n— MERGE: he's inside gun range, fly the pass";
          }
          // The half of it a pilot only learns by running dry: arriving costs
          // exactly as much as leaving, because nothing out here slows you
          // down for free. Say so while there is still time to ease off.
          if (c.advice && c.rel.closing > 45 && c.rel.range < 1600) {
            chase += "   · EASE OFF — you pay again to stop";
          }
          orbitChips.textContent = `${line}\n${chase}${warn ? `\n${warn}` : ""}`;
        } else {
          orbitChips.textContent = warn ? `${line}\n${warn}` : line;
        }
        spaceChipPaint?.(); // the touch deck's PRO/RETRO chips track the holds
        // The barge's plot ring: fly the release on the kernel, on this same
        // slow clock, and lay the ring where it lands. Hidden the moment the
        // rack is empty, the hull is down, or there's no orbit to drop from.
        if (rodPlot) {
          const rods = (planeTypes[selfPlane]?.rods ?? 0) > 0;
          const spd = Math.hypot(pred.vel.x, pred.vel.y, pred.vel.z);
          let placed = false;
          rodImpactX = null;
          if (rods && selfBombs > 0 && !pred.landed && spd > 20) {
            const k = -30 / spd; // the server's spaceRodKickMS, mirrored
            const s = {
              x: pred.pos.x, h: pred.pos.y, z: pred.pos.z,
              vx: pred.vel.x * (1 + k), vh: pred.vel.y * (1 + k), vz: pred.vel.z * (1 + k),
            };
            const sdt = 1 / 8;
            for (let t = 0; t < 100; t += sdt) {
              stepOrbit(s, spaceConsts.mu, spaceConsts.R, spaceConsts.wrapM, sdt, 0, 0, 0);
              if (s.h <= (field?.surfaceAt?.(s.x, s.z) ?? 0)) {
                // Nearest wrap image of the strike, relative to the camera.
                const ix = s.x + Math.round((camera.position.x - s.x) / spaceConsts.wrapM) * spaceConsts.wrapM;
                rodPlot.position.set(ix, s.h + 2.5, s.z);
                rodImpactX = s.x;
                placed = true;
                break;
              }
            }
          }
          rodPlot.visible = placed;
        }
        // The ribbon flies the same kernel forward 180 s and lays the points
        // in the camera's wrap image; red from the first sample that sinks
        // inside the danger margin, and the impact ring where the arc ends.
        if (trajRibbon) {
          // The ribbon stands down while the telescope is up: scoped, the
          // camera rides the nose and the ribbon's first segment passes
          // straight through the lens — a smooth curve crossing the near
          // plane at 14° projects as a hard dogleg across the eyepiece (a
          // phone screenshot, verbatim). The optic is for the target; the
          // plotted future returns with the wide view.
          if (pred.landed || spaceZoomKey || spaceZoomLatch) {
            trajRibbon.visible = trajMark.visible = false;
          } else {
            const posA = trajRibbon.geometry.attributes.position;
            const colA = trajRibbon.geometry.attributes.color;
            const s = {
              x: pred.pos.x, h: pred.pos.y, z: pred.pos.z,
              vx: pred.vel.x, vh: pred.vel.y, vz: pred.vel.z,
            };
            let n = 0, hit = false, trav = 0;
            const camX = camera.position.x;
            for (let i = 0; i < 121 && !hit; i++) {
              const surf = field?.surfaceAt?.(s.x, s.z) ?? 0;
              const danger = s.h < surf + 160;
              const ix = s.x + Math.round((camX - s.x) / spaceConsts.wrapM) * spaceConsts.wrapM;
              posA.setXYZ(n, ix, s.h, s.z);
              if (danger) colA.setXYZ(n, 1.0, 0.32, 0.26);
              else colA.setXYZ(n, 0.42, 0.85, 1.0);
              n++;
              if (s.h <= surf) {
                trajMark.position.set(ix, surf + 2.0, s.z);
                hit = true;
                break;
              }
              // Stop before the future laps the moon: on the half-size map
              // 180 s is most of an orbit, and any sample past half a wrap
              // ahead snaps to the camera's OTHER image — the phone
              // screenshot's "hula hoop" with a chord sliced across the sky.
              trav += Math.abs(s.vx) * 1.5;
              if (trav > spaceConsts.wrapM * 0.42) break;
              stepOrbit(s, spaceConsts.mu, spaceConsts.R, spaceConsts.wrapM, 1.5, 0, 0, 0);
            }
            // Collapse the unused tail onto the last point so the line ends
            // where the future does.
            for (let i = n; i < 121; i++) {
              posA.setXYZ(i, posA.getX(n - 1), posA.getY(n - 1), posA.getZ(n - 1));
              colA.setXYZ(i, colA.getX(n - 1), colA.getY(n - 1), colA.getZ(n - 1));
            }
            posA.needsUpdate = colA.needsUpdate = true;
            trajRibbon.visible = true;
            trajMark.visible = hit;
          }
        }
        // The chart redraws on the chips' own slow clock — the arcs are a
        // few hundred kernel steps, cheap at this cadence, silly at 60 Hz.
        if (spaceChart?.visible) {
          const contacts = [];
          for (const [id, ent] of entities) {
            const last = ent.buffer[ent.buffer.length - 1];
            if (!last || last.status === 0 || last.status === 6) continue;
            contacts.push({
              x: last.pos.x, y: last.pos.y, vx: last.vel.x, vy: last.vel.y,
              team: roster.get(id)?.team ?? -1,
            });
          }
          spaceChart.draw({
            pos: pred.pos, vel: pred.vel, selfTeam,
            contacts,
            stationX: score.stx ?? null,
            zones: (navalZoneCenters ?? []).map((z, i) => ({
              x: z.x, z: z.z ?? 0, y: z.y ?? 1600, r: z.r > 0 ? z.r : i === 0 ? 500 : 420,
              orbital: i === 0, // the plan's convention: the station leads (spaceprops.js)
            })),
            holders: score.zs ?? [],
            rodImpactX,
          });
        }
        // Headless-harness introspection, the freeCam convention.
        if (window.__va) {
          window.__va.orbit = {
            ...ro, prop: pred.prop ?? 0, landed: !!pred.landed, alt: pred.pos.y,
            thr: predCtl.throttle, sh: spaceHold, vx: pred.vel.x, vy: pred.vel.y,
            guid: keeperOn ? keeperMode : "", fov: camera.fov,
            camYaw: spaceCam.yaw, camPitch: spaceCam.pitch,
            fireOK: spaceFireOK, eng: spaceEngineCmd, lever: touch ? touch.throttleValue : input.throttle,
            gunYaw: spaceGunYaw, gunPitch: spaceGunPitch, guns: spaceGunsMode,
            // The chase panel, for the harness: the numbers a pilot closes on.
            chase: spaceChase
              ? {
                  name: spaceChase.name,
                  range: spaceChase.rel.range,
                  closing: spaceChase.rel.closing,
                  miss: spaceChase.missM,
                  closeMS: spaceChase.advice ? spaceChase.advice.closeMS : 0,
                  eta: spaceChase.advice ? spaceChase.advice.etaSec : -1,
                }
              : null,
            t: perfNow(), // staleness = the pilot is no longer flying
          };
        }
      }
      // Reconciliation happens in handleSelfRow (replay); here we just let
      // the residual visual offsets bleed away.
      const decay = Math.exp(-8 * dt);
      visualOffset.multiplyScalar(decay);
      attOffset.slerp(IDENTITY_Q, 1 - decay);
      // Draw the plane between the last two physics ticks (plus the
      // error-hiding offsets) so motion is smooth at any refresh rate.
      const alpha = Math.min(physAcc / PHYS_DT, 1);
      renderPos.set(
        prevPos.x + (pred.pos.x - prevPos.x) * alpha + visualOffset.x,
        prevPos.y + (pred.pos.y - prevPos.y) * alpha + visualOffset.y,
        prevPos.z + (pred.pos.z - prevPos.z) * alpha + visualOffset.z,
      );
      predQ.set(pred.att.x, pred.att.y, pred.att.z, pred.att.w);
      renderAtt.copy(prevAtt).slerp(predQ, alpha).premultiply(attOffset);
    } else if (falling) {
      // Wreck follow (roadmap #3): pred is snapped to the latest server row in
      // handleSelfRow; ease the render pose toward it so the 20 Hz snapshots
      // don't strobe the tumble.
      const k = 1 - Math.exp(-16 * dt);
      renderPos.lerp(pred.pos, k);
      predQ.set(pred.att.x, pred.att.y, pred.att.z, pred.att.w);
      renderAtt.slerp(predQ, k);
    }

    // --- own visual + cosmetic gunfire ---
    if (selfVisual) {
      // Keep the airframe on screen while it tumbles as a wreck (roadmap #3),
      // not just while it's a live plane.
      selfVisual.group.visible = alive || falling;
      if (alive || falling) {
        selfVisual.group.position.copy(renderPos);
        selfVisual.group.quaternion.copy(renderAtt);
        // Space: the bell and the gun narrate what actually goes out on the
        // wire — the director's translated engine order and the gated
        // trigger — not the raw lever/button (a plume without thrust, or a
        // muzzle flash while the nose is still swinging, is the HUD lying).
        selfVisual.throttle = falling ? 0 : spaceTheater ? predCtl.throttle : input.throttle;
        const firing = alive && input.trigger && spaceFireOK;
        // Someone may be riding our own bomber's turrets — pose them.
        selfVisual.setTurrets(turretsByHost.get(selfId));
        // Space: pose the gun ring where it is actually looking. This is the
        // decoupling made visible — the hull holds whatever attitude the
        // burn wants while the barrels track the mark, and the muzzle flash
        // is on the mount, not on the nose.
        // Our own barrels ride the DEFLECTED bore, so a burst is something you
        // watch walk off the target rather than something you infer from the
        // rounds missing. (Remote craft already carry their bore on the wire.)
        if (spaceTheater && alive) {
          poseSpaceRings(
            selfVisual, renderAtt,
            spaceGunYaw + spaceGunKickYaw, spaceGunPitch + spaceGunKickPitch, firing,
          );
        }
        selfVisual.update(dt, firing);
        selfVisual.setBlink(selfStatus === 2);
        // The sheared wing + torched engine ride the subsystem bytes, so the
        // wreck sheds and flames straight from applyDamage.
        selfVisual.applyDamage({
          engine: selfDmg.engine, wingL: selfDmg.wingL, wingR: selfDmg.wingR,
          fuel: selfDmg.fuel, pilot: selfDmg.pilot, tail: selfDmg.tail,
          hp: selfHp / selfMaxHp(),
        });
        if (alive) {
          gunCooldown -= dt;
          if (firing && gunCooldown <= 0) {
            // Volley cadence mirrors the server: wing batteries volley at
            // the classic pair cadence with every barrel talking (a quad's
            // shot is four rounds), single mounts — the Ironclad's chin
            // cannon, the Starfang's gatling — stream at the full
            // per-round rate.
            const single = (planeTypes[selfPlane]?.gun?.barrels ?? 2) === 1;
            gunCooldown = (single ? 1 : 2) / (selfCaliber().roundsPerSec ?? 10);
            audio.gunshot();
          }
        }
      }
    }

    // Contrail/streamer emitter shared by the local plane and remotes
    // (roadmap #45). store carries per-plane cooldowns; quat is a
    // THREE.Quaternion, pos/vel are {x,y,z}. Contrails cut off hard below
    // the band floor by simply not emitting; old segments age out.
    const emitPlaneTrails = (store, planeIdx, pos, vel, quat, g, edt) => {
      // Vacuum leaves no vapour. A contrail is water freezing out of engine
      // exhaust into cold WET air, and the Brick Moon has neither — but the
      // band floor is an altitude, and every orbit is above it, so the whole
      // patrol used to fly with white ribbons streaming off hulls that carry
      // no water and no wings for a tip vortex to spin off.
      if (spaceTheater) return;
      const fx = config.effects ?? {};
      const altBase = fx.contrailAltM ?? 900;
      const geom = CLASS_TRAILS[planeIdx] ?? CLASS_TRAILS[0];
      store.c = (store.c ?? 0) - edt;
      if (store.c <= 0 && pos.y > altBase) {
        const cad = contrails.cadence();
        store.c = cad;
        const k = Math.min(1, (pos.y - altBase) / 200);
        trailOff.set(0, 0, geom.nacelles).applyQuaternion(quat);
        const life = fx.contrailFadeSec ?? 22;
        contrails.add(pos.x + trailOff.x, pos.y + trailOff.y, pos.z + trailOff.z,
          vel.x, vel.y, vel.z, cad, k, life);
        contrails.add(pos.x - trailOff.x, pos.y - trailOff.y, pos.z - trailOff.z,
          vel.x, vel.y, vel.z, cad, k, life);
      }
      const sg = fx.streamerG ?? 3.5;
      if (g > sg && Math.hypot(vel.x, vel.y, vel.z) > 40) {
        store.s = (store.s ?? 0) - edt;
        if (store.s <= 0) {
          store.s = 0.06;
          const w = Math.min(1, (g - sg) / ((fx.streamerMaxG ?? 6) - sg));
          trailOff.set(0, 0, geom.tip).applyQuaternion(quat);
          streamers.add(pos.x + trailOff.x, pos.y + trailOff.y, pos.z + trailOff.z,
            vel.x, vel.y, vel.z, 0.05, w);
          streamers.add(pos.x - trailOff.x, pos.y - trailOff.y, pos.z - trailOff.z,
            vel.x, vel.y, vel.z, 0.05, w);
        }
      }
    };

    // --- remotes ---
    // Advance the interpolation clock by frame time and only nudge it toward
    // the (jittery) network estimate; recomputing it raw each frame makes
    // every remote plane shimmy with clock noise.
    if (timeOffset !== null) {
      // The director interpolates behind its own lagged apply clock, so its
      // planes sit just behind the delayed snapshots the pump just applied.
      const lag = autoDirector ? DIRECTOR_LAG : 0;
      const targetT = perfNow() + timeOffset - INTERP_DELAY - lag;
      const err = targetT - (renderT + dt);
      renderT = renderT === 0 || Math.abs(err) > 0.5 ? targetT : renderT + dt + err * 0.06;
    }
    for (const ent of entities.values()) {
      const s = interpolateRemote(ent, renderT);
      const fresh = ent.buffer.length && lastSnapNow - ent.buffer.at(-1).t < 0.8;
      // Gunners (status 3), drivers (status 4) and turret crew (status 5)
      // sit inside their mount/hull/bomber: no plane of their own to draw.
      // While the killcam rolls, its replayed visuals stand in for the live
      // planes (which are a few seconds ahead of the clip).
      const visible =
        s && s.status !== 0 && s.status !== 3 && s.status !== 4 && s.status !== 5 && fresh && !replay;
      ent.visual.group.visible = !!visible;
      if (!visible) continue;
      ent.visual.group.position.copy(s.pos);
      ent.visual.group.quaternion.copy(s.quat);
      ent.visual.throttle = s.thr;
      // Pose this bomber's crew turrets off the snapshot (our own seat is
      // overridden with the locally predicted aim so it answers instantly).
      ent.visual.setTurrets(turretsByHost.get(ent.id));
      // Space: ease this craft's ring onto its wired aim and pose the
      // barrels. Firing isn't wired for players, so the flash stays dark on
      // remote mounts — the tracers do that storytelling already.
      if (spaceTheater) {
        const k = 1 - Math.exp(-14 * dt);
        ent.gunYawSm = ent.gunYawSm === undefined ? ent.gunYaw : easeAngle(ent.gunYawSm, ent.gunYaw, k);
        ent.gunPitchSm =
          ent.gunPitchSm === undefined ? ent.gunPitch : ent.gunPitchSm + (ent.gunPitch - ent.gunPitchSm) * k;
        poseSpaceRings(ent.visual, s.quat, ent.gunYawSm, ent.gunPitchSm, false);
      }
      const ridingThis = turretting && ent.id === selfTurretHost;
      if (ridingThis) {
        ent.visual.setTurretAim(selfTurretSeat, turAim.yaw, turAim.pitch, input.trigger);
      }
      // Our host's floating callsign hangs right over the turret camera.
      ent.visual.setNameplateVisible(!ridingThis);
      ent.visual.setNemesis(ent.id === nemesisId);
      ent.visual.setKillMarks(board.get(ent.id)?.k ?? 0); // one stencil per round kill (roadmap #25)
      ent.visual.update(dt, undefined, s.pos.distanceTo(camera.position));
      ent.visual.setBlink(s.status === 2);
      // Contrails + G streamers for remote planes; G is derived from the
      // last two snapshot velocities (gravity removed), no wire change.
      const lastS = ent.buffer.at(-1);
      if (lastS?.vel) {
        emitPlaneTrails(ent, ent.plane, s.pos, lastS.vel, s.quat, remoteG(ent), dt);
        // The ace drags a gold ribbon at any altitude (roadmap #14).
        if (ent.id === aceId) {
          ent._gt = (ent._gt ?? 0) - dt;
          if (ent._gt <= 0) {
            ent._gt = 0.1;
            goldTrail.add(s.pos.x, s.pos.y, s.pos.z,
              lastS.vel.x, lastS.vel.y, lastS.vel.z, 0.11, 0.5, 3.5);
          }
        }
      }
      const hpFrac = ent.hp / maxHpOf(ent.plane);
      const entDmg = { engine: ent.engine, fuel: ent.fuel };
      if (isFxDamaged(entDmg, hpFrac)) {
        ent.smokeAt -= dt;
        if (ent.smokeAt <= 0) {
          ent.smokeAt = 0.09;
          damageFx(tmpV2.copy(s.pos), entDmg, hpFrac);
        }
      }
    }
    // own smoke/fire/fuel trail
    if (alive) {
      // Own contrails/streamers off the predicted state: nz is the FDM's
      // accelerometer load factor, the same number the G-meter shows.
      selfTrailQ.set(pred.att.x, pred.att.y, pred.att.z, pred.att.w);
      emitPlaneTrails(selfTrailStore, selfPlane, pred.pos, pred.vel, selfTrailQ, pred.nz ?? 1, dt);
      const hpFrac = selfHp / selfMaxHp();
      if (isFxDamaged(selfDmg, hpFrac)) {
        smokeCooldown -= dt;
        if (smokeCooldown <= 0) {
          smokeCooldown = 0.09;
          damageFx(new THREE.Vector3(pred.pos.x, pred.pos.y, pred.pos.z), selfDmg, hpFrac);
        }
      }
    }
    // Our own wreck trails flame and smoke as it corkscrews down (roadmap #3).
    if (falling) {
      smokeCooldown -= dt;
      if (smokeCooldown <= 0) {
        smokeCooldown = 0.09;
        tmpV2.copy(renderPos);
        particles.fire(tmpV2);
        particles.smoke(tmpV2);
      }
    }
    // Tumbling wreckage shed when a wing or canopy tears off.
    updateDebris(fxDt);

    // --- camera ---
    if (alive || falling) {
      // A falling wreck (roadmap #3) keeps the ordinary chase camera on the
      // tumbling airframe instead of cutting away to the respawn screen.
      const fwd = FDM.qRotate(renderAtt, FDM.v3(1, 0, 0));
      // Sit a touch higher than the tail so the nose never blocks the target.
      const desired = tmpV2.set(
        renderPos.x - fwd.x * 19,
        renderPos.y - fwd.y * 19 + 8.5,
        renderPos.z - fwd.z * 19,
      );
      camTarget.set(renderPos.x + fwd.x * 40, renderPos.y + fwd.y * 40, renderPos.z + fwd.z * 40);
      if (spaceTheater && alive && (spaceZoomKey || spaceZoomLatch)) {
        // THE TELESCOPE EYE rides the sight line, not the chase boom —
        // through a 14° optic the boom's own hull fills the frame (the
        // first zoom screenshot was a portrait of our own engine bell).
        // Under the director the sight line is the GAZE (the scope is the
        // gunsight now — look, and the glass follows); raw mode keeps the
        // nose. Eye a hull-length out, sight line pushed deep, stiffer
        // spring: an optic that floats is an optic you can't lay on a glint.
        const sd = keeperOn ? spaceCamDir : fwd;
        desired.set(renderPos.x + sd.x * 7, renderPos.y + sd.y * 7 + 1.0, renderPos.z + sd.z * 7);
        camTarget.set(renderPos.x + sd.x * 1200, renderPos.y + sd.y * 1200, renderPos.z + sd.z * 1200);
        chaseFollow.update(camera.position, desired, 26, dt);
        const upz = FDM.qRotate(renderAtt, FDM.v3(0, 1, 0));
        camera.up.set(0, 1, 0).lerp(tmpV.set(upz.x, upz.y, upz.z), keeperOn ? 0 : 0.35).normalize();
        camera.lookAt(camTarget);
      } else if (spaceTheater && alive && keeperOn) {
        // THE DIRECTOR'S EYE — a free orbit on the gaze, decoupled from the
        // hull. The craft underneath swings its nose wherever the burns and
        // the gun solutions need it; your view never lurches with it (the
        // nose living under the camera is what made every laying burn read
        // as "the craft janks terribly"). The LOOK point tracks the gaze
        // instantly — aiming stays crisp — while the eye follows on a
        // spring.
        desired.set(
          renderPos.x - spaceCamDir.x * 19,
          renderPos.y - spaceCamDir.y * 19 + 6,
          renderPos.z - spaceCamDir.z * 19,
        );
        camTarget.set(
          renderPos.x + spaceCamDir.x * 60,
          renderPos.y + spaceCamDir.y * 60,
          renderPos.z + spaceCamDir.z * 60,
        );
        chaseFollow.update(camera.position, desired, 14, dt);
        camera.up.set(0, 1, 0);
        camera.lookAt(camTarget);
      } else {
        const lk = input.look;
        if (lk.blend > 0.001) {
          // Right-button freelook: orbit the plane with world-up yaw/pitch,
          // starting from directly behind the current heading.
          let hx = fwd.x, hz = fwd.z;
          const hl = Math.hypot(hx, hz);
          if (hl > 1e-3) { hx /= hl; hz /= hl; } else { hx = 1; hz = 0; }
          const p = Math.min(1.45, Math.max(-1.25, 0.42 + lk.pitch));
          const cy = Math.cos(lk.yaw), sy = Math.sin(lk.yaw);
          const ox = -hx * cy + hz * sy; // behind-vector rotated by -yaw
          const oz = -hx * sy - hz * cy; // (mouse right pans the view right)
          const R = 21, cp = Math.cos(p), sp = Math.sin(p);
          tmpV.set(renderPos.x + ox * cp * R, renderPos.y + sp * R, renderPos.z + oz * cp * R);
          desired.lerp(tmpV, lk.blend);
          camTarget.lerp(renderPos, lk.blend);
        }
        chaseFollow.update(camera.position, desired, 9 + 8 * lk.blend, dt);
        const up = FDM.qRotate(renderAtt, FDM.v3(0, 1, 0));
        camera.up.set(0, 1, 0).lerp(tmpV.set(up.x, up.y, up.z), 0.35 * (1 - lk.blend)).normalize();
        camera.lookAt(camTarget);
      }
    } else if (manning && manningMain && updateCineCam(dt)) {
      // The firing cutaway owns the camera while a salvo is in the air.
    } else if (manning) {
      // Turret view: trail a fixed distance behind and above the mount and
      // look along the locally predicted aim. The main battery sits farther
      // back — the turret is a house, not a pintle mount.
      const back = manningMain ? 30 : 13;
      const up = manningMain ? 11 : 5.5;
      const pivot = aaGuns.pivotOf(selfGunId);
      const ch = Math.cos(gunAim.pitch);
      tmpV.set(
        Math.cos(gunAim.yaw) * ch,
        Math.sin(gunAim.pitch),
        -Math.sin(gunAim.yaw) * ch,
      );
      // Trail straight behind the mount's heading at a steady height rather
      // than along the (pitched) aim vector. Following the aim would swing the
      // eye down under the barrel as we elevate — sinking it through the
      // ground/sea and filling the screen with turret. Keeping the pull-back
      // horizontal leaves the sky, and the aircraft we're tracking, open while
      // the barrel and its tracers rise into frame ahead of us.
      const hx = Math.cos(gunAim.yaw);
      const hz = -Math.sin(gunAim.yaw);
      tmpV2.set(pivot.x - hx * back, pivot.y + up, pivot.z - hz * back);
      // Safety net: never let terrain (or the sea) behind the mount swallow
      // the camera on a slope.
      const groundY = field.surfaceAt(tmpV2.x, tmpV2.z) + 2;
      if (tmpV2.y < groundY) tmpV2.y = groundY;
      camera.position.lerp(tmpV2, 1 - Math.exp(-16 * dt));
      camTarget.set(pivot.x + tmpV.x * 400, pivot.y + tmpV.y * 400, pivot.z + tmpV.z * 400);
      camera.up.set(0, 1, 0);
      camera.lookAt(camTarget);
    } else if (turretting) {
      // Bomber turret view: first person from the crew station (a chase
      // camera would sit inside the fuselage whenever the mount faces aft).
      // The eye rides just outside the glass, above the gun line, looking
      // over the twin barrels along the locally predicted aim.
      const hostEnt = entities.get(selfTurretHost);
      const s = interpolateRemote(hostEnt, renderT);
      if (s) {
        const tcfg = turretCfg();
        turQuat.copy(s.quat); // own copies: s.pos/s.quat are shared scratch
        turPivot
          .set(tcfg?.pos?.x ?? 0, tcfg?.pos?.y ?? 0, tcfg?.pos?.z ?? 0)
          .applyQuaternion(turQuat)
          .add(s.pos);
        const ch = Math.cos(turAim.pitch);
        turDir
          .set(
            Math.cos(turAim.yaw) * ch,
            Math.sin(turAim.pitch),
            -Math.sin(turAim.yaw) * ch,
          )
          .applyQuaternion(turQuat);
        // Airframe up (not world up) so the eye clears the glass in a bank.
        tmpV.set(0, 1, 0).applyQuaternion(turQuat);
        tmpV2.set(
          turPivot.x + turDir.x * 1.4 + tmpV.x * 1.1,
          turPivot.y + turDir.y * 1.4 + tmpV.y * 1.1,
          turPivot.z + turDir.z * 1.4 + tmpV.z * 1.1,
        );
        camTarget.set(
          turPivot.x + turDir.x * 400,
          turPivot.y + turDir.y * 400,
          turPivot.z + turDir.z * 400,
        );
        // Freelook (hold right mouse / the look key): a tail gunner is a
        // passenger, so let them take their eyes off the gun and look around the
        // bomber they're riding — forward over the pilot, out at the fight, up at
        // the sky — without slewing the mount. The aim (turAim, sent to the
        // server) is untouched; this is view-only, and input.look's spring eases
        // it back to the gunsight on release. Orbits the crew station from behind
        // the bomber's nose, the same maths the plane chase camera freelooks by.
        const lk = input.look;
        if (lk.blend > 0.001) {
          turFwd.set(1, 0, 0).applyQuaternion(turQuat); // bomber nose in world
          let hx = turFwd.x, hz = turFwd.z;
          const hl = Math.hypot(hx, hz);
          if (hl > 1e-3) { hx /= hl; hz /= hl; } else { hx = 1; hz = 0; }
          const p = Math.min(1.45, Math.max(-1.25, 0.2 + lk.pitch));
          const cy = Math.cos(lk.yaw), sy = Math.sin(lk.yaw);
          const ox = -hx * cy + hz * sy; // behind-vector rotated by -yaw
          const oz = -hx * sy - hz * cy;
          const R = 16, cp = Math.cos(p), sp = Math.sin(p);
          turFreeEye.set(
            turPivot.x + ox * cp * R,
            turPivot.y + sp * R + 2,
            turPivot.z + oz * cp * R,
          );
          tmpV2.lerp(turFreeEye, lk.blend); // ease the eye out to the orbit
          camTarget.lerp(turPivot, lk.blend); // ...and look back at the bomber
        }
        camera.position.lerp(tmpV2, 1 - Math.exp(-24 * dt));
        camera.up.set(0, 1, 0);
        camera.lookAt(camTarget);
      }
    } else if (driving) {
      const pivot = vehiclesR.pivotOf(selfVehicleId);
      const ship = navalCrew();
      const ch = Math.cos(vehAim.pitch);
      tmpV.set(
        Math.cos(vehAim.yaw) * ch,
        Math.sin(vehAim.pitch),
        -Math.sin(vehAim.yaw) * ch,
      );
      if (ship && navalScoped) {
        // Binocular gunsight, World-of-Warships style: the camera climbs well
        // above the director (the spotter's vantage) and looks down the gun
        // line under heavy magnification, so a hull ten kilometres out is a
        // silhouette you can lay on instead of a speck lost in the sea clutter.
        // The height must match NAVAL_DIRECTOR_Y so the ranged spot sits under
        // the crosshair.
        tmpV2.set(pivot.x, pivot.y + 26, pivot.z);
        camera.position.lerp(tmpV2, 1 - Math.exp(-22 * dt));
        camTarget.set(
          camera.position.x + tmpV.x * 800,
          camera.position.y + tmpV.y * 800,
          camera.position.z + tmpV.z * 800,
        );
        camera.up.set(0, 1, 0);
        camera.lookAt(camTarget);
        setCameraFov(NAVAL_SCOPE_FOV);
      } else if (landCrew() && navalScoped) {
        // The gunner's optic: eye at the sight head over the turret, looking
        // down the lay under each vehicle's own magnification. The eye height
        // matches LAND_SIGHT_Y so the ranged point sits under the crosshair.
        // An UNSTABILIZED mount (the Jackal's pintle, the Whirlwind's bed)
        // bounces with the hull: the sight picture shakes with speed and only
        // settles when the hull halts — the halt-to-fire rhythm made visible.
        tmpV2.set(pivot.x, pivot.y - 2.4 + LAND_SIGHT_Y, pivot.z);
        const stab = !!config.groundVehicles?.types?.[vehType()]?.stabilized;
        if (!stab && lastHullSpeed > 0.5) {
          const j = Math.min(1, lastHullSpeed / 10) * 0.6;
          tmpV2.x += (Math.random() - 0.5) * j;
          tmpV2.y += (Math.random() - 0.5) * j * 0.7;
          tmpV2.z += (Math.random() - 0.5) * j;
        }
        camera.position.lerp(tmpV2, 1 - Math.exp(-26 * dt));
        camTarget.set(
          camera.position.x + tmpV.x * 800,
          camera.position.y + tmpV.y * 800,
          camera.position.z + tmpV.z * 800,
        );
        camera.up.set(0, 1, 0);
        camera.lookAt(camTarget);
        setCameraFov(LAND_SCOPE_FOV[landVehIdx()] ?? 8);
      } else {
        // Chase view: behind and above the hull, looking along the mount's aim. A
        // warship needs a much longer, higher chase boom than a ground hull so the
        // whole ship and the sea around her read.
        const back = ship ? 150 : 16;
        const up = ship ? 60 : 7;
        tmpV2.set(pivot.x - tmpV.x * back, pivot.y - tmpV.y * back + up, pivot.z - tmpV.z * back);
        // Never let the camera dip below the sea surface astern.
        if (ship && tmpV2.y < config.world.waterLevelM + 8) {
          tmpV2.y = config.world.waterLevelM + 8;
        }
        // ...nor clip into a hillside behind a land hull.
        if (landCrew()) {
          const g = field.surfaceAt(tmpV2.x, tmpV2.z) + 2.5;
          if (tmpV2.y < g) tmpV2.y = g;
        }
        camera.position.lerp(tmpV2, 1 - Math.exp(-14 * dt));
        camTarget.set(pivot.x + tmpV.x * 400, pivot.y + tmpV.y * 400, pivot.z + tmpV.z * 400);
        camera.up.set(0, 1, 0);
        camera.lookAt(camTarget);
        setCameraFov(72);
      }
    } else if (spectating && autoDirector && perfNow() < director.sinkUntil) {
      // Founder cam owns the frame while a hull goes down — the one shot the
      // "sank the battleship" caption is about. Outranks the establishing orbit.
      directorSinkShot(dt);
    } else if (spectating && autoDirector && director.cover && directorCoverShot(dt)) {
      // Duel coverage owns the frame: shot/reverse on the tail-chase, ending on
      // the wreck. Returns false the frame the engagement's done → falls through.
    } else if (spectating && autoDirector && perfNow() < director.wideUntil) {
      // Establishing orbit owns the camera for its run; events still heat the
      // world so the next close follow lands on whatever just broke out.
      directorWideShot(dt);
    } else if (spectating) {
      // The auto-director re-scores the live vantages a few times a second and
      // owns the cuts; manual spectating just hops on death.
      if (autoDirector && perfNow() >= director.nextTick) {
        directorTick(perfNow());
        director.nextTick = perfNow() + 0.25;
      }
      if (spectateTarget && !spectateCam(dt)) {
        // The subject is gone (shot down, destroyed, sunk): repick at once
        // (director) or hop to the next live vantage, else orbit the island.
        if (autoDirector) directorTick(perfNow(), true);
        else cycleSpectate(1);
        if (spectateTarget && !spectateCam(dt)) spectateTarget = null;
      }
      if (!spectateTarget && hullTheater) {
        // N5: don't orbit empty ocean at sea — grab the most-active warship to
        // follow (the same score the director uses). Fall back to orbiting the
        // nearest hull so even a lull frames a ship, not open water.
        let best = null, bestScore = -Infinity, nearest = null, nearestD2 = Infinity;
        for (const [id, v] of vehiclesR.vehicles) {
          if ((v.hp ?? 0) <= 0) continue;
          const s = directorScore({ kind: "vehicle", id }, 0);
          if (s > bestScore) { bestScore = s; best = { kind: "vehicle", id }; }
          const d2 = v.root.position.x ** 2 + v.root.position.z ** 2;
          if (d2 < nearestD2) { nearestD2 = d2; nearest = v; }
        }
        if (best) {
          focusSpectate(best);
        } else if (nearest) {
          menuAngle += dt * 0.05;
          const p = nearest.root.position;
          camera.position.set(p.x + Math.cos(menuAngle) * 300, p.y + 160, p.z + Math.sin(menuAngle) * 300);
          camera.up.set(0, 1, 0);
          camera.lookAt(p.x, p.y + 6, p.z);
        }
      }
      if (!spectateTarget && !hullTheater) {
        menuAngle += dt * 0.05;
        camera.position.set(Math.cos(menuAngle) * 1400, 650, Math.sin(menuAngle) * 1400);
        camera.up.set(0, 1, 0);
        camera.lookAt(0, 350, 0);
      }
    } else if (haveSpawned && stepKillcam(dt)) {
      // Killcam replay drove the camera this frame.
    } else if (haveSpawned) {
      // Killer cam: chase whoever got us while we wait to respawn (their
      // interpolation buffer is already here); fall back to orbiting the
      // crash site for environment deaths or a killer we can't see.
      const killerEnt = deathKiller ? entities.get(deathKiller) : null;
      const killerFresh =
        killerEnt && killerEnt.status !== 0 && killerEnt.buffer.length &&
        lastSnapNow - killerEnt.buffer.at(-1).t < 0.8;
      const killerS = killerFresh ? interpolateRemote(killerEnt, renderT) : null;
      const killerGunPos =
        deathKiller && isAAId(deathKiller) ? aaGuns.positionOf(deathKiller) : null;
      if (killerS) {
        // Same treatment as the spectator follow: interpolated subject and
        // a constant-lag chase, so the killer cam doesn't judder either.
        specPos.copy(killerS.pos);
        tmpV.set(1, 0, 0).applyQuaternion(killerS.quat);
        killerFollow.update(
          camera.position,
          tmpV2.set(
            specPos.x - tmpV.x * 26,
            specPos.y - tmpV.y * 26 + 10,
            specPos.z - tmpV.z * 26,
          ),
          6,
          dt,
        );
        camera.up.set(0, 1, 0);
        camera.lookAt(specPos);
      } else if (killerGunPos) {
        menuAngle += dt * 0.4;
        camera.position.set(
          killerGunPos.x + Math.cos(menuAngle) * 60,
          killerGunPos.y + 30,
          killerGunPos.z + Math.sin(menuAngle) * 60,
        );
        camera.up.set(0, 1, 0);
        camera.lookAt(killerGunPos);
      } else {
        menuAngle += dt * 0.4;
        camera.position.set(
          deathPos.x + Math.cos(menuAngle) * 70,
          Math.max(deathPos.y + 26, field.surfaceAt(deathPos.x, deathPos.z) + 30),
          deathPos.z + Math.sin(menuAngle) * 70,
        );
        camera.up.set(0, 1, 0);
        camera.lookAt(deathPos);
      }
    } else {
      menuAngle += dt * 0.05;
      camera.position.set(Math.cos(menuAngle) * 1400, 650, Math.sin(menuAngle) * 1400);
      camera.up.set(0, 1, 0);
      camera.lookAt(0, 350, 0);
    }
    if (!alive && !falling) chaseFollow.reset(); // re-snap the chase on the next spawn

    // Debug/test free camera overrides whatever the mode logic chose.
    if (window.__va.freeCam) {
      const fc = window.__va.freeCam;
      camera.position.set(fc.pos[0], fc.pos[1], fc.pos[2]);
      camera.up.set(0, 1, 0);
      camera.lookAt(fc.look[0], fc.look[1], fc.look[2]);
    }

    terrainR.update(camera.position);
    // Day/night cycle rides the shared server clock once it's known.
    // OVERTIME finale state rides the snapshot phase byte (score.ot), so
    // late joiners and killcam-returners pick it up without the event.
    const overtimeNow = !!score.ot && score.ph !== "end";
    sky.overtime = overtimeNow;
    if (overtimeNow !== lastOvertime) {
      lastOvertime = overtimeNow;
      let chip = document.getElementById("overtime-chip");
      if (!chip && overtimeNow) {
        chip = document.createElement("div");
        chip.id = "overtime-chip";
        chip.textContent = "OVERTIME";
        document.body.appendChild(chip);
      }
      chip?.classList.toggle("hidden", !overtimeNow);
    }
    // Space: hand the sky the camera's longitude — on a cylinder under a
    // fixed sun, where you are IS what time it is (world.js).
    if (spaceTheater) {
      sky.spaceLon = camera.position.x / spaceWrapM;
      // The star sphere rides the camera: a fixed dome on the half-size
      // moon could be flown clean out of (see buildSky).
      sky.stars?.position.copy(camera.position);
      // THE CURVATURE. One uniform write per frame moves the whole moon:
      // every patched material bends around the camera's own position, so the
      // ground under the eye is undistorted and the limb sits exactly where
      // √(2Rh) says it should. New meshes (a fresh hull, a tracer batch, a
      // chaff cluster) are swept up on the slow tick below — the walk is
      // cheap because each material only ever compiles once.
      setCurveCamera(camera.position.x, camera.position.y, camera.position.z);
      if (++curveTick % 30 === 0) curveScene(scene);
      spaceProps?.update(dt, score.stx, camera.position.x, score.zs, camera.position);
      // The arena rides with the group, so its anchor is refreshed from the
      // same f32 the props are drawn from — one source, no second opinion.
      if (score.stx != null) spaceStationX = score.stx;
      // The tactical chart follows the war down the cylinder with it.
      if (spaceStationX != null) hud.refreshSpaceChart(field, spaceStationX);
      if (window.__va) window.__va.stationX = score.stx ?? null;
      // The telescope owns the FOV while held (Z) or latched (ZOOM chip);
      // everything else in this theater flies the wide 72°. Written every
      // frame like the chase cam's restore, and the kill-confirm punch
      // already leaves any scoped view alone (killconfirm.js gates on <70°).
      setCameraFov(alive && !spectating && (spaceZoomKey || spaceZoomLatch) ? SPACE_SCOPE_FOV : 72);
      // The deck stands down off-duty: the verb chips and the orbit
      // instruments belong to a live cockpit, not the hangar (the phone
      // screenshot had GUID..ASAT printed across the TAKE OFF button).
      const deckUp = alive && !spectating;
      if (spaceChipsRow) spaceChipsRow.style.display = deckUp ? "flex" : "none";
      if (orbitChips) orbitChips.style.visibility = deckUp ? "visible" : "hidden";
    }
    if (timeOffset !== null) sky.update(perfNow() + timeOffset);
    zone.setSightHidden?.(navalScoped); // no ring bands across the gunsight
    zone.update(dt, hullTheater || armadaTheater ? (score.zs ?? []) : score.z);
    ringMarker?.update(dt, score.z ?? -1);
    tracers.update(fxDt);
    bombsR.update(fxDt);
    chutesR.update(fxDt);
    const rescueList = [...rescueMarkers.values()]; // roadmap #2
    rescuesR.setMarkers(rescueList);
    rescuesR.update(fxDt);
    hud.setRescues(rescueList);
    scarsR.update(fxDt, camera.position); // roadmap #5 crash-fire particles, LOD-gated
    smokeR.update();
    contrails.update(fxDt, score.windX ?? 0, score.windZ ?? 0);
    streamers.update(fxDt);
    goldTrail.update(fxDt);
    trucksR.update(dt);
    supplyShipsR.update(dt);
    vehiclesR.update(dt);
    // Burning hulls: lick flames and a smoke column off the deck (naval) or
    // the engine deck (land). Repairing hulls throw off a green "patching up"
    // sparkle so the heal reads on any hull — yours or a visible enemy's.
    if (hullTheater || armadaTheater) {
      for (const [vid, v] of vehiclesR.vehicles) {
        const shipHull = vehiclesR.shipType?.(v.type) ?? navalTheater;
        const fy = shipHull ? 7 : 2.2;
        const sy = shipHull ? 11 : 4.5;
        const rr = shipHull ? 16 : 4;
        const p = v.root.position;
        if (v.onfire) {
          tmpV.set(p.x, p.y + fy, p.z);
          if (Math.random() < 0.7) particles.fire(tmpV);
          if (Math.random() < 0.9) particles.smoke(tmpV.clone().setY(p.y + sy));
        }
        if (v.repairing && Math.random() < 0.8) {
          particles.spawn(
            tmpV.set(p.x + (Math.random() - 0.5) * rr, p.y + fy - 1, p.z + (Math.random() - 0.5) * rr),
            { count: 1, speed: 4, life: 0.9, size: 1.3, colors: [0x7cfc7c, 0xbfffbf, 0x4fd14f], gravity: -7, drag: 1.1 },
          );
        }
        // Main battery firing: on the rising edge of the snapshot's firing
        // window, a muzzle blast off every tube — ours or an enemy's, so an
        // opening salvo announces itself at the guns. The flash mesh itself
        // lives on the ship model (GroundVehicles); a distant report carries
        // for other ships' salvos (the local gunner already hears the cannon).
        if (v.firing && !v.firedEdge) {
          const mz = vehiclesR.muzzlesOf(vid);
          if (mz) {
            for (const pt of mz.points) particles.muzzleBlast(pt, mz.dir, mz.k);
            if (vid !== selfVehicleId) {
              audio.salvo(mz.k, Math.max(60, mz.points[0].distanceTo(camera.position) * 0.6));
            }
          } else if (landTheater && (v.type === 0 || v.type === 3) && vid !== selfVehicleId) {
            // Land main guns have no naval muzzle table; a distant report
            // still announces another tank's shot across the steppe.
            audio.explosion(Math.max(80, v.root.position.distanceTo(camera.position) * 0.7));
          }
        }
        v.firedEdge = v.firing;
      }
    }
    ships.update(dt);
    lightsR.update(dt);
    // Civilian life + living airbases (roadmap #42/#43): advance the ambient
    // scenery off the shared server clock. probes[0] is the local plane; the
    // rest are visible remotes, used for gull/ground-crew scatter and whale
    // "did you see it" analytics — reactive, not synced.
    if (ambient) {
      const svrT = perfNow() + (timeOffset ?? 0);
      const probes = [{
        x: pred.pos.x + visualOffset.x, y: pred.pos.y + visualOffset.y, z: pred.pos.z + visualOffset.z,
        spd: Math.hypot(pred.vel.x, pred.vel.y, pred.vel.z),
      }];
      for (const ent of entities.values()) {
        const last = ent.buffer.at(-1);
        if (!last) continue;
        probes.push({ x: last.pos.x, y: last.pos.y, z: last.pos.z, spd: last.vel ? last.vel.length() : 0 });
      }
      ambient.update(fxDt, svrT, sky, probes, { x: score.windX ?? 0, z: score.windZ ?? 0 });
    }
    // Storm front: march the thunderheads, flicker the lightning, drop the
    // rain — every theater, keyed to the shared server clock. Runs after
    // sky.update so its in-cell fog squeeze layers onto the mood the sky
    // just set (and its sky.lightning feeds the next frame's flash).
    if (timeOffset !== null) stormR.update(fxDt, perfNow() + timeOffset, camera, sky);
    // Shell vapor trails: thin white streaks tiled backwards along each
    // main-battery round's velocity, so salvos hang long unbroken contrails
    // in the air (seen by everyone) without the old smoke-puff clutter.
    // Sampled from the bomb renderer's INTEGRATED shells (advanced every
    // frame), not the raw 20 Hz snapshot rows — stale rows laid duplicate
    // segments and then jumped, breaking the line into a skewed ladder.
    contrailAt -= dt;
    if (contrailAt <= 0) {
      contrailAt = 0.06;
      for (const b of bombsR.bombs) {
        if (b.shell) shellTrails.add(b.pos.x, b.pos.y, b.pos.z, b.vel.x, b.vel.y, b.vel.z, 0.065);
        // Missiles write their story in the sky: a fat white motor-smoke
        // rope off every round in flight (heat-seekers and the land TOW
        // wire alike), laid through the contrail pool so it billows wide
        // with age and drifts on the wind. The trail IS the counterplay —
        // you don't see the missile, you see the finger pointing at whoever
        // it's chasing, with time to break and drop flares.
        else if (b.msl) contrails.add(b.pos.x, b.pos.y, b.pos.z, b.vel.x, b.vel.y, b.vel.z, 0.068, 1.3, 7);
      }
    }
    shellTrails.update(fxDt);
    particles.update(fxDt);

    // --- hud ---
    if (net.joined && !spectating) {
      // G-polish: prune the fall-of-shot impacts every frame, not only inside the
      // air battleship plot-table branch — in naval nobody mans that gun, so the
      // list used to grow unbounded. (N2 draws these on the naval minimap below.)
      fc.impacts = fc.impacts.filter((im) => perfNow() - im.at < 6);
      const speed = FDM.vLen(pred.vel);
      const vehCfg = driving ? config.groundVehicles?.types?.[vehType()] : null;
      // A ship's speed is its actual way through the water (derived from the
      // hull's track), not the commanded engine telegraph — so a beached or
      // backing hull reads honestly instead of showing the ordered speed.
      let hullSpeed = 0;
      if (driving && (hullTheater || armadaTheater)) {
        const hp = vehiclesR.positionOf(selfVehicleId);
        if (hp && lastHullPos) {
          let raw = hp.distanceTo(lastHullPos) / Math.max(dt, 1e-3);
          // G-polish: the raw track speed spikes to impossible values on snapshot
          // position jumps (130-170 km/h on an 18 m/s hull). Clamp to a shade over
          // flank and ease toward it so the SPD readout stays honest and steady.
          const cap = 1.15 * (vehCfg?.speedMS ?? 30);
          raw = Math.min(raw, cap);
          navalSpdSmooth += (raw - navalSpdSmooth) * (1 - Math.exp(-4 * dt));
          hullSpeed = navalSpdSmooth;
        }
        lastHullPos = hp ? hp.clone() : null;
      } else {
        lastHullPos = null;
        navalSpdSmooth = 0;
      }
      lastHullSpeed = hullSpeed; // the unstabilized gunsight bounces with this
      hud.setFlight(
        driving && (hullTheater || armadaTheater) ? hullSpeed
          : driving ? vehCtl.throttle * (vehCfg?.speedMS ?? 0)
          : alive || turretting ? speed : 0,
        pred.pos.y,
        driving ? vehCtl.throttle : manning || turretting ? 0 : input.throttle,
        selfHp,
        driving
          ? (vehCfg?.hp ?? selfHp)
          : manning
            ? gunProf().hp
            : turretting
              ? maxHpOf(entities.get(selfTurretHost)?.plane ?? 0) // the bomber's pool
              : selfMaxHp(),
      );
      // Localized-damage panel + pilot-wound veil (flying planes only).
      hud.setDamage(selfDmg, alive && !manning && !driving);
      // Energy cues from the locally-predicted flight model: a G-meter with
      // grey-/red-out veils, and a pre-stall buffet (view shake + rumble).
      // Orbit has neither: free fall reads 0.0 g forever and there is no
      // wing to buffet, so the aeroplane's energy row stays retired there
      // (the boot-time hide would otherwise be re-toggled every frame here).
      const flying = alive && !manning && !spaceTheater;
      hud.gForce(flying ? (pred.nz ?? 1) : 1, dt, flying);
      const stallFrac = flying
        ? Math.abs(pred.alpha ?? 0) / (planeTypes[selfPlane]?.alphaStallRad || 0.25)
        : 0;
      const buffeting = hud.setStall(stallFrac, flying);
      hud.setStallRibbon(stallFrac, flying); // roadmap #56: how much turn is left
      buffetCooldown -= dt;
      if (buffeting && buffetCooldown <= 0) {
        audio.buffet(Math.min(1, (stallFrac - 0.9) * 4));
        buffetCooldown = 0.15;
      }
      const torpPlane = (planeTypes[selfPlane]?.torpedoes?.count ?? 0) > 0;
      const rocketPlane = (planeTypes[selfPlane]?.rockets?.count ?? 0) > 0;
      // The space corvette's 85 mm magazine rides the same ordnance byte the
      // bombers use — the gauge just learns to say so.
      const heavyPlane = (planeTypes[selfPlane]?.heavyGun?.count ?? 0) > 0;
      const rodPlane = (planeTypes[selfPlane]?.rods ?? 0) > 0;
      const hasBombs =
        alive && !manning &&
        ((planeTypes[selfPlane]?.bombs?.count ?? 0) > 0 || torpPlane || rocketPlane || heavyPlane || rodPlane);
      hud.setBombs(
        hasBombs, selfBombs,
        rocketPlane ? "rocket" : torpPlane ? "torp" : rodPlane ? "rod" : heavyPlane ? "heavy" : "bomb",
      );
      // The 85 mm through your own frame: vacuum silences the world, not
      // the mount you are bolted to. A magazine drop on the wire means the
      // round left — one heavy report per shell, none for the respawn refill.
      if (spaceTheater && heavyPlane && alive && prevHeavyMag > selfBombs) audio.cannon();
      prevHeavyMag = selfBombs;
      // The BOMB button doubles as the torpedo launcher while conning a warship
      // with tubes, or the ATGM on a land vehicle (B key equivalent).
      touch?.setBombVisible(
        hasBombs ||
          (driving && navalCrew() && !!vehMainTorp()) ||
          (driving && navalCrew() && jetsEra && (vehTypesCfg[vehType()]?.ssm?.count ?? 0) > 0) ||
          (driving && landCrew() && (config.groundVehicles?.types?.[vehType()]?.atgm?.damage ?? 0) > 0),
      );
      // Jets era: MSL / FLARE buttons for a plane carrying the kit.
      if (jetsEra) {
        touch?.setJetsVisible(
          alive && !manning && !driving && (planeTypes[selfPlane]?.missiles?.count ?? 0) > 0,
          alive && !manning && !driving && (planeTypes[selfPlane]?.flares ?? 0) > 0,
        );
      }
      hud.setProtect(alive && selfStatus === 2);
      // The shared crew panel wears whichever hull's labels the player is
      // actually crewing — one exclusive decision, one hide. (setLand and
      // setNaval drive the SAME DOM node; the old shape called setNaval(null)
      // unconditionally after setLand had populated it, which hid the land
      // fighting-compartment panel every frame — a defect the combined-arms
      // playtest caught in the pure land theater too.)
      if (landCrew() && driving) {
        const lv = vehiclesR.vehicles.get(selfVehicleId);
        const t = config.groundVehicles?.types?.[vehType()];
        hud.setLand({
          ammo: navalAmmo,
          hasAmmoToggle: (t?.mainGun?.heDamage ?? 0) > 0 && !t?.mainGun?.heat,
          atgm: (t?.atgm?.damage ?? 0) > 0 ? perfNow() >= torpReadyAtClient : undefined,
          smoke: perfNow() >= smokeReadyAtClient,
          repairActive: !!lv?.repairing,
          repairReady: perfNow() >= repairReadyAtClient,
          arty: perfNow() >= artyReadyAtClient,
          boostActive: perfNow() < boostActiveUntilClient,
          boostReady: perfNow() >= boostReadyAtClient,
          onfire: !!lv?.onfire,
        });
      } else if (navalCrew() && driving) {
        const lv = vehiclesR.vehicles.get(selfVehicleId);
        const dcActive = !!lv?.dc;
        if (dcActive && !dcWasActive) {
          dcReadyAtClient = perfNow() + (config.naval?.dcCooldownSec ?? 60);
        }
        dcWasActive = dcActive;
        const repairActive = !!lv?.repairing;
        hud.setNaval({
          ammo: navalAmmo,
          dcActive,
          dcReady: !dcActive && perfNow() >= dcReadyAtClient,
          onfire: !!lv?.onfire,
          flooding: !!lv?.flooding,
          torp: vehMainTorp() ? perfNow() >= torpReadyAtClient : undefined,
          smoke: config.naval?.smokeEnabled ? perfNow() >= smokeReadyAtClient : null,
          repairActive,
          repairReady: !repairActive && perfNow() >= repairReadyAtClient,
          boostActive: perfNow() < boostActiveUntilClient,
          boostReady: perfNow() >= boostReadyAtClient,
        });
      } else {
        hud.setNaval(null);
      }
      // Jets era: the seeker ring + rails/flares chips, and the growl/steady
      // lock tones. State is SERVER truth (the snapshot's jets section), so
      // the ring never promises a launch the server would refuse. The space
      // theater flies the same instrument on the same wire: the rails are
      // the ASAT rack, the flare chip is the chaff magazine, and the coaching
      // speaks radar track, not tailpipes.
      if (jetsEra || spaceTheater) {
        const kit = planeTypes[selfPlane];
        const hasKit = spaceTheater
          ? (kit?.asats ?? 0) > 0 || (kit?.decoys ?? 0) > 0
          : (kit?.missiles?.count ?? 0) > 0 || (kit?.flares ?? 0) > 0;
        const flying = alive && !driving && selfStatus !== 3 && selfStatus !== 5;
        if (flying && hasKit && seekerSrv) {
          const rails = spaceTheater ? (kit?.asats ?? 0) > 0 : (kit?.missiles?.count ?? 0) > 0;
          const mode =
            !rails || !seekerSrv.lock ? "search" : seekerSrv.prog >= 1 ? "lock" : "track";
          // A flare-only rack (the Arclight) gets the chips but no ring — a
          // boresight circle on a plane with no seeker head is a lie.
          hud.setSeeker({
            mode, prog: seekerSrv.prog, msl: seekerSrv.msl, fl: seekerSrv.fl, ring: rails,
            // The launch hint speaks the pilot's actual trigger: the MSL
            // button on the touch deck, the LIVE binding on a keyboard.
            hint: touch ? "TAP MSL" : `${codeHint(keymap.map.missile?.[0])} TO LAUNCH`,
          });
          audio.setSeeker?.(mode);
          // A launch press without a wound lock is refused by the server in
          // silence — and that silence read as "there's nothing for the
          // missiles" on the first mobile sortie. Narrate the refusal WITH
          // the recovery, edge-triggered and throttled: a mashed thumb gets
          // one lesson at a time, not a marquee.
          if (rails && input.msl && !mslPressEdge && mode !== "lock" && perfNow() > mslNagAt) {
            mslNagAt = perfNow() + 2.5;
            hud.message(
              (seekerSrv.msl ?? 0) <= 0
                ? "RAILS EMPTY"
                : mode === "track"
                  ? "TRACK WINDING UP — hold them on the nose"
                  : spaceTheater
                    ? "NO TRACK — put a SPOTTED enemy inside the nose cone"
                    : "NO LOCK — get behind them and hold the ring on their tailpipe",
              2200,
              "#7fd1ff",
            );
          }
          mslPressEdge = input.msl;
        } else {
          hud.setSeeker(null);
          audio.setSeeker?.(null);
          mslPressEdge = false;
        }
      }
      // Deck resupply banner: mirror of the server's stepResupply check.
      let rearming = false;
      if (alive) {
        const deck = config.world.waterLevelM + 15; // carrierDeckY
        const sc = config.ships ?? {};
        const speed2 = FDM.vLen(pred.vel);
        if (
          pred.pos.y >= deck && pred.pos.y <= deck + (sc.resupplyHeightM ?? 30) &&
          speed2 <= (sc.resupplyMaxSpeedMS ?? 55) &&
          (selfHp < selfMaxHp() || selfBombs < ((planeTypes[selfPlane]?.torpedoes?.count || planeTypes[selfPlane]?.bombs?.count) ?? 0))
        ) {
          for (const sh of ships.ships.values()) {
            if (!sh.alive || sh.site.team !== selfTeam || sh.site.kind !== 0) continue;
            const f = { x: Math.cos(sh.site.yaw), z: -Math.sin(sh.site.yaw) };
            const dx = pred.pos.x - sh.site.x, dz = pred.pos.z - sh.site.z;
            const along = dx * f.x + dz * f.z, abeam = dx * -f.z + dz * f.x;
            if (Math.abs(along) <= 62 && Math.abs(abeam) <= 13.5) { rearming = true; break; }
          }
        }
      }
      const coned = alive && lightsR.conedAt(pred.pos, selfTeam);
      const r2 = pred.pos.x * pred.pos.x + pred.pos.z * pred.pos.z;
      const myHull = (hullTheater || armadaTheater) && driving ? vehiclesR.vehicles.get(selfVehicleId) : null;
      const aground = !!myHull?.aground;
      const detected = !!myHull?.spotted;
      // P4a: predictive shoal warning, recomputed a couple of times a second.
      if (myHull && !aground && navalCrew()) {
        const nowP = perfNow();
        if (nowP >= navalShoalAt) {
          navalShoalAt = nowP + 500;
          navalShoalAhead = shoalAheadCheck(myHull.target?.yaw ?? 0);
        }
      } else {
        navalShoalAhead = false;
      }
      // Land blocked-hull warning: ordered to drive but the hull isn't moving
      // (see landBlockedSince). Mirrors the server's landIntegrate blockers.
      let landBlocked = false;
      if (myHull && landCrew()) {
        // Same wire→drive mapping as the server's landThrottle (bottom band
        // of the slider is reverse).
        const thr = vehCtl.throttle;
        const drive = thr < 0.25 ? -((0.25 - thr) / 0.25) * 0.4 : (thr - 0.25) / 0.75;
        if (Math.abs(drive) > 0.3 && lastHullSpeed < 0.6) {
          if (!landBlockedSince) landBlockedSince = perfNow();
          landBlocked = perfNow() - landBlockedSince > 1.2;
        } else {
          landBlockedSince = 0;
        }
      } else {
        landBlockedSince = 0;
      }
      // P3: on DETECTED, append the bearing to the nearest rendered enemy so a
      // spotted banner with an empty horizon reads as a threat, not a mystery.
      let detectedText = "◎ DETECTED — the enemy can see you";
      if (detected) {
        const foe = nearestRenderedEnemy();
        if (foe) {
          const c = compassTo(foe.root.position.x, foe.root.position.z);
          if (c) detectedText = `◎ DETECTED — enemy ${c}`;
        }
      }
      // N3: incoming-torpedo warning. Enemy fish are visible wakes but easy to
      // miss; at 72 m/s a spread gives ~10 s. Scan the rendered torps for an
      // enemy one inside 900 m and closing, and shout its bearing (audio once per
      // fresh contact via the threat edge).
      let torpWarn = "";
      if (myHull) {
        const me = vehiclesR.positionOf(selfVehicleId);
        let nearest = null, nearestD2 = 900 * 900;
        for (const b of bombsR.bombs) {
          if (!b.torp || b.team === selfTeam) continue;
          const dx = b.pos.x - me.x, dz = b.pos.z - me.z;
          const d2 = dx * dx + dz * dz;
          if (d2 > nearestD2) continue;
          if (b.vel.x * dx + b.vel.z * dz >= 0) continue; // not closing
          nearestD2 = d2; nearest = b;
        }
        if (nearest) {
          torpWarn = `⚠ TORPEDOES — ${compassTo(nearest.pos.x, nearest.pos.z)} ⚠`;
          if (!torpThreatActive) audio.damaged();
          torpThreatActive = true;
        } else {
          torpThreatActive = false;
        }
      } else {
        torpThreatActive = false;
      }
      // N7: capture-zone status while conning inside a cap (the zone rings render,
      // but nothing told you you were on the point). Callout on entering / on the
      // holder changing under you.
      if (myHull) {
        const zi = hullZoneIndex();
        if (zi >= 0 && zi !== curZoneIdx) {
          const holder = (score?.zs ?? [])[zi];
          const msg =
            holder === 2 ? [`⚔ CONTESTED — CLEAR ZONE ${zoneLetter(zi)}`, "#f4c542"]
              : holder === selfTeam ? [`⚓ HOLDING ZONE ${zoneLetter(zi)}`, "#7CFC7C"]
                : [`⚓ CAPTURING ZONE ${zoneLetter(zi)}`, "#8fd0ff"];
          hud.message(msg[0], 1800, msg[1]);
        }
        curZoneIdx = zi;
      } else {
        curZoneIdx = -1;
      }
      // Ground-proximity warning (flying): if the current flight path puts us
      // under the terrain within the next ~2.5 s, flash PULL UP. Sampled at two
      // points so ridge lines ahead register, not just the ground below.
      // Suppressed slow (taxiing / carrier deck) where a low reading is fine.
      let pullUp = false;
      const groundSpeed = Math.hypot(pred.vel.x, pred.vel.z);
      if (alive && pred.vel.y < 0 && groundSpeed > 16.7) {
        for (const t of [1.2, 2.5]) {
          const px = pred.pos.x + pred.vel.x * t;
          const pz = pred.pos.z + pred.vel.z * t;
          const py = pred.pos.y + pred.vel.y * t;
          const ground = Math.max(field.heightAt(px, pz), config.world.waterLevelM);
          if (py < ground + 12) { pullUp = true; break; }
        }
      }
      const checkSixNow = alive && perfNow() < checkSixUntil;
      hud.setWarning(
        torpWarn
          ? torpWarn
          : pullUp
          ? "⚠ PULL UP ⚠"
          : checkSixNow
          ? "⚠ CHECK SIX!"
          : aground
          ? "⚠ RUN AGROUND — EASE ASTERN & STEER CLEAR ⚠"
          : landBlocked
          ? "⚠ HULL BLOCKED — BACK UP (S) AND STEER AROUND ⚠"
          : navalShoalAhead
            ? "⚠ SHOAL AHEAD — COME AROUND ⚠"
            : alive && spaceTheater && perfNow() < spaceDetectedUntil
              ? "⚠ DETECTED — YOU ARE ON SOMEONE'S PLOT ⚠"
            : alive && !spaceTheater && r2 > config.world.radiusM * config.world.radiusM
              ? // A disc test means nothing on a cylinder: the space theater's
                // bounds are the ceiling, warned by the orbit chips instead.
                "⚠ RETURN TO THE COMBAT AREA ⚠"
              : coned
                ? "⚠ CONED — every gun can see you"
                : rearming
                  ? "⚙ REARMING — deck crew at work"
                  : detected
                    ? detectedText
                    : "",
        pullUp || checkSixNow,
      );
      // The respawn screen is just the join screen shown again; it only appears
      // once the killcam replay finishes — while `replay` is live, or while one
      // is still inbound (awaitingKillcam), it would obscure or pre-empt the
      // death cam (which is what names your killer). Plain deaths have no replay
      // coming, so the screen shows right away.
      if ((waitingBomber || (selfStatus === 0 && haveSpawned && perfNow() >= awaitingKillcam)) && !replay) {
        // Once this screen is up for a death we've committed to it: forfeit any
        // still-inbound clip so a straggler (the killcam is a fat binary that
        // can crawl in seconds behind its own kill event on a slow link) can't
        // pop the replay back over the spawn card. The extended hold above gives
        // the clip its full window first; this is only the give-up backstop, and
        // it degrades gracefully to "no replay" instead of a jarring flash-back.
        killcamSkipped = true;
        hud.setRespawn(true);
        // Spawn tactical picture (roadmap #55): the live map + kill heat,
        // repainted while the respawn screen is up. Snapshots keep flowing
        // while dead, so every layer stays fresh.
        {
          const tac = document.getElementById("spawn-tac");
          const cv = document.getElementById("spawn-map");
          if (tac && cv) {
            tac.classList.remove("hidden");
            const nowH = perfNow();
            while (heatEvents.length && nowH - heatEvents[0].t > 45) heatEvents.shift();
            const tacBlips = [];
            for (const [id, ent] of entities) {
              const last = ent.buffer.at(-1);
              if (last) {
                tacBlips.push({
                  id, x: last.pos.x, z: last.pos.z,
                  team: roster.get(id)?.team ?? 0,
                  status: ent.status,
                  nemesis: id === nemesisId,
                  ace: id === aceId,
                });
              }
            }
            hud.drawMinimap(
              field, config.match.zoneRadiusM, tacBlips, selfId, null, 0, null, null,
              cv.getContext("2d"), cv.width,
              heatEvents.map((h) => ({ x: h.x, z: h.z, w: h.w, age: nowH - h.t })),
            );
          }
        }
        // Death-recap coach on the respawn card, restating the line the
        // killcam / crash replay showed for anyone who missed it.
        const coachEl = document.getElementById("death-coach");
        if (coachEl) {
          coachEl.textContent = deathCoach?.line ?? "";
          coachEl.classList.toggle("hidden", !deathCoach);
        }
        // The field-manual line rides along: same tip the killcam showed, so
        // a skipped replay doesn't cost the reader his page.
        const tipEl = document.getElementById("respawn-tip");
        if (tipEl) {
          tipEl.textContent = deathTip ? `★ DID YOU KNOW — ${deathTip.text}` : "";
          tipEl.classList.toggle("hidden", !deathTip);
        }
        // Respawn is manual: TAKE OFF stays locked until the cooldown lifts
        // (the anti-spam floor), with the status line counting it down. Once
        // unlocked, pressing it asks the server to put us back in.
        const left = deathRespawnSec - (perfNow() - deathAt);
        // The tower is always open: SPECTATE works from the respawn card too
        // (it was left disabled from the first join, which read as broken).
        if (!spectating && !respawnSent) spectateBtn.disabled = false;
        if (waitingBomber) {
          // No friendly bomber to crew yet. The server auto-seats us the moment
          // one launches, so TAKE OFF has nothing to do — spell out the wait and
          // point at the picker for anyone who'd rather fly now than hold out.
          playBtn.disabled = true;
          playBtn.textContent = "WAITING FOR A BOMBER";
          joinStatus.textContent =
            "no friendly bomber airborne — you'll crew the next one up · or pick a unit to fly now";
        } else if (respawnSent) {
          playBtn.disabled = true;
          playBtn.textContent = "TAKE OFF";
          joinStatus.textContent = "taking off…";
        } else {
          playBtn.disabled = left > 0;
          // Put the countdown in the button label too — the status line under
          // the row is easy to miss on a phone, so the locked state reads as an
          // intentional cooldown rather than a broken/blank button (U10).
          playBtn.textContent = left > 0 ? `TAKE OFF — ${Math.ceil(left)}` : "TAKE OFF";
          joinStatus.textContent =
            left > 0 ? `respawning in ${Math.ceil(left)}…` : "ready — press TAKE OFF";
        }
        let carrierUp = false;
        for (const sh of ships.ships.values()) {
          if (sh.site.team === selfTeam && sh.site.kind === 0 && sh.alive) {
            carrierUp = true;
            break;
          }
        }
        hud.updateDeckSelect(pendingDeck, carrierUp);
      } else {
        hud.setRespawn(false);
        respawnSent = false; // cleared once we're off the spawn screen
        document.getElementById("spawn-tac")?.classList.add("hidden");
      }
      // Never stack the scoreboard on the join/respawn card — the top-bar tap
      // toggle otherwise leaves it latched on when you die (U4). The round-end
      // board still shows over everything.
      const spawnOpen = !hud.el.joinScreen.classList.contains("hidden");
      if (spawnOpen) input.scoreboard = false;
      hud.setScoreboard((input.scoreboard && !spawnOpen) || score.ph === "end", roster, board, selfId);
      // The drone answers the engine, not the throttle lever: a dead or
      // fuel-starved engine falls to an idle mutter at any handle setting —
      // the pitch dropping out from under you IS the engine-out warning.
      audio.setFlight(input.throttle * (alive ? selfDamageMods().thrustScale : 1), FDM.vLen(pred.vel), alive, CLASS_JET.has(selfPlane));
      // N4: warship engine + bow wash while conning a hull; silent otherwise.
      if (navalCrew()) {
        const boostOn = perfNow() < boostActiveUntilClient;
        audio.setShipEngine?.(vehCtl.throttle, hullSpeed, driving, boostOn);
      }

      // Battleship fire control: full-screen plot table + status banner. The
      // table yields the screen to the firing cutaway while a salvo is aloft.
      if (!manningMain && cine.mode !== "off") cine.mode = "off";
      const cutaway = manningMain && cine.mode !== "off";
      hud.setManningMain(manningMain);
      hud.setPlotVisible(manningMain && !cutaway);
      if (!manningMain) plotAim.pending = null;
      const spotted = manningMain && spotterOnStation();
      if (manningMain) {
        const pivot = aaGuns.pivotOf(selfGunId);
        let banner = "FIRE CONTROL — TAP THE CHART TO AIM";
        let ready = false;
        let primary = { label: "TAP MAP TO AIM", kind: "", enabled: false, reaim: false };
        // The fire-control solution readout (powder charge, quadrant elevation,
        // range/bearing, …): populated only once the solve timer expires and a
        // solution is on the board, otherwise the slip stays stowed.
        let fireSolution = null;
        if (plotAim.pending && pivot) {
          const d = Math.hypot(plotAim.pending.x - pivot.x, plotAim.pending.z - pivot.z);
          const onMap = Math.hypot(plotAim.pending.x, plotAim.pending.z) <= field.radius;
          const inRange = d >= bbCfg.minRangeM && d <= spottedRange && onMap;
          const overHorizon = d > directRange; // needs a spotter to fire out here
          const km = (d / 1000).toFixed(2);
          if (!inRange) {
            banner = "OUT OF RANGE";
            primary = { label: "OUT OF RANGE", kind: "", enabled: false, reaim: false };
          } else if (overHorizon) {
            banner = spotted
              ? `AIM · ${km} km · SPOTTER LINKED — DRAG TO ADJUST`
              : `AIM · ${km} km · SPOTTER RANGE (NEEDS EYES ON TARGET)`;
            primary = { label: "✓ SET TARGET", kind: "set", enabled: true, reaim: false };
          } else {
            banner = `AIM · ${km} km — DRAG TO ADJUST`;
            primary = { label: "✓ SET TARGET", kind: "set", enabled: true, reaim: false };
          }
        } else if (fc.target && pivot) {
          const now = perfNow();
          const reloadLeft = fc.reloadUntil - now;
          const solveLeft = fc.plottedAt + (bbCfg.solveTimeSec ?? 4) - now;
          const dT = Math.hypot(fc.target.x - pivot.x, fc.target.z - pivot.z);
          const overHorizon = dT > directRange;
          if (reloadLeft > 0) {
            banner = `RELOADING ${Math.ceil(reloadLeft)}s`;
            primary = { label: `RELOADING ${Math.ceil(reloadLeft)}s`, kind: "", enabled: false, reaim: true };
          } else if (solveLeft > 0) {
            banner = `COMPUTING SOLUTION ${solveLeft.toFixed(1)}s`;
            primary = { label: `COMPUTING ${solveLeft.toFixed(1)}s`, kind: "", enabled: false, reaim: true };
          } else if (overHorizon && !spotted) {
            // Over-the-horizon target with no friendly eyes: the battery holds.
            banner = "SPOTTER REQUIRED — NO EYES ON TARGET";
            primary = { label: "NEED SPOTTER", kind: "", enabled: false, reaim: true };
          } else {
            const to = { x: fc.target.x, y: field.surfaceAt(fc.target.x, fc.target.z), z: fc.target.z };
            const sol = solveFireMission(pivot, to);
            ready = true;
            banner = `SOLUTION READY · FLIGHT ${(sol?.tof ?? 0).toFixed(0)}s`;
            // Flag a dropped charge / plunging fire so the gunner knows the
            // computer lobbed it over terrain rather than shooting flat.
            const reduced = sol && sol.chargeMS < fullChargeMS - chargeStep;
            if (reduced) {
              banner += ` · ${Math.round((sol.chargeMS / fullChargeMS) * 100)}% CHARGE`;
            }
            if (sol && sol.highArc) banner += " · HIGH ANGLE";
            primary = { label: "FIRE", kind: "fire", enabled: true, reaim: true };
            // Lay the solution's data points out on the readout slip. These are
            // exactly the figures the ballistic computer settled on for the plot:
            // the powder charge it walked down to clear terrain, the quadrant
            // elevation, the bearing/range, time of flight, the arc's apex, and
            // the dispersion the spotter (if any) tightened the pattern to.
            if (sol) {
              const g = 9.81;
              const pct = Math.round((sol.chargeMS / fullChargeMS) * 100);
              const elevDeg = (Math.atan2(sol.vy, Math.hypot(sol.vx, sol.vz)) * 180) / Math.PI;
              // Compass bearing with North = -Z (the captured-heading reference).
              const brg = ((Math.atan2(to.x - pivot.x, -(to.z - pivot.z)) * 180) / Math.PI + 360) % 360;
              const apex = (sol.vy * sol.vy) / (2 * g); // max ordinate above the muzzle
              const spreadM = spotted ? (bbCfg.salvoSpreadSpottedM ?? 25) : (bbCfg.salvoSpreadM ?? 80);
              const windSpd = Math.hypot(score.windX ?? 0, score.windZ ?? 0);
              fireSolution = [
                { label: "RANGE", value: `${(dT / 1000).toFixed(2)} km` },
                { label: "BEARING", value: `${brg.toFixed(0).padStart(3, "0")}°` },
                { label: "CHARGE", value: `${pct}% · ${Math.round(sol.chargeMS)} m/s`, tone: reduced ? "hot" : "" },
                { label: "ELEVATION", value: `${elevDeg.toFixed(1)}°`, tone: sol.highArc ? "hot" : "" },
                { label: "ARC", value: sol.highArc ? "HIGH / PLUNGING" : reduced ? "REDUCED" : "FLAT / DIRECT", tone: sol.highArc || reduced ? "hot" : "" },
                { label: "FLIGHT", value: `${sol.tof.toFixed(1)} s` },
                { label: "APEX", value: `${Math.round(apex).toLocaleString()} m` },
                { label: "PATTERN", value: `${Math.round(spreadM)} m`, tone: spotted ? "good" : "" },
                ...(windSpd >= 1 ? [{ label: "WIND", value: `${Math.round(windSpd)} m/s` }] : []),
                { label: "SPOTTER", value: spotted ? "LINKED" : "—", tone: spotted ? "good" : "" },
              ];
            }
          }
          if (spotted) banner += " · SPOTTER ON STATION";
          const windSpd = Math.hypot(score.windX ?? 0, score.windZ ?? 0);
          if (windSpd >= 1) banner += ` · WIND ${Math.round(windSpd)} m/s`;
        }
        hud.setFcs(banner, ready);
        hud.setPlotActions(primary);
        hud.setFireSolution(fireSolution);
      }

      // Mouse-aim cursor + gunnery lead pip (hidden while freelooking; the
      // main battery aims by plotting, not by cursor). The cursor only helps
      // while flying — when manning a gun or driving a vehicle the barrel
      // aimpoint and lead pip are what matter, so the loose mouse dot is just
      // clutter and stays hidden. Exception (G1 stage 1): an UNLOCKED mouse
      // conning a hull is a rate-based virtual joystick driven by cursor
      // offset from centre, so hiding the cursor would mean pushing an
      // invisible stick. Under pointer lock the sight is position-controlled
      // (the crosshair IS the mouse) and the loose dot goes back to clutter.
      hud.setCursor(
        (alive || (driving && (hullTheater || armadaTheater) && !input.pointerLocked)) &&
          input.mouseAim && !input.pad.active && !input.look.active,
        input.cursor.x,
        input.cursor.y,
      );
      let pipShown = false;
      if (alive || (manning && !manningMain) || driving || turretting) {
        let fwd;
        if (turretting) {
          // World aim off the turret camera step (host attitude × local aim).
          fwd = FDM.v3(turDir.x, turDir.y, turDir.z);
        } else if (manning || driving) {
          const aim = manning ? gunAim : vehAim;
          const ch = Math.cos(aim.pitch);
          fwd = FDM.v3(
            Math.cos(aim.yaw) * ch,
            Math.sin(aim.pitch),
            -Math.sin(aim.yaw) * ch,
          );
        } else {
          // Space: the GAZE is the gunsight, in every mode — you designate by
          // looking and the ring lays the barrels on the mark (the nose is
          // busy flying). In raw mode the gaze rides the nose anyway, so
          // this is the old behaviour there for free. Every other theater:
          // the nose picks, because that's where its guns are bolted.
          fwd = spaceTheater
            ? FDM.v3(spaceCamDir.x, spaceCamDir.y, spaceCamDir.z)
            : FDM.qRotate(pred.att, FDM.v3(1, 0, 0));
        }
        const target = pickLeadTarget(fwd);
        if (spaceTheater) spaceGunSolution = null; // no target / no solve = no stale mark
        if (target) {
          const mg = driving ? vehMainGun() : null;
          const cal = manning
            ? (calByName.get(gunProf().caliber) ?? selfCaliber())
            : turretting
              ? (calByName.get(turretCfg()?.gun?.caliber) ?? selfCaliber())
              : mg
                ? { muzzleSpeedMS: mg.muzzleSpeedMS }
                : driving
                  ? (calByName.get(vehProf()?.caliber) ?? selfCaliber())
                  : selfCaliber();
          // Full firing solution. Space: the flat-ballistics lead is wrong by
          // hundreds of meters at gun range — the mark comes from the same
          // kernel the server's solver runs, so the pip and the round agree.
          // Air: deflection + gravity holdover (client/src/gunnery.js).
          const solved = spaceTheater
            ? spaceLeadPoint(pred, target.last, cal.muzzleSpeedMS ?? 300, spaceConsts, leadAim)
            : (gunLeadPoint(pred.pos, pred.vel, target.last.pos, target.last.vel, cal.muzzleSpeedMS ?? 320, leadAim), true);
          if (spaceTheater && solved) {
            // Stash the mark for the director's GUNS branch (next frame's
            // steering): the nose it lays and the pip on the glass are the
            // same solution by construction.
            spaceGunSolution = { x: leadAim.x, y: leadAim.y, z: leadAim.z };
            spaceGunSolutionAt = perfNow();
          }
          // The world bends for the space theater (curveworld.js), so a mark
          // computed in the flat kernel has to be bent before it is projected
          // or the pip sits off the hull it is solving for — by degrees, at
          // exactly the ranges gunnery cares about.
          bendPoint(tmpV, leadAim.x, leadAim.y, leadAim.z).project(camera);
          if (solved && tmpV.z < 1 && Math.abs(tmpV.x) < 1.1 && Math.abs(tmpV.y) < 1.1) {
            hud.setLeadPip(true, tmpV.x, tmpV.y);
            pipShown = true;
          }
        }
      }
      if (!pipShown) hud.setLeadPip(false);

      // Ranged gun reticle: mark where a centered burst from the wing guns
      // actually crosses — the harmonization (convergence) range — dropped for
      // the gravity fall over that flight, so the glyph sits on the true point
      // of impact rather than the bore line. Manned mounts and vehicles keep
      // the plain centered crosshair (their camera already looks down the bore).
      let reticleRanged = false;
      if (alive && !manning && !driving) {
        const conv = planeTypes[selfPlane]?.gun?.convergenceM ?? 0;
        if (conv > 0) {
          const fwd = FDM.qRotate(pred.att, FDM.v3(1, 0, 0));
          gunBoresightReticle(pred.pos, pred.vel, fwd, conv, selfCaliber().muzzleSpeedMS ?? 320, leadAim);
          tmpV.set(leadAim.x, leadAim.y, leadAim.z).project(camera);
          if (tmpV.z < 1) {
            hud.setCrosshair(tmpV.x, tmpV.y);
            reticleRanged = true;
          }
        }
      }
      if (!reticleRanged) hud.setCrosshair(0, 0);

      // Level-bombing pipper: only the Leviathan carries a bombsight. Project
      // the predicted gravity-bomb impact point to screen so a human pilot can
      // walk the pipper onto the target before pickling, the way the bots aim
      // off their impact-point math. Hidden once the bay is empty.
      let sightShown = false;
      if (alive && !manning && selfBombs > 0 && planeTypes[selfPlane]?.levelBomber) {
        const wind = { x: score.windX ?? 0, z: score.windZ ?? 0 };
        if (predictBombImpact(pred.pos, pred.vel, pred.att, wind, bombImpactV)) {
          bombRingWorld.copy(bombImpactV); // keep the world impact for the ring
          bombImpactV.project(camera);
          if (bombImpactV.z < 1 && Math.abs(bombImpactV.x) < 1.1 && Math.abs(bombImpactV.y) < 1.1) {
            hud.setBombsight(true, bombImpactV.x, bombImpactV.y);
            sightShown = true;
            // Confidence ring (roadmap #56): release dispersion under the
            // current flight state — a hard pull, a steep bank, or high release
            // blooms it; steady and level tightens it to a killing solution.
            const nz = pred.nz ?? 1;
            const rgt = FDM.qRotate(pred.att, FDM.v3(0, 0, 1));
            const bank = Math.asin(Math.max(-1, Math.min(1, rgt.y)));
            const altAGL = Math.max(0, pred.pos.y - field.surfaceAt(pred.pos.x, pred.pos.z));
            const radiusM = 12 + 90 * Math.abs(nz - 1) + 60 * (1 - Math.cos(bank)) + 0.02 * altAGL;
            bombRingCamR.setFromMatrixColumn(camera.matrixWorld, 0); // camera right
            bombRingEdge.copy(bombRingWorld).addScaledVector(bombRingCamR, radiusM).project(camera);
            const pxR = Math.hypot(
              (bombRingEdge.x - bombImpactV.x) * canvas.clientWidth * 0.5,
              (bombRingEdge.y - bombImpactV.y) * canvas.clientHeight * 0.5,
            );
            hud.setBombRing(true, bombImpactV.x, bombImpactV.y, pxR, radiusM <= 25);
          }
        }
      }
      if (!sightShown) {
        hud.setBombsight(false);
        hud.setBombRing(false);
      }

      // While manning the big guns the plot table is the primary view: build
      // its overlay and redraw it every frame (reticle, pulses, shells) so it
      // stays responsive. The corner minimap is hidden, so skip it.
      if (manningMain && !cutaway) {
        const pivot = aaGuns.pivotOf(selfGunId);
        if (pivot) {
          const now = perfNow();
          while (spotMarks.length && now - spotMarks[0].at > spotMarkSec) spotMarks.shift();
          hud.setShips(ships.blips());
          // Friendly planes are spotters: each carries a live bubble (spot
          // radius) that reveals enemy ground around it. The gunner sees enemy
          // installations only inside the direct-fire ring or inside one of
          // those bubbles — everything deeper stays in the fog.
          const spotViewR = bbCfg.spotRadiusM ?? 600;
          const spotR2 = spotViewR * spotViewR;
          const directR2 = directRange * directRange;
          const spotters = [];
          const blips = [];
          for (const [id, ent] of entities) {
            const last = ent.buffer.at(-1);
            if (!last || ent.status === 0) continue;
            const team = roster.get(id)?.team ?? 0;
            blips.push({ x: last.pos.x, z: last.pos.z, team });
            if (team === selfTeam) spotters.push({ x: last.pos.x, z: last.pos.z });
          }
          const visible = (x, z) => {
            if ((x - pivot.x) ** 2 + (z - pivot.z) ** 2 <= directR2) return true;
            for (const s of spotters) {
              if ((x - s.x) ** 2 + (z - s.z) ** 2 <= spotR2) return true;
            }
            return false;
          };
          const targets = [];
          for (const [, g] of aaGuns.guns) {
            // Balloons hang far above any shell burst: not chart targets.
            if (g.team === selfTeam || !g.alive || g.kind === 4) continue;
            const tx = g.root.position.x, tz = g.root.position.z;
            if (!visible(tx, tz)) continue;
            targets.push({ x: tx, z: tz, big: g.kind >= 2 });
          }
          const overlay = {
            minR: bbCfg.minRangeM ?? 600,
            maxR: directRange,
            spotR: spottedRange,
            spotViewR,
            spotters,
            target: fc.target,
            spreadM: spotted ? (bbCfg.salvoSpreadSpottedM ?? 25) : (bbCfg.salvoSpreadM ?? 80),
            spotted, targets, blips,
            // Surface hulls (driven warships and armor) from the snapshot —
            // the contacts the gunner is actually hunting. Shell blips were
            // dropped from the chart: fall of shot is the impact rings.
            hulls: vehiclesR.plotBlips(),
            impacts: fc.impacts.map((im) => ({ x: im.x, z: im.z, age: now - im.at })),
            marks: spotMarks
              .map((mk) => ({ x: mk.x, z: mk.z, age: now - mk.at, ttl: spotMarkSec }))
              .concat(commsPings.map((pg) => ({ x: pg.x, z: pg.z, age: now - pg.at, ttl: config.comms?.markSec ?? 20 }))),
          };
          const view = { cx: pivot.x, cz: pivot.z, r: spottedRange * 1.12 };
          hud.drawPlot(field, view, overlay, plotAim.pending, pivot, gunAim.yaw);
        }
      }

      minimapAt -= dt;
      if (minimapAt <= 0) {
        minimapAt = 0.1;
        if (!manningMain) {
          hud.setTrucks(trucksR.blips());
          hud.setSupplyShips(supplyShipsR.blips());
          hud.setVehicles(vehiclesR.blips(), selfTeam);
          hud.setShips(ships.blips());
          const blips = [];
          for (const [id, ent] of entities) {
            const last = ent.buffer.at(-1);
            if (last) {
              blips.push({
                id, x: last.pos.x, z: last.pos.z,
                team: roster.get(id)?.team ?? 0,
                status: ent.status,
                nemesis: id === nemesisId,
                ace: id === aceId,
                ad: !!roster.get(id)?.ad, // the circus reads gold, not bandit
              });
            }
          }
          const fwd = FDM.qRotate(pred.att, FDM.v3(1, 0, 0));
          const yaw = manning
            ? gunAim.yaw
            : driving
              ? vehAim.yaw
              : turretting
                ? Math.atan2(-turDir.z, turDir.x) // world bearing of the mount
                : Math.atan2(-fwd.z, fwd.x);
          // N2: feed the last-6-seconds fall-of-shot to the naval minimap.
          if (navalTheater || armadaTheater) {
            const now2 = perfNow();
            hud.setNavalImpacts?.(fc.impacts.map((im) => ({ x: im.x, z: im.z, age: now2 - im.at })));
          }
          const nowPing = perfNow();
          const pingTtl = config.comms?.markSec ?? 20;
          while (commsPings.length && nowPing - commsPings[0].at > pingTtl) commsPings.shift();
          // "You are here" needs the hull's position while driving — pred.pos
          // is the flight prediction, which a ship's captain doesn't have.
          const mePos = alive || manning || turretting
            ? pred.pos
            : driving ? vehiclesR.positionOf(selfVehicleId) : null;
          hud.drawMinimap(
            field, config.match.zoneRadiusM, blips, selfId,
            mePos, yaw, null,
            commsPings.map((pg) => ({ x: pg.x, z: pg.z, age: nowPing - pg.at, ttl: pingTtl })),
          );
        }
      }
    } else if (spectating) {
      const specSpawnOpen = !hud.el.joinScreen.classList.contains("hidden");
      if (specSpawnOpen) input.scoreboard = false;
      hud.setScoreboard((input.scoreboard && !specSpawnOpen) || score.ph === "end", roster, board, 0);
      // Drive the engine drone from the plane we're following, so the stream
      // carries its engine note instead of only the event sounds. Ships, guns,
      // and the establishing orbit have no plane, so the drone fades out.
      const specEnt = spectateTarget?.kind === "plane" ? entities.get(spectateTarget.id) : null;
      const specLast = specEnt && specEnt.status !== 0 ? specEnt.buffer.at(-1) : null;
      audio.setFlight(specLast?.thr ?? 0, specLast ? FDM.vLen(specLast.vel) : 0, !!specLast, CLASS_JET.has(specEnt?.plane));
      music?.setIntensity(director.intensity); // arrangement thickens with the action
      // Positional engine sound for nearby planes — a close pass roars and pans
      // by, distant traffic stays silent. Skip the followed plane (its drone
      // covers it). camera.quaternion is current here (set by spectateCam).
      flybyRight.set(1, 0, 0).applyQuaternion(camera.quaternion);
      flybyPlanes.length = 0;
      for (const [id, ent] of entities) {
        if (ent.status === 0 || !ent.buffer.length) continue;
        if (spectateTarget?.kind === "plane" && id === spectateTarget.id) continue;
        const b = ent.buffer.at(-1);
        flybyPlanes.push({
          id, x: b.pos.x, y: b.pos.y, z: b.pos.z,
          vx: b.vel.x, vy: b.vel.y, vz: b.vel.z,
          throttle: b.thr ?? 0, speed: FDM.vLen(b.vel),
          jet: CLASS_JET.has(ent.plane),
        });
      }
      audio.updateFlybys(flybyPlanes, camera.position, flybyRight);
      hud.setCursor(false);
      hud.setLeadPip(false);
      hud.setBombsight(false);
      minimapAt -= dt;
      if (minimapAt <= 0) {
        minimapAt = 0.1;
        hud.setTrucks(trucksR.blips());
        hud.setSupplyShips(supplyShipsR.blips());
        hud.setVehicles(vehiclesR.blips(), selfTeam);
        hud.setShips(ships.blips());
        const blips = [];
        for (const [id, ent] of entities) {
          const last = ent.buffer.at(-1);
          if (last) {
            blips.push({
              id, x: last.pos.x, z: last.pos.z,
              team: roster.get(id)?.team ?? 0,
              status: ent.status,
            });
          }
        }
        hud.drawMinimap(field, config.match.zoneRadiusM, blips, 0, null, 0);
      }
    }

    // --- off-screen enemy edge markers (touch HUD) ---
    // Near-miss cues: sweep the latest snapshot's enemy bullets for
    // supersonic cracks and check-six pressure. Flying or manning a gun only
    // (being straddled at a flak mount is exactly the drama); a manned gun
    // has no six, so the threat accumulator is skipped there.
    if (nearMissRows && !replay && (alive || manning)) {
      camRight.setFromMatrixColumn(camera.matrixWorld, 0);
      const f = alive && !manning ? FDM.qRotate(pred.att, FDM.v3(1, 0, 0)) : null;
      const res = nearMiss.sweep(
        nearMissRows, selfTeam, camera.position, camRight, f,
        1 / config.net.snapshotRateHz, perfNow(),
      );
      for (const pan of res.cracks) audio.crack(pan);
      if (res.sting) {
        audio.checkSix();
        checkSixUntil = perfNow() + 1.5;
        analytics.count("check_six_warnings");
      }
    }
    nearMissRows = null;

    // --- dynamic combat music (roadmap #46) ---
    // The soundtrack is the threat meter: raw intensity from local combat
    // state, shaped by an asymmetric envelope (fast attack, ~12 s release,
    // and a 20 s simmering floor after a real fight) so it never whiplashes.
    // The spectator/director path feeds setIntensity elsewhere — skip it here.
    if (music.started && !spectating) {
      let raw = 0;
      if (alive) {
        let close = 0;
        for (const ent of entities.values()) {
          if (ent.status === 0) continue;
          const info = roster.get(ent.id);
          if ((info?.team ?? selfTeam) === selfTeam || info?.ad) continue; // a billboard is not a threat
          const lp = ent.buffer.at(-1)?.pos;
          if (!lp) continue;
          const dx = lp.x - pred.pos.x, dy = lp.y - pred.pos.y, dz = lp.z - pred.pos.z;
          if (dx * dx + dy * dy + dz * dz < 2000 * 2000 && ++close >= 4) break;
        }
        raw += 1.2 * close;
        if (input.trigger || perfNow() - lastOwnHitAt < 1) raw += 0.8;
        if (perfNow() - lastDamagedAt < 4) raw += 1.5;
        if (lightsR.conedAt(pred.pos, selfTeam)) raw += 2.0;
        if (score.z) {
          const zx = pred.pos.x - score.z.x, zz = pred.pos.z - score.z.z;
          const zr = config.match?.zoneRadiusM ?? 450;
          if (zx * zx + zz * zz < zr * zr) raw += 0.6;
        }
      }
      if (lastOvertime) raw = Math.max(raw, 4); // the finale holds the top gear
      musicDrive += (raw - musicDrive) * Math.min(1, dt * (raw > musicDrive ? 1.5 : 0.083));
      if (musicDrive > 2.5) musicFloorUntil = perfNow() + 20;
      const drive = Math.max(musicDrive, perfNow() < musicFloorUntil ? 1.0 : 0);
      music.setIntensity(drive);
      if (drive >= 2.0) {
        musicCalmSince = null;
        if (music.requestThemeClass("combat")) analytics.count("music_combat_switches");
      } else if (drive < 0.8) {
        musicCalmSince ??= perfNow();
        if (perfNow() - musicCalmSince > 45) music.requestThemeClass("calm");
      } else {
        musicCalmSince = null;
      }
    }

    // Project each live enemy within range with the render camera; anything
    // behind us or outside the safe box gets a marker clamped to that edge,
    // pointing off-screen toward the bandit. Data path is cheap and desktop
    // hides the markers via CSS, so it runs on every platform.
    if (spaceTheater && alive) {
      // THE TALLY BOARD — the space half of the 1960s optics. Out here a
      // craft is a glint kilometres away, and the fog of war already gates
      // what reaches the client (binsnap sends only spotted hulls), so every
      // entity we hold IS a confirmed track: mark it. On-screen contacts get
      // a diamond with the range in km (the telescope's firing solution),
      // off-screen bandits get the edge arrow; wingmates only mark when
      // they're actually in frame — arrows are for threats.
      camera.updateMatrixWorld();
      camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
      edgeMarkers.length = 0;
      const lockId = seekerSrv?.lock ?? 0;
      for (const ent of entities.values()) {
        if (ent.status === 0 || ent.status === 3 || ent.status === 4 || ent.status === 5) continue;
        if (!ent.visual.group.visible) continue;
        const info = roster.get(ent.id);
        if (info?.ad) continue; // no diamond for a billboard
        const friend = (info?.team ?? 0) === selfTeam;
        const wp = ent.visual.group.position;
        const dist = Math.hypot(wp.x - renderPos.x, wp.y - renderPos.y, wp.z - renderPos.z);
        // A track with no number marks nothing: one entity has been caught
        // wearing a NaN position (a literal "◇ NaN" on a phone's tally
        // board, and the bounding-sphere console warning is likely the same
        // hull's trail). Guard the board; the root cause hunt is its own
        // task.
        if (!Number.isFinite(dist)) continue;
        if (dist < 250) continue; // formation-close hulls read fine unmarked
        if (friend && dist > 9000) continue;
        bendPoint(edgeBent, wp.x, wp.y, wp.z);
        edgeNdc.copy(edgeBent).applyMatrix4(camera.matrixWorldInverse);
        const behind = edgeNdc.z >= -0.1;
        edgeNdc.copy(edgeBent).project(camera);
        let nx = edgeNdc.x, ny = edgeNdc.y;
        if (behind) { nx = -nx; ny = -ny; } // project() mirrors points behind us
        const off = behind || Math.abs(nx) > 0.95 || Math.abs(ny) > 0.92;
        if (off) {
          if (friend) continue;
          const s = Math.max(Math.abs(nx) / 0.95, Math.abs(ny) / 0.92);
          if (s > 1e-4) { nx /= s; ny /= s; }
        }
        edgeMarkers.push({
          ndcX: nx, ndcY: ny, dist, team: info?.team ?? 0,
          // The chase panel's mark wears the same brackets the ASAT lock does:
          // one contact at a time is THE one, and the glass should not make the
          // pilot cross-reference a name against a readout.
          on: !off, space: true, lock: ent.id === lockId || ent.id === spaceDesigId,
        });
      }
      // The locked track always keeps its diamond; the rest by distance.
      edgeMarkers.sort((a, b) => (a.lock ? -1 : b.lock ? 1 : a.dist - b.dist));
      if (edgeMarkers.length > 8) edgeMarkers.length = 8;
      hud.setEdgeMarkers(edgeMarkers);
      if (window.__va && !window.__va.spaceMarks) window.__va.spaceMarks = edgeMarkers;
    } else if (alive && !manning && !driving) {
      camera.updateMatrixWorld();
      camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
      edgeMarkers.length = 0;
      for (const ent of entities.values()) {
        if (ent.status === 0 || ent.status === 3 || ent.status === 4 || ent.status === 5) continue;
        if (!ent.visual.group.visible) continue;
        const info = roster.get(ent.id);
        if ((info?.team ?? 0) === selfTeam || info?.ad) continue; // no bandit arrow for a billboard
        const wp = ent.visual.group.position;
        const dist = Math.hypot(wp.x - renderPos.x, wp.y - renderPos.y, wp.z - renderPos.z);
        if (dist > EDGE_RANGE_M) continue;
        // Camera-space z tells us front/behind reliably (camera looks down -z).
        edgeNdc.copy(wp).applyMatrix4(camera.matrixWorldInverse);
        const behind = edgeNdc.z >= -0.1;
        edgeNdc.copy(wp).project(camera);
        let nx = edgeNdc.x, ny = edgeNdc.y;
        if (behind) { nx = -nx; ny = -ny; } // project() mirrors points behind us
        const off = behind || Math.abs(nx) > 0.95 || Math.abs(ny) > 0.92;
        if (!off) continue;
        // Clamp onto the edge box, preserving direction.
        const s = Math.max(Math.abs(nx) / 0.95, Math.abs(ny) / 0.92);
        if (s > 1e-4) { nx /= s; ny /= s; }
        edgeMarkers.push({ ndcX: nx, ndcY: ny, dist, team: roster.get(ent.id)?.team ?? 0 });
      }
      edgeMarkers.sort((a, b) => a.dist - b.dist);
      if (edgeMarkers.length > 4) edgeMarkers.length = 4;
      hud.setEdgeMarkers(edgeMarkers);
    } else if (driving && (hullTheater || armadaTheater)) {
      // Hull theaters: every enemy hull the server has spotted for us gets a
      // marker — an edge arrow when off-screen, a caret hovering over it when
      // on-screen. A spotted tank is otherwise a 3-px silhouette lost in the
      // steppe (and a distant ship a smudge on the sea), which made finding
      // the enemy the hardest part of the fight. Unspotted enemies never
      // reach the client at all, so this leaks nothing the fog of war hides.
      camera.updateMatrixWorld();
      camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
      edgeMarkers.length = 0;
      const reach = landTheater ? 3200 : 12000;
      for (const [vid, v] of vehiclesR.vehicles) {
        if (vid === selfVehicleId || (v.team ?? 0) === selfTeam || (v.hp ?? 0) <= 0) continue;
        const wp = v.root.position;
        const dist = Math.hypot(
          wp.x - camera.position.x, wp.y - camera.position.y, wp.z - camera.position.z);
        if (dist > reach) continue;
        edgeNdc.copy(wp).applyMatrix4(camera.matrixWorldInverse);
        const behind = edgeNdc.z >= -0.1;
        edgeNdc.copy(wp).project(camera);
        let nx = edgeNdc.x, ny = edgeNdc.y;
        if (behind) { nx = -nx; ny = -ny; } // project() mirrors points behind us
        const off = behind || Math.abs(nx) > 0.95 || Math.abs(ny) > 0.92;
        if (off) {
          const s = Math.max(Math.abs(nx) / 0.95, Math.abs(ny) / 0.92);
          if (s > 1e-4) { nx /= s; ny /= s; }
        }
        edgeMarkers.push({ ndcX: nx, ndcY: ny, dist, team: v.team ?? 0, on: !off, hull: true });
      }
      edgeMarkers.sort((a, b) => a.dist - b.dist);
      if (edgeMarkers.length > 6) edgeMarkers.length = 6;
      hud.setEdgeMarkers(edgeMarkers);
    } else {
      hud.setEdgeMarkers(EDGE_EMPTY);
    }

    // Kill-confirm camera: nudge the posed chase cam toward the falling
    // victim with a small FOV punch, easing home (stateless per frame).
    killConfirm.applyCamera(camera, setCameraFov, perfNow());

    renderer.render(scene, camera);
  }

  // One-shot load metric: how long from navigation to the client being ready
  // to fly (config fetched, scene built, first frame about to run).
  if (typeof performance !== "undefined" && performance.now) {
    analytics.metric("client_ready", performance.now(), { unit: "ms" });
  }

  frame();
}

function escapeName(s) {
  return String(s).replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
}

// Wire the join-screen "MISSIONS" link to an overlay that fetches today's daily
// challenges (/daily.json) on each open. Progress isn't shown here (it's
// per-pilot and rides the join handshake) — this is the at-a-glance brief.
function setupMissions() {
  const screen = document.getElementById("daily-screen");
  const body = document.getElementById("daily-body");
  const status = document.getElementById("daily-status");
  const link = document.getElementById("missions-link");
  const close = document.getElementById("daily-close");
  if (!screen || !link) return;

  const hide = () => screen.classList.add("hidden");

  async function open() {
    screen.classList.remove("hidden");
    body.innerHTML = "";
    status.textContent = "loading…";
    status.classList.remove("hidden");
    try {
      const res = await fetch(serverURL("/daily.json"), { cache: "no-store" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const list = data?.challenges ?? [];
      if (!list.length) {
        status.textContent = "no missions today — check back tomorrow.";
        return;
      }
      status.classList.add("hidden");
      body.innerHTML = list
        .map(
          (c) => `<div class="daily-row">
              <div class="daily-desc"><span>${escapeName(c.desc ?? "")}</span><span class="daily-have">0/${c.goal}</span></div>
              <div class="daily-bar"><div class="daily-fill" style="width:0%"></div></div>
            </div>`,
        )
        .join("");
    } catch (err) {
      status.textContent = "missions unavailable";
      console.warn("daily fetch failed:", err);
    }
  }

  link.addEventListener("click", () => {
    open();
    analytics.event("missions_open");
  });
  close?.addEventListener("click", hide);
  screen.addEventListener("click", (e) => {
    if (e.target === screen) hide();
  });
  addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !screen.classList.contains("hidden")) hide();
  });
}

// Settings > Keybindings (desktop). An overlay that edits the LIVE keymap
// (client/src/keybindings.js) and persists it under "va-keybindings". Click a key
// chip to re-record it — the next physical key you press takes the slot, stealing
// it from whatever control held it before (a key can only do one flight thing at
// a time). input.js reads the same live map, so a rebind applies the instant it
// saves: no reload, no replug. The three pilot toggles (mouse aim, auto-trim,
// inverted pitch) ride along as checkboxes so they stop being blind-press secrets.
function setupSettings(input) {
  const screen = document.getElementById("settings-screen");
  const link = document.getElementById("settings-link");
  if (!screen || !link) return;
  const listEl = document.getElementById("keybind-list");
  const prefsEl = document.getElementById("settings-prefs");
  const noteEl = document.getElementById("keybind-note");
  const closeBtn = document.getElementById("settings-close");
  const doneBtn = document.getElementById("settings-done");
  const resetBtn = document.getElementById("keybind-reset");

  const labelById = new Map(ACTIONS.map((a) => [a.id, a.label]));
  const nameOf = (id) => escapeName(labelById.get(id) ?? id);
  const noteDefault = noteEl?.innerHTML ?? "";

  // The chip currently waiting for a keypress, or null. oldCode is the code that
  // chip already held (null for a fresh "+" slot we're adding into).
  let listening = null;
  let isOpen = false;

  function flashNote(html) {
    if (!noteEl) return;
    noteEl.innerHTML = html;
    noteEl.classList.add("kb-flash");
  }
  function restoreNote() {
    if (!noteEl) return;
    noteEl.innerHTML = noteDefault;
    noteEl.classList.remove("kb-flash");
  }

  // Rebuild the whole control list from the live map. Cheap, and it keeps the
  // DOM honest after a steal changes some other row's chips out from under us.
  function render() {
    if (!listEl) return;
    const groups = [...GROUPS];
    for (const a of ACTIONS) if (!groups.includes(a.group)) groups.push(a.group);
    let html = "";
    for (const g of groups) {
      const rows = ACTIONS.filter((a) => a.group === g);
      if (!rows.length) continue;
      html += `<div class="kb-group-head">${escapeName(g.toUpperCase())}</div>`;
      for (const a of rows) {
        const codes = keymap.keysFor(a.id);
        const chips = codes.length
          ? codes
              .map(
                (c) =>
                  `<button class="kb-chip" data-act="${a.id}" data-code="${escapeName(c)}">${escapeName(codeLabel(c))}</button>`,
              )
              .join("")
          : `<button class="kb-chip kb-empty" data-act="${a.id}" data-code="">unbound</button>`;
        html += `<div class="kb-row" data-row="${a.id}">
          <span class="kb-name">${escapeName(a.label)}${a.hold ? ' <span class="kb-hold">(hold)</span>' : ""}</span>
          <span class="kb-binds">${chips}<button class="kb-add" data-act="${a.id}" title="add another key" aria-label="add another key">+</button><button class="kb-reset-one" data-act="${a.id}" title="reset to default" aria-label="reset this control">&#8635;</button></span>
        </div>`;
      }
    }
    listEl.innerHTML = html;
  }

  // Begin recording a key for `actionId`. We rebuild the list first (authoritative
  // fresh DOM — this also restores any chip that was already showing "press…"),
  // THEN locate or create the chip to highlight in that fresh DOM. Doing it in
  // this order is what lets you click a second chip mid-record and see the
  // highlight jump: a stored chip reference would have been detached by the
  // rebuild. `oldCode` is the code being re-recorded (null when adding a new key);
  // `transient` means a throwaway "+ add" slot with no code yet.
  function startListen(actionId, oldCode, transient) {
    endListen(); // clear any prior highlight/state (no render — we do it next)
    render();
    const binds = listEl?.querySelector(`.kb-row[data-row="${actionId}"] .kb-binds`);
    let chipEl = null;
    if (transient && binds) {
      chipEl = document.createElement("button");
      chipEl.className = "kb-chip";
      chipEl.dataset.act = actionId;
      chipEl.dataset.code = "";
      binds.insertBefore(chipEl, binds.querySelector(".kb-add"));
    } else if (oldCode) {
      // code values are safe identifiers (KeyW, ArrowUp, Space…), so no escaping.
      chipEl = binds?.querySelector(`.kb-chip[data-code="${oldCode}"]`) ?? null;
    } else {
      chipEl = binds?.querySelector(".kb-chip.kb-empty") ?? null; // the "unbound" chip
    }
    if (chipEl) {
      chipEl.classList.add("kb-listening");
      chipEl.classList.remove("kb-empty");
      chipEl.textContent = "press…";
    }
    listening = { actionId, oldCode: oldCode || null, chipEl };
    flashNote(`Press a key for <b>${nameOf(actionId)}</b> — <b>Esc</b> cancels, <b>Bksp</b> clears.`);
  }
  function endListen() {
    if (listening?.chipEl) listening.chipEl.classList.remove("kb-listening");
    listening = null;
  }
  function cancelListen() {
    if (!listening) return;
    endListen();
    render();
    restoreNote();
  }

  // Capture phase: we intercept the key before it reaches anything else. (input.js
  // is already muzzled by setCapturing() for as long as the modal is open, so this
  // is belt-and-suspenders — but it also stops the page from acting on the key.)
  function onCaptureKey(e) {
    if (!isOpen) return;
    if (!listening) {
      // Esc closes, and so does the settings key itself (read from the live
      // map, so a rebound key still closes what it opened) — unless a chip is
      // recording, where every key is a candidate binding. Swallow the event
      // whole: hide() un-muzzles input.js, and the same press reaching it
      // would fire settingsRequested and re-open the modal it just closed.
      if (e.code === "Escape" || keymap.matches("settings", e.code)) {
        e.preventDefault();
        e.stopPropagation();
        hide();
      }
      return;
    }
    e.preventDefault();
    e.stopPropagation();
    const code = e.code;
    const id = listening.actionId;
    if (code === "Escape") {
      cancelListen();
      return;
    }
    if (code === "Backspace" || code === "Delete") {
      const had = listening.oldCode;
      if (had) keymap.clear(id, had);
      endListen();
      render();
      flashNote(had ? `Cleared a key from <b>${nameOf(id)}</b>.` : "Cancelled.");
      return;
    }
    // Re-recording a chip keeps its slot (replace in place); "+ add" appends.
    const stolen = keymap.replace(id, listening.oldCode, code).filter((s) => s !== id);
    endListen();
    render();
    if (stolen.length) {
      flashNote(
        `<b>${escapeName(codeLabel(code))}</b> → <b>${nameOf(id)}</b> (taken from ${stolen.map(nameOf).join(", ")}).`,
      );
    } else {
      flashNote(`<b>${escapeName(codeLabel(code))}</b> → <b>${nameOf(id)}</b>.`);
    }
  }
  addEventListener("keydown", onCaptureKey, true);

  listEl?.addEventListener("click", (e) => {
    const t = e.target.closest("button");
    if (!t || !listEl.contains(t)) return;
    const actionId = t.dataset.act;
    if (t.classList.contains("kb-add")) {
      startListen(actionId, null, true); // throwaway slot; startListen creates it
    } else if (t.classList.contains("kb-reset-one")) {
      keymap.reset(actionId);
      render();
      flashNote(`Reset <b>${nameOf(actionId)}</b> to default.`);
    } else if (t.classList.contains("kb-chip")) {
      startListen(actionId, t.dataset.code, false);
    }
  });

  // The three pilot toggles, mirrored as checkboxes. They flip the same booleans
  // and localStorage keys the I/V/T keys do, and take effect live on `input`.
  function renderPrefs() {
    if (!prefsEl) return;
    const prefs = [
      { key: "mouseAim", label: "Mouse aim steers the plane", on: input.mouseAim },
      { key: "autoTrim", label: "Auto-trim (hold altitude hands-off)", on: input.autoTrim },
      { key: "invertPitch", label: "Inverted elevator — pull mouse/keys back to climb", on: input.invertPitch },
    ];
    prefsEl.innerHTML =
      `<div class="kb-group-head">PREFERENCES</div>` +
      prefs
        .map(
          (p) =>
            `<label class="kb-pref"><input type="checkbox" data-pref="${p.key}"${p.on ? " checked" : ""}><span class="kb-pref-name">${p.label}</span></label>`,
        )
        .join("") +
      // The sensitivity slider: how far the aim travels per centimetre of
      // mouse. Scales the aim AND the freelook orbit, never the airframe's
      // response — the low-DPI mercy the flight-controls feedback asked for.
      `<label class="kb-pref kb-sens"><span class="kb-pref-name">Mouse flight sensitivity</span>
        <input type="range" data-pref="mouseSens" min="${MOUSE_SENS_MIN}" max="${MOUSE_SENS_MAX}" step="0.05" value="${input.mouseSens}">
        <span class="kb-sens-val">${input.mouseSens.toFixed(2)}×</span></label>`;
  }
  prefsEl?.addEventListener("input", (e) => {
    const sl = e.target.closest('input[type=range][data-pref="mouseSens"]');
    if (!sl) return;
    input.setMouseSens(parseFloat(sl.value));
    const val = prefsEl.querySelector(".kb-sens-val");
    if (val) val.textContent = `${input.mouseSens.toFixed(2)}×`;
  });
  prefsEl?.addEventListener("change", (e) => {
    const cb = e.target.closest("input[type=checkbox]");
    if (!cb) return;
    const on = cb.checked;
    switch (cb.dataset.pref) {
      case "mouseAim":
        input.mouseAim = on;
        localStorage.setItem("va-mouse-aim", on ? "1" : "0");
        if (!on && document.pointerLockElement) document.exitPointerLock();
        break;
      case "autoTrim":
        input.autoTrim = on;
        localStorage.setItem("va-auto-trim", on ? "1" : "0");
        input.onAutoTrim?.(on);
        break;
      case "invertPitch":
        input.invertPitch = on;
        localStorage.setItem("va-invert-pitch", on ? "1" : "0");
        break;
    }
  });

  function show() {
    // Opening mid-sortie: give the mouse back to the modal — the pointer lock
    // owns it otherwise and the checkboxes are unclickable. The next canvas
    // click after closing re-takes the lock exactly like after an Esc.
    if (document.pointerLockElement) document.exitPointerLock();
    render();
    renderPrefs();
    restoreNote();
    screen.classList.remove("hidden");
    isOpen = true;
    setCapturing(true); // freeze the game's key handling while we edit bindings
    analytics.event("settings_open");
  }
  function hide() {
    cancelListen();
    screen.classList.add("hidden");
    isOpen = false;
    setCapturing(false);
  }

  link.addEventListener("click", show);
  // The O key opens Settings from inside the cockpit — the join-screen link
  // was the only door, and a pilot who wanted to flip a toggle mid-sortie had
  // to die first. The same key closes it (see onCaptureKey above).
  input.settingsRequested = () => (isOpen ? hide() : show());
  closeBtn?.addEventListener("click", hide);
  doneBtn?.addEventListener("click", hide);
  resetBtn?.addEventListener("click", () => {
    keymap.resetAll();
    render();
    flashNote("Every control reset to its default.");
  });
  screen.addEventListener("click", (e) => {
    if (e.target === screen) hide();
  });
}

boot().catch((err) => {
  document.getElementById("join-status").textContent = `boot failed: ${err.message}`;
  console.error(err);
  analytics.exception(`boot failed: ${err?.message ?? err}`, true);
});
